import { pipelineCapacityDisplay } from '../../lib/pipeline-capacity';
import React, { useMemo, useState } from 'react';
import { meinersPipelineCapacity } from '../../lib/meiners-pipeline-capacity';
import { selectedPipelineCapacitySummary, formatOptionCapacity } from '../../lib/pipeline-option-summary';
import { isUvaldeBriefing, reconcileUvaldeChecklist, reconcileUvaldeConfidence, uvaldePowerFindings, uvaldePowerSummary, uvaldeRisks } from '../../lib/uvalde-briefing';
import { useSectionRef, useScrollSync, type HoverFeature, type BriefingSection } from './ScrollSyncProvider';
import { HoverLink } from './HoverLink';
import { SlopeHistogram } from './SlopeHistogram';
import { ElevationCrossSection } from './ElevationCrossSection';
import { ProximityRings } from './ProximityRings';
import { GradeCard } from './GradeCard';
import { KeyNumbers, type KeyNumberItem } from './KeyNumbers';
import { DimensionBar } from './DimensionBar';
import { ResolveBeforeLOI } from './ResolveBeforeLOI';
import { ConfidenceDot } from './ConfidenceDot';
import { OperatorGasZoneCallout } from './OperatorGasZoneCallout';
import { CandidateCarveOutPanel } from './CandidateCarveOutPanel';
import { OperatorSupplySection } from './OperatorSupplySection';
import { GridKillSheet, GridSitePanel, GridNodeSection } from './GridBrief';
import { SectionTitle, InfoRow, SourceFootnote, REPORT, TierEyebrow } from '../print/PrintAtoms';
import { isEntityOwner, getEntityType, formatAquiferName, floodSeverity } from '../../lib/score-helpers';
import {
  computeGateStatus, computeStructuredRisks, computePermitPathway, buildConfidenceItems,
  findFeaturedPipeline, computeResolveItems, computeEffectiveWaterTier, waterTierLabel,
  CONFIDENCE_COLORS, CONFIDENCE_LABELS, isDesertSW, BTM_CALIBRATION,
  hasLandownerEarthwork, pipelineIsEffectivelyOnSite, ownerOwnsPipeline,
  classifyAmlOverlap, isPriorityAmlOverlap, type AmlTier,
  type ConfidenceLevel, type StructuredRisk,
  lateralEstimatesSuppressed,
} from '../../lib/parcel-analysis';
import type { BriefingData, ParcelDetail, NearbyPipeline, GasSupplyResponse, SiteLayoutRow } from '../../lib/api';
import { roadSurfaceShort, feetInches } from '../../lib/api';
import { SLOPE_GRID_SOURCE, acOr, pctOr, meanOr } from './slopeRange';
import { SlopeMethodNote } from './SlopeMethodNote';
import { computeCapacityTier, CAPACITY_TIER_CONFIG } from '../../lib/grid-capacity';
import {
  buildEnvironmentalScreeningCopy,
} from '../../lib/cinco-frio-environmental';

// ---- Paces Creek Woodlands LP — Jasper Co, TX (4-parcel single-owner assembly)
// Per-parcel curated overrides for the active investor track. The auto-derived
// blocks miss this site in three ways: the nearest_* logistics columns are
// uncomputed for Jasper Co; Amp Z's Project Lufkin (the in-AOI playbook) sits
// 56 mi away — just outside the 50-mi DC auto-precedent radius; and the AOI
// precedent block currently lives in OperatorSupplySection rather than the
// permitting precedents area where it belongs alongside Sandow/Matador/GW
// Ranch. Drop these IDs out of the set when the compute jobs run for the AOI.
const PACES_PARCEL_IDS = new Set<string>([
  'a9ec56ff-f6dc-490d-9ae0-fabfa790081f',
  'd1236816-e201-48b1-9680-38902302968c',
  'c98cd978-6a07-48e4-9546-d3704d0923bc',
  'e2324b24-70ef-401a-a28d-fc223b6c4650',
]);

// Integra co-brand: shown only on the client-shared frozen briefings (Goff + Paces).
// Every other briefing defaults to Atwater-only branding; re-add per client when a
// briefing is explicitly prepared for Integra (matches api-server FROZEN_PARCEL_IDS).
const INTEGRA_COBRAND_IDS = new Set<string>([
  '967b74e0-03e4-4437-9310-4ad5e7f43872', // Goff GCSTREAM (Harrison WV) — shared link
  'a9ec56ff-f6dc-490d-9ae0-fabfa790081f', // Paces Creek Woodlands (Jasper TX) — shared link
  '132e5561-a3e7-4568-b645-c202397bad7c', // Cinco Frio Ranch (Frio TX) — Integra link 2026-08-26
]);

interface PacesPrecedent {
  name: string;
  location: string;
  mw: number;
  timeline: string;
  detail: string;
  highlight?: boolean;
}

const PACES_PRECEDENTS: PacesPrecedent[] = [
  {
    name: 'Amp Z Project Lufkin',
    location: 'Angelina Co, TX — 56 mi northwest — same East Texas corridor',
    mw: 2100,
    timeline: 'announced · in-AOI',
    detail:
      "$1.05B committed; Amp Z (Carlyle-backed). Day-one ~55 MW from re-energized Paper Mill substation; 1.1 GW grid-connected by 2028; full 2.1 GW by 2029, layering ~1 GW BTM gas on top. Live local stress-test of TX SB 6, PUCT Rule 25.194, and TCEQ permitting in unincorporated Angelina County. The working playbook this site inherits.",
    highlight: true,
  },
  {
    name: 'Sandow Lakes Energy',
    location: 'Lee Co, TX — 1,200 MW CCGT — Permit Nos. 177380, PSDTX1650',
    mw: 1200,
    timeline: '~14 months (PSD)',
    detail:
      'Contested case hearing requested by "Move the Gas Plant" coalition — denied by TCEQ Oct 2025 in a 90-second vote. Demonstrates the PSD timeline holds even with organized opposition.',
  },
  {
    name: 'Project Matador / Fermi America (Carson County)',
    location: 'Carson Co, TX — 6,000 MW — Permit Nos. 181009, PSDTX1670',
    mw: 6000,
    timeline: '~7 months (Expedited PSD)',
    detail:
      'Filed Aug 2025, approved Feb 2026. $20K Subchapter J expedited surcharge plus 90 identical Siemens SGT-800 turbines simplified TCEQ review. Largest expedited PSD on record.',
  },
  {
    name: 'GW Ranch / Pacifico Energy (Pecos County)',
    location: 'Pecos Co, TX — 7,650 MW — Jan 2026',
    mw: 7650,
    timeline: '~16 months (PSD)',
    detail:
      'Largest air permit in US history. Remote Permian Basin siting eliminated contested-case risk. TCEQ has signed off on multi-GW BTM at scale.',
  },
];

function generateVerdict(
  parcel: ParcelDetail,
  pipelines: NearbyPipeline[] | null,
  structuredRisks: StructuredRisk[],
  carveOut: import('../../lib/api').OperatorCarveOut | null,
): string {
  const acres = parcel.acreage ? Number(parcel.acreage).toLocaleString() : 'unknown';
  const landUse = parcel.land_use_category?.toLowerCase() || 'vacant';
  const county = parcel.county || 'Unknown County';
  const state = parcel.state || '??';
  const featured = findFeaturedPipeline(parcel, pipelines, carveOut?.slug);

  // ── Strengths (what the site offers) ──
  const strengths: string[] = [];

  const featuredOnSiteVerdict = pipelineIsEffectivelyOnSite(parcel, featured);
  // Crown Pine A1: no MW claim on the Texoma line (no observed utilization — the
  // ?? 50 fallback below would manufacture one). Lead with the verified facts.
  if (carveOut?.slug === MEINERS_SLUG && featured) {
    strengths.push('12.75″ Camino Real at 5.3 mi — pipeline capacity, site delivery and firm transport unconfirmed');
  } else if (carveOut?.slug === 'crownpine-blk-194-a1' && featured) {
    strengths.push('RRC-verified 30″ ET Texoma transmission crossing the site’s western band (~0.52 mi on-premises); gas availability and supply confirmed — firm transport and tap engineering remaining');
  } else if (featured) {
    const leadName = featured.name || featured.operator || 'pipeline';
    const diamNote = featured.diameter_inches ? ` ${featured.diameter_inches}\u2033` : '';
    const locator = featuredOnSiteVerdict ? 'crossing the parcel' : `at ${featured.distance_miles} mi`;
    strengths.push(`${diamNote} ${leadName} ${locator} (capacity unconfirmed)`);

    // Supply redundancy
    if (pipelines && pipelines.length > 1) {
      const withCap = pipelines.filter(p => (pipelineCapacityDisplay(p).capacityMmcf ?? 0) > 0 && Number(p.distance_miles) <= 5);
      if (withCap.length > 1) {
        const ops = [...new Set(withCap.map(p => p.operator).filter(Boolean))];
        strengths.push(`${withCap.length} pipelines from ${ops.length} operator${ops.length > 1 ? 's' : ''} within 5 mi`);
      }
    }
  } else if (pipelines && pipelines.length > 0) {
    strengths.push(`${pipelines[0].name || pipelines[0].operator || 'pipeline'} at ${pipelines[0].distance_miles} mi`);
  }

  if (parcel.in_nonattainment === false) strengths.push('EPA attainment area');
  if (parcel.avg_slope_pct != null && Number(parcel.avg_slope_pct) < 2) strengths.push(`${parcel.avg_slope_pct}% avg slope`);
  // Crown Pine A1: `is_absentee` is a tax-mailing-address datum, not evidence of
  // idle land or transaction willingness. It must never read as a site strength.
  if (parcel.is_absentee && carveOut?.slug !== 'crownpine-blk-194-a1') strengths.push('absentee owner');
  if (parcel.state === 'TX') strengths.push('no county zoning');

  // Water sourcing
  const wt = computeEffectiveWaterTier(parcel);
  const landownerWater = (parcel as any).structured_intel?.landowner_disclosures?.water_on_site;
  if (wt === 'municipal') strengths.push('municipal water');
  else if (wt === 'aquifer_no_permit') strengths.push(`groundwater (${formatAquiferName(parcel.aquifer_name) || 'aquifer'}, rule of capture)`);
  else if (wt === 'aquifer_gcd') strengths.push(`groundwater (${formatAquiferName(parcel.aquifer_name) || 'aquifer'}, GCD permit req.)`);
  else if (wt === 'on_site_well') strengths.push('on-site well potential (productive wells within 2 mi)');
  else if (landownerWater?.display_note) strengths.push('landowner-verified water on site (specs pending)');

  // Enterprise carrier route proximity is the relevant BTM signal. FCC BDC
  // coverage and pipeline-company ROWs do not establish enterprise service.
  if (carveOut?.slug === MEINERS_SLUG) {
    strengths.push('FiberLight route ~3.1 mi and Zayo route ~6.3 mi from the block boundary; service unconfirmed');
  } else if (parcel.nearest_carrier_fiber_miles != null) {
    strengths.push(`${parcel.nearest_carrier_fiber_name || 'enterprise carrier'} route ${parcel.nearest_carrier_fiber_miles} mi away`);
  }

  // ── Limitations (what needs work) ──
  const limits: string[] = [];

  if (featured && isDesertSW(featured.name || '')) limits.push('primary pipeline (DSW) under construction, 2027 in-service');
  // Lateral-cost limitation only applies when the featured pipeline is NOT effectively on site
  // (i.e. not owner-operated and not within the geometry threshold). Owner-operated pipelines
  // that cross the parcel do not require a third-party lateral build.
  if (carveOut?.slug !== MEINERS_SLUG && featured && !featuredOnSiteVerdict && featured.distance_miles != null && Number(featured.distance_miles) >= 0.5) {
    const d = Number(featured.distance_miles);
    // A1: the line crosses the proposed premises, so a distance-derived lateral
    // price is the wrong instrument \u2014 the scope and cost come from the ET study.
    if (carveOut?.slug === 'crownpine-blk-194-a1') {
      limits.push('interconnect facilities and routing scope \u2014 cost TBD pending tap engineering');
    } else {
      limits.push(lateralEstimatesSuppressed(parcel)
        ? `${d} mi lateral required`
        : `${d} mi lateral required ($${(d * 1).toFixed(1)}\u2013${(d * 2).toFixed(1)}M)`);
    }
  }
  // Water: respect landowner disclosures before flagging as unconfirmed
  const waterDisclosed = !!((parcel as any).structured_intel?.landowner_disclosures?.water_on_site?.display_note);
  if (!wt && !waterDisclosed) limits.push('water sourcing unconfirmed');
  else if (wt === 'trucked') limits.push('trucked water only (no wells within 2 mi)');
  if (wt === 'nm_closure') limits.push('NM closure area \u2014 no new water appropriations');
  if (carveOut?.slug !== MEINERS_SLUG && parcel.nearest_carrier_fiber_miles == null)
    limits.push('enterprise carrier route proximity not established');
  {
    // Flood limitation: only flag when partial/severe drainage exposure. A
    // "Mostly clear" carve-out (e.g. Paces 8.4%) is not a limitation worth
    // surfacing in the executive summary.
    const sev = floodSeverity(parcel, carveOut);
    if (sev.level === 'severe') limits.push(`${Math.round(sev.pct ?? 0)}% in FEMA Zone A \u2014 major siting constraint`);
    else if (sev.level === 'partial') limits.push(`${sev.pct != null ? Math.round(sev.pct) + '% ' : ''}drainage corridor \u2014 site-plan around`);
  }

  if (parcel.in_nonattainment) {
    const na0 = parcel.nonattainment_areas?.[0];
    limits.push(`EPA nonattainment${na0?.pollutant ? ` (${na0.pollutant})` : ''} \u2014 NNSR permitting`);
  }
  if (parcel.avg_slope_pct != null && Number(parcel.avg_slope_pct) >= 5) limits.push(`${parcel.avg_slope_pct}% avg slope \u2014 grading cost`);

  // ── Compose: strengths sentence + limitations sentence ──
  const intro = `${county}, ${state} \u2014 ${acres}-acre ${landUse} parcel.`;
  const strengthLine = strengths.length > 0 ? ` Strengths: ${strengths.join(', ')}.` : '';
  const limitLine = limits.length > 0 ? ` Limitations: ${limits.join(', ')}.` : ' No material limitations identified at screening level.';

  return intro + strengthLine + limitLine;
}

// Section intro generators
function generateFuelIntro(pipelines: NearbyPipeline[] | null, parcel: ParcelDetail, _mw: number | null, feat: NearbyPipeline | null, isA1 = false, isMeiners = false): string | null {
  if (!pipelines || pipelines.length === 0) return null;

  // Crown Pine Site A1 (carve-out focus): single-track ET gas framing. Kill the
  // generic "N MW observed headroom on <line>" lead — the position is Energy
  // Transfer's RRC-verified 30" Texoma line, which crosses the site's western
  // band, and whose capacity is pending an operator interconnect study. No
  // headroom figure is claimed anywhere on A1.
  if (isA1) {
    return "A1's gas position is direct: Energy Transfer's in-service 30″ Texoma line crosses the site's 594-acre western band for ~0.52 mi, and runs 1.35 mi from the analyzed core's western boundary. The line is RRC-verified (Energy Transfer, T-4 00749, 30″). Gas availability and supply have been confirmed, with delivery expected in tranches as project phases build out; firm transport and tap engineering are the remaining commercial steps. The verified downstream signal and the labeled screening scenario are below — no headroom figure is claimed.";
  }
  if (isMeiners) {
    return 'Camino Real is WTG Bexar Transmission’s 12.75-inch line, 5.28 miles from the full block boundary. Its total capacity is unconfirmed. Published capacities below describe entire pipeline systems or project designs; site delivery, tap rights, pressure and firm transport require operator confirmation.';
  }

  // "Supply redundancy" claims need to count tap candidates only — gathering lines
  // can't supply BTM-scale loads, so including them in the headline count would
  // overstate redundancy. Gathering still appears in the corridor section (collapsed).
  const within5 = pipelines.filter(p => Number(p.distance_miles) <= 5 && !p.is_gathering);
  const operators = [...new Set(within5.map(p => p.operator).filter(Boolean))];
  const featuredOnSite = pipelineIsEffectivelyOnSite(parcel, feat);
  const anyOnSite = featuredOnSite || pipelines.some(p => pipelineIsEffectivelyOnSite(parcel, p));

  // Prefer the registry-canonical system_name (e.g. "El Paso Natural Gas") over the
  // raw RRC subsystem name (e.g. "3000 SERIES PERMIAN NORTH FIELD FEEDER L") so the
  // intro paragraph reads as a recognizable pipeline brand to the IC.
  const featuredName = feat?.system_name || feat?.name || feat?.operator || parcel.pipeline_available_name || null;
  const featuredDiam = feat?.diameter_inches ? `${feat.diameter_inches}"` : (parcel.pipeline_available_diameter ? `${parcel.pipeline_available_diameter}"` : null);

  let intro = '';
  if (featuredName) {
    const diamNote = featuredDiam ? ` (${featuredDiam})` : '';
    const published = feat ? pipelineCapacityDisplay(feat) : null;
    const capacityNote = published?.capacityMmcf != null
      ? ` Published ${published.capacityMmcf.toLocaleString()} MMcf/d ${published.label.toLowerCase()} remains scoped to its source.`
      : ' Segment capacity and site deliverability are unconfirmed.';
    intro = `${featuredName}${diamNote} ${featuredOnSite ? 'crosses the property' : `is ${feat?.distance_miles ?? 'nearby'} miles from the property`}.${capacityNote}`;
  } else if (anyOnSite) {
    intro = `Pipeline infrastructure crosses the property boundary.`;
  } else {
    intro = `${pipelines.length} pipeline system${pipelines.length > 1 ? 's' : ''} identified within 10 miles.`;
  }

  // Redundancy signal
  if (within5.length > 0) {
    intro += ` ${within5.length} system${within5.length > 1 ? 's' : ''} within 5 miles across ${operators.length} operator${operators.length > 1 ? 's' : ''}.`;
  }

  // Lateral / crossing-parcel note. We deliberately do NOT claim "no lateral
  // construction" for any crossing line — without operator confirmation we
  // cannot assert pipe quality (transmission vs raw-gas gathering) or that
  // a tap is grantable. The framing is geometry + interconnect-study path.
  if (featuredOnSite) {
    const ownerOps = ownerOwnsPipeline(parcel.owner_name, feat!);
    intro += ownerOps
      ? ' Pipeline crosses the parcel and is operated by the landowner — tap grantable on-parcel.'
      : ' Pipeline crosses the parcel — geometry signal only; tap, receipt point, and firm-transport availability subject to a standard operator interconnect study.';
  } else if (anyOnSite) {
    intro += ' At least one pipeline crosses the property boundary.';
  }

  return intro;
}

function generateLandIntro(parcel: ParcelDetail): string {
  const entityType = isEntityOwner(parcel.owner_name) ? getEntityType(parcel.owner_name) : null;
  // Entities don't "reside" anywhere — use "locally-based" for a non-absentee entity.
  // Also fixes the a/an article agreement for the individual-owner branch.
  const ownerDesc = entityType
    ? (parcel.is_absentee
        ? `an absentee ${entityType.toLowerCase()} entity`
        : `a locally-based ${entityType.toLowerCase()} entity`)
    : (parcel.is_absentee ? 'an absentee individual owner' : 'a resident individual owner');
  const colocated = parcel.colocated_owners?.length || 0;
  const assemblyNote = colocated > 2 ? `, with ${colocated + 1} total owners suggesting potential land assembly complexity` : colocated > 0 ? `, with ${colocated + 1} total owners on the site` : '';
  const advisory = parcel.is_absentee
    ? 'Absentee ownership typically signals greater willingness to transact.'
    : entityType
      ? 'Locally-based entity ownership — transact directly via the operating principal.'
      : 'Resident ownership may require more engaged negotiation.';
  return `The parcel is held by ${ownerDesc}${assemblyNote}. ${advisory}`;
}

function generateRiskIntro(risks: StructuredRisk[]): string | null {
  if (risks.length === 0) return null;
  const hardFails = risks.filter(r => r.severity === 'HIGH').length;
  const softFails = risks.length - hardFails;
  if (hardFails > 0) return `${hardFails} hard gate failure${hardFails > 1 ? 's' : ''} and ${softFails} additional risk factor${softFails !== 1 ? 's' : ''} identified. Hard gate failures typically require mitigation before advancing to diligence.`;
  return `${risks.length} risk factor${risks.length > 1 ? 's' : ''} identified, none representing a fatal flaw. Each has a defined next step for resolution.`;
}

// --- Shared table header style ---

const TH: React.CSSProperties = {
  padding: '4px 6px', fontWeight: 500, fontSize: '9px',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  color: REPORT.secondary, background: REPORT.surface,
  fontFamily: 'monospace',
};

// --- Pipeline table with collapsible non-Desert-SW rows ---

// isDesertSW imported from parcel-analysis

// The A1 carve-out identity, renamed server-side in api-server/routes/briefing.js —
// these strings exist in no other payload, so keying copy off them keeps every other
// briefing's pipeline table byte-identical.
const isA1TexomaRow = (pl: { name?: string | null; system_name?: string | null }) =>
  pl.system_name === 'Energy Transfer Texoma' || pl.name === 'Energy Transfer Texoma 30″';

const PipelineRow: React.FC<{ pl: NearbyPipeline; parcel: ParcelDetail; isMeiners?: boolean; onHover?: (f: HoverFeature) => void; expanded?: boolean; onToggle?: () => void }> = ({ pl, parcel, isMeiners = false, onHover, expanded, onToggle }) => {
  const desert = isDesertSW(pl.name);
  const headline = !!pl.headline_override || !!pl.co_headline;
  const knownCapacity = isMeiners ? meinersPipelineCapacity(pl) : null;
  // Headline row gets the same visual prominence DSW corridor rows get — sand background +
  // highlight border-left — so the IC can visually parse "this is the recommended tap" even
  // before reading the row content. DSW + headline coincidence: highlight wins.
  const highlight = headline || desert;
  const hasSegments = pl.segments && pl.segments.length > 1;
  const isOnSite = pipelineIsEffectivelyOnSite(parcel, pl);
  const segmentKeys = useMemo(() => {
    const keys = new Set<string>();
    const add = (n?: string | null, o?: string | null) => keys.add(`${n || ''}|${o || ''}`);
    add(pl.name, pl.operator);
    if (pl.segments) for (const s of pl.segments) add(s.name, s.operator);
    return Array.from(keys);
  }, [pl]);
  return (
    <>
    <tr
      style={{
        borderBottom: `1px solid ${REPORT.rule}80`,
        background: highlight ? REPORT.sand : undefined,
        cursor: hasSegments ? 'pointer' : (onHover ? 'pointer' : undefined),
      }}
      onClick={hasSegments ? onToggle : undefined}
      onMouseEnter={() => onHover?.({ type: 'pipeline', name: pl.name || pl.operator || 'Unknown', operator: pl.operator, segmentKeys })}
      onMouseLeave={() => onHover?.(null)}
    >
      <td style={{ padding: '3px 4px', overflow: 'hidden', color: REPORT.body }}>
        <div style={{ fontWeight: highlight ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: highlight ? REPORT.highlight : undefined }}>
          {headline && <span title={pl.co_headline && !pl.headline_override ? 'Featured tap candidate' : 'Featured tap candidate — matches the briefing headline'} style={{ fontSize: '9px', background: REPORT.highlight, color: REPORT.white, padding: '2px 8px', borderRadius: '3px', marginRight: '4px', fontWeight: 600, letterSpacing: '0.03em', fontFamily: 'monospace', textTransform: 'uppercase' as const }}>Headline</span>}
          {pl.is_small_transmission && !headline && <span title="Small transmission line (< 6&quot; diameter). Technically gas transmission per RRC, but physically can't supply a 200 MW+ BTM project — operator confirmation needed if considering for early-phase load." style={{ fontSize: '9px', background: REPORT.caution + '20', color: REPORT.caution, padding: '2px 6px', borderRadius: '3px', marginRight: '4px', fontWeight: 600, letterSpacing: '0.03em', fontFamily: 'monospace', textTransform: 'uppercase' as const }}>Small</span>}
          {desert && !headline && <span style={{ fontSize: '9px', background: REPORT.sand, color: REPORT.body, padding: '2px 8px', borderRadius: '3px', marginRight: '4px', fontWeight: 500, letterSpacing: '0.03em', fontFamily: 'monospace', textTransform: 'uppercase' as const }}>Corridor</span>}
          <HoverLink feature={{ type: 'pipeline', name: pl.name || pl.operator || 'Unknown', operator: pl.operator, segmentKeys }}>
            {pl.system_display_name || pl.system_name || pl.name || pl.operator || 'Unknown'}
          </HoverLink>
        </div>
        <div style={{ fontSize: '9px', color: REPORT.tertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pl.operator}
          {(pl as any).operator_parent && !(pl.operator || "").toLowerCase().startsWith((pl as any).operator_parent.toLowerCase()) && <span> · {(pl as any).operator_parent}</span>}
          {hasSegments && <span style={{ cursor: 'pointer' }}> · {expanded ? '\u25BC' : '\u25B6'} {pl.segment_count} segments</span>}
        </div>
      </td>
      <td style={{ textAlign: 'right', padding: '3px 4px', color: REPORT.body }} title={isOnSite ? 'Pipeline geometry crosses the parcel boundary — does not imply an existing tap or receipt point' : undefined}>{isOnSite ? 'crosses parcel' : `${pl.distance_miles} mi`}</td>
      <td style={{ textAlign: 'right', padding: '3px 4px', color: REPORT.body }}>
        {pl.diameter_inches ? `${pl.diameter_inches}\u2033` : '\u2014'}
      </td>
      <td style={{ textAlign: 'right', padding: '3px 4px', color: REPORT.body }}>
        {pl.maop_psig ? `${Number(pl.maop_psig).toLocaleString()} psi` : '\u2014'}
        <ConfidenceDot
          level={pl.maop_psig ? 'confirmed' : 'pending'}
          source={pl.maop_psig ? (pl.maop_source || 'FERC Form 2') : 'Operator confirmation needed'}
        />
      </td>
      <td style={{ textAlign: 'right', padding: '3px 4px', color: REPORT.body }} title={knownCapacity ? knownCapacity.detail : pipelineCapacityDisplay(pl).capacityMmcf != null ? `${pipelineCapacityDisplay(pl).label}: ${pipelineCapacityDisplay(pl).capacityMmcf!.toLocaleString()} MMcf/d` : 'Diameter unknown \u2014 capacity to confirm with operator'}>
        {(() => {
          if (knownCapacity) return <><span>{knownCapacity.value}</span><span style={{ display: 'block', fontSize: '8px', color: REPORT.tertiary }}>{knownCapacity.sourceUrl && knownCapacity.value !== 'Unconfirmed' ? <a href={knownCapacity.sourceUrl} target="_blank" rel="noopener noreferrer" title={knownCapacity.sourceTitle} onClick={e => e.stopPropagation()} style={{ color: 'inherit' }}>{knownCapacity.label}</a> : knownCapacity.label}</span></>;
          const dot = (pl.data_gap_segment_count != null && pl.data_gap_segment_count > 0) ? (
            <span title={`${pl.data_gap_segment_count} segment${pl.data_gap_segment_count === 1 ? '' : 's'} with diameter unknown \u2014 verify with operator`}
              style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: REPORT.caution, marginLeft: 4, verticalAlign: 'middle', cursor: 'help' }} />
          ) : null;
          const published = pipelineCapacityDisplay(pl).capacityMmcf;
          return <>{published != null ? `${published.toLocaleString()} MMcf/d` : '\u2014'}{dot}</>;
        })()}
      </td>
      <td style={{ textAlign: 'right', padding: '3px 4px', color: REPORT.body }} title="Published capacity is scoped to its source and does not establish local delivery or available firm capacity.">
        {isMeiners ? 'Unconfirmed' : pipelineCapacityDisplay(pl).capacityMmcf != null ? `${pipelineCapacityDisplay(pl).capacityMmcf!.toLocaleString()} MMcf/d` : 'Unconfirmed'}
      </td>
    </tr>
    {expanded && pl.segments && pl.segments.map((seg, si) => {
      const segCapacity = pipelineCapacityDisplay(seg);
      const knownSegmentCapacity = isMeiners ? meinersPipelineCapacity(seg) : null;
      return (
        <tr key={si} style={{ borderBottom: `1px solid ${REPORT.rule}40`, background: REPORT.surface }}>
          <td style={{ padding: '2px 4px 2px 16px', fontSize: '9px', color: REPORT.secondary, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            <div style={{ fontWeight: 500 }}>
              {seg.name || seg.operator || 'Segment'}
              {seg.segment_count != null && seg.segment_count > 1 && (
                <span title={`${seg.segment_count} segments share this subsystem and diameter — collapsed into one row.`}
                  style={{ color: REPORT.tertiary, marginLeft: 4, fontSize: '8.5px' }}>
                  ×{seg.segment_count}
                </span>
              )}
            </div>
            {seg.subsystem && (() => {
              const sub = seg.subsystem.replace(/^\d+[-\s]*/, '').replace(/\*$/, '').trim();
              return <div style={{ color: REPORT.tertiary, fontSize: '8.5px', marginTop: 1 }} title={seg.subsystem}>{sub}</div>;
            })()}
          </td>
          <td style={{ textAlign: 'right', padding: '2px 4px', fontSize: '9px', color: REPORT.secondary }} title={seg.distance_range ? `Closest ${seg.distance_range.min} mi · farthest ${seg.distance_range.max} mi (${seg.segment_count ?? '?'} segments)` : undefined}>
            {seg.distance_range && seg.distance_range.min !== seg.distance_range.max
              ? `${seg.distance_range.min}–${seg.distance_range.max} mi`
              : `${seg.distance_miles} mi`}
          </td>
          <td style={{ textAlign: 'right', padding: '2px 4px', fontSize: '9px', color: REPORT.secondary }}>
            {seg.diameter_inches ? `${seg.diameter_inches}\u2033` : '\u2014'}
          </td>
          <td style={{ textAlign: 'right', padding: '2px 4px', fontSize: '9px', color: REPORT.secondary }}>
            {seg.maop_psig ? `${Number(seg.maop_psig).toLocaleString()} psi` : '\u2014'}
          </td>
          <td style={{ textAlign: 'right', padding: '2px 4px', fontSize: '9px', color: REPORT.secondary }} title={knownSegmentCapacity ? knownSegmentCapacity.detail : 'Published capacity is scoped to its source; local delivery remains unconfirmed.'}>
            {knownSegmentCapacity ? knownSegmentCapacity.value : segCapacity.capacityMmcf != null ? `${segCapacity.capacityMmcf.toLocaleString()} MMcf/d` : '\u2014'}
          </td>
          <td style={{ textAlign: 'right', padding: '2px 4px', fontSize: '9px', color: REPORT.secondary }}>
            Unconfirmed
          </td>
        </tr>
      );
    })}
    </>
  );
};

const PipelineTableHead = ({ isMeiners = false }: { a1?: boolean; isMeiners?: boolean }) => (
  <thead>
    <tr style={{ borderBottom: `1px solid ${REPORT.rule}` }}>
      <th style={{ ...TH, textAlign: 'left' }}>Pipeline</th>
      <th style={{ ...TH, textAlign: 'right' }}>Distance</th>
      <th style={{ ...TH, textAlign: 'right' }} title="Pipe outside diameter in inches. Larger diameter = more gas throughput capacity. ≥20&quot; is considered large transmission.">Diameter</th>
      <th style={{ ...TH, textAlign: 'right' }} title="Maximum Allowable Operating Pressure from FERC and state regulator filings. Higher pressure = more gas throughput. Data pending operator confirmation on most lines.">Pressure</th>
      <th style={{ ...TH, textAlign: 'right' }} title={isMeiners ? 'Published total system capacity or project design capacity; not a site-delivery commitment.' : 'Published capacity in native MMcf/d units, when explicitly sourced. Diameter and source scope remain separate facts.'}>{isMeiners ? 'Total capacity' : 'Published capacity'}</th>
      <th style={{ ...TH, textAlign: 'right' }} title="Site delivery and firm transportation require operator confirmation.">{isMeiners ? 'Site delivery' : 'Site delivery'}</th>
    </tr>
  </thead>
);

const PIPELINE_TABLE_STYLE: React.CSSProperties = { width: '100%', fontSize: '10.5px', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: '6px' };
const PIPELINE_COLGROUP = (
  <colgroup>
    <col style={{ width: '24%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '9%' }} />
    <col style={{ width: '12%' }} />
    <col style={{ width: '17%' }} />
    <col style={{ width: '14%' }} />
  </colgroup>
);

const PipelineTable: React.FC<{ pipelines: NearbyPipeline[]; parcel: ParcelDetail; isMeiners?: boolean; onHover?: (f: HoverFeature) => void }> = ({ pipelines, parcel, isMeiners = false, onHover }) => {
  const [othersExpanded, setOthersExpanded] = useState(false);
  const [gatheringExpanded, setGatheringExpanded] = useState(false);
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());

  const toggleSystem = (id: string) => setExpandedSystems(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Server-side ordering: pinFeaturedSystem moved the headline pipeline to index 0
  // and sorted the rest by AVAILABLE MW DESC (war-room decision). Server already caps
  // each bucket separately (100 transmission + 50 gathering raw segments → typically
  // ~5 tap + ~10–15 gathering grouped systems), so no client slice is needed here —
  // every system the server returns is decision-relevant or context-relevant.
  // Headline-overridden rows always count as tap candidates regardless of underlying type.
  const sorted = pipelines;
  const tapCandidates = useMemo(
    () => sorted.filter(p => !p.is_gathering || p.headline_override),
    [sorted]
  );
  const gatheringLines = useMemo(
    () => sorted.filter(p => p.is_gathering && !p.headline_override),
    [sorted]
  );

  // Show top 5 tap candidates always, collapse the rest. Headline (index 0)
  // always visible. The 5-row threshold (vs the historical 3) keeps Magasco +
  // 14" Gulf South + TGP 100 + Jasper-CS-anchor lines all in the visible list
  // for East Texas briefings without hiding TGP behind an expand toggle.
  const shouldCollapse = tapCandidates.length > 5;
  const topRows = shouldCollapse ? tapCandidates.slice(0, 5) : tapCandidates;
  const otherRows = shouldCollapse ? tapCandidates.slice(5) : [];

  return (
    <>
      <table style={PIPELINE_TABLE_STYLE}>
        {isMeiners ? <colgroup><col style={{ width: '30%' }} /><col style={{ width: '10%' }} /><col style={{ width: '9%' }} /><col style={{ width: '11%' }} /><col style={{ width: '24%' }} /><col style={{ width: '16%' }} /></colgroup> : PIPELINE_COLGROUP}
        <PipelineTableHead a1={pipelines.some(isA1TexomaRow)} isMeiners={isMeiners} />
        <tbody>
          {topRows.map(pl => <PipelineRow key={pl.id} pl={pl} parcel={parcel} isMeiners={isMeiners} onHover={onHover} expanded={expandedSystems.has(pl.id)} onToggle={() => toggleSystem(pl.id)} />)}
        </tbody>
      </table>
      {otherRows.length > 0 && (
        <>
          <div
            onClick={() => setOthersExpanded(!othersExpanded)}
            title="Tap candidates: gas transmission pipelines (interstate, intrastate, or RRC-classified gas transmission) that could physically supply a BTM data center. Excludes gas gathering lines, which collect from wells and are too small to support a 200 MW+ load."
            style={{
              fontSize: '9px', color: REPORT.tertiary, cursor: 'pointer',
              padding: '4px 0', userSelect: 'none',
            }}
          >
            {othersExpanded ? '\u25BC' : '\u25B6'} {otherRows.length} other tap candidate{otherRows.length > 1 ? 's' : ''} within range
          </div>
          {othersExpanded && (
            <table style={PIPELINE_TABLE_STYLE}>
              {isMeiners ? <colgroup><col style={{ width: '30%' }} /><col style={{ width: '10%' }} /><col style={{ width: '9%' }} /><col style={{ width: '11%' }} /><col style={{ width: '24%' }} /><col style={{ width: '16%' }} /></colgroup> : PIPELINE_COLGROUP}
              <tbody>
                {otherRows.map(pl => <PipelineRow key={pl.id} pl={pl} parcel={parcel} isMeiners={isMeiners} onHover={onHover} expanded={expandedSystems.has(pl.id)} onToggle={() => toggleSystem(pl.id)} />)}
              </tbody>
            </table>
          )}
        </>
      )}
      {gatheringLines.length > 0 && (
        <>
          <div
            onClick={() => setGatheringExpanded(!gatheringExpanded)}
            title="Gathering lines connect wells to the main pipeline. They are not BTM tap candidates — a single gathering line cannot supply a 200 MW+ project. Listed for corridor context."
            style={{
              fontSize: '9px', color: REPORT.tertiary, cursor: 'pointer',
              padding: '4px 0', userSelect: 'none',
            }}
          >
            {gatheringExpanded ? '▼' : '▶'} {gatheringLines.length} gathering line{gatheringLines.length > 1 ? 's' : ''} in corridor (not tap candidates)
          </div>
          {gatheringExpanded && (
            <table style={PIPELINE_TABLE_STYLE}>
              {isMeiners ? <colgroup><col style={{ width: '30%' }} /><col style={{ width: '10%' }} /><col style={{ width: '9%' }} /><col style={{ width: '11%' }} /><col style={{ width: '24%' }} /><col style={{ width: '16%' }} /></colgroup> : PIPELINE_COLGROUP}
              <tbody>
                {gatheringLines.map(pl => <PipelineRow key={pl.id} pl={pl} parcel={parcel} isMeiners={isMeiners} onHover={onHover} expanded={expandedSystems.has(pl.id)} onToggle={() => toggleSystem(pl.id)} />)}
              </tbody>
            </table>
          )}
        </>
      )}
      <SourceFootnote>
        Source: {parcel.state === 'TX' ? 'TX RRC pipeline permits, ' : ''}EIA natural gas pipelines, PHMSA NPMS. MAOP from FERC Form 2 filings where available.
      </SourceFootnote>
    </>
  );
};

interface MapSnapshots {
  cover: string;
  fuelDelivery: string;
  infrastructure: string;
}

interface BriefingContentProps {
  data: BriefingData;
  mapSnapshots?: MapSnapshots | null;
  parcelId?: string;
  token?: string;
  // Grid lens: current lens + change handler. Being an in-app (non-token) view
  // is necessary but no longer sufficient — SiteBriefing passes onLensChange
  // only when there is also curated node intel behind the grid rubric
  // (data.gridLensAvailable) or the viewer forced the lens with ?profile=grid.
  // Share/token pages never get it, so the protected client links can't change.
  lensProfile?: 'btm' | 'grid';
  onLensChange?: (profile: 'btm' | 'grid') => void;
  // Which outline the map's SITE LAYOUT switcher is showing (A1 Alt edition
  // only — 'original' everywhere else). Drives the hero acreage tile, the Site
  // Intelligence banner and the highlighted row of the layouts table; nothing
  // scored reads it.
  siteLayout?: string;
}

const MapSnapshot: React.FC<{ src: string; caption: string; maxHeight?: string }> = ({ src, caption, maxHeight }) => (
  <div className="briefing-map-snapshot" style={{ display: 'none', marginBottom: '12px' }}>
    <img src={src} alt={caption} style={{ width: '100%', borderRadius: '4px', border: `1px solid ${REPORT.rule}`, ...(maxHeight ? { maxHeight, objectFit: 'cover' as const } : {}) }} />
    <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '2px', textAlign: 'center' }}>{caption}</div>
  </div>
);

// ── Shared Power Economics Constants ──
// Single source of truth — used by both Gas Supply and Power Infrastructure sections
// ANNUAL REFRESH: Update ERCOT_4CP_RATE after PUCT publishes (Sep each year)
const ERCOT_4CP_RATE = 66.76;         // $/kW-yr, 2025 PUCT rate
const ERCOT_4CP_YEAR = 2025;
const ERCOT_4CP_PER_MWH = 8;          // 4CP at ~95% CF: $66.76 / 8.76 MWh ≈ $7.62, rounded
const TDU_DELIVERY_PER_MWH = 4;       // typical large industrial, varies by TDU territory
const GRID_ADDER = ERCOT_4CP_PER_MWH + TDU_DELIVERY_PER_MWH;
const GRID_RANGE_SPREAD = 10;         // range width for delivery + ancillary variability
// BTM LCOE uses DELIVERED gas (hub + transport + lateral), not the Waha index
const DELIVERED_GAS_CURRENT = 0.50;    // $/MMBtu, current Permian delivered (hub ~$0 + transport)
const DELIVERED_GAS_NORMALIZED = 1.50; // $/MMBtu, post-pipeline-buildout normalized
const HEAT_RATE = 8500;               // BTU/kWh, recip engine / simple cycle CT
const BTM_LCOE_LOW = 21;              // recip/CT, current Permian delivered gas
const BTM_LCOE_HIGH = 40;             // recip/CT, normalized Permian delivered

// ── Data Coverage Banner ──
// For states outside our primary ingest scope (TX/NM/AZ), surface which national
// layers are not yet ingested. Prevents silent zeros from reading as measured zeros.
// When an ingest expands to cover a state, remove the layer from the list here.
const PRIMARY_COVERAGE_STATES = new Set(['TX', 'NM', 'AZ']);
const DEFAULT_UNINGESTED_LAYERS = [
  'County zoning & land-use rules',
  'Parcel universe & acquisition grading',
  'Water regulation boundaries (GCD / AMA / NM closure)',
];
// Per-state overrides — list only what is still un-ingested for that state.
// WV: Harrison has county zoning (migration 124) + SWPA zones (WVDHHR) — both covered.
const UNINGESTED_LAYERS_BY_STATE: Record<string, string[]> = {
  WV: [],
};

const CoverageBanner: React.FC<{ state: string | null | undefined; hasGrades?: boolean }> = ({ state, hasGrades }) => {
  if (!state || PRIMARY_COVERAGE_STATES.has(state)) return null;
  const base = UNINGESTED_LAYERS_BY_STATE[state] ?? DEFAULT_UNINGESTED_LAYERS;
  const layers = base.filter(l =>
    !(hasGrades && l === 'Parcel universe & acquisition grading')
  );
  if (layers.length === 0) return null;
  return (
    <div style={{
      marginBottom: '12px', padding: '10px 12px',
      border: `1px solid ${REPORT.caution}40`, background: `${REPORT.caution}08`,
      borderRadius: '4px', fontSize: '9px', lineHeight: 1.5, color: REPORT.body,
    }}>
      <div style={{ fontWeight: 700, color: REPORT.caution, marginBottom: '4px', letterSpacing: '0.04em' }}>
        REGIONAL COVERAGE — {state}
      </div>
      <div style={{ color: REPORT.secondary, marginBottom: '4px' }}>
        Pipeline Prospector's primary ingest scope is TX / NM / AZ. National layers below are not yet ingested for {state}; zeros or blanks in those sections mean "not measured," not "none present."
      </div>
      <ul style={{ margin: '4px 0 0 16px', padding: 0, color: REPORT.tertiary }}>
        {layers.map(l => <li key={l}>{l}</li>)}
      </ul>
      <div style={{ marginTop: '6px', color: REPORT.tertiary, fontStyle: 'italic' }}>
        Pipelines (EIA), power plants / substations / transmission (HIFLD), EPA nonattainment, EJScreen, FEMA flood, FCC fiber, USGS terrain — all render for {state}.
      </div>
    </div>
  );
};

// ── Gas Supply & Logistics Section ──

type FindingType = 'FAVORABLE' | 'CAUTION' | 'UNFAVORABLE' | 'INFO';
export type DDStatus = 'ADDRESSED' | 'FLAGGED' | 'REQUIRES INQUIRY';

const FINDING_COLORS: Record<FindingType, string> = {
  FAVORABLE: REPORT.tierOk, CAUTION: REPORT.tierWarn, UNFAVORABLE: REPORT.tierFail, INFO: REPORT.tierInfo,
};
const DD_COLORS: Record<DDStatus, string> = {
  ADDRESSED: REPORT.pass, FLAGGED: REPORT.caution, 'REQUIRES INQUIRY': REPORT.secondary,
};

interface MeinersBriefingOverrides {
  gateSummary: string;
  risks: Array<{ title: string; description: string; severity: 'HIGH' | 'MED'; mitigation: string }>;
}

const MEINERS_SLUG = 'meiners-ranch-la-salle';
const COOPER_DRAW_SLUG = 'cooper-draw-ranch';
const MEINERS_UTILITY_LEADS = 'Serving utility unconfirmed. Karnes Electric and Medina Electric are leads from overlapping service-territory maps; confirm the parcel-specific provider, delivery point and auxiliary capacity with the co-ops.';

const MEINERS_BRIEFING_OVERRIDES: MeinersBriefingOverrides = {
    gateSummary: 'PRE-FEASIBILITY — SURFACE CONTROL UNRESOLVED',
    risks: [
      {
        title: 'Surface rights and pad exclusions',
        description: 'RRC attributes 14 producers; Enverus verifies 12 active and 1 inactive, with the Gray B1H producer outside the export. INEOS operates 11 attributed producers and FW Eagle Ford I operates 3.',
        severity: 'HIGH',
        mitigation: 'Obtain both operators’ surface-use positions, pad/flowline exclusion map, easements and drilling plans; verify Gray B1H and the five 2017 Gray Unit II locations.',
      },
      {
        title: 'Gas delivery',
        description: 'Camino Real is a nearby transmission tap candidate, but lateral route, pressure, tap rights, receipt point and firm transport remain unconfirmed.',
        severity: 'MED',
        mitigation: 'Obtain WTG’s operator response and interconnect study.',
      },
      {
        title: 'Flood and layout',
        description: 'The TWDB 1% screen maps 23% of the block, concentrated along Cibolo Creek.',
        severity: 'MED',
        mitigation: 'Confirm hydrology and route the campus around the mapped corridor, well exclusion rings and gathering rights-of-way before advancing.',
      },
    ],
};

function getMeinersBriefingOverrides(slug?: string | null): MeinersBriefingOverrides | null {
  return slug === MEINERS_SLUG ? MEINERS_BRIEFING_OVERRIDES : null;
}

const GasSupplyLogisticsSection = React.forwardRef<HTMLDivElement, { data: BriefingData; parcel: ParcelDetail }>(
  ({ data, parcel }, ref) => {
    const isUvalde = isUvaldeBriefing(data);
    const isMeiners = data.carveOutAggregate?.slug === MEINERS_SLUG;
    const gi = data.gasInfrastructure;
    const safety = data.pipelineSafety;
    const pipelines = data.pipelines || [];
    const featured = pipelines[0];

    const nearestCompressor = gi?.compressors?.[0];
    const nearestReceipt = gi?.receipt_delivery?.[0];
    const nearestInterconnect = gi?.interconnects?.[0];
    const nearestStorage = gi?.storage?.[0];
    const landownerDelivery = (parcel as any).structured_intel?.landowner_disclosures?.delivery_point_on_site;
    const gasSupplyZone = (data as any).gasSupplyZone as { operatorName?: string; assetName?: string; peakBcfd?: number; peakYear?: number } | undefined;
    // AOI framing follows the ACTIVE zone payload, not parcels.gas_supply_zone_slug.
    // The slug is a stale column left on parcels whose zone has since been archived
    // (Crown Pine A1 and Paces both still carry 'revenant-east-tx' with no live
    // zone), so keying off it rendered operator-gathering "firm path" language for
    // a commitment that no longer backs the page.
    const isAoi = !!gasSupplyZone;

    // 15-mi threshold (vs the historical 10) — for BTM corridor analysis a
    // 12-13 mi compressor with substantial HP is operationally relevant: it
    // indicates the operator runs pressure-control infrastructure in the
    // corridor and provides a defensible lateral anchor (CSs typically have
    // receipt/delivery headers, regulators, and bypass valves engineered
    // for taps). Stricter "on-parcel" check (<0.5 mi) is preserved in the DD
    // checklist where it gates "operator-confirmed delivery point" framing.
    const hasCompressor = nearestCompressor && Number(nearestCompressor.distance_miles) <= 15;
    const hasReceipt = nearestReceipt && Number(nearestReceipt.distance_miles) <= 10;
    const hasInterconnect = nearestInterconnect && Number(nearestInterconnect.distance_miles) <= 10;
    const incidentCount = safety?.total_incidents || 0;
    const seriousCount = safety?.serious || 0;

    // ── Assessment findings ──
    const findings: Array<{ label: string; type: FindingType; desc: string; detail?: string }> = [];

    // 0. Operator-committed gathering (AOI parcels only) — the firm path.
    //    Public midstream below is supplementary; the operator (Revenant for
    //    revenant-east-tx, etc.) supplies the BTM site directly via bespoke
    //    gathering. This is the commercial commitment, not a market thesis.
    if (isAoi && gasSupplyZone) {
      const op = gasSupplyZone.operatorName || 'Zone operator';
      const peak = gasSupplyZone.peakBcfd != null
    ? `${Number(gasSupplyZone.peakBcfd).toFixed(1)} Bcf/d native system forecast`
        : 'multi-Bcf/d';
      const peakYr = gasSupplyZone.peakYear ? ` by ${gasSupplyZone.peakYear}` : '';
      findings.push({
        label: 'Operator Gathering · Firm Path',
        type: 'FAVORABLE',
        // Canonical phrasing — same wording used in the DD checklist's
        // "Operator gathering commitment" item, the BriefingContent What
        // This Means lede, and the v2 zone callout / supply story.
        desc: `${op} has indicated they will build operator gathering to qualified BTM buyers in the AOI — ${peak} peak${peakYr}, drawn from operator-controlled acreage and a multi-decade drilling inventory.`,
        detail: 'Public-midstream tap candidates below are redundancy and scale optionality — not the firm path.',
      });
    }

    // 1. Nearest compressor station
    if (hasCompressor) {
      const hp = nearestCompressor.horsepower;
      const methane = nearestCompressor.methane_mt;
      // Quartile thresholds from NAICS 486210 pipeline compressors (Q1=77.3, Q3=685.5 MT/yr, 2023 data)
      // Update after running ingest-epa-ghgrp.js with new --year data
      const methaneTier = methane != null
        ? methane < 77 ? 'Low emitter' : methane < 686 ? 'Moderate emitter' : 'High emitter'
        : null;
      findings.push({
        label: 'Compressor Station',
        type: hp && hp >= 10000 ? 'FAVORABLE' : 'INFO',
        desc: `${nearestCompressor.name || nearestCompressor.operator || 'Unnamed'} at ${nearestCompressor.distance_miles} mi${hp ? ` — ${hp.toLocaleString()} HP` : ''}${nearestCompressor.num_units ? `, ${nearestCompressor.num_units} units` : ''}`,
        detail: [
          nearestCompressor.pipeline_company ? `Pipeline: ${nearestCompressor.pipeline_company}` : nearestCompressor.operator ? `Operator: ${nearestCompressor.operator}` : null,
          methaneTier ? `CH₄: ${methaneTier} (${Math.round(methane)} MT/yr)` : null,
        ].filter(Boolean).join(' · ') || undefined,
      });
    } else {
      findings.push({
        label: 'Compressor Station',
        type: 'INFO',
        desc: nearestCompressor ? `Nearest at ${nearestCompressor.distance_miles} mi (outside 15 mi corridor)` : 'No compressor station data in area',
        detail: 'Compressor stations indicate active, high-flow pipeline segments and are typically engineered with receipt/delivery headers that simplify tap design.',
      });
    }

    // 2. Interconnect / Receipt Point (where to tap in)
    if (landownerDelivery?.display_note) {
      findings.push({
        label: 'Pipeline Receipt/Delivery Point',
        type: 'FAVORABLE',
        desc: landownerDelivery.display_note,
        detail: landownerDelivery.source ? `Source: ${landownerDelivery.source}` : 'Operator-confirmed delivery point per landowner disclosure.',
      });
    } else if (hasReceipt) {
      findings.push({
        label: 'Pipeline Receipt/Delivery Point',
        type: 'FAVORABLE',
        desc: `${nearestReceipt.name || 'Unnamed'} at ${nearestReceipt.distance_miles} mi${nearestReceipt.capacity_dth_day ? ` — ${Number(nearestReceipt.capacity_dth_day).toLocaleString()} Dth/d max capacity` : ''}`,
        detail: nearestReceipt.pipeline_company ? `Pipeline: ${nearestReceipt.pipeline_company}` : undefined,
      });
    } else if (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1') {
      // A1: no finding card. The landowner "delivery point on site" disclosure was
      // removed 2026-08-09 (there is no delivery point on the tract), and the generic
      // fallback would leave an empty-shell card in its place. Nothing is hidden: the
      // nearest PHMSA-mapped receipt point is still reported in the DD checklist's
      // "Interconnects (wholesale/retail)" row, and the gas panel states the position
      // is a new tap on the transmission line 1.35 mi west.
    } else {
      findings.push({
        label: 'Pipeline Receipt/Delivery Point',
        type: nearestReceipt ? 'CAUTION' : 'UNFAVORABLE',
        desc: nearestReceipt ? `Nearest at ${nearestReceipt.distance_miles} mi` : 'No receipt/delivery points in area',
        detail: 'Receipt/delivery points are physical locations where gas enters or leaves a pipeline — potential interconnect sites.',
      });
    }

    // 3. Pipeline interconnect (pipeline-to-pipeline)
    if (hasInterconnect) {
      const companies = nearestInterconnect.connecting_companies?.filter(Boolean) || [];
      findings.push({
        label: 'Pipeline Interconnect',
        type: 'FAVORABLE',
        desc: `${nearestInterconnect.name !== 'NOT AVAILABLE' ? nearestInterconnect.name || '' : ''}${companies.length > 1 ? companies.join(' ↔ ') : companies[0] || 'Unnamed'} at ${nearestInterconnect.distance_miles} mi`,
        detail: 'Multiple pipeline systems accessible — increases gas supply options and negotiating leverage.',
      });
    }

    // 4. Pipeline safety record
    if (safety) {
      findings.push({
        label: 'Pipeline Safety Record',
        type: incidentCount === 0 ? 'FAVORABLE' : seriousCount > 0 ? 'UNFAVORABLE' : incidentCount <= 3 ? 'CAUTION' : 'UNFAVORABLE',
        desc: incidentCount === 0
          ? `Zero reportable incidents within 10 mi (${safety.years_covered})`
          : `${incidentCount} incident${incidentCount !== 1 ? 's' : ''} within 10 mi (${safety.years_covered})${seriousCount > 0 ? ` — ${seriousCount} serious` : ''}`,
        detail: incidentCount > 0
          ? `${safety.significant} significant (>$50K damage)${safety.total_fatalities > 0 ? `, ${safety.total_fatalities} fatalities` : ''}`
          : 'Source: PHMSA Pipeline Safety, DOT',
      });
    }

    // 5. Gas storage proximity
    if (nearestStorage && Number(nearestStorage.distance_miles) <= 25) {
      findings.push({
        label: 'Gas Storage',
        type: 'INFO',
        desc: `${nearestStorage.name || 'Unnamed'} at ${nearestStorage.distance_miles} mi${nearestStorage.facility_type ? ` (${nearestStorage.facility_type.toLowerCase()})` : ''}`,
        detail: 'Nearby storage provides supply buffer during peak demand or pipeline disruptions.',
      });
    }

    // 6. Measurement & easements (guidance)
    findings.push({
      label: 'Metering & Easements',
      type: 'INFO',
      desc: `Developer-built metering station standard for >100 MW BTM.${parcel.state === 'TX' ? (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? ' Easement widths are negotiated; Texas provides only a rebuttable 50-ft presumption for certain common-carrier lines (Tex. Nat. Res. Code §111.0194), excluding gathering lines.' : ' TX default pipeline easement: 50 ft.') : ''}`,
      detail: 'Specific easements require title search. Metering terms negotiated per interconnect agreement.',
    });

    // 7. Supply contract guidance
    findings.push({
      label: 'Gas Supply Contracting',
      type: 'INFO',
      desc: isMeiners
        ? 'Firm transportation is recommended for 24/7 operations. Project-specific tariff, transport, tap/facilities and delivered-gas quote are pending operator inquiry.'
        : parcel.state === 'TX' ? `Firm Transportation recommended for 24/7 DC operations. Delivered gas ${data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? 'screening scenario' : 'est.'} $${DELIVERED_GAS_CURRENT.toFixed(2)}–${DELIVERED_GAS_NORMALIZED.toFixed(2)}/MMBtu (Waha basis + transport).` : 'Firm Transportation recommended for 24/7 DC operations.',
      detail: isMeiners
        ? 'Operator quote required before pricing gas, power or savings.'
        : parcel.state === 'TX' ? `Est. BTM power: $${BTM_LCOE_LOW}–${BTM_LCOE_HIGH}/MWh vs grid ~$50–60/MWh (ERCOT all-in). BTM advantage: ~$10–39/MWh.` : 'Contact pipeline operator for rate schedule.',
    });

    // Pipeline proximity upgrades readiness: a transmission line within 5 mi means gas is accessible even without an existing tap
    const hasPipelineAccess = pipelines.length > 0 && pipelines.some(p => Number(p.distance_miles) <= 5);
    // AOI parcels (live zone payload only): the operator-build commitment is the
    // firm path, so the gas-supply readiness summary is FAVORABLE regardless of
    // whether a public-midstream receipt point sits within 10 mi — pipeline access
    // is redundancy, not the gating signal.
    const summaryType: FindingType = isUvalde ? 'CAUTION' : isAoi ? 'FAVORABLE'
      : hasReceipt && incidentCount <= 3 ? 'FAVORABLE'
      : hasReceipt ? 'CAUTION'
      : hasPipelineAccess ? 'CAUTION'
      : 'UNFAVORABLE';
    const summaryLabel = summaryType === 'FAVORABLE' ? 'Favorable' : summaryType === 'CAUTION' ? 'Caution' : 'Unfavorable';

    // Pipeline proximity signal — same systems as the pipeline table below.
    const tapPool = pipelines.filter(p =>
      !p.is_gathering
      && !p.is_small_transmission
      && p.id !== 'revenant-operator-gathering'
    );
    const tapPoolWithin10 = tapPool.filter(p => Number(p.distance_miles) <= 10);
    const isA1Corridor = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
    const tapPoolMinDist = tapPoolWithin10.length > 0
      ? Math.min(...tapPoolWithin10.map(p => Number(p.distance_miles))) : null;
    const tapPoolMaxDist = tapPoolWithin10.length > 0
      ? Math.max(...tapPoolWithin10.map(p => Number(p.distance_miles))) : null;
    const tapDistRange = tapPoolMinDist != null && tapPoolMaxDist != null
      ? (tapPoolMinDist === tapPoolMaxDist
          ? `at ${tapPoolMinDist.toFixed(1)} mi`
          : `${tapPoolMinDist <= 0.05 ? 'crosses parcel' : `${tapPoolMinDist.toFixed(1)} mi`}–${tapPoolMaxDist.toFixed(1)} mi`)
      : null;

    return (
      <div ref={ref} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>{isAoi ? 'Pipeline Tap Candidates · Corridor Liquidity' : <>Gas Supply &amp; Logistics</>}</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px', }}>
            {isAoi ? (
              <>
                <div style={{ marginBottom: '8px' }}>
                  Tiered supply stack. The operator's gathering line is the firm path; the public-midstream pipelines below are an infrastructure-density signal and tap-anchor optionality, not a guaranteed feed — a tap off a transmission line still requires operator commercial cooperation.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '6px', fontSize: '10px' }}>
                  <div style={{ padding: '8px 10px', borderRadius: '3px', border: `1px solid ${REPORT.heading}33`, background: 'transparent' }}>
                    <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.tierOk, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Firm path</div>
                    <div style={{ color: REPORT.heading }}>Operator gathering</div>
                    <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '1px' }}>Bespoke build for qualified BTM buyers</div>
                  </div>
                  <div style={{ padding: '8px 10px', borderRadius: '3px', border: `1px solid ${REPORT.heading}33`, background: 'transparent' }}>
                    <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.heading, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Pipeline density</div>
                    <div style={{ color: REPORT.heading }}>
                      {tapPoolWithin10.length > 0
                        ? <>{tapPoolWithin10.length} transmission line{tapPoolWithin10.length === 1 ? '' : 's'} within 10 mi{tapDistRange ? <> · {tapDistRange}</> : null}</>
                        : 'No transmission lines within 10 mi'}
                    </div>
                    <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '1px' }}>
                      {isA1Corridor
                        ? 'No headroom figure is claimed — gas availability and supply are confirmed, with firm transport and tap engineering remaining'
                        : 'Published capacity and local deliverability are unconfirmed; no observed headroom is shown.'}
                    </div>
                  </div>
                  <div style={{ padding: '8px 10px', borderRadius: '3px', border: `1px solid ${REPORT.heading}33`, background: 'transparent' }}>
                    <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.heading, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Compressor anchor</div>
                    <div style={{ color: REPORT.heading }}>
                      {hasCompressor && nearestCompressor
                        ? <>{nearestCompressor.name || 'Compressor station'} · {nearestCompressor.distance_miles} mi{nearestCompressor.horsepower ? ` · ${Number(nearestCompressor.horsepower).toLocaleString()} HP` : ''}</>
                        : 'No compressor station within 15 mi'}
                    </div>
                    <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '1px' }}>
                      {hasCompressor
                        ? 'Engineered receipt headers + bypass valves simplify tap design vs. mid-pipe taps'
                        : 'Compressor stations indicate active, high-flow segments and provide tap anchors'}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '8px', fontSize: '10px', color: REPORT.tertiary }}>
                  BTM scoring does not penalize pipeline distance inside the zone. Capacity and proximity numbers are informational.
                </div>
              </>
            ) : (
              <>
                Gas supply infrastructure assessment for behind-the-meter power generation.
                Evaluates pipeline interconnect options, equipment proximity, safety record, and contracting pathway.
              </>
            )}
          </div>
        </div>
        {(isUvalde ? [
          { label: 'Delivery Facilities', type: 'CAUTION' as FindingType, desc: 'No site delivery point, meter or tap is confirmed. Public infrastructure records are insufficient to establish an interconnect design.' },
          { label: 'Lateral & Easements', type: 'CAUTION' as FindingType, desc: 'Confirm the source, route, pressure, volume, land rights, construction cost and delivery date with WTG. Existing utility easements do not establish rights for a new gas lateral.' },
        ] : findings).map((f, i) => (
          <div key={i} className="briefing-finding-row" style={{
            padding: '8px 12px', marginBottom: '4px', borderRadius: '4px',
            background: REPORT.sand,
          }}>
            <TierEyebrow color={FINDING_COLORS[f.type]}>{f.type}</TierEyebrow>
            <div style={{ fontSize: '10px', fontWeight: 600, color: REPORT.heading, marginBottom: '2px' }}>{f.label}</div>
            <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{f.desc}</div>
            {f.detail && <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '2px' }}>{f.detail}</div>}
          </div>
        ))}
        <div className="briefing-readiness-summary" style={{
          marginTop: '8px', padding: '8px 12px', borderRadius: '4px',
          background: REPORT.sand,
        }}>
          <TierEyebrow color={FINDING_COLORS[summaryType]} marginBottom={6}>
            Gas Supply Readiness · {summaryLabel}
          </TierEyebrow>
          <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>
            {/* A1: "clean safety record" contradicted the 2010 PHMSA incident shown
                above; summary reframed to the commercial-diligence next step. */}
            {isMeiners ? 'Camino Real is a nearby transmission tap candidate with total capacity unconfirmed. Published system capacities do not establish delivery at the ranch. Obtain operator confirmation of tap, pressure, receipt point, deliverable volume and firm transport.'
              : isA1Corridor ? 'Advance to commercial diligence: gas availability and supply confirmed on an in-service 30″ transmission line crossing the premises; firm transport and tap engineering are the remaining steps.'
              : (parcel as any).structured_intel?.gas_readiness_note ? (parcel as any).structured_intel.gas_readiness_note
              : summaryType === 'FAVORABLE' ? 'Interconnect points accessible, clean safety record.'
              : hasPipelineAccess && !hasReceipt ? 'Pipeline access confirmed — new tap/meter station construction required.'
              : summaryType === 'CAUTION' ? 'Interconnect feasible — review incident history.'
              : 'No nearby pipeline or interconnect infrastructure identified.'}
          </div>
        </div>
        <SourceFootnote>Source: HIFLD gas infrastructure ({data.freshness?.gas_infrastructure || 'Sep 2020'}), PHMSA incidents ({data.freshness?.pipeline_incidents || '2010–2025'}), EIA pipeline capacity, FERC EBB daily postings.</SourceFootnote>
      </div>
    );
  }
);

// ── Power Infrastructure & Capacity Section ──
// FINDING_COLORS and DD_COLORS are defined above (shared with Gas Supply section)

// Power economics constants defined above (Shared Power Economics Constants section)

const fmtSub = (name: string | null | undefined, kv: number | null, dist: number | null, county?: string | null) => {
  const isUnknown = !name || /^(UNKNOWN|TAP|NOT AVAILABLE)/i.test(name);
  let label: string;
  if (isUnknown) {
    const base = kv ? `${kv.toLocaleString()} kV substation` : 'substation';
    const countyLabel = county ? county.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : null;
    label = countyLabel ? `${base} in ${countyLabel} County` : base;
  } else {
    label = name as string;
  }
  return dist != null ? `${label}, ${dist} mi` : label;
};

const fmtTxLine = (line: { name?: string; voltage_kv?: number | null; operator?: string; volt_class?: string | null; distance_miles: number } | undefined) => {
  if (!line) return null;
  const hasKv = line.voltage_kv != null && Number(line.voltage_kv) > 0;
  const kv = hasKv
    ? `${Number(line.voltage_kv).toLocaleString()} kV`
    : (line.volt_class ? `${line.volt_class} kV class` : '');
  // HIFLD frequently fills in "NOT AVAILABLE" instead of NULL for the
  // operator; treat that as missing data rather than displaying it as the
  // utility's name.
  const opRaw = line.operator || '';
  const op = /^NOT AVAILABLE$/i.test(opRaw) ? '' : opRaw;
  return [kv, op ? `(${op})` : '', `${line.distance_miles} mi`].filter(Boolean).join(' ');
};

// Pick the closest transmission line whose operator field isn't a HIFLD
// "NOT AVAILABLE" placeholder. Falls back to the closest line if every
// candidate has a missing operator.
const pickNearestNamedTxLine = (lines: Array<{ operator?: string }> | undefined) => {
  if (!lines || lines.length === 0) return undefined;
  const named = lines.find(l => l.operator && !/^NOT AVAILABLE$/i.test(l.operator));
  return named || lines[0];
};

const titleCaseAirport = (s: string) => s.split(/\s+/).map(w => w.split('/').map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('/')).join(' ').replace(/\bFld\b/g, 'Field').replace(/\bMuni\b/g, 'Municipal');

// Extract the longest paved runway from an airport's runway_data array.
// Paved = ASPH or CONC variants (asphalt/concrete). Returns null if no paved runways.
interface RunwayLike { id?: string | null; len_ft?: number; width_ft?: number | null; surface?: string | null }
const longestPavedRunway = (runways: RunwayLike[] | null | undefined): { len: number; width: number | null } | null => {
  if (!Array.isArray(runways) || runways.length === 0) return null;
  const paved = runways.filter(r => r.surface && /^(ASPH|CONC)/i.test(r.surface));
  if (paved.length === 0) return null;
  const longest = paved.reduce((best, r) => (Number(r.len_ft || 0) > Number(best.len_ft || 0) ? r : best), paved[0]);
  const len = Number(longest.len_ft || 0);
  if (len <= 0) return null;
  return { len, width: longest.width_ft != null ? Number(longest.width_ft) : null };
};
const fmtRunway = (rwy: { len: number; width: number | null } | null) => {
  if (!rwy) return '';
  return `${rwy.len.toLocaleString()} ft${rwy.width ? ` × ${rwy.width} ft` : ''} paved`;
};

const fmtTdu = (name: string) => name.replace(/\s*-\s*\([A-Z]{2}\)$/, '')
  .toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
  .replace(/\bLlc\b/g, 'LLC').replace(/\bInc\b/g, 'Inc').replace(/\bCo\b/g, 'Co')
  .replace(/\bCoop\b/g, 'Co-op').replace(/\bElec\b/g, 'Electric').replace(/\bAssn\b/g, 'Assn')
  .replace(/\bFirstenergy\b/g, 'FirstEnergy').replace(/\bPacificorp\b/g, 'PacifiCorp')
  .replace(/\bComed\b/g, 'ComEd').replace(/\bNextera\b/g, 'NextEra').replace(/\bEverusa\b/g, 'Eversource');

const PowerInfraSection = React.forwardRef<HTMLDivElement, { data: BriefingData; parcel: ParcelDetail }>(
  ({ data, parcel }, ref) => {
    const isMeiners = data.carveOutAggregate?.slug === MEINERS_SLUG;
    const sub1 = data.nearestFeatures?.substation;
    const sub2 = data.nearestFeatures?.secondSubstation;
    const isUvalde = isUvaldeBriefing(data);
    const pi = data.powerInfra;
    const sub1Dist = parcel.nearest_substation_miles != null ? Number(parcel.nearest_substation_miles) : null;
    const sub1Kv = parcel.nearest_substation_kv != null ? Number(parcel.nearest_substation_kv) : null;
    const sub2Dist = sub2?.distance_miles != null ? Number(sub2.distance_miles) : (parcel.second_substation_miles != null ? Number(parcel.second_substation_miles) : null);
    const sub2Kv = sub2?.max_voltage_kv != null ? Number(sub2.max_voltage_kv) : (parcel.second_substation_kv != null ? Number(parcel.second_substation_kv) : null);
    const nearestTxLine = pickNearestNamedTxLine(pi?.transmissionLines);
    const txLineOp = nearestTxLine?.operator || sub1?.operator;
    const totalMW = pi?.totalInstalledMW || 0;
    const tduName = pi?.tduName ? fmtTdu(pi.tduName) : null;
    const tduDisplay = tduName || txLineOp;
    const gens = pi?.generators || [];
    const genTotalMW = gens.reduce((s, g) => s + g.total_mw, 0);
    const genGasMW = gens.filter(g => g.fuel_type === 'Natural Gas').reduce((s, g) => s + g.total_mw, 0);
    const genRetiringMW = gens.reduce((s, g) => s + (g.retiring_mw || 0), 0);
    const hasRedundancy = sub2Dist != null && sub2Dist <= 25;
    const bridgingFeasible = sub1Dist != null && sub1Dist <= 10 && sub1Kv != null && sub1Kv >= 69;
    const gridChallenging = (sub1Dist != null && sub1Dist > 15) || (sub1Kv != null && sub1Kv < 69);

    // Phase 3 ERCOT data
    const plannedTx = pi?.plannedTransmission || [];
    const queue = pi?.queueCongestion;
    const lmpZones = pi?.lmpZones || {};
    // Determine LMP zone: prefer queue CDR mapping, fall back to TDU-based inference
    const lmpZone = queue?.lmp_zone || (() => {
      const tdu = (pi?.tduName || '').toUpperCase();
      if (tdu.includes('CENTERPOINT')) return 'LZ_HOUSTON';
      if (tdu.includes('AEP')) return 'LZ_SOUTH';
      if (tdu.includes('ONCOR')) return 'LZ_NORTH'; // Oncor spans NORTH + WEST, NORTH is more common
      return 'LZ_WEST'; // default for Permian/unmatched
    })();
    const localLmpData = (lmpZones as any)[lmpZone] || {};
    const localLmp = localLmpData.t12mo || null;
    const localLmp6mo = localLmpData.t6mo || null;
    const planned765 = plannedTx.filter((p: any) => p.voltage_kv === 765);
    const planned345 = plannedTx.filter((p: any) => p.voltage_kv === 345);
    const gasPlants = useMemo(() => (pi?.powerPlants || []).filter((p: any) => p.fuel_source?.toLowerCase().includes('gas')), [pi?.powerPlants]);

    // ── Assessment findings (refined, deduped) ──
    const findings: Array<{ label: string; type: FindingType; desc: string; detail?: string }> = [];

    // 1. Utility provider
    findings.push({
      label: 'Utility Provider',
      type: isMeiners ? 'INFO' : tduName ? 'FAVORABLE' : txLineOp ? 'INFO' : 'CAUTION',
      desc: isMeiners ? MEINERS_UTILITY_LEADS : tduName
        ? `Service territory: ${tduName}${pi?.tduHoldingCompany && fmtTdu(pi.tduHoldingCompany) !== tduName ? ` (${fmtTdu(pi.tduHoldingCompany)})` : ''}`
        : txLineOp ? `Transmission owner: ${txLineOp}` : 'TDU identification requires service territory lookup',
      detail: isMeiners ? undefined : tduName
        ? (txLineOp && txLineOp !== tduName ? `Transmission line operator: ${txLineOp}` : undefined)
        : txLineOp ? 'Heuristic — confirm via utility service territory map.' : undefined,
    });

    // 2. Grid backup feasibility (reframed — BTM generates its own power)
    const backupType: FindingType = bridgingFeasible ? 'FAVORABLE' : sub1Dist != null && sub1Dist <= 20 ? 'CAUTION' : 'UNFAVORABLE';
    const queueDetail = queue && queue.active_projects > 0
      ? `Queue congestion: ${queue.pending_mw.toLocaleString()} MW pending (${queue.active_projects} projects${queue.avg_queue_months ? `, avg ${queue.avg_queue_months} mo in queue` : ''})`
      : null;
    const genGasDetail = genGasMW > 0
      ? `Gas generation in county: ${genGasMW.toLocaleString()} MW`
      : gasPlants.length > 0
      ? `${gasPlants.length} gas plant${gasPlants.length > 1 ? 's' : ''} within 25 mi (${gasPlants.reduce((s: number, p: any) => s + (p.capacity_mw || 0), 0).toLocaleString()} MW)`
      : null;
    findings.push({
      label: 'Grid Backup Feasibility',
      type: isMeiners ? 'INFO' : backupType,
      desc: isMeiners
        ? `${fmtSub(sub1?.name, sub1Kv, sub1Dist, parcel.county)} — mapped substation proximity and voltage do not establish usable auxiliary capacity. Capacity is unconfirmed; confirm service and capacity with the serving co-op.`
        : bridgingFeasible
        ? `${fmtSub(sub1?.name, sub1Kv, sub1Dist, parcel.county)} — adequate for <10 MW bridging and auxiliary load`
        : sub1Dist != null
        ? `Nearest substation at ${sub1Dist} mi${sub1Kv ? ` (${sub1Kv} kV)` : ''} — ${sub1Dist > 15 ? 'on-site temporary generation likely required during construction' : 'feasible with extended gen-tie'}`
        : 'Substation data unavailable',
      detail: [queueDetail, genGasDetail].filter(Boolean).join(' | ') || undefined,
    });

    // 3. Substation redundancy — gated on transmission-class voltage (>=69 kV).
    // A NULL- or sub-69 kV "2nd substation" is distribution and does not constitute redundancy.
    if (sub1Dist != null) {
      const sub2IsTransmission = sub2Kv != null && sub2Kv >= 69;
      const redundancyType: FindingType = hasRedundancy && sub2IsTransmission && sub1Dist <= 10
        ? 'FAVORABLE'
        : hasRedundancy && sub2IsTransmission
        ? 'CAUTION'
        : hasRedundancy
        ? 'INFO'
        : 'UNFAVORABLE';
      const redundancyDesc = hasRedundancy
        ? sub2IsTransmission
          ? `2nd substation: ${fmtSub(sub2?.name, sub2Kv, sub2Dist, parcel.county)}`
          : `2nd facility within ${sub2Dist} mi is distribution-class — not transmission redundancy`
        : 'No redundant substation within 25 mi';
      findings.push({
        label: 'Substation Redundancy',
        type: redundancyType,
        desc: redundancyDesc,
        detail: parcel.nearest_substation_lines ? `${parcel.nearest_substation_lines} transmission lines at nearest substation` : undefined,
      });
    }

    // 3b. OSM switchyard — surfaces net-new infrastructure HIFLD lacks
    const osmDist = parcel.nearest_osm_switchyard_miles != null ? Number(parcel.nearest_osm_switchyard_miles) : null;
    const osmKv = parcel.nearest_osm_switchyard_voltage_kv != null ? Number(parcel.nearest_osm_switchyard_voltage_kv) : null;
    const osmName = parcel.nearest_osm_switchyard_name;
    const osmHifldBacked = parcel.nearest_osm_switchyard_hifld_backed;
    if (osmDist != null && osmDist <= 15 && osmKv != null && osmKv >= 138) {
      const hifldName = parcel.nearest_substation_name || '';
      const hifldIsPlaceholder = /^(UNKNOWN\d+|TAP\d+|SUB\d+)$/i.test(hifldName);
      if (osmHifldBacked === false) {
        findings.push({
          label: 'Switchyard (OSM, HIFLD-missing)',
          type: osmKv >= 345 && osmDist <= 5 ? 'FAVORABLE' : 'INFO',
          desc: `${osmName || '(unnamed)'} — ${osmKv.toLocaleString()} kV at ${osmDist.toFixed(2)} mi`,
          detail: 'OSM-sourced switching facility not in HIFLD. Verify status and owner before LOI — likely absent from standard commercial datasets as well.',
        });
      } else if (hifldIsPlaceholder && osmName) {
        findings.push({
          label: 'Nearest Substation Identification',
          type: 'INFO',
          desc: `OSM names this "${osmName}" (${osmKv.toLocaleString()} kV); HIFLD carries only "${hifldName}"`,
          detail: 'HIFLD placeholder name resolved via OSM cross-reference.',
        });
      }
    }

    // 4. Voltage level + retirement risk
    if (sub1Kv != null) {
      findings.push({
        label: 'Voltage Level',
        type: sub1Kv >= 345 ? 'FAVORABLE' : sub1Kv >= 69 ? 'CAUTION' : 'UNFAVORABLE',
        desc: isMeiners
          ? `${sub1Kv.toLocaleString()} kV — mapped voltage only; usable auxiliary capacity is unconfirmed.`
          : `${sub1Kv.toLocaleString()} kV${sub1Kv >= 345 ? ' (transmission backbone)' : sub1Kv >= 69 ? ' (adequate for <10 MW)' : ' (below 69 kV minimum)'}`,
        detail: [
          nearestTxLine ? `Nearest transmission line: ${fmtTxLine(nearestTxLine)}` : null,
          genRetiringMW > 0 ? `Retirement risk: ${genRetiringMW.toLocaleString()} MW retiring within 5 yr` : null,
        ].filter(Boolean).join(' | ') || undefined,
      });
    }

    // 4b. 500 kV backbone proximity — surfaces ≥345 kV transmission within 25 mi.
    // Material for any future wholesale-export option (HB 2014 microgrid <10% export, ERCOT export, etc.)
    // and as regional infrastructure validation. Lines from pi.transmissionLines, substations from pi.bulkSubstations.
    const backbone345Lines = (pi?.transmissionLines || []).filter((l: any) => l.voltage_kv != null && Number(l.voltage_kv) >= 345);
    const backbone345Subs = ((pi as any)?.bulkSubstations || []) as Array<{ name: string | null; voltage_kv: number | null; operator: string | null; distance_miles: number }>;
    const nearestBackboneSub = backbone345Subs[0];
    const nearestBackboneLine = backbone345Lines[0];
    if (nearestBackboneSub || nearestBackboneLine) {
      const subPart = nearestBackboneSub
        ? `${nearestBackboneSub.name && !/^UNKNOWN\d+/i.test(nearestBackboneSub.name) ? nearestBackboneSub.name : `${Number(nearestBackboneSub.voltage_kv).toLocaleString()} kV substation`} at ${Number(nearestBackboneSub.distance_miles).toFixed(1)} mi (${Number(nearestBackboneSub.voltage_kv).toLocaleString()} kV)`
        : null;
      const linePart = nearestBackboneLine
        ? `${nearestBackboneLine.name || `${Number(nearestBackboneLine.voltage_kv).toLocaleString()} kV line`} at ${Number(nearestBackboneLine.distance_miles).toFixed(1)} mi`
        : null;
      const otherLines = backbone345Lines.slice(1, 3).map((l: any) => `${l.name || `${l.voltage_kv} kV`} (${Number(l.distance_miles).toFixed(1)} mi)`).join(', ');
      const headline = subPart || linePart;
      const closestDist = Math.min(
        nearestBackboneSub ? Number(nearestBackboneSub.distance_miles) : Infinity,
        nearestBackboneLine ? Number(nearestBackboneLine.distance_miles) : Infinity,
      );
      findings.push({
        label: 'Bulk Transmission Backbone',
        type: closestDist <= 10 ? 'FAVORABLE' : 'INFO',
        desc: `${headline}${subPart && linePart ? ` · nearest 345+ kV line: ${linePart}` : ''}`,
        detail: [
          backbone345Lines.length > 1 ? `Additional ≥345 kV lines in 25 mi: ${otherLines}` : null,
          'Optional path for future wholesale export or large-scale interconnection beyond BTM auxiliary load.',
        ].filter(Boolean).join(' '),
      });
    }

    // 5. 75 MW Grid Capacity Assessment (uses shared tier logic from grid-capacity.ts)
    // Recharacterized to INFO when parcel has material BTM pipeline-available capacity —
    // grid hosting tier is informational on a BTM site (load path is on-parcel gas-to-wire).
    if (sub1Kv != null && sub1Dist != null) {
      const sub1Lines = parcel.nearest_substation_lines ? Number(parcel.nearest_substation_lines) : 0;
      const tier = computeCapacityTier(sub1Kv, sub1Lines);
      const tierTypes: Record<string, FindingType> = { high: 'FAVORABLE', likely: 'FAVORABLE', moderate: 'CAUTION', low: 'UNFAVORABLE' };
      // Generic stored MW/headroom claims do not establish a deliverable gas path.
      // Keep the grid assessment independent until native operator observations exist.
      const isBtmSite = false;
      if (isMeiners) {
        findings.push({
          label: 'Auxiliary utility service',
          type: 'INFO',
          desc: 'The proposed load path is BTM gas-to-wire. Pipeline total capacity and the volume available for delivery at the ranch require operator confirmation.',
          detail: `${sub1Kv} kV infrastructure at ${sub1Dist} mi is informational for auxiliary service; utility capacity requires a study.`,
        });
      } else if (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1') {
        // A1: off-grid screening keeps nearby grid infrastructure informational.
        findings.push({
          label: '75 MW Grid Capacity',
          type: 'INFO',
          desc: '138 kV infrastructure nearby; hosting capacity unconfirmed. Not a site gate for an off-grid build; utility study required for auxiliary service.',
        });
      } else if (tier) {
        const cfg = CAPACITY_TIER_CONFIG[tier];
        findings.push({
          label: '75 MW Grid Capacity',
          type: isBtmSite ? 'INFO' : tierTypes[tier],
          desc: isBtmSite
            ? `${cfg.label} grid host tier — informational only. A gas delivery path remains unconfirmed.`
            : `${cfg.label} — ${sub1Kv} kV substation${sub1Lines ? ` with ${sub1Lines} transmission line${sub1Lines > 1 ? 's' : ''}` : ''} at ${sub1Dist} mi`,
          detail: isBtmSite
            ? `Grid hosting capacity (${cfg.label.toLowerCase()}) does not gate this site. ${sub1Kv} kV / ${sub1Lines || 'unknown'}-line at ${sub1Dist} mi remains adequate for <10 MW Will Serve auxiliary.`
            : `${cfg.description} Confirm with ${parcel.state === 'TX' ? 'ERCOT' : 'serving ISO/RTO'} screening study.`,
        });
      } else {
        findings.push({
          label: '75 MW Grid Capacity',
          type: isBtmSite ? 'INFO' : 'UNFAVORABLE',
          desc: isBtmSite
            ? `Sub-138 kV grid host (${sub1Kv} kV) — informational. A gas delivery path remains unconfirmed.`
            : `Nearest substation below 138 kV (${sub1Kv} kV) — cannot serve 75 MW load`,
        });
      }
    }

    // 6. Bridging power & interconnection (merged #7 + #9) + queue congestion
    const queueCongested = queue && queue.pending_mw > 2000;
    findings.push({
      label: 'Bridging Power & Interconnection',
      type: isMeiners ? 'INFO' : bridgingFeasible && !queueCongested ? 'FAVORABLE' : bridgingFeasible ? 'CAUTION' : 'CAUTION',
      desc: isMeiners
        ? 'Auxiliary connection requirements and capacity are unconfirmed; confirm service, delivery point, facilities and any applicable interconnection process with the serving co-op.'
        : `<10 MW auxiliary load qualifies for Will Serve letter (not full Interconnection Agreement).${parcel.state === 'TX' ? (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? ' As structured (fully off-grid), outside SB 6 co-location approval — confirm with counsel.' : ' SB 6 co-location exemption applies.') : parcel.state === 'WV' ? ' WV HB 2014 microgrid framework: BTM with <10% wholesale export exempt from PSC siting.' : ''}`,
      detail: isMeiners
        ? queue
          ? `${queue.pending_mw.toLocaleString()} MW in ERCOT's generation queue for ${parcel.county} Co. (${queue.active_projects} projects). Queue context does not establish auxiliary service availability.`
          : 'Mapped infrastructure does not establish auxiliary service availability.'
        : queue
        ? `${queue.pending_mw.toLocaleString()} MW in ${parcel.state === 'TX' ? 'ERCOT' : 'PJM'} gen queue for ${parcel.county} Co. (${queue.active_projects} projects${queue.dominant_fuel ? `, mostly ${queue.dominant_fuel.toLowerCase()}` : ''}). ${queueCongested ? 'High queue volume may signal transmission congestion.' : 'Moderate queue — Will Serve pathway likely clear.'}`
        : bridgingFeasible ? 'Standard utility service pathway.' : 'Extended gen-tie or temporary generation will require utility capacity study.',
    });

    // 6. Utility expansion (data-driven from ERCOT TPIT)
    if (plannedTx.length > 0) {
      const topProject = planned765[0] || planned345[0] || plannedTx[0];
      const inService = topProject.in_service ? new Date(topProject.in_service).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : 'TBD';
      findings.push({
        label: 'Utility Expansion',
        type: planned765.length > 0 ? 'FAVORABLE' : 'INFO',
        desc: `${plannedTx.length} transmission project${plannedTx.length > 1 ? 's' : ''} planned in ${parcel.county} County${planned765.length > 0 ? ` — includes ${planned765.length} at 765 kV` : planned345.length > 0 ? ` — ${planned345.length} at 345 kV` : ''}`,
        detail: isMeiners
          ? `${topProject.title} (${topProject.voltage_kv} kV, ${topProject.owner}) — historical target ${inService} in the February 2026 ERCOT TPIT record; current completion and any usable auxiliary capacity are unverified.${plannedTx.length > 1 ? ` ${plannedTx.length - 1} other projects appear in that record.` : ''}`
          : `${topProject.title} (${topProject.voltage_kv} kV, ${topProject.owner}, target ${inService})${plannedTx.length > 1 ? ` + ${plannedTx.length - 1} more` : ''}`,
      });
    } else if (queue && queue.active_projects > 0 && parcel.state !== 'TX') {
      findings.push({
        label: 'Utility Expansion',
        type: queue.pending_mw > 500 ? 'FAVORABLE' : 'INFO',
        desc: `${queue.active_projects} project${queue.active_projects > 1 ? 's' : ''} in ${parcel.state === 'TX' ? 'ERCOT' : 'PJM'} interconnection queue for ${parcel.county} County (${(queue.pending_mw || 0).toLocaleString()} MW pending)`,
        detail: `${queue.completed_projects || 0} completed (${(queue.completed_mw || 0).toLocaleString()} MW in service). ${queue.dominant_fuel ? `Dominant fuel: ${queue.dominant_fuel.toLowerCase()}.` : ''} Source: PJM New Services Queue.`,
      });
    } else {
      findings.push({
        label: 'Utility Expansion',
        type: parcel.state === 'TX' ? 'INFO' : 'CAUTION',
        desc: parcel.state === 'TX' ? 'No planned transmission projects in this county per ERCOT TPIT' : 'Expansion plans require utility inquiry',
        detail: parcel.state === 'TX' ? 'Source: ERCOT Transmission Project Information Tracking (Feb 2026)' : undefined,
      });
    }

    // 7. Cost structure (data-driven from ERCOT DAM LMP — IC-grade format)
    const fuelCurrentMWh = Math.round(DELIVERED_GAS_CURRENT * HEAT_RATE / 1000);
    const fuelNormalMWh = Math.round(DELIVERED_GAS_NORMALIZED * HEAT_RATE / 1000);
    const costMethodology = localLmp && !isMeiners && !isUvalde ? [
      `Current Permian: delivered gas ~$${DELIVERED_GAS_CURRENT.toFixed(2)}/MMBtu → fuel $${fuelCurrentMWh}/MWh + capex $12–15 + O&M $5–8 = $${BTM_LCOE_LOW}–28/MWh`,
      `Normalized Permian: delivered gas ~$${DELIVERED_GAS_NORMALIZED.toFixed(2)}/MMBtu → fuel $${fuelNormalMWh}/MWh + capex $12–15 + O&M $5–8 = $30–36/MWh`,
      `Grid alternative: DAM avg $${localLmp.avg_lmp.toFixed(0)}/MWh + 4CP ~$${ERCOT_4CP_PER_MWH} + TDU ~$${TDU_DELIVERY_PER_MWH} = $${Math.round(localLmp.avg_lmp) + GRID_ADDER}–${Math.round(localLmp.avg_lmp) + GRID_ADDER + GRID_RANGE_SPREAD}/MWh`,
      `Lazard reference: grid-connected CCGT at Henry Hub ($3.45/MMBtu) = $48–109/MWh — different fuel basis and configuration`,
      `Delivered gas = hub price + transport ($0.30–0.80) + lateral amortization. Waha spot is the index, not the delivery price.`,
      `Sources: ERCOT DAM SPP trailing 12 mo, PUCT 4CP rate (${ERCOT_4CP_YEAR}), Permian delivered gas basis, industry capex benchmarks (2025)`,
    ] : null;
    if (isMeiners) {
      findings.push({
        label: 'Power Cost Structure',
        type: 'INFO',
        desc: 'Project-specific fuel, transport, lateral, generation capex/O&M and utility-tariff quotes are pending.',
        detail: 'No local delivered-gas price, BTM LCOE or savings figure is established at screening.',
      });
    } else if (localLmp && localLmp.avg_lmp != null) {
      const damAvg = Math.round(localLmp.avg_lmp);
      const gridAllInLow = damAvg + GRID_ADDER;
      const gridAllInHigh = gridAllInLow + GRID_RANGE_SPREAD;
      const advantageLow = gridAllInLow - BTM_LCOE_HIGH;
      const advantageHigh = gridAllInHigh - BTM_LCOE_LOW;
      const zone = lmpZone.replace('LZ_', '');
      const trend6mo = localLmp6mo ? ` | 6-mo avg: $${localLmp6mo.avg_lmp.toFixed(0)}/MWh` : '';
      findings.push({
        label: 'Power Cost Structure',
        type: advantageLow > 15 ? 'FAVORABLE' : advantageLow > 5 ? 'INFO' : 'CAUTION',
        desc: `Grid all-in: ~$${gridAllInLow}–${gridAllInHigh}/MWh (ERCOT ${zone}, trailing 12 mo). BTM gas-to-wire: est. $${BTM_LCOE_LOW}–${BTM_LCOE_HIGH}/MWh (recip/CT, Permian fuel basis).`,
        detail: `Indicative BTM advantage: ~$${advantageLow}–${advantageHigh}/MWh. DAM avg: $${damAvg}/MWh (12 mo)${trend6mo}. 4CP: $${ERCOT_4CP_RATE}/kW-yr (${ERCOT_4CP_YEAR}).`,
      });
    } else {
      findings.push({
        label: 'Power Cost Structure',
        type: 'INFO',
        desc: parcel.state === 'TX'
          ? `ERCOT 4CP transmission charge: $${ERCOT_4CP_RATE}/kW-yr (${ERCOT_4CP_YEAR}). Grid backup at <10 MW minimizes exposure.`
          : parcel.state === 'WV'
          ? `PJM APS zone — grid wholesale ~$40–65/MWh trailing 12 mo (DAM LMP, typical APS range). BTM gas-to-wire est. $${BTM_LCOE_LOW}–${BTM_LCOE_HIGH}/MWh at Appalachian hub gas pricing.`
          : 'Utility rate schedule inquiry required',
        detail: parcel.state === 'WV'
          ? 'Zone-specific LMP averages not yet ingested — quote is indicative. Confirm with utility tariff schedule.'
          : undefined,
      });
    }

    // 8. Major gas plants under construction nearby (curated — signals gas infra commitment)
    const ucPlants = gasPlants.filter((p: any) => p.status === 'under_construction');
    if (ucPlants.length > 0) {
      const top = ucPlants[0];
      const meta = top.metadata || {};
      const detail = [
        meta.technology && `${meta.technology}`,
        meta.backer && `backed by ${meta.backer}`,
        meta.investment_usd && `$${(meta.investment_usd / 1e9).toFixed(1)}B FID`,
        meta.gas_supply && `Gas: ${meta.gas_supply}`,
        meta.grid_connection && `Grid: ${meta.grid_connection}`,
        meta.expected_online && `Expected ${meta.expected_online}`,
      ].filter(Boolean).join(' · ');
      findings.push({
        label: 'Major Gas Plant Under Construction',
        type: 'FAVORABLE',
        desc: `${top.name} (${top.capacity_mw?.toLocaleString()} MW) at ${top.distance_miles} mi — validates regional gas infrastructure`,
        detail: detail || undefined,
      });
    }

    // 9. HV lead times (conditional — only when grid interconnection is challenging)
    if (gridChallenging) {
      findings.push({
        label: 'Long-Lead HV Equipment',
        type: 'CAUTION',
        desc: 'Extended gen-tie or island-mode requires early long-lead equipment orders.',
        detail: 'Transformers: 128–144 weeks. Gas turbines: 5–7 years. Recip engines: 12–24 months.',
      });
    }

    // Summary verdict
    const summaryType: FindingType = isMeiners ? 'INFO' : isUvalde ? 'CAUTION' : bridgingFeasible ? 'FAVORABLE' : sub1Dist != null && sub1Dist <= 20 ? 'CAUTION' : 'UNFAVORABLE';
    const summaryLabel = isMeiners ? 'Unverified' : summaryType === 'FAVORABLE' ? 'Favorable' : summaryType === 'CAUTION' ? 'Caution' : 'Unfavorable';

    return (
      <div ref={ref} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Power Infrastructure &amp; Capacity</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px', }}>
            {isUvalde ? 'Mapped facilities identify utility contacts and study options. Proximity and voltage do not establish available capacity.' : <>Grid interconnection assessment for construction bridging and auxiliary operations. Feasibility depends on substation proximity (&lt;10 mi), voltage class (69 kV+), and utility queue position.</>}
          </div>
        </div>
        {(isUvalde ? uvaldePowerFindings : findings).map((f, i) => (
          <div key={i} className="briefing-finding-row" style={{
            padding: '8px 12px', marginBottom: '4px', borderRadius: '4px',
            background: REPORT.sand,
          }}>
            <TierEyebrow color={FINDING_COLORS[f.type]}>{f.type}</TierEyebrow>
            <div style={{ fontSize: '10px', fontWeight: 600, color: REPORT.heading, marginBottom: '2px' }}>{f.label}</div>
            <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{f.desc}</div>
            {f.detail && <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '2px' }}>{f.detail}</div>}
          </div>
        ))}
        {/* Summary verdict */}
        <div className="briefing-readiness-summary" style={{
          marginTop: '8px', padding: '8px 12px', borderRadius: '4px',
          background: REPORT.sand,
        }}>
          <TierEyebrow color={FINDING_COLORS[summaryType]} marginBottom={6}>
            Grid Readiness · {summaryLabel}
          </TierEyebrow>
          <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>
            {isMeiners ? 'Confirm the serving utility, auxiliary capacity, delivery point and connection requirements before relying on grid service.' : isUvalde ? uvaldePowerSummary : bridgingFeasible ? 'Standard will-serve pathway for <10 MW bridging.' : 'Utility capacity study recommended before commitment.'}
          </div>
        </div>
        {/* Cost methodology disclosure — collapsed on screen, visible in print */}
        {costMethodology && (<>
          <details className="briefing-no-print" style={{ marginTop: '8px' }}>
            <summary style={{ fontSize: '9px', color: REPORT.secondary, cursor: 'pointer', letterSpacing: '0.03em' }}>
              How we calculated power cost estimates
            </summary>
            <div style={{ fontSize: '9px', color: REPORT.tertiary, lineHeight: 1.6, marginTop: '4px', padding: '6px 10px', background: `${REPORT.rule}22`, borderRadius: '4px' }}>
              {costMethodology.map((line, i) => <div key={i}>{line}</div>)}
            </div>
          </details>
          <div className="briefing-no-print" style={{ display: 'none', marginTop: '6px', fontSize: '8px', color: REPORT.tertiary, lineHeight: 1.5, padding: '4px 10px', borderTop: `1px solid ${REPORT.rule}` }}>
            <div style={{ fontWeight: 600, marginBottom: '2px' }}>Cost estimate methodology</div>
            {costMethodology.map((line, i) => <div key={i}>{line}</div>)}
          </div>
        </>)}
        <SourceFootnote>Source: HIFLD substations &amp; power plants, EIA-860 generators (2024), EIA service territories{parcel.state === 'TX' ? ', ERCOT TPIT (Feb 2026), ERCOT GIS queue (Apr 2026), ERCOT DAM SPP (trailing 12 mo)' : ''}.</SourceFootnote>
      </div>
    );
  }
);

// ── Water Sourcing Section ──

const WaterSourcingSection = React.forwardRef<HTMLDivElement, { data: BriefingData; parcel: ParcelDetail }>(
  ({ data, parcel }, ref) => {
    const isMeiners = data.carveOutAggregate?.slug === MEINERS_SLUG;
    const isA1CarveOut = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
    const isUvalde = isUvaldeBriefing(data);
    const effective = computeEffectiveWaterTier(parcel);
    const wells = parcel.nearby_water_wells || [];
    const within2 = wells.filter((w: any) => Number(w.distance_miles) <= 2);
    const depths = within2.map((w: any) => w.total_depth_ft).filter(Boolean).map(Number);
    const yields = within2.map((w: any) => w.yield_gpm).filter(Boolean).map(Number);
    const avgDepth = depths.length > 0 ? Math.round(depths.reduce((a, b) => a + b, 0) / depths.length) : null;
    const avgYield = yields.length > 0 ? Math.round(yields.reduce((a, b) => a + b, 0) / yields.length) : null;

    const findings: Array<{ label: string; type: FindingType; desc: string; detail?: string }> = [];

    // 0. Landowner disclosure (if any) — shown above computed pathway when owner has directly
    // disclosed on-site water. Kept as a first-class finding so verification tier is explicit.
    const waterDisclosure = (parcel as any).structured_intel?.landowner_disclosures?.water_on_site;
    if (waterDisclosure?.display_note) {
      const outstanding = Array.isArray(waterDisclosure.outstanding_items)
        ? waterDisclosure.outstanding_items.join(' · ')
        : null;
      const src = waterDisclosure.source || 'Landowner';
      const reported = waterDisclosure.reported_date ? ` (${waterDisclosure.reported_date})` : '';
      findings.push({
        label: 'Landowner-Confirmed Water',
        type: 'FAVORABLE',
        desc: waterDisclosure.display_note,
        detail: `Disclosed by ${src}${reported}.${outstanding ? ` Pending: ${outstanding}.` : ''}`,
      });
    }

    // 1. Sourcing pathway
    const pathwayDesc = (() => {
      if (isMeiners) return 'Seller-reported water wells are unverified. Confirm well logs, condition, yield, water rights and current chemistry before selecting a supply.';
      switch (effective) {
        case 'municipal': return 'Inside municipal water service area — connect to utility for bulk supply.';
        case 'aquifer_no_permit': return `${formatAquiferName(parcel.aquifer_name) || 'Local aquifer'}${parcel.state === 'TX' ? ' — rule of capture applies. No groundwater permit required. TDLR-licensed driller required for well construction.' : ' — confirm state and local groundwater permit requirements.'}`;
        case 'aquifer_gcd': return `${formatAquiferName(parcel.aquifer_name) || 'Local aquifer'} inside ${parcel.water_regulatory_zone || 'a Groundwater Conservation District'}. Permit required (60–120 days typical).`;
        case 'aquifer_az_free': return `${formatAquiferName(parcel.aquifer_name) || 'Local aquifer'} outside Active Management Area. AZ reasonable-use doctrine — unregulated at this volume.`;
        case 'aquifer_ama': return `Inside ${parcel.water_regulatory_zone || 'AMA'}. ADWR permit required.`;
        case 'aquifer_nm': return `${formatAquiferName(parcel.aquifer_name) || 'Local aquifer'} under NM prior appropriation. OSE permit required for all commercial groundwater use.`;
        case 'nm_closure': return `NM closure area${parcel.water_regulatory_zone ? ` (${parcel.water_regulatory_zone})` : ''} — no new groundwater appropriations. Must purchase or lease existing water right.`;
        case 'on_site_well': return `On-site well drilling is feasible — ${within2.length} TWDB-registered well${within2.length > 1 ? 's' : ''} within 2 mi confirm groundwater accessibility.`;
        case 'trucked': return 'No municipal service, mapped aquifer, or productive wells within 2 mi. Sourcing via commercial hauling.';
        default: return 'Water sourcing tier not yet computed for this parcel.';
      }
    })();
    const pathwayType: FindingType = isMeiners ? 'INFO' : effective === 'nm_closure' || effective === 'trucked' ? 'CAUTION'
      : effective === null ? 'INFO'
      : 'FAVORABLE';
    findings.push({
      label: 'Sourcing Pathway',
      type: pathwayType,
      desc: pathwayDesc,
      detail: isMeiners ? 'An air-cooled configuration limits routine auxiliary demand, but well supply and any hauling alternative still require verification.' : 'BTM air-cooled cooling (zero evaporative makeup) requires ~300–400K gal/yr (~1,000 GPD) for auxiliary needs only — de minimis for any of these pathways.',
    });

    // 2. Regulatory zone (if inside one)
    if (parcel.water_regulatory_zone) {
      const permitReq = effective === 'aquifer_gcd' || effective === 'aquifer_ama' || effective === 'aquifer_nm' || effective === 'nm_closure';
      findings.push({
        label: 'Regulatory Jurisdiction',
        type: permitReq ? 'CAUTION' : 'INFO',
        desc: `${parcel.water_regulatory_zone}${parcel.water_regulatory_type ? ` (${parcel.water_regulatory_type.toUpperCase()})` : ''}`,
        detail: permitReq ? 'Contact district for spacing rules, production limits, and fees.' : 'Jurisdiction is informational — no active permitting constraint at BTM volumes.',
      });
    } else if (effective === 'aquifer_no_permit' || effective === 'on_site_well') {
      findings.push({
        label: 'Regulatory Jurisdiction',
        type: 'FAVORABLE',
        desc: parcel.state === 'TX' ? 'Outside any Groundwater Conservation District' : 'Outside any groundwater management district',
        detail: parcel.state === 'TX' ? 'TX rule of capture applies. No groundwater permit required.' : 'Confirm state and local groundwater permitting requirements before drilling.',
      });
    }

    // 3. Aquifer
    if (parcel.aquifer_name) {
      findings.push({
        label: 'Aquifer',
        type: isMeiners ? 'INFO' : 'FAVORABLE',
        desc: formatAquiferName(parcel.aquifer_name) || parcel.aquifer_name,
        detail: 'Source: TWDB aquifer boundaries (major + minor).',
      });
    }

    // 3b. Brackish availability (TWDB BRACS extent polygons)
    const brackishZones = parcel.brackish_zones;
    if (brackishZones && brackishZones.length > 0) {
      const names = brackishZones.map(z => z.aquifer_name).filter(Boolean) as string[];
      const namesList = names.length > 0 ? names.map(n => n.replace(/_/g, ' ')).join(' + ') : 'mapped aquifer';
      findings.push({
        label: 'Brackish Availability',
        type: isMeiners ? 'INFO' : 'FAVORABLE',
        desc: `Mapped brackish extent — ${namesList}`,
        detail: isMeiners ? 'TWDB BRACS 2024 maps regional brackish extent. On-site yield, full chemistry, treatment and disposal requirements remain unverified.' : isA1CarveOut
          // A1: options framing — disposal routes and costs are site-specific and
          // unverified; no "cheapest strategy" claim without site cost evidence.
          ? 'TWDB BRACS 2024. Brackish groundwater (TDS ~1,000–10,000 mg/L) is present beneath this parcel. Possible supply option: single-pass RO with brine disposal (e.g., a permitted Class II SWD); disposal routing and costs are site-specific — confirm during DD.'
          : 'TWDB BRACS 2024. Brackish groundwater (TDS ~1,000–10,000 mg/L) is present beneath this parcel. Single-pass RO produces potable supply; brine disposal via nearby Class II SWD. For BTM cooling at ~1 AFY, a brackish well is typically the cheapest viable strategy.',
      });
    }

    // 3c. Measured TDS samples (TWDB GWDB, nearest within 25 mi)
    const tdsSamples = parcel.nearby_tds_samples;
    if (tdsSamples && tdsSamples.length > 0) {
      const nearest = tdsSamples[0];
      const tds = Number(nearest.tds_mgl);
      const band = tds < 1000 ? 'fresh'
        : tds < 3000 ? 'slightly brackish'
        : tds < 10000 ? 'moderately brackish'
        : tds < 35000 ? 'highly brackish'
        : 'brine';
      // A1: the nearest sample is decades old and off-site — no treatment
      // conclusion can rest on it (779 mg/L also exceeds EPA's 500 mg/L
      // secondary TDS guideline). Defer to on-site well sampling.
      const treatment = isMeiners ? 'Current on-site sampling and full chemistry are required to determine treatment; these historical samples do not confirm supply.' : isA1CarveOut ? 'Treatment requirements pending well sampling — current full chemistry needed.'
        : tds < 1000 ? 'No treatment required.'
        : tds < 3000 ? 'Single-pass RO — ideal BTM supply.'
        : tds < 10000 ? 'Single-pass RO with pretreatment. BRACS sweet spot.'
        : tds < 35000 ? 'High-pressure or two-pass RO. Marginal economics at BTM volumes.'
        : 'Thermal/MVC required — not practical at site scale.';
      const dateClean = nearest.sample_date ? String(nearest.sample_date).slice(0, 10) : null;
      const ageY = dateClean ? new Date().getFullYear() - parseInt(dateClean.slice(0, 4), 10) : null;
      const ageStr = ageY != null && ageY > 0 ? ` (${ageY}y old)` : '';
      const findingType: FindingType = isMeiners ? 'INFO' : tds < 10000 ? 'FAVORABLE' : tds < 35000 ? 'INFO' : 'CAUTION';
      const median = [...tdsSamples].sort((a, b) => Number(a.tds_mgl) - Number(b.tds_mgl))[Math.floor(tdsSamples.length / 2)];
      const medianTds = median ? Math.round(Number(median.tds_mgl)) : null;
      findings.push({
        label: 'Water Quality (TDS)',
        type: findingType,
        desc: isMeiners
          ? `Historical quality evidence — nearest sample ${dateClean || 'undated'}: ${tds.toLocaleString()} mg/L at ${nearest.distance_miles} mi (${band})${nearest.aquifer_name ? ` — ${nearest.aquifer_name}` : ''}`
          : `${tds.toLocaleString()} mg/L at ${nearest.distance_miles} mi (${band})${nearest.aquifer_name ? ` — ${nearest.aquifer_name}` : ''}`,
        detail: isMeiners
          ? `${tdsSamples.length} legacy TWDB GWDB samples (1956–1977) provide regional context. ${treatment}`
          : `Total dissolved solids (TDS) is the standard water quality metric for assessing treatment needs. ${treatment} Nearest sample${ageStr}. ${tdsSamples.length} samples within 25 mi, median ${medianTds ? medianTds.toLocaleString() + ' mg/L' : 'n/a'}. Source: TWDB GWDB.`,
      });
    } else {
      // No TDS within range — flag as DD item
      findings.push({
        label: 'Water Quality (TDS)',
        type: 'INFO',
        desc: 'No measured total dissolved solids (TDS) samples within 25 mi',
        detail: 'TWDB GWDB has no nearby TDS values. Pull a fresh water sample during site DD before sizing treatment capex.',
      });
    }

    // 4. Nearby well evidence
    if (within2.length > 0) {
      findings.push({
        label: 'Nearby Well Evidence',
        type: 'FAVORABLE',
        desc: `${within2.length} TWDB-registered well${within2.length > 1 ? 's' : ''} within 2 mi${avgDepth ? ` — avg depth ${avgDepth} ft` : ''}${avgYield ? `, avg yield ${avgYield} GPM` : ''}`,
        detail: 'TWDB Submitted Drillers Reports. Wells nearby indicate drillable groundwater at these depths/yields.',
      });
    } else if (wells.length > 0) {
      const nearest = wells[0];
      findings.push({
        label: 'Nearest Well',
        type: 'INFO',
        desc: `${nearest.distance_miles} mi${nearest.total_depth_ft ? `, ${nearest.total_depth_ft} ft deep` : ''}${nearest.yield_gpm ? `, ${nearest.yield_gpm} GPM` : ''}`,
        detail: 'No productive wells within 2 mi — field hydrogeological assessment recommended.',
      });
    }

    // 5. BTM consumption context
    findings.push({
      label: 'BTM Volume Requirement',
      type: 'INFO',
      desc: '~300–400K gal/yr (~1,000 GPD) auxiliary needs — air-cooled heat rejection',
      detail: isMeiners ? 'Zero evaporative makeup. Verify that a well or contracted hauling can meet auxiliary and fire-water requirements before selecting the supply.' : 'Zero evaporative makeup. Fire suppression, domestic, and equipment cooling only. Orders of magnitude below wet-cooled DC water demand. Solvable by single well OR commercial hauling at this volume.',
    });

    if (isUvalde) findings.splice(0, findings.length,
      { label: 'Supply Options', type: 'CAUTION', desc: 'Edwards Aquifer mapping and nearby water-quality records support further inquiry. No site supply, well yield or water right is confirmed.', detail: 'Confirm the applicable Edwards Aquifer Authority and local requirements, an existing well or utility connection, and commercial hauling as a fallback.' },
      { label: 'Well and Quality Evidence', type: 'INFO', desc: 'The nearest stored driller-report well is about 51 mi away; this reflects limited dataset coverage. A historic water-quality sample about 1.4 mi away does not establish site yield or current quality.', detail: 'Obtain local well logs, pump tests and current samples before selecting a source or treatment system.' },
      { label: 'Demand Basis', type: 'INFO', desc: 'Air cooling reduces water demand. Size domestic, equipment and fire-water supply from the actual facility design.' },
    );
    const summaryType: FindingType = isUvalde ? 'CAUTION' : pathwayType;
    const summaryLabel = isMeiners ? 'Unverified' : summaryType === 'FAVORABLE' ? 'Favorable' : summaryType === 'CAUTION' ? 'Caution' : 'Info';
    const summaryDetail = isMeiners ? 'Verify seller-reported wells, current yield and chemistry, rights and district requirements. Historical off-site samples do not establish a usable on-site supply.' : isUvalde ? 'Confirm a permitted source and reliable yield before fixing the water design.' : effective === 'on_site_well' ? 'Drill a well — nearby wells confirm feasibility.'
      : effective === 'municipal' ? 'Connect to municipal utility.'
      : effective === 'aquifer_no_permit' ? 'Drill a well — no permit required.'
      : effective === 'aquifer_gcd' ? 'Apply for GCD permit — 60–120 day timeline.'
      : effective === 'trucked' ? 'Commercial hauling or field verification of well potential.'
      : 'See sourcing pathway above.';

    return (
      <div ref={ref} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Water Sourcing</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px', }}>
            {isUvalde ? 'Water supply and treatment remain to be verified for the selected design.' : <>Water supply assessment for BTM air-cooled configuration (~300–400K gal/yr auxiliary needs; zero evaporative makeup). Evaluates sourcing pathway, regulatory jurisdiction, and nearby well evidence for on-site drilling feasibility.</>}
          </div>
        </div>
        {findings.map((f, i) => (
          <div key={i} className="briefing-finding-row" style={{
            padding: '8px 12px', marginBottom: '4px', borderRadius: '4px',
            background: REPORT.sand,
          }}>
            <TierEyebrow color={FINDING_COLORS[f.type]}>{f.type}</TierEyebrow>
            <div style={{ fontSize: '10px', fontWeight: 600, color: REPORT.heading, marginBottom: '2px' }}>{f.label}</div>
            <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{f.desc}</div>
            {f.detail && <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '2px' }}>{f.detail}</div>}
          </div>
        ))}
        <div className="briefing-readiness-summary" style={{
          marginTop: '8px', padding: '8px 12px', borderRadius: '4px',
          background: REPORT.sand,
        }}>
          <TierEyebrow color={FINDING_COLORS[summaryType]} marginBottom={6}>
            Water Readiness · {summaryLabel}
          </TierEyebrow>
          <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{summaryDetail}</div>
        </div>
        <SourceFootnote>Source: {['TX','NM','AZ'].includes(parcel.state || '') ? 'TWDB aquifer boundaries, TWDB Submitted Drillers Reports, TX GCD / AZ AMA / NM closure boundaries.' : 'USGS National Water Information System, state groundwater regulator. Confirm local permitting before drilling.'}</SourceFootnote>
      </div>
    );
  }
);

// ── Mine Hazards Section (Atwater Intelligence — Appalachia DD) ──

const PROBLEM_CODE_LABELS: Record<string, string> = {
  S: 'Subsidence', GO: 'Gob Pile / Refuse', DS: 'Dangerous Slide',
  DPE: 'Dangerous Pile/Embankment', SB: 'Surface Burning',
  UMF: 'Underground Mine Fire', SA: 'Spoil Area',
  IRW: 'Industrial/Residential Waste', CSL: 'Clogged Stream Lands',
  SL: 'Slide', SP: 'Spoil Pile', HR: 'Highwall Remnant',
  BE: 'Bench', PI: 'Pit Impoundment', DP: 'Dangerous Pile',
};

const SEVERITY_COLORS: Record<string, string> = {
  emergency: REPORT.fail, active: REPORT.caution, unfunded: REPORT.caution,
  completed: REPORT.pass, unknown: REPORT.secondary,
};

const MineHazardsSection = React.forwardRef<HTMLDivElement, { parcel: ParcelDetail; redacted?: boolean }>(
  ({ parcel, redacted }, ref) => {
    const hasMineData = parcel.in_mine_subsidence_zone != null
      || parcel.nearest_mine_subsidence_miles != null
      || parcel.in_aml_site != null
      || parcel.nearest_aml_miles != null;

    if (!hasMineData) return null;

    const overMine = parcel.in_mine_subsidence_zone === true;
    const onAml = parcel.in_aml_site === true;
    const mineMi = parcel.nearest_mine_subsidence_miles != null ? Number(parcel.nearest_mine_subsidence_miles) : null;
    const amlMi = parcel.nearest_aml_miles != null ? Number(parcel.nearest_aml_miles) : null;
    const sev = parcel.aml_severity || 'unknown';
    const sevColor = SEVERITY_COLORS[sev] || REPORT.secondary;
    const probLabel = parcel.aml_problem_code ? (PROBLEM_CODE_LABELS[parcel.aml_problem_code] || parcel.aml_problem_code) : null;

    const findings: Array<{ label: string; type: FindingType; desc: string; detail?: string }> = [];

    const parcelAc = parcel.acreage != null ? Number(parcel.acreage) : null;
    const mineAc = parcel.mine_overlap_acres != null ? Number(parcel.mine_overlap_acres) : null;
    const amlAc = parcel.aml_overlap_acres != null ? Number(parcel.aml_overlap_acres) : null;
    const minePct = (mineAc != null && parcelAc && parcelAc > 0) ? (mineAc / parcelAc) * 100 : null;
    const amlPct = (amlAc != null && parcelAc && parcelAc > 0) ? (amlAc / parcelAc) * 100 : null;
    const fmtAc = (ac: number | null) => ac == null ? '?' : ac.toFixed(ac < 10 ? 1 : 0);

    if (overMine) {
      findings.push({
        label: mineAc != null && parcelAc
          ? `${fmtAc(mineAc)} of ${fmtAc(parcelAc)} ac (${minePct?.toFixed(0)}%) sit above historic mine workings`
          : 'Parcel over historic underground mine',
        type: minePct != null && minePct >= 40 ? 'UNFAVORABLE' : 'CAUTION',
        desc: parcel.mine_subsidence_seam
          ? `Mapped ${parcel.mine_subsidence_seam} seam working below. Continued static settlement plus latent subsidence risk.`
          : 'Sits above a mapped WVDEP underground mine footprint.',
        detail: minePct != null && minePct < 40
          ? `Mine footprint covers ${minePct.toFixed(0)}% of parcel — pad layout can likely route around it. Confirm with geotechnical survey ($80K–$250K) before finalizing site plan.`
          : 'DC foundation spec cannot use standard shallow mat over the affected area. Options: grouted void fill, deep piers to sound rock below workings, or relocate pads. Added cost: $200K–$2M per building depending on depth and void volume.',
      });
    } else if (mineMi != null) {
      findings.push({
        label: `Nearest underground mine working: ${mineMi.toFixed(2)} mi (off parcel)`,
        type: mineMi < 0.25 ? 'CAUTION' : 'FAVORABLE',
        desc: parcel.mine_subsidence_seam
          ? `${parcel.mine_subsidence_seam} seam. Subsidence risk layer does not intersect this parcel. ${mineMi < 0.25 ? 'Close enough to warrant a geotechnical line-of-sight confirmation before LOI.' : 'Off the mine footprint — standard foundation spec likely applies pending a confirmatory bore.'}`
          : `Subsidence risk layer does not intersect this parcel. ${mineMi < 0.25 ? 'Close enough to warrant a geotechnical bore confirming no off-permit workings.' : 'Standard foundation spec likely applies.'}`,
      });
    }

    const overlaps = parcel.aml_overlaps || [];
    const tiers: Record<AmlTier, { count: number; ac: number }> = {
      FUNDED: { count: 0, ac: 0 }, ACTIVE: { count: 0, ac: 0 },
      INVENTORIED: { count: 0, ac: 0 }, COMPLETED: { count: 0, ac: 0 },
    };
    for (const o of overlaps) {
      const t = classifyAmlOverlap(o);
      tiers[t].count += 1;
      tiers[t].ac += Number(o.overlap_acres || 0);
    }
    const hasLandownerGrading = hasLandownerEarthwork(parcel);
    const cleanAc = parcelAc != null && amlAc != null ? Math.max(0, parcelAc - amlAc) : null;
    const cleanPct = cleanAc != null && parcelAc ? (cleanAc / parcelAc) * 100 : null;

    if (onAml && overlaps.length > 0) {
      const lineItems = [...overlaps]
        .sort((a, b) => Number(b.overlap_acres || 0) - Number(a.overlap_acres || 0))
        .map(o => {
          const tier = classifyAmlOverlap(o);
          const ac = o.overlap_acres != null ? Number(o.overlap_acres) : 0;
          const nm = o.pad_name || o.pad_number || 'Unnamed pad';
          const amlisNote = o.amlis_status === 'Not in AMLIS' ? ' · not on federal priority inventory' : o.amlis_status === 'In AMLIS' ? ' · federal inventory' : '';
          const fundNote = o.status_raw === 'U' ? ' · unfunded' : '';
          const updated = o.date_updated ? ` · WVDEP last updated ${new Date(o.date_updated).getFullYear()}` : '';
          return `${fmtAc(ac)} ac — ${nm} (${tier.toLowerCase()}${amlisNote}${fundNote}${updated})`;
        });

      const headline = amlAc != null && parcelAc
        ? `${fmtAc(amlAc)} of ${fmtAc(parcelAc)} ac (${amlPct?.toFixed(0)}%) overlap WVDEP AML surface-hazard features`
        : `Parcel overlaps WVDEP AML surface-hazard features`;
      const priorityCount = tiers.FUNDED.count + tiers.ACTIVE.count;
      const priorityAcTotal = tiers.FUNDED.ac + tiers.ACTIVE.ac;
      const inventoriedOnly = priorityCount === 0 && tiers.INVENTORIED.count > 0;
      const detailLines: string[] = [];
      if (priorityCount > 0)
        detailLines.push(`${priorityCount} feature${priorityCount === 1 ? '' : 's'} with funded or federally-listed priority (${fmtAc(priorityAcTotal)} ac) — request WVDEP file before site layout.`);
      if (tiers.INVENTORIED.count > 0)
        detailLines.push(`${tiers.INVENTORIED.count} feature${tiers.INVENTORIED.count === 1 ? '' : 's'} inventoried only (${fmtAc(tiers.INVENTORIED.ac)} ac) — unfunded and not on the federal reclamation priority list (AMLIS). These are catalogued for completeness; no active state or federal action expected.`);
      if (tiers.COMPLETED.count > 0)
        detailLines.push(`${tiers.COMPLETED.count} feature${tiers.COMPLETED.count === 1 ? '' : 's'} already reclaimed (${fmtAc(tiers.COMPLETED.ac)} ac) — confirm post-reclamation certification.`);
      detailLines.push('Per-feature: ' + lineItems.join(' · '));

      // Escalate to CAUTION only when priority overlap is material (≥50 ac or ≥5% of
      // parcel); otherwise INFO. Keeps de minimis active features from reading as alarms.
      const priorityPct = parcelAc && parcelAc > 0 ? (priorityAcTotal / parcelAc) * 100 : 0;
      const materialPriority = priorityAcTotal >= 50 || priorityPct >= 5;
      const findingType: FindingType = materialPriority ? 'CAUTION' : 'INFO';
      findings.push({
        label: headline,
        type: findingType,
        desc: `Surface features from WVDEP's Abandoned Mine Land inventory — pre-SMCRA (pre-1977) highwalls, spoil piles, or pits. Distinct from the mine-subsidence layer, which maps underground workings. ${parcel.mine_overlap_acres != null && Number(parcel.mine_overlap_acres) === 0 ? 'No underground workings on this parcel — no foundation-collapse risk.' : ''}`,
        detail: detailLines.join(' '),
      });

      // Mitigation plan finding — pairs with the overlap finding, explicit and quantitative
      const mitigationLines: string[] = [];
      if (cleanAc != null && cleanPct != null) {
        mitigationLines.push(`Clean acreage: ~${fmtAc(cleanAc)} ac (${cleanPct.toFixed(0)}% of parcel) is unaffected — more than adequate for a 160 MW ${redacted ? 'initial' : 'Integra'} tranche with laydown, and contiguous enough to stage multi-phase buildout.`);
      }
      if (inventoriedOnly) {
        mitigationLines.push('Regulatory signal: every overlapping feature is unfunded and not on the federal AMLIS priority list. WVDEP records have not been updated on the largest pads since 2014 — strong dormancy signal.');
      } else if (tiers.FUNDED.ac + tiers.ACTIVE.ac < 10) {
        mitigationLines.push(`Regulatory signal: only ${fmtAc(tiers.FUNDED.ac + tiers.ACTIVE.ac)} ac fall under federally-listed or funded priority — <1% of the parcel. The remaining ${fmtAc(tiers.INVENTORIED.ac)} ac are inventoried only.`);
      }
      mitigationLines.push('Engineering scope: highwalls are surface-grading problems (key-back, drainage routing, revegetation) — well-understood earthwork, not the mine-void grouting that subsidence zones would require.');
      if (hasLandownerGrading) {
        mitigationLines.push('Landowner commitment: per LAND READINESS clause, landowner has committed to earthwork and site preparation for qualified buyers — the grading exposure sits with the seller, not the buyer.');
      }
      mitigationLines.push('DD scope: Phase I ESA (standard on every BTM parcel) will document each feature. CERCLA Bona Fide Prospective Purchaser defense is routine; Phase II sampling at the toe of each highwall recommended to screen acid mine drainage.');
      findings.push({
        label: 'Mitigation path',
        type: 'FAVORABLE',
        desc: mitigationLines.join(' '),
      });
    } else if (amlMi != null) {
      findings.push({
        label: `Nearest AML site: ${amlMi.toFixed(2)} mi${probLabel ? ` (${probLabel})` : ''}`,
        type: amlMi < 0.25 ? 'CAUTION' : 'FAVORABLE',
        desc: amlMi < 0.25 ? 'Close enough that Phase I ESA should note it explicitly; runoff, combustion, or visual impact possible.' : 'No AML overlap on parcel. Phase I ESA will still survey the broader area.',
      });
    }

    const mostlyInventoried = onAml && (tiers.FUNDED.ac + tiers.ACTIVE.ac) < 10 && tiers.INVENTORIED.ac > 0;
    const verdict = overMine
      ? 'Mine-related conditions impact foundation spec or usable acreage. Requires geotechnical and/or ESA follow-up before LOI.'
      : onAml && mostlyInventoried
      ? `${cleanAc != null ? `~${fmtAc(cleanAc)} ac of clean SW acreage available for pad siting. ` : ''}AML overlap is concentrated in the opposite lobe and is predominantly unfunded, not on the federal priority list. No foundation-collapse risk (no underground workings on parcel). Standard Phase I ESA + Phase II sampling at highwall toes covers DD${hasLandownerGrading ? '; landowner-funded earthwork per LAND READINESS clause' : ''}.`
      : onAml
      ? 'AML overlap on parcel. Regulatory timing depends on WVDEP reclamation queue; confirm with Office of AML before LOI. No foundation-collapse risk from mine subsidence.'
      : (mineMi != null && mineMi < 0.25) || (amlMi != null && amlMi < 0.25)
      ? 'Mine hazards nearby but off parcel. Standard DD scope plus a note in the Phase I is likely sufficient.'
      : 'No mine hazards identified on or immediately adjacent to parcel.';

    return (
      <div ref={ref} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Mine Subsidence &amp; AML Screening</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px' }}>
            Flags parcels sitting over historic longwall / room-and-pillar workings (foundation risk)
            and pre-SMCRA Abandoned Mine Land features (physical hazard + CERCLA exposure).
            Derived from WVDEP TAGIS.
          </div>
        </div>
        {findings.map((f, i) => (
          <div key={i} className="briefing-finding-row" style={{
            padding: '8px 12px', marginBottom: '4px', borderRadius: '4px',
            background: REPORT.sand,
          }}>
            <TierEyebrow color={FINDING_COLORS[f.type]}>{f.type}</TierEyebrow>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '2px' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, color: REPORT.heading }}>{f.label}</span>
              {onAml && f.label.includes('AML') && f.type !== 'INFO' && f.type !== 'FAVORABLE' && (
                <span style={{ fontSize: '9px', fontWeight: 600, color: sevColor, marginLeft: 'auto' }}>
                  {sev.toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{f.desc}</div>
            {f.detail && (
              <div style={{ fontSize: '10px', color: REPORT.secondary, lineHeight: 1.4, marginTop: '3px', fontStyle: 'italic' }}>
                {f.detail}
              </div>
            )}
          </div>
        ))}
        <div style={{ marginTop: '10px', fontSize: '11px', color: REPORT.body, lineHeight: 1.5, }}>
          {verdict}
        </div>
        <SourceFootnote>
          Source: WVDEP TAGIS · mining_reclamation MapServer layer 10 (underground mining limits) +
          abandoned_mine_lands MapServer layers 3 &amp; 4 (AMLPolygons + AMLPAS).
          Atwater-generated overlay — not part of standard commercial DD datasets.
        </SourceFootnote>
      </div>
    );
  }
);

// ── Oil & Gas Surface Activity ──
// Renders only when the payload carries a targeted RRC well screen (`oilGasWells`,
// parcel_proximity layer 'oil_gas_wells_screen'). Every other briefing: null.
const WELL_STATUS_LABEL: Record<string, string> = {
  producing: 'Producing', plugged: 'Plugged', permitted: 'Permitted location', dry_hole: 'Dry hole',
  shut_in: 'Shut-in', injection: 'Injection / disposal', other: 'Well',
};
const OilGasWellsSection = React.forwardRef<HTMLDivElement, { data: BriefingData }>(
  ({ data }, ref) => {
    const screen = data.oilGasWells;
    if (!screen || !screen.summary || !Array.isArray(screen.wells)) return null;
    const s = screen.summary;
    const isMeiners = data.carveOutAggregate?.slug === MEINERS_SLUG;
    const inside = screen.wells.filter(w => w.inside);
    const n = (v: number | null | undefined) => (v ?? 0).toLocaleString();
    const plural = (v: number, one: string, many: string) => `${n(v)} ${v === 1 ? one : many}`;
    const laterals = inside.filter(w => w.lateral_length_ft != null && Number(w.lateral_length_ft) > 0);
    const latMin = laterals.length ? Math.min(...laterals.map(w => Number(w.lateral_length_ft))) : null;
    const latMax = laterals.length ? Math.max(...laterals.map(w => Number(w.lateral_length_ft))) : null;
    const bhInside = inside.filter(w => w.bottom_hole?.inside === true).length;
    const permitted = inside.filter(w => w.status === 'permitted');
    const permittedYears = permitted.map(w => w.permit_approved).filter(Boolean).map(d => Number(String(d).slice(0, 4)));
    const latestPermitYear = permittedYears.length ? Math.max(...permittedYears) : null;
    const permittedOps = [...new Set(permitted.map(w => w.operator).filter(Boolean))] as string[];
    const producingOps = (s.operators || []).filter(o => o.name !== 'Unknown');
    const plugged = s.inside_plugged + s.inside_dry_hole;
    const stateRuleNote = isMeiners
      ? 'The 500-ft and 1,000-ft map rings are illustrative development screens, not regulatory setbacks; operator surface-use terms and survey control the final exclusions.'
      : 'Texas has no statewide building setback from a wellhead; operators and insurers typically hold 150–300 ft, and the surface-use agreement fixes the number.';

    const findings: Array<{ label: string; type: FindingType; desc: string; detail?: string }> = [];
    findings.push({
      label: 'Surface footprint',
      type: s.inside_producing > 0 ? 'CAUTION' : 'INFO',
      desc: isMeiners
        ? '18 drilled wells on 9 pads plus 5 permitted locations in 2 clusters. RRC attributes 14 producers; Enverus verifies 12 active and 1 inactive, with Gray B1H outside the export.'
        : `${plural(s.inside_total, 'RRC well record', 'RRC well records')} inside the tract — ${n(s.inside_producing)} producing, ${n(s.inside_plugged)} plugged, ${n(s.inside_dry_hole)} dry hole, ${plural(s.inside_permitted, 'permitted location', 'permitted locations')} — on about ${plural(s.inside_pads, 'surface pad', 'surface pads')}; ${n(s.inside_horizontal)} are horizontal.`,
      detail: `Pads, access roads, flowlines and tank batteries are the surface encumbrances a campus plan works around. ${stateRuleNote}`,
    });
    if (producingOps.length) {
      findings.push({
        label: 'Operators of record',
        type: 'INFO',
        desc: producingOps.map(o => `${o.name} (${o.wells_inside})`).join(' · '),
        detail: 'The surface-use agreement counterparty is the operator of record for the producing wells; where the mineral estate is severed, the mineral owner is a separate party with the right of reasonable surface use.',
      });
    }
    if (s.inside_permitted > 0) {
      const recent = s.permits_active_recent > 0;
      findings.push({
        label: 'Drilling permits',
        type: recent ? 'CAUTION' : 'INFO',
        desc: recent
          ? `${plural(s.permits_active_recent, 'permitted location', 'permitted locations')} approved since 2022 — active drilling plans on the tract.`
          : `${plural(s.inside_permitted, 'permitted location', 'permitted locations')}${permittedOps.length ? ` (${permittedOps.join(', ')})` : ''}, all approved before 2022${latestPermitYear ? ` — latest ${latestPermitYear}` : ''}. An RRC drilling permit lapses two years after approval unless the well is spudded, so these read as historical intent, not scheduled work.`,
        detail: s.permit_year_range ? `Permits on the tract span ${s.permit_year_range[0]}–${s.permit_year_range[1]}. Confirm current intent with the operator; a successor operator can re-permit the same locations.` : undefined,
      });
    }
    if (plugged > 0) {
      findings.push({
        label: 'Plugged wells and dry holes',
        type: 'CAUTION',
        desc: `${plural(plugged, 'abandoned wellbore', 'abandoned wellbores')} inside the tract${inside.some(w => (w.status === 'plugged' || w.status === 'dry_hole') && w.permit_approved && Number(String(w.permit_approved).slice(0, 4)) < 2000) ? ' — legacy Austin Chalk-era wells' : ''}.`,
        detail: 'Locate each wellbore on the ground, pull the RRC W-3 plugging record, and keep foundations off the casing (RRC Rule 14 is the plugging standard; a re-plug is the remedy for a legacy well that leaks).',
      });
    }
    if (laterals.length) {
      findings.push({
        label: 'Horizontal laterals under the tract',
        type: 'INFO',
        desc: `${plural(laterals.length, 'horizontal well', 'horizontal wells')} with mapped laterals of ${n(latMin)}–${n(latMax)} ft${bhInside ? `; ${n(bhInside)} bottom holes sit under the tract` : ''}.`,
        detail: 'Laterals at roughly 8,000 ft do not constrain surface use. They show the mineral estate is being actively developed and that the operator will want continued access to its pads and flowlines.',
      });
    }
    findings.push({
      label: `Within ${screen.scope?.buffer_mi ?? 0.5} mi of the boundary`,
      type: 'INFO',
      desc: `${plural(s.buffer_total, 'more well record', 'more well records')} (${n(s.buffer_producing)} producing) — an active field on every side of the tract.`,
    });

    const sorted = [...inside].sort((a, b) => (a.section || '').localeCompare(b.section || '') || a.status.localeCompare(b.status) || (a.operator || '').localeCompare(b.operator || ''));
    const cell: React.CSSProperties = { padding: '3px 6px', fontSize: '9px', color: REPORT.body, borderBottom: `1px solid ${REPORT.sand}`, verticalAlign: 'top' };
    const head: React.CSSProperties = { ...cell, fontWeight: 700, color: REPORT.heading, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '8px' };

    return (
      <div ref={ref} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Oil &amp; Gas Surface Activity</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px' }}>
            Railroad Commission well records intersected with the tract{screen.scope?.buffer_mi ? ` and a ${screen.scope.buffer_mi}-mile buffer` : ''}: who operates on the surface, what is producing, plugged or merely permitted, and how the horizontal laterals run beneath it. Map layer: Oil &amp; Gas Wells.
          </div>
        </div>
        {findings.map((f, i) => (
          <div key={i} className="briefing-finding-row" style={{ padding: '8px 12px', marginBottom: '4px', borderRadius: '4px', background: REPORT.sand }}>
            <TierEyebrow color={FINDING_COLORS[f.type]}>{f.type}</TierEyebrow>
            <div style={{ fontSize: '10px', fontWeight: 600, color: REPORT.heading, marginBottom: '2px' }}>{f.label}</div>
            <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{f.desc}</div>
            {f.detail && <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '2px' }}>{f.detail}</div>}
          </div>
        ))}
        {sorted.length > 0 && (
          <div style={{ marginTop: '10px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>Section</th>
                  <th style={{ ...head, textAlign: 'left' }}>Operator</th>
                  <th style={{ ...head, textAlign: 'left' }}>Lease · well</th>
                  <th style={{ ...head, textAlign: 'left' }}>{isMeiners ? 'RRC status / Enverus activity' : 'Status'}</th>
                  <th style={{ ...head, textAlign: 'left' }}>Profile</th>
                  <th style={{ ...head, textAlign: 'right' }}>Permit</th>
                  <th style={{ ...head, textAlign: 'right' }}>TD ft</th>
                  <th style={{ ...head, textAlign: 'left' }}>API</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(w => (
                  <tr key={w.api}>
                    <td style={cell}>{w.section || '—'}</td>
                    <td style={cell}>{w.operator || '—'}</td>
                    <td style={cell}>{[w.lease_name, w.well_no].filter(Boolean).join(' · ') || '—'}</td>
                    <td style={cell}>
                      {isMeiners ? <>RRC: {WELL_STATUS_LABEL[w.status] || w.symbol}{w.enverusActivityStatus && <div style={{ marginTop: '3px', color: REPORT.secondary }}>Enverus: {w.enverusActivityStatus}{w.enverusLastProductionMonth ? ` · last production ${w.enverusLastProductionMonth}` : ''}</div>}</> : WELL_STATUS_LABEL[w.status] || w.symbol}
                    </td>
                    <td style={cell}>{w.lateral_length_ft ? `Horizontal · ${n(w.lateral_length_ft)} ft` : (w.wellbore_profile || (w.horizontal ? 'Horizontal' : '—'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{w.permit_approved ? String(w.permit_approved).slice(0, 4) : '—'}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{w.api_depth_ft ? n(w.api_depth_ft) : '—'}</td>
                    <td style={{ ...cell, fontFamily: 'monospace' }}>{w.api}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <SourceFootnote>Source: Texas Railroad Commission public GIS (well locations, horizontal surface locations and lateral lines) joined to RRC wellbore and W-1 drilling-permit records, queried {screen.scope?.fetched_at || 'this cycle'}. Status is the RRC map symbol; permit year is the latest W-1 approval. Lease-level production volumes are not included.{isMeiners && ' Enverus activity and last-production month are shown separately for covered wells; inactive production does not release surface or mineral rights.'}</SourceFootnote>
      </div>
    );
  }
);

// Curator flood-gate override (structured_intel.flood_gate): a briefing whose flood
// story rests on a completed non-FEMA screen (Cinco: TWDB 1% SFP overlay) passes the
// gate with curated text instead of the generic unmapped-county inquiry. Inert
// wherever the key is absent — every FEMA-determined briefing is untouched.
function applyCuratedFloodGate(sev: ReturnType<typeof floodSeverity>, parcel: ParcelDetail): ReturnType<typeof floodSeverity> {
  const o = (parcel as any).structured_intel?.flood_gate;
  if (!o) return sev;
  if (o.status === 'PASS') {
    return { ...sev, level: 'clear', label: o.label || 'Screened', color: REPORT.pass, ddStatus: 'ADDRESSED', subLine: o.summary || sev.subLine };
  }
  // CAUTION: a completed non-FEMA screen with a material mapped share (Meiners Ranch:
  // TWDB 1% overlay, 23% of the block). Stays a caveated gate with curated copy so the
  // generic FEMA wording never describes a TWDB result; optional pct/total_acres carry
  // the screen's own basis into the Upland Buildable hero.
  if (o.status === 'CAUTION') {
    const pct = Number.isFinite(Number(o.pct)) ? Number(o.pct) : sev.pct;
    const totalAcres = Number.isFinite(Number(o.total_acres)) ? Number(o.total_acres) : sev.totalAcres;
    const floodAcres = pct != null && totalAcres != null ? (pct / 100) * totalAcres : sev.floodAcres;
    const buildableAcres = totalAcres != null && floodAcres != null ? Math.max(0, totalAcres - floodAcres) : sev.buildableAcres;
    return { ...sev, level: 'partial', label: o.label || 'Screened — caution', color: REPORT.caution, ddStatus: 'FLAGGED', subLine: o.summary || sev.subLine, pct, totalAcres, floodAcres, buildableAcres };
  }
  return sev;
}

// ── Site Intelligence analyst notes ──

// Renders seeded site_evaluations.notes. An optional "EVIDENCE & ANALYSIS" section —
// everything from a line starting with that header to the end of the notes — renders
// collapsed by default, keeping the decision layer short while provenance-grade
// detail (permit chronology, filings reconciliation, source ledger) stays one click
// away. The body stays in the DOM when collapsed (hidden by inline style, forced
// visible by the print stylesheet) so the Print PDF always carries the evidence.
// Structured Site Intelligence layout (structured_intel.site_intel) — curator-authored
// card grid (head + sectioned two-column cards) replacing the prose decision layer.
// The notes then carry only the EVIDENCE & ANALYSIS record. Briefings without the
// key render the legacy prose path unchanged.
// Optional per-card map binding (curated JSON, absent on every briefing that does not
// set it): hovering the card emphasizes that feature class on the map. A pipeline
// binding resolves by name to the Fuel Delivery table's per-line keys so only the
// named line lights; an unmatched name falls back to the wholesale brighten.
type SiteIntelCardMap =
  | { type: 'pipeline'; name: string }
  | { type: 'parcel' | 'oil-gas-wells' | 'flood' | 'wetlands' | 'highway' | 'railroad' | 'airport' | 'substation' | 'fiber' | 'compressor' | 'gas-plant' };
interface SiteIntelCard { label: string; tone?: 'inquiry' | 'model'; headline: string; body: string; bullets?: string[]; map?: SiteIntelCardMap }
const CARD_HOVER_TYPES = new Set(['parcel', 'oil-gas-wells', 'flood', 'wetlands', 'highway', 'railroad', 'airport', 'substation', 'fiber', 'compressor', 'gas-plant']);
function resolveCardHover(map: SiteIntelCardMap | undefined, pipelines: NearbyPipeline[] | null | undefined): HoverFeature {
  if (!map || typeof map !== 'object' || typeof map.type !== 'string') return null;
  if (map.type === 'pipeline') {
    if (!map.name) return null;
    const needle = map.name.toUpperCase();
    const keys = new Set<string>();
    for (const pl of pipelines || []) {
      const hit = [pl.name, pl.system_display_name, pl.system_name].some(n => (n || '').toUpperCase().includes(needle));
      if (!hit) continue;
      keys.add(`${pl.name || ''}|${pl.operator || ''}`);
      if (pl.segments) for (const s of pl.segments) keys.add(`${s.name || ''}|${s.operator || ''}`);
    }
    return { type: 'pipeline', name: map.name, segmentKeys: Array.from(keys) };
  }
  return CARD_HOVER_TYPES.has(map.type) ? ({ type: map.type } as HoverFeature) : null;
}
interface SiteIntelLayout { headline: string; chip?: string; summary: string; sections: Array<{ title: string; cards: SiteIntelCard[] }> }
const siCardLabelColor = (tone?: string) =>
  tone === 'inquiry' ? REPORT.caution : tone === 'model' ? REPORT.highlight : REPORT.secondary;

const SiteIntelStructuredBody: React.FC<{ intel: SiteIntelLayout; pipelines?: NearbyPipeline[] | null; isMeiners?: boolean; onInspectWells?: () => void }> = ({ intel, pipelines, isMeiners = false, onInspectWells }) => {
  const { hoverFeature, setHoverFeature } = useScrollSync();
  const renderCard = (c: SiteIntelCard, i: number, rightCol: boolean, laterRow: boolean) => {
            const hover = resolveCardHover(c.map, pipelines);
            const active = hover != null && hoverFeature != null && hoverFeature.type === hover.type
              && (hover.type !== 'pipeline' || (hoverFeature as any).name === (hover as any).name);
            return (
              <article
                key={i}
                onMouseEnter={hover ? () => setHoverFeature(hover) : undefined}
                onMouseLeave={hover ? () => setHoverFeature(null) : undefined}
                style={{
                  minWidth: 0,
                  padding: `${laterRow ? 8 : 4}px ${rightCol ? 0 : 11}px 9px ${rightCol ? 13 : 0}px`,
                  borderLeft: rightCol ? `1px solid ${REPORT.rule}` : undefined,
                  borderTop: laterRow ? `1px solid ${REPORT.rule}80` : undefined,
                  cursor: hover ? 'pointer' : undefined,
                  background: active ? `${REPORT.accent}10` : undefined,
                  transition: 'background 0.15s',
                }}>
                <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.055em', textTransform: 'uppercase', color: siCardLabelColor(c.tone) }}>{c.label}</span>
                <strong style={{ display: 'block', fontSize: '10px', margin: '4px 0 3px', textTransform: 'uppercase', color: REPORT.heading }}>{c.headline}</strong>
                {c.bullets && c.bullets.length > 0 && (
                  <ul className={isMeiners && c.label === 'Gas position' ? 'meiners-gas-options' : undefined} style={{ margin: '6px 0 8px', paddingLeft: 17, fontSize: '10px', lineHeight: 1.45, color: REPORT.body }}>
                    {c.bullets.map((bullet, bi) => <li key={bi} style={{ paddingLeft: 2, marginBottom: 7 }}>{bullet}</li>)}
                  </ul>
                )}
                <p style={{ fontSize: '10px', lineHeight: 1.38, margin: 0, color: REPORT.body }}>{c.body}</p>
              </article>
            );
  };
  return (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, borderBottom: `1px solid ${REPORT.rule}`, padding: '3px 0 12px' }}>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.025em', textTransform: 'uppercase', color: REPORT.heading, margin: '3px 0 0' }}>{intel.headline}</div>
        <div style={{ fontSize: '11px', lineHeight: 1.45, margin: '5px 0 0', color: REPORT.body, maxWidth: 640 }}>{intel.summary}</div>
        {onInspectWells && (
          <button type="button" className="briefing-no-print meiners-well-inspect-button" onClick={onInspectWells}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 12, minHeight: 38, padding: '9px 12px', border: `1px solid ${REPORT.highlight}`, borderRadius: 4, background: REPORT.highlight, color: REPORT.white, fontFamily: 'inherit', fontSize: '11px', fontWeight: 600, lineHeight: 1.35, textAlign: 'left', cursor: 'pointer' }}>
            <span>Inspect well exclusions and candidate footprints</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </button>
        )}
      </div>
      {intel.chip && (
        <span style={{ flex: 'none', border: `1px solid ${REPORT.highlight}99`, color: REPORT.highlight, padding: '4px 7px', fontSize: '9px', fontWeight: 600, letterSpacing: '0.055em', textTransform: 'uppercase' }}>{intel.chip}</span>
      )}
    </div>
    {intel.sections.map((sec, si) => {
      const wells = isMeiners ? sec.cards.filter(c => c.map?.type === 'oil-gas-wells') : [];
      const secondary = sec.cards.filter(c => c.map?.type !== 'oil-gas-wells');
      const gas = isMeiners ? sec.cards.find(c => c.label === 'Gas position' && c.bullets?.length) : undefined;
      const otherCards = gas ? sec.cards.filter(c => c !== gas) : sec.cards;
      return (
      <div key={si} style={{ borderTop: si > 0 ? `1px solid ${REPORT.rule}80` : undefined, paddingTop: 10, marginTop: si > 0 ? 10 : 0 }}>
        <div style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.055em', textTransform: 'uppercase', color: REPORT.secondary }}>{sec.title}</div>
        {wells.length > 0 ? (
          <div className="meiners-intel-grid" style={{ marginTop: 6, alignItems: 'start' }}>
            <div className="meiners-intel-wells">{wells.map((c, i) => renderCard(c, i, false, i > 0))}</div>
            <div className="meiners-intel-secondary">{secondary.map((c, i) => renderCard(c, i, true, i > 0))}</div>
          </div>
        ) : gas ? (
          <>
            <div style={{ marginTop: 6 }}>{renderCard(gas, 0, false, false)}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginTop: 6 }}>
              {otherCards.map((c, i) => renderCard(c, i, i % 2 === 1, i >= 2))}
            </div>
          </>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: sec.cards.length > 1 ? '1fr 1fr' : '1fr', marginTop: 6 }}>
            {sec.cards.map((c, i) => renderCard(c, i, sec.cards.length > 1 && i % 2 === 1, sec.cards.length > 1 ? i >= 2 : i >= 1))}
          </div>
        )}
      </div>
    ); })}
  </div>
  );
};

const isNotesHeader = (line: string) => /^[A-Z][A-Z &—\d]+$/.test(line.trim()) || /^[A-Z][A-Z &—\d]+ —/.test(line.trim());

// Swap the body under a heading ("THE LAND", …) for a per-layout override while a
// seller alternative is on the map (A1 Alt edition). Headings not in the map keep
// the seeded text; the override text may carry its own line breaks.
function applyNotesOverrides(lines: string[], overrides?: Record<string, string>): string[] {
  if (!overrides || Object.keys(overrides).length === 0) return lines;
  const byHeading = new Map(Object.entries(overrides).map(([k, v]) => [k.trim().toUpperCase(), v]));
  const out: string[] = [];
  let skipping = false;
  for (const line of lines) {
    if (isNotesHeader(line)) {
      const override = byHeading.get(line.trim().toUpperCase());
      out.push(line);
      if (override != null) { out.push(...override.split('\n')); skipping = true; } else skipping = false;
      continue;
    }
    if (!skipping) out.push(line);
  }
  return out;
}

const SiteIntelligenceNotes: React.FC<{ notes: string; intel?: SiteIntelLayout; overrides?: Record<string, string>; note?: string; pipelines?: NearbyPipeline[] | null; isMeiners?: boolean; onInspectWells?: () => void }> = ({ notes, intel, overrides, note, pipelines, isMeiners = false, onInspectWells }) => {
  const [evidenceOpen, setEvidenceOpen] = React.useState(false);
  const lines = notes.split('\n');
  const evidenceStart = lines.findIndex(l => l.trim().toUpperCase().startsWith('EVIDENCE & ANALYSIS'));
  const mainLines = applyNotesOverrides(evidenceStart === -1 ? lines : lines.slice(0, evidenceStart), overrides);
  const evidenceLabel = evidenceStart === -1 ? null : lines[evidenceStart].trim();
  const evidenceLines = evidenceStart === -1 ? [] : lines.slice(evidenceStart + 1);
  const renderLines = (ls: string[]) => ls.map((line: string, i: number) => {
    const isHeader = isNotesHeader(line);
    return line.trim() ? <div key={i} style={isHeader ? { fontWeight: 700, marginTop: i > 0 ? 8 : 0, marginBottom: 2, fontSize: '10px', letterSpacing: '0.04em', textTransform: 'uppercase' as const } : {}}>{line}</div> : null;
  });
  return (
    <div className={isMeiners ? 'meiners-site-intelligence' : undefined} style={{ padding: '10px 14px', background: REPORT.highlight + '10', border: `1px solid ${REPORT.highlight}30`, borderRadius: 6, marginBottom: 12, ...(isMeiners ? { containerType: 'inline-size' as const } : {}) }}>
      {isMeiners && <style>{`
        .meiners-intel-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
        .meiners-well-inspect-button:hover { filter: brightness(1.12); }
        .meiners-well-inspect-button:focus-visible { outline: 2px solid ${REPORT.accent}; outline-offset: 3px; }
        @container (max-width: 420px) { .meiners-intel-grid { grid-template-columns: 1fr; } .meiners-intel-secondary article { border-left: 0 !important; padding-left: 0 !important; } }
      `}</style>}
      <div style={{ fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: REPORT.secondary, marginBottom: 4 }}>Site Intelligence</div>
      {note && (
        <div style={{ fontSize: '10.5px', lineHeight: 1.45, color: REPORT.heading, background: REPORT.amberWash, border: `1px solid ${REPORT.accent}40`, borderRadius: 4, padding: '7px 10px', marginBottom: 8 }}>
          {note}
        </div>
      )}
      {intel
        ? <SiteIntelStructuredBody intel={intel} pipelines={pipelines} isMeiners={isMeiners} onInspectWells={onInspectWells} />
        : <div style={{ fontSize: '11px', color: REPORT.heading, lineHeight: '1.4' }}>{renderLines(mainLines)}</div>}
      {evidenceLabel && (
        <div style={{ marginTop: 10, borderTop: `1px solid ${REPORT.rule}80`, paddingTop: 8 }}>
          <button
            type="button"
            onClick={() => setEvidenceOpen(o => !o)}
            className="briefing-no-print"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
              color: REPORT.secondary, fontFamily: 'inherit',
            }}
          >
            <span>{evidenceLabel}</span>
            <span style={{ fontSize: '12px', lineHeight: 1 }}>{evidenceOpen ? '−' : '+'}</span>
          </button>
          {/* Print-only header stand-in for the interactive toggle row */}
          <div className="briefing-print-only" style={{ display: 'none', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: REPORT.secondary }}>{evidenceLabel}</div>
          <div
            className="briefing-evidence-body"
            style={{ display: evidenceOpen ? 'block' : 'none', marginTop: 6, fontSize: '11px', color: REPORT.heading, lineHeight: '1.4', overflowWrap: 'anywhere' }}
          >
            {renderLines(evidenceLines)}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Fiber & Network Connectivity Section ──

const FiberConnectivitySection = React.forwardRef<HTMLDivElement, { data: BriefingData; parcel: ParcelDetail }>(
  ({ data, parcel }, ref) => {
    const isUvalde = isUvaldeBriefing(data);
    const isA1 = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
    const isMeiners = data.carveOutAggregate?.slug === MEINERS_SLUG;
    const carrierName = isMeiners ? 'FiberLight' : parcel.nearest_carrier_fiber_name;
    const carrierMiles = isMeiners ? 3.1 : parcel.nearest_carrier_fiber_miles;

    const findings: Array<{ label: string; type: FindingType; desc: string; detail?: string }> = [];

    // Enterprise carrier route proximity leads this section. The stored distance is
    // parcel-boundary to route; it does not establish service at the parcel.
    if (carrierMiles != null) {
      const dist = Number(carrierMiles);
      findings.push({
        label: 'Enterprise Carrier Route',
        type: dist <= 20 ? 'INFO' : 'CAUTION',
        desc: isMeiners
          ? `FiberLight route ~3.1 mi · Zayo route ~6.3 mi from the three-account block boundary`
          : `${carrierName || 'Carrier'} route ${dist} mi from the parcel boundary`,
        detail: 'Route proximity is a sourcing lead. Serviceability, capacity, lateral path, delivery schedule, SLA and physical diversity require direct carrier confirmation.',
      });
    } else {
      findings.push({
        label: 'Enterprise Carrier Route',
        type: 'CAUTION',
        desc: 'No mapped enterprise carrier route for this location',
        detail: 'Carrier engagement is required to identify a serviceable route and confirm capacity, terms and schedule.',
      });
    }

    // Per-carrier distances are measured from the representative parcel centroid.
    const carrierNearest = parcel.carrier_fiber_nearest || [];
    const closeCarriers = carrierNearest.filter(c => Number(c.nearest_mi) <= 1);
    if (closeCarriers.length >= 2) {
      const sorted = [...closeCarriers].sort((a, b) => Number(a.nearest_mi) - Number(b.nearest_mi));
      findings.push({
        label: 'Multiple Carrier Routes',
        type: 'INFO',
        desc: sorted.map(c => `${c.carrier} ${c.nearest_mi} mi from representative parcel centroid`).join('  \u00b7  '),
        detail: 'Mapped routes do not prove service or physical diversity. Confirm separate entrances, conduits, upstream paths, capacity and commercial terms with each carrier.',
      });
    }

    // 3. Nearest IXP with latency estimate.
    // Only surface as "IXP" when the facility actually hosts ≥5 networks — otherwise it's
    // a regional ISP POP, not an Internet Exchange Point. In that case the Colocation row
    // below (which filters ≥5 networks) is the authoritative peering reference; skip this row.
    const ixpNetworks = parcel.nearest_ixp_networks != null ? Number(parcel.nearest_ixp_networks) : null;
    if (parcel.nearest_ixp_name && parcel.nearest_ixp_miles != null && ixpNetworks != null && ixpNetworks >= 5) {
      const ixpDist = Number(parcel.nearest_ixp_miles);
      // Speed of light in fiber ~200,000 km/s → ~5 μs/km → 5 ns/m → per mi: ~8 μs → per 100 mi: ~0.8 ms
      // A1: quote round-trip at the WonderNetwork-calibrated 0.022 ms/mi so every latency
      // figure on the page shares one basis (the 114 mi → ~2.5 ms RTT Houston line).
      const latMs = isA1 ? (ixpDist * 0.022).toFixed(1) : (ixpDist * 1.609 * 5 / 1000).toFixed(1);
      const type: FindingType = ixpDist <= 50 ? 'FAVORABLE' : ixpDist <= 200 ? 'INFO' : 'CAUTION';
      findings.push({
        label: 'Nearest IXP',
        type,
        desc: `${parcel.nearest_ixp_name} at ${ixpDist} mi — ${ixpNetworks} networks`,
        detail: isUvalde ? `Distance-based latency estimate: ~${latMs} ms one way. Confirm actual routing, latency and service levels with the carrier.` : `${isA1 ? `Est. ~${latMs} ms RTT (distance-based estimate, not a site measurement).` : `Est. one-way fiber latency: ~${latMs} ms.`} ${ixpDist > 100 ? 'Suitable for latency-insensitive AI/HPC training. Not suitable for user-facing cloud services.' : 'Serviceable for both AI training and user-facing services.'}`,
      });
    }

    // 4. Nearest colocation facility (carrier diversity)
    if (parcel.nearest_colo_facility && parcel.nearest_colo_miles != null) {
      const carriers = parcel.nearest_colo_networks;
      const type: FindingType = isUvalde ? 'INFO' : carriers != null && carriers >= 10 ? 'FAVORABLE' : 'INFO';
      findings.push({
        label: 'Carrier Diversity (Colocation)',
        type,
        desc: `${parcel.nearest_colo_facility}${parcel.nearest_colo_city ? ` (${parcel.nearest_colo_city}, ${parcel.nearest_colo_state || ''})` : ''} at ${parcel.nearest_colo_miles} mi${carriers != null ? ` — ${carriers} carriers` : ''}`,
        detail: isUvalde ? 'These carriers serve the San Antonio facility. Separate routes, carrier choice and redundancy at this tract require carrier confirmation.' : carriers != null && carriers >= 10
          ? 'Strong carrier diversity at nearest colocation — supports redundant routing and competitive pricing.'
          : 'Limited carrier count — redundancy options may be constrained.',
      });
    }

    // Summary
    const backboneOk = carrierMiles != null && Number(carrierMiles) <= 20;
    const summaryType: FindingType = backboneOk ? 'INFO' : 'CAUTION';
    const summaryLabel = backboneOk ? 'Carrier inquiry' : 'Route search required';
    const summaryDetail = backboneOk
      ? 'A mapped enterprise carrier route is within sourcing range. Obtain a route-specific serviceability response, capacity proposal, lateral design, SLA and diversity plan.'
      : 'No enterprise carrier route is established within the screened range. Begin a carrier route and serviceability study.';

    return (
      <div ref={ref} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Fiber &amp; Network Connectivity</SectionTitle>
          {/* Corridor-level fiber narrative for AOI parcels. Lifted from the
              Revenant zone deck's Fiber Reach section: BGP-confirmed customer
              ports + measured Houston Greenspoint RTT. Sits above the
              per-parcel data so the reader gets the corridor framing first. */}
          {parcel.gas_supply_zone_slug === 'revenant-east-tx' && (
            <div style={{
              fontSize: '11px', color: REPORT.body, lineHeight: 1.55,
              marginBottom: '12px', padding: '12px 14px',
              background: REPORT.passWash,
              borderRadius: '4px',
            }}>
              <TierEyebrow color={REPORT.tierOk} marginBottom={8}>
                Cloud-Zone-Grade Corridor · BGP-Confirmed
              </TierEyebrow>
              <div>
                Two carriers serve customer ports in this corridor today, both verifiable in ARIN's public route table. ~114 mi / ~2.5 ms RTT{isA1 ? ' (estimated — calibrated against WonderNetwork’s public Houston–Dallas ping reference; carrier test pending)' : ''} to the Houston Greenspoint hyperscaler cluster (DataBank HOU West, Digital Realty 12001 N. Freeway). AZ-pair viable; this is a real cloud zone, not an edge-cache site.
              </div>
            </div>
          )}
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px', }}>
            Connectivity assessment for BTM data center operations.
            Evaluates enterprise carrier route proximity, IXP latency, and carrier options at the nearest colocation facility.
          </div>
        </div>
        {findings.map((f, i) => (
          <div key={i} className="briefing-finding-row" style={{
            padding: '8px 12px', marginBottom: '4px', borderRadius: '4px',
            background: REPORT.sand,
          }}>
            <TierEyebrow color={FINDING_COLORS[f.type]}>{f.type}</TierEyebrow>
            <div style={{ fontSize: '10px', fontWeight: 600, color: REPORT.heading, marginBottom: '2px' }}>{f.label}</div>
            <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{f.desc}</div>
            {f.detail && <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '2px' }}>{f.detail}</div>}
          </div>
        ))}
        <div className="briefing-readiness-summary" style={{
          marginTop: '8px', padding: '8px 12px', borderRadius: '4px',
          background: REPORT.sand,
        }}>
          <TierEyebrow color={FINDING_COLORS[summaryType]} marginBottom={6}>
            Connectivity Readiness · {summaryLabel}
          </TierEyebrow>
          <div style={{ fontSize: '10px', color: REPORT.body, lineHeight: 1.4 }}>{summaryDetail}</div>
        </div>
        <SourceFootnote>Source: enterprise carrier route mapping (including Zayo, FiberLight and Segra where available) and PeeringDB IXP/colo facilities. Route proximity does not confirm service.</SourceFootnote>
      </div>
    );
  }
);

// ── Unified DD Checklist — all ~60 Integra items in one view ──
// Status rule (do not upgrade guidance-only items to ADDRESSED):
//   ADDRESSED    = we deliver site-specific data OR cite settled law/known project parameters
//   FLAGGED      = we've screened the item (indicators or industry-standard guidance); developer still acts
//   REQUIRES INQUIRY = no screening possible at desktop stage; field work or per-deal negotiation required

export interface DDItem {
  item: string;
  status: DDStatus;
  summary: string;
}

export interface DDCategory {
  name: string;
  items: DDItem[];
}

// `memoView` = rendered under the /v2 memo route (DDChecklistV2). The memo
// carries no capacity number on A1 per the 2026-08-10 ruling; the screening
// derivation stays in the analytical view, which is where this defaults.
// A1 Alt: the flood line follows the outline on the map. Measured Zone A share of the
// seller's outline plus the row's own note — server-served figures, never a client
// recompute, and never the analyzed core's screen (that stays the original's literal).
function layoutFloodLine(row: SiteLayoutRow): string {
  const pct = row.acres > 0 ? (row.flood_100yr_ac / row.acres) * 100 : 0;
  return `${Math.round(row.flood_100yr_ac).toLocaleString()} ac FEMA Zone A (${pct.toFixed(1)}% of the ${Math.round(row.acres).toLocaleString()}-ac outline)${row.flood_note ? ` — ${row.flood_note}` : ''}`;
}

// `layoutRow`: the seller-proposed alternative on the map (A1 Alt edition only, null =
// original). Only the A1 flood and slope rows read it; every status is unchanged.
export function buildUnifiedDDChecklist(data: BriefingData, parcel: ParcelDetail, permitPathway: ReturnType<typeof computePermitPathway>, memoView = false, layoutRow: SiteLayoutRow | null = null): DDCategory[] {
  // Crown Pine A1 carve-out: several rows below carry corrected copy (gas basis,
  // permitting instrument, latency units, Section 7 / SB 6 framing). Gated so every
  // other briefing's checklist stays byte-identical.
  const isA1 = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
  const isMeiners = data.carveOutAggregate?.slug === MEINERS_SLUG;
  const enterpriseCarrierName = isMeiners ? 'FiberLight' : parcel.nearest_carrier_fiber_name;
  const enterpriseCarrierMiles = isMeiners ? 3.1 : parcel.nearest_carrier_fiber_miles;
  const enterpriseCarrierScope = isMeiners ? 'three-account block boundary' : 'parcel boundary';
  const altRow = isA1 ? layoutRow : null;
  const pipelines = data.pipelines || [];
  // The `revenant-operator-gathering` pseudo-row was retired 2026-08-07; the id
  // filter stays as a no-op guard. Downstream items (Pressure & Volume, Supply
  // Timeline) expect a real geometry signal, so a build-to-site commitment row
  // with null util / null diameter must never become the featured pipeline.
  const realPipelines = pipelines.filter(p => p.id !== 'revenant-operator-gathering');
  const featured = realPipelines[0];
  const gi = data.gasInfrastructure;
  const safety = data.pipelineSafety;
  const pi = data.powerInfra;
  const sub1Dist = parcel.nearest_substation_miles != null ? Number(parcel.nearest_substation_miles) : null;
  const sub1Kv = parcel.nearest_substation_kv != null ? Number(parcel.nearest_substation_kv) : null;
  const sub2 = data.nearestFeatures?.secondSubstation;
  const sub2Dist = sub2?.distance_miles != null ? Number(sub2.distance_miles) : (parcel.second_substation_miles != null ? Number(parcel.second_substation_miles) : null);
  const sub2Kv = sub2?.max_voltage_kv != null ? Number(sub2.max_voltage_kv) : (parcel.second_substation_kv != null ? Number(parcel.second_substation_kv) : null);
  const sub1 = data.nearestFeatures?.substation;
  const tduName = pi?.tduName ? fmtTdu(pi.tduName) : null;
  const txLineOp = pickNearestNamedTxLine(pi?.transmissionLines)?.operator || sub1?.operator;
  const gens = pi?.generators || [];
  const genTotalMW = gens.reduce((s, g) => s + g.total_mw, 0);
  const genRetiringMW = gens.reduce((s, g) => s + (g.retiring_mw || 0), 0);
  const hasRedundancy = sub2Dist != null && sub2Dist <= 25;
  const bridgingFeasible = sub1Dist != null && sub1Dist <= 10 && sub1Kv != null && sub1Kv >= 69;
  const nearestTxLine = pickNearestNamedTxLine(pi?.transmissionLines);
  const nearestCompressor = gi?.compressors?.[0];
  const hasCompressor = !!nearestCompressor;
  const nearestReceipt = gi?.receipt_delivery?.[0];
  const hasReceipt = !!nearestReceipt;
  const incidentCount = safety?.total_incidents ?? 0;
  const seriousCount = safety?.serious ?? 0;

  // Fields returned by briefing API but not in TS type
  const p = parcel as any;
  const intel = p.structured_intel || {} as Record<string, string>;
  const fuelOptions = selectedPipelineCapacitySummary(pipelines || [], intel.fuel_delivery_options, isMeiners ? meinersPipelineCapacity : undefined);
  const assembly = (data as any).assembly;
  const hasAssembly = !!p.assembly_group_id;

  // ERCOT data
  const plannedTx = pi?.plannedTransmission || [];
  const queue = pi?.queueCongestion;
  const lmpZones = pi?.lmpZones || {};
  const lmpZone = queue?.lmp_zone || (() => {
    const tdu = (pi?.tduName || '').toUpperCase();
    if (tdu.includes('CENTERPOINT')) return 'LZ_HOUSTON';
    if (tdu.includes('AEP')) return 'LZ_SOUTH';
    if (tdu.includes('ONCOR')) return 'LZ_NORTH';
    return 'LZ_WEST';
  })();
  const localLmpData = (lmpZones as any)[lmpZone] || {};
  const localLmp = localLmpData.t12mo || null;
  const localLmp6mo = localLmpData.t6mo || null;

  // Shared summary — used in both Gas Supply and Risk categories
  const incidentSummary = safety ? (incidentCount === 0
    ? `No PHMSA-reportable incidents within 10 mi (${safety.years_covered}). PHMSA reporting applies above fatality, injury, or $122K property-damage thresholds — minor events may be excluded.`
    : `${incidentCount} PHMSA-reportable incident${incidentCount !== 1 ? 's' : ''} within 10 mi (${safety.years_covered})${seriousCount > 0 ? ` — ${seriousCount} serious` : ''}`) : 'PHMSA incident data loading';

  const categories: DDCategory[] = [];

  // ── 1. Site & Title ──
  // Wetlands DD helpers — three states: has overlap, no overlap but nearby, clear
  const hasWetlandData = parcel.wetland_acres != null;
  const hasWetlandOverlap = hasWetlandData && Number(parcel.wetland_acres) > 0;
  const environmentalScreeningCopy = buildEnvironmentalScreeningCopy(data.environmentalContext);
  const wetlandScreening = (): { status: DDStatus; summary: string } => {
    if (isMeiners) return { status: 'FLAGGED', summary: '10.97 acres NWI-mapped across the 1,803.86-acre three-account block (0.6%). Desktop mapping only; field delineation and USACE jurisdiction remain open.' };
    if (!hasWetlandData) return { status: 'REQUIRES INQUIRY', summary: 'USFWS NWI data not yet computed — field verification recommended' };
    if (hasWetlandOverlap && data.environmentalContext) return { status: 'FLAGGED', summary: `${Number(parcel.wetland_acres).toFixed(2)} acres (${Number(parcel.wetland_pct).toFixed(2)}% of parcel) — ${parcel.wetland_types || 'type TBD'}. NWI desktop mapping only; field delineation is required before deciding jurisdiction or Section 404 applicability.` };
    if (hasWetlandOverlap) return { status: 'ADDRESSED', summary: `${Number(parcel.wetland_acres).toFixed(1)} acres (${Number(parcel.wetland_pct).toFixed(1)}% of parcel) — ${parcel.wetland_types || 'type TBD'}. Section 404 review likely required.` };
    if (parcel.nearest_wetland_m != null) return { status: 'ADDRESSED', summary: `No NWI-mapped wetlands on parcel. Nearest mapped wetland (${parcel.nearest_wetland_type || 'type TBD'}) is ${parcel.nearest_wetland_m}m from parcel boundary. Seasonal or unmapped wetlands possible — Phase 1 assessment recommended.` };
    return { status: 'ADDRESSED', summary: 'No NWI-mapped wetlands on or near parcel. Desktop screening complete — formal delineation not indicated.' };
  };
  const wetlandSpeciesSurvey = (): { status: DDStatus; summary: string } => {
    if (isMeiners) return { status: 'FLAGGED', summary: '10.97 acres NWI-mapped across the whole block. Obtain an IPaC species list and field delineation before deciding Section 404 applicability.' };
    if (!hasWetlandData) return { status: 'REQUIRES INQUIRY', summary: 'USFWS IPaC species list and NWI wetland delineation. Required before any Section 404 permit application.' };
    if (!hasWetlandOverlap) return { status: 'ADDRESSED', summary: isA1
      // A1: NWI absence does not decide jurisdiction; the north-drain AJD question
      // stays open (matches the risk register instead of contradicting it).
      ? 'No NWI-mapped wetlands on parcel; jurisdictional status of the north drain remains open. AJD/delineation required if the design affects it.'
      : 'No NWI-mapped wetlands on parcel. Desktop screening complete — no Section 404 action indicated.' };
    let summary = `NWI wetlands present (${parcel.wetland_types || 'type TBD'}).`;
    if (parcel.in_critical_habitat) summary += ` Within critical habitat (${parcel.critical_habitat_species || 'species TBD'}) — Section 7 consultation required.`;
    else if (parcel.critical_habitat_species) summary += ` Nearby critical habitat: ${parcel.critical_habitat_species}.`;
    summary += ' USFWS IPaC species list recommended before Section 404 application.';
    return { status: parcel.in_critical_habitat ? 'ADDRESSED' : 'FLAGGED', summary };
  };
  const wetlandDelineation = (): { status: DDStatus; summary: string } => {
    if (isMeiners) return { status: 'FLAGGED', summary: '10.97 acres NWI-mapped across the whole block — formal field delineation is recommended; NWI does not establish jurisdiction.' };
    if (!hasWetlandData) return { status: 'REQUIRES INQUIRY', summary: 'Formal wetland delineation by qualified biologist if desktop survey identifies potential jurisdictional wetlands on site.' };
    if (hasWetlandOverlap) return { status: 'FLAGGED', summary: `${Number(parcel.wetland_acres).toFixed(1)} acres NWI-mapped wetlands — formal delineation by qualified biologist recommended. Est. cost: $15–50K. Timeline: 3–6 months.` };
    if (parcel.nearest_wetland_m != null) return { status: 'FLAGGED', summary: `No wetlands on parcel, but nearest NWI feature (${parcel.nearest_wetland_type || 'type TBD'}) is ${parcel.nearest_wetland_m}m away. NWI does not capture all jurisdictional wetlands — Phase 1 assessment recommended.` };
    return { status: 'ADDRESSED', summary: 'No NWI-mapped wetlands on or near parcel. Formal delineation not indicated.' };
  };

  const pga = p.seismic_pga == null ? null : Number(p.seismic_pga);
  const seismicLabel = pga == null ? null : pga < 0.1 ? 'low' : pga < 0.3 ? 'moderate' : 'elevated';
  const seismicStatus: DDStatus = pga == null ? 'REQUIRES INQUIRY' : pga >= 0.1 ? 'FLAGGED' : 'ADDRESSED';

  // Centralized flood severity. Reads carve-out pct first (the assembly-level
  // value softens the rep parcel's standalone pct — Paces' rep is 22%, the
  // 2,320-ac carve-out is 8.4%). Threshold-driven label/status replaces the
  // binary FEMA-Zone-A FAIL behavior across ~10 DD/hero/limit sites below.
  const floodSev = applyCuratedFloodGate(floodSeverity(parcel, data.carveOuts?.[0] ?? null), parcel);
  const floodStatus: DDStatus = floodSev.ddStatus;

  const oppFactors = [
    parcel.ej_max_pctile != null && Number(parcel.ej_max_pctile) >= 80 ? `high EJ (${Math.round(Number(parcel.ej_max_pctile))}th pctile)` : null,
    p.nearest_urban_area_miles != null && Number(p.nearest_urban_area_miles) < 5 ? 'near urban area' : null,
    parcel.in_nonattainment ? 'nonattainment area' : null,
  ].filter((f): f is string => !!f);

  categories.push({ name: 'Site & Title', items: [
    // Oil & gas surface use — only when the payload carries a targeted RRC well screen.
    ...(data.oilGasWells?.summary ? [(() => {
      const s = data.oilGasWells.summary;
      const ops = (s.operators || []).filter(o => o.name !== 'Unknown').slice(0, 3).map(o => o.name).join(', ');
      const latest = data.oilGasWells.wells.filter(w => w.inside && w.status === 'permitted' && w.permit_approved).map(w => Number(String(w.permit_approved).slice(0, 4)));
      return {
        item: 'Oil & gas surface use / mineral estate',
        status: 'FLAGGED' as DDStatus,
        summary: `${s.inside_total} RRC well records inside the tract — ${s.inside_producing} producing on ~${s.inside_pads} pads, ${s.inside_plugged + s.inside_dry_hole} plugged or dry, ${s.inside_permitted} permitted location${s.inside_permitted === 1 ? '' : 's'}${latest.length ? ` (latest permit ${Math.max(...latest)})` : ''}${ops ? `; operators of record ${ops}` : ''}. Surface-use agreement, pad exclusion zones and plugged-well verification are pre-LOI items.`,
      };
    })()] : []),
    { item: 'Parcel boundaries & acreage', ...(() => {
      const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
      const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
      const useAgg = aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
      const acres = useAgg ? Number(aggAcres) : (parcel.acreage != null ? Number(parcel.acreage) : null);
      if (acres == null) return { status: 'REQUIRES INQUIRY' as DDStatus, summary: 'Acreage data unavailable' };
      // A1: the premises is the core plus the western band. The core figure stays
      // visible because every terrain/flood/water number on this page is measured
      // against it.
      if (data.premises) {
        return { status: 'ADDRESSED' as DDStatus, summary: `${data.premises.total_acres.toLocaleString()} acres — ${data.premises.core_acres.toLocaleString()}-ac analyzed core (${aggParcelCount}-parcel assembly) + ${data.premises.band_acres.toLocaleString()}-ac western band (parcel 21865, in part), ${parcel.county} County, ${parcel.state}` };
      }
      const note = useAgg ? ` (${aggParcelCount}-parcel assembly)` : '';
      return { status: 'ADDRESSED' as DDStatus, summary: `${acres.toLocaleString()} acres${note}, ${parcel.county} County, ${parcel.state}` };
    })() },
    // A1: the absentee/local/resident triad is an acquisition lead heuristic keyed
    // on the owner's tax mailing address — the wrong frame for an institutional
    // timberland owner. The partner branch must not reconstruct the name.
    { item: 'Ownership identification', status: parcel.owner_name ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: isA1
      ? (data.redacted === 'partner'
          ? 'Single private ownership entity (LP; identity withheld)'
          : 'CROWN PINE TIMBER 1 LP (institutional timberland owner; LP)')
      : (parcel.owner_name ? `${parcel.owner_name} (${parcel.is_absentee ? 'absentee' : (isEntityOwner(parcel.owner_name) ? 'local' : 'resident')}, ${isEntityOwner(parcel.owner_name) ? getEntityType(parcel.owner_name) : 'individual'})` : 'Owner data unavailable') },
    { item: isMeiners ? 'Base site definition' : 'Assembly / adjacent site availability', status: isMeiners ? 'FLAGGED' : hasAssembly ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: isMeiners
      ? 'Base site is the three-account, 1,803.86-acre block. Existing wells, surface-use rights, title, easements and a contiguous development footprint are the go/no-go items; no adjacent-acreage expansion is assumed.'
      : hasAssembly && assembly ? `Assembly group: ${assembly.parcel_count || '?'} parcels, ${assembly.total_acreage ? Number(assembly.total_acreage).toLocaleString() + ' acres' : 'acreage pending'}` : 'No assembly group identified — single parcel. Adjacent site availability requires county plat review.' },
    { item: 'Title search guidance', status: 'REQUIRES INQUIRY', summary: 'County clerk records required for title commitment. Mineral rights split-estate check recommended.' },
    { item: 'Survey (ALTA/NSPS)', status: 'REQUIRES INQUIRY', summary: 'ALTA/NSPS survey required for title insurance. Boundary, easement, and encroachment identification.' },
    { item: 'Subdivision & parcel status', status: parcel.parcel_id_local ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: parcel.parcel_id_local ? `Parcel ID: ${parcel.parcel_id_local}. Verify subdivision plat and legal description with county.` : 'Verify subdivision plat, legal description, and parcel status with county assessor.' },
    { item: 'Environmental restrictions review', status: 'REQUIRES INQUIRY', summary: 'Review deed restrictions, conservation easements, and environmental covenants. Phase I ESA recommended.' },
    { item: 'Access rights confirmation', status: 'REQUIRES INQUIRY', summary: 'Confirm legal ingress/egress. Verify road maintenance agreements and utility access easements.' },
    { item: 'Mineral rights / ownership', status: 'REQUIRES INQUIRY', summary: parcel.state === 'TX' ? 'Split-estate check required. TX mineral rights often severed — verify mineral deed and active leases via county clerk and RRC records.' : 'Split-estate check required. Verify mineral deed and active leases via county recorder and state oil & gas regulator.' },
    { item: 'Zoning & land use verification', status: data.countyZoning?.zoning_status ? 'ADDRESSED' : parcel.state === 'TX' ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: data.countyZoning?.zoning_status ? `${data.countyZoning.zoning_status === 'unzoned' ? 'No county zoning' : data.countyZoning.zoning_status}${data.countyZoning.zoning_notes ? ` — ${data.countyZoning.zoning_notes}` : ''}. See Legal & Regulatory → Zoning authority.` : parcel.state === 'TX' ? 'TX counties cannot zone — no county land use restrictions. Verify municipal ETJ if within 5 mi of incorporated city. See Legal & Regulatory → Zoning authority.' : 'County zoning and land use ordinance review required. See Legal & Regulatory → Zoning authority.' },
  ]});

  // ── 2. Power Infrastructure & Capacity ──
  categories.push({ name: 'Power Infrastructure', items: [
    { item: 'Utility provider identification', status: isMeiners ? 'REQUIRES INQUIRY' : tduName ? 'ADDRESSED' : txLineOp ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: (() => {
      if (isMeiners) return MEINERS_UTILITY_LEADS;
      if (!tduName) return txLineOp ? `Transmission owner: ${txLineOp}` : 'Requires service territory lookup';
      const secondary = (pi as any)?.tduSecondary || [];
      const holding = pi?.tduHoldingCompany && fmtTdu(pi.tduHoldingCompany) !== tduName ? ` (${fmtTdu(pi.tduHoldingCompany)})` : '';
      const secondaryStr = secondary.length > 0 ? ` + ${secondary.map((s: any) => fmtTdu(s.name)).join(', ')}` : '';
      if (isA1) return `Oncor Electric Delivery — per nearest distribution infrastructure (Huntington Substation, 138 kV, 9.5 mi). Confirm retail delivery point and tariff with Oncor for auxiliary load.`;
      return `TDU: ${tduName}${holding}${secondaryStr}`;
    })() },
    { item: 'Power feed capacity', status: isMeiners ? 'REQUIRES INQUIRY' : bridgingFeasible ? 'ADDRESSED' : sub1Dist != null ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: isMeiners ? `${fmtSub(sub1?.name, sub1Kv, sub1Dist, parcel.county)} is mapped nearby, but proximity and voltage do not establish usable auxiliary capacity. Capacity is unconfirmed; confirm service and capacity with the serving co-op.` : sub1Dist != null ? `${fmtSub(sub1?.name, sub1Kv, sub1Dist, parcel.county)}${gens.length > 0 ? ` | ${genTotalMW.toLocaleString()} MW county gen` : ''}` : 'No substation data' },
    { item: 'Substation proximity & redundancy', status: hasRedundancy ? 'ADDRESSED' : 'FLAGGED', summary: `Nearest: ${fmtSub(sub1?.name, sub1Kv, sub1Dist, parcel.county)}${hasRedundancy ? ` | 2nd: ${fmtSub(sub2?.name, sub2Kv, sub2Dist, parcel.county)}` : ' | No 2nd within 25 mi'}` },
    { item: 'Voltage level & reliability', status: sub1Kv != null && sub1Kv >= 69 ? 'ADDRESSED' : 'FLAGGED', summary: sub1Kv ? `${sub1Kv.toLocaleString()} kV${nearestTxLine ? ` | Line: ${fmtTxLine(nearestTxLine)}` : ''}${intel.grid_connection ? ` | ${intel.grid_connection}` : genRetiringMW > 0 ? ` | ${genRetiringMW.toLocaleString()} MW retiring` : ''}` : 'Voltage data unavailable' },
    { item: 'Utility expansion plan', status: plannedTx.length > 0 || intel.power_expansion || (queue && queue.active_projects > 0) ? 'ADDRESSED' : parcel.state === 'TX' ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: isMeiners ? 'February 2026 ERCOT TPIT lists 3 county projects. LeMay’s May 2026 target is historical; current completion and any usable auxiliary capacity are unverified.' : intel.power_expansion || (plannedTx.length > 0 ? `${plannedTx.length} ERCOT projects in county` : queue && queue.active_projects > 0 ? `${queue.active_projects} projects in ${parcel.state === 'TX' ? 'ERCOT' : 'PJM'} queue (${(queue.pending_mw || 0).toLocaleString()} MW pending)` : parcel.state === 'TX' ? 'No ERCOT TPIT projects in county' : 'Contact local utility') },
    { item: 'Power cost structure', status: isMeiners ? 'REQUIRES INQUIRY' : localLmp ? 'ADDRESSED' : parcel.state === 'WV' ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: isMeiners ? 'Project-specific fuel, transport, lateral, generation capex/O&M and utility-tariff quotes are pending.' : localLmp ? `Grid ~$${Math.round(localLmp.avg_lmp) + GRID_ADDER}–${Math.round(localLmp.avg_lmp) + GRID_ADDER + GRID_RANGE_SPREAD}/MWh (${lmpZone.replace('LZ_', '')}). BTM est. $${BTM_LCOE_LOW}–${BTM_LCOE_HIGH}/MWh.` : parcel.state === 'WV' ? `PJM APS zone — grid wholesale ~$40–65/MWh trailing 12mo (DAM LMP, typical APS range). BTM est. $${BTM_LCOE_LOW}–${BTM_LCOE_HIGH}/MWh at Appalachian hub gas pricing. Zone-specific LMP averages not yet ingested — quote is indicative.` : 'Request utility tariff schedule' },
    { item: 'Interconnect pathway', status: 'REQUIRES INQUIRY', summary: isMeiners ? `Auxiliary connection requirements and capacity are unconfirmed; confirm the service pathway, delivery point and facilities with the serving co-op.${queue ? ` Queue context: ${queue.pending_mw.toLocaleString()} MW pending.` : ''}` : `<10 MW auxiliary = Will Serve letter.${parcel.state === 'TX' ? (isA1 ? ' As structured (fully off-grid), outside SB 6 co-location approval — confirm with counsel.' : ' SB 6 co-location exemption.') : ''}${queue ? ` Queue: ${queue.pending_mw.toLocaleString()} MW pending.` : ''}` },
    { item: 'HV long-lead equipment', status: 'REQUIRES INQUIRY', summary: 'Transformers: 128–144 wk. Gas turbines: 5–7 yr. Recip engines: 12–24 mo.' },
    { item: 'Bridging power (<10 MW)', status: isMeiners ? 'REQUIRES INQUIRY' : bridgingFeasible ? 'ADDRESSED' : sub1Dist != null && sub1Dist <= 20 ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: isMeiners ? 'Confirm auxiliary connection requirements and capacity with the serving co-op; those requirements remain unconfirmed, and mapped infrastructure does not establish <10 MW service.' : bridgingFeasible ? `${fmtSub(sub1?.name, sub1Kv, sub1Dist, parcel.county)} — adequate` : sub1Dist != null ? `${sub1Dist} mi to nearest — ${sub1Dist > 15 ? 'temp generation likely' : 'extended gen-tie'}` : 'No substation data' },
    { item: 'PPA in place', status: 'REQUIRES INQUIRY', summary: isMeiners ? 'Project pricing pending; obtain project-specific utility and generation quotes.' : localLmp ? `Per-deal. Grid ~$${Math.round(localLmp.avg_lmp) + GRID_ADDER}–${Math.round(localLmp.avg_lmp) + GRID_ADDER + GRID_RANGE_SPREAD}/MWh. BTM est. $${BTM_LCOE_LOW}–${BTM_LCOE_HIGH}/MWh.` : 'Per-deal negotiation required' },
  ]});

  // ── 3. Gas Supply & Logistics ──
  // AOI parcels lead with the operator-build commitment (firm path); pipeline
  // tap candidates below are framed as redundancy. Single canonical sentence —
  // mirrors the lede card in `GasSupplyLogisticsSection` and the v2 thesis so
  // the briefing/v2/checklist all read the same on this point.
  const gasZoneDD = (data as any).gasSupplyZone as { operatorName?: string; peakBcfd?: number; peakYear?: number; assetName?: string } | undefined;
  // Phase 1 deliverability annotation (Revenant-specific for now). Surfaces
  // the day-1 ramp number alongside the long-term peak so the IC reads the
  // schedule, not just the ceiling.
  const aoiPhase1 = (data as any).gasSupplyZone?.slug === 'revenant-east-tx'
    ? ' (~250 MW deliverable by Jun 2027 · Phase 1)'
    : '';
  const aoiOperatorCommit = gasZoneDD && gasZoneDD.operatorName
    ? `${gasZoneDD.operatorName} has indicated they will build operator gathering to qualified BTM buyers in the AOI${gasZoneDD.peakBcfd != null ? ` — ${Number(gasZoneDD.peakBcfd).toFixed(1)} Bcf/d native system forecast${gasZoneDD.peakYear ? ` by ${gasZoneDD.peakYear}` : ''}${aoiPhase1}` : ''}. Public-midstream tap candidates are redundancy and scale optionality, not the firm path.`
    : null;
  categories.push({ name: 'Gas Supply & Logistics', items: [
    ...(aoiOperatorCommit ? [{
      item: 'Operator gathering commitment',
      status: 'ADDRESSED' as DDStatus,
      summary: aoiOperatorCommit,
    }] : []),
    { item: 'Pressure & volume', status: 'REQUIRES INQUIRY', summary: isMeiners
      ? 'Camino Real is a nearby transmission tap candidate. Pressure, deliverable volume, tap rights, receipt point and firm transport are unconfirmed.'
      : isA1
      ? 'Energy Transfer Texoma 30″ (RRC T-4 00749): intrastate HPL system — no public utilization or pressure postings exist. Gas availability and supply have been confirmed; pressure, deliverable volume, and tap specifics are established in firm transport and tap engineering, the remaining commercial steps. The downstream FGT "HPL Texoma" receipt point (twelve monthly snapshots) is downstream scheduling context, not local capacity.'
      : featured ? `${featured.name || featured.operator}: operator-posted capacity and pressure require confirmation${featured.diameter_inches ? ` | ${featured.diameter_inches}" diameter` : ''}` : 'No nearby pipeline' },
    { item: 'Equipment (compressors)', status: hasCompressor ? 'ADDRESSED' : 'FLAGGED', summary: (() => {
      if (!hasCompressor) return 'No pipeline compressor stations within 10 mi';
      const d = Number(nearestCompressor!.distance_miles);
      const onParcel = d < 0.5;
      const label = onParcel
        ? `${nearestCompressor!.name || 'Compressor station'} on parcel`
        : `${nearestCompressor!.name || 'Compressor station'} at ${nearestCompressor!.distance_miles} mi`;
      const op = nearestCompressor!.operator ? ` — ${nearestCompressor!.operator}` : '';
      const hp = nearestCompressor!.horsepower ? ` · ${nearestCompressor!.horsepower.toLocaleString()} HP` : '';
      const ch4 = nearestCompressor!.methane_mt != null ? ` · CH₄: ${Math.round(nearestCompressor!.methane_mt)} MT/yr` : '';
      const onSiteSuffix = onParcel ? ' · operator-confirmed pressure / flow control already in place for qualified buyers' : '';
      return `${label}${op}${hp}${ch4}${onSiteSuffix}`;
    })() },
    { item: 'Supply availability', status: fuelOptions || isMeiners ? 'REQUIRES INQUIRY' : isA1 ? 'ADDRESSED' : featured || intel.gas_lateral ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: (() => {
      if (fuelOptions) {
        const capacity = fuelOptions.totalMmcfDay == null ? 'Combined capacity unconfirmed'
          : `${formatOptionCapacity(fuelOptions.totalMmcfDay)} combined ${fuelOptions.hasEstimates ? 'estimated ' : ''}system${fuelOptions.projectMmcfDay ? ' / project design' : ''} capacity`;
        const split = fuelOptions.operatingMmcfDay != null && fuelOptions.projectMmcfDay != null
          ? ` ${formatOptionCapacity(fuelOptions.operatingMmcfDay)} operating${fuelOptions.projectMmcfDay > 0 ? `; ${formatOptionCapacity(fuelOptions.projectMmcfDay)} project design` : ''}.` : '';
        return `${fuelOptions.names.join(' / ')}: ${capacity}.${split} Diameter ${fuelOptions.diameterLabel}; distance ${fuelOptions.distanceLabel}${data.pipelinesDistanceBasis?.type === 'carve_out_boundary' ? ' from the full block boundary' : ''}. ${isMeiners ? 'Blackcomb in-service expected Q4 2026; GCX is fully subscribed. Camino Real remains the nearer 5.28-mi candidate with total capacity unconfirmed.' : fuelOptions.details.join(' ')} System totals do not establish gas available to this site. Site delivery, tap, pressure and firm transport remain unconfirmed; confirm deliverable volume and terms with the operators.`;
      }
      if (isMeiners) return 'Camino Real total capacity is unconfirmed. Published capacities for the larger systems describe the whole pipeline or project design, not gas committed to this site. Obtain operator confirmation of deliverable volume, tap, pressure and firm transport.';
      if (isA1 && memoView) return 'ET Texoma 30″ crossing the proposed western band (1.35 mi from the analyzed core) — gas availability and supply confirmed, with delivery expected in tranches as project phases build out; firm transport and tap engineering are the remaining commercial steps.';
      if (isA1) return 'ET Texoma 30″ crossing the proposed western band (1.35 mi from the analyzed core) — gas availability and supply confirmed, with delivery expected in tranches as project phases build out; firm transport and tap engineering are the remaining commercial steps. The 923 MMcf/d figure is a diameter-only screening estimate (525 MMcf/d at 24″, scaled D^2.53), not posted, rated, or contracted capacity.';
      const capacity = featured ? pipelineCapacityDisplay(featured) : null;
      const ddCap = capacity?.capacityMmcf;
      const onSiteFeatured = featured ? pipelineIsEffectivelyOnSite(parcel, featured) : false;
      const ownerOps = featured ? ownerOwnsPipeline(parcel.owner_name, featured) : false;
      const base = ddCap
        ? `${Math.round(ddCap).toLocaleString()} MMcf/d ${capacity!.label.toLowerCase()} on ${featured!.name || featured!.operator}; source scope does not establish local delivery`
        : featured
          ? (onSiteFeatured
              ? (ownerOps
                  ? `${featured.name || featured.operator} crosses parcel — owner-operated; tap grantable on-parcel`
                  : `${featured.name || featured.operator} crosses parcel — site delivery unconfirmed`)
              : `Pipeline at ${featured.distance_miles} mi — segment capacity unconfirmed`)
          : 'No nearby pipeline';
      return intel.gas_lateral ? `${base}. ${intel.gas_lateral}` : base;
    })() },
    { item: 'Supply timeline', status: (featured && pipelineIsEffectivelyOnSite(parcel, featured)) ? 'ADDRESSED' : 'FLAGGED', summary: (() => {
      if (featured && pipelineIsEffectivelyOnSite(parcel, featured)) return 'Pipeline crosses parcel — tap / meter station timeline subject to operator interconnect study and firm-transport agreement (pipe quality and tap-grantability not assumed without operator confirmation).';
      // A1: the third pre-priced lateral on this page. The line crosses the
      // proposed premises, so an industry $/mile range prices the wrong thing —
      // same treatment as the Resolve item and the kill-criteria row.
      if (isA1) return 'Interconnect facilities and routing: Cost TBD pending tap engineering; 6–18-month critical-path allowance from acceptance of a complete request. Confirm timeline with pipeline operator.';
      if (lateralEstimatesSuppressed(parcel)) return 'Lateral required; routing, cost and schedule are not estimated here — they follow the pipeline operator\u2019s interconnect study and firm-transport terms.';
      return 'Lateral construction: 6–18 months, $1–3M/mile (industry standard range). Confirm timeline with pipeline operator.';
    })() },
    { item: 'Interconnects (wholesale/retail)', status: (hasReceipt || (hasCompressor && Number(nearestCompressor!.distance_miles) < 0.5)) ? 'ADDRESSED' : 'FLAGGED', summary: (() => {
      const compOnParcel = hasCompressor && Number(nearestCompressor!.distance_miles) < 0.5;
      const compNote = compOnParcel
        ? `On-parcel tap via ${nearestCompressor!.name || 'compressor station'}${nearestCompressor!.operator ? ` (${nearestCompressor!.operator})` : ''} — operator-confirmed delivery point for qualified buyers`
        : null;
      const receiptNote = hasReceipt
        ? `${nearestReceipt!.name || 'Receipt point'} at ${nearestReceipt!.distance_miles} mi${nearestReceipt!.pipeline_company ? ` (${nearestReceipt!.pipeline_company})` : ''}`
        : null;
      if (compNote && receiptNote) return `${compNote}. Nearest PHMSA-mapped receipt point: ${receiptNote}.`;
      if (compNote) return compNote;
      if (receiptNote) return receiptNote;
      return 'No receipt/delivery points within 10 mi';
    })() },
    { item: 'Measurement stations', status: 'FLAGGED', summary: 'Developer-built metering is industry standard for >100 MW BTM. Confirm per-interconnect facilities agreement.' },
    { item: 'Easements', status: 'REQUIRES INQUIRY', summary: parcel.state === 'TX' ? (isA1 ? 'Easement widths are negotiated. Texas provides only a rebuttable 50-ft presumption for certain common-carrier pipeline easements (Tex. Nat. Res. Code §111.0194) — it does not cover gathering lines, and temporary workspace has no statutory default. Title search required.' : 'TX statutory default: 50 ft permanent, 50–100 ft temporary. Title search required.') : 'Title search required to identify permanent and temporary easements.' },
    { item: 'Transmission agreement', status: isMeiners ? 'REQUIRES INQUIRY' : 'FLAGGED', summary: isMeiners ? 'Request pipeline-specific tariff, transport, tap/facilities and firm-delivery terms from WTG.' : parcel.state === 'TX' ? `Firm Transport recommended. Delivered gas screening est. $${DELIVERED_GAS_CURRENT.toFixed(2)}–${DELIVERED_GAS_NORMALIZED.toFixed(2)}/MMBtu — contact pipeline commercial desk for tariff and reservation terms.` : 'Firm Transportation recommended — contact pipeline commercial desk for tariff terms.' },
    { item: 'Firm vs interruptible', status: isMeiners ? 'REQUIRES INQUIRY' : 'FLAGGED', summary: isMeiners
      ? 'Firm delivery and pressure remain unconfirmed. Obtain WTG tariff, transport and reservation terms before advancing.'
      : isA1
      ? 'Firm capacity required for 24/7 DC; interruptible not suitable for baseload. This briefing claims no headroom figure for the Texoma line (intrastate — no public utilization postings exist). Gas availability and supply have been confirmed; firm transport on ET is the remaining commercial negotiation.'
      : 'Firm capacity required for 24/7 DC; interruptible is not suitable for baseload. Capacity available to a new shipper is unknown until the operator confirms receipt, delivery, pressure and firm transportation terms.' },
    { item: 'Environmental concerns', status: safety ? (seriousCount > 0 ? 'FLAGGED' : 'ADDRESSED') : 'REQUIRES INQUIRY', summary: incidentSummary },
    { item: 'Cost of gas', status: isMeiners ? 'REQUIRES INQUIRY' : parcel.state === 'TX' ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: isMeiners ? 'Request pipeline-specific tariff, transport, tap/facilities, lateral scope and delivered-gas quote before pricing power or savings.' : parcel.state === 'TX' ? `Delivered gas${isA1 ? ' screening scenario' : ''}: $${DELIVERED_GAS_CURRENT.toFixed(2)}–${DELIVERED_GAS_NORMALIZED.toFixed(2)}/MMBtu. BTM power: $${BTM_LCOE_LOW}–${BTM_LCOE_HIGH}/MWh.` : 'Hub pricing varies by region.' },
  ]});

  // ── 4. Network Connectivity ──
  categories.push({ name: 'Network Connectivity', items: [
    { item: 'Fiber availability', status: enterpriseCarrierMiles != null ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: enterpriseCarrierMiles != null
      ? `${enterpriseCarrierName || 'Enterprise carrier'} route ${enterpriseCarrierMiles} mi from the ${enterpriseCarrierScope}${isMeiners ? '; Zayo route ~6.3 mi from the block boundary' : ''}; serviceability and capacity are unconfirmed.`
      : 'No mapped enterprise carrier route; carrier serviceability study required.' },
    { item: 'Enterprise service confirmation', status: 'REQUIRES INQUIRY', summary: enterpriseCarrierName
      ? `Request a written serviceability, capacity, delivery schedule and SLA proposal from ${enterpriseCarrierName}${isMeiners ? ' and Zayo' : ''}. Mapped route proximity does not confirm service.`
      : 'Carrier engagement required to confirm enterprise service, capacity, delivery schedule and SLA.' },
    { item: 'Fiber distance & buildout', status: enterpriseCarrierMiles != null ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: enterpriseCarrierMiles != null
      ? `${enterpriseCarrierName || 'Enterprise carrier'} route ${enterpriseCarrierMiles} mi from the ${enterpriseCarrierScope}; carrier must define the lateral path, cost and schedule.`
      : 'Enterprise route distance and lateral buildout are unconfirmed.' },
    { item: 'Backbone fiber proximity', status: enterpriseCarrierMiles != null ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: enterpriseCarrierMiles != null ? `${enterpriseCarrierName || 'Enterprise carrier'} mapped route ${enterpriseCarrierMiles} mi from the ${enterpriseCarrierScope}${isMeiners ? '; Zayo ~6.3 mi' : ''}` : 'No enterprise carrier route data available' },
    { item: 'Diversity & redundancy verification', status: parcel.nearest_colo_networks != null && parcel.nearest_colo_networks >= 10 ? 'ADDRESSED' : parcel.nearest_colo_networks != null ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: parcel.nearest_colo_facility ? `${parcel.nearest_colo_networks} carriers at ${parcel.nearest_colo_facility} (${parcel.nearest_colo_city}, ${parcel.nearest_colo_miles} mi)` : 'Carrier facility data pending' },
    { item: 'Latency metrics & IXP proximity', status: (() => {
      const ixpNets = parcel.nearest_ixp_networks != null ? Number(parcel.nearest_ixp_networks) : null;
      const useIxp = parcel.nearest_ixp_miles != null && ixpNets != null && ixpNets >= 5;
      const useColo = parcel.nearest_colo_miles != null;
      if (useIxp && Number(parcel.nearest_ixp_miles) <= 300) return 'ADDRESSED';
      if (useColo && Number(parcel.nearest_colo_miles) <= 300) return 'ADDRESSED';
      if (useIxp || useColo) return 'FLAGGED';
      return 'REQUIRES INQUIRY';
    })(), summary: (() => {
      const ixpNets = parcel.nearest_ixp_networks != null ? Number(parcel.nearest_ixp_networks) : null;
      // Prefer true IXP (≥5 networks) when nearby; otherwise use colocation facility (already ≥5 networks).
      if (parcel.nearest_ixp_name && parcel.nearest_ixp_miles != null && ixpNets != null && ixpNets >= 5) {
        if (isA1) return `${parcel.nearest_ixp_name} at ${parcel.nearest_ixp_miles} mi (${ixpNets} networks, ~${(Number(parcel.nearest_ixp_miles) * 0.022).toFixed(1)} ms est. RTT); Houston Greenspoint hyperscaler cluster ~114 mi / ~2.5 ms est. RTT — distance-based estimates, not site measurements`;
        return `${parcel.nearest_ixp_name} at ${parcel.nearest_ixp_miles} mi (${ixpNets} networks, ~${((Number(parcel.nearest_ixp_miles) * 1.609 * 5) / 1000).toFixed(1)}ms est. fiber latency)`;
      }
      if (parcel.nearest_colo_facility && parcel.nearest_colo_miles != null) {
        const nets = parcel.nearest_colo_networks;
        if (isA1) return `${parcel.nearest_colo_facility} at ${parcel.nearest_colo_miles} mi${nets ? ` (${nets} carriers, ` : ' ('}~${(Number(parcel.nearest_colo_miles) * 0.022).toFixed(1)} ms est. RTT); Houston Greenspoint hyperscaler cluster ~114 mi / ~2.5 ms est. RTT — distance-based estimates, not site measurements`;
        return `${parcel.nearest_colo_facility} at ${parcel.nearest_colo_miles} mi${nets ? ` (${nets} carriers` : ''}${nets ? `, ~${((Number(parcel.nearest_colo_miles) * 1.609 * 5) / 1000).toFixed(1)}ms est. fiber latency)` : ''}`;
      }
      return 'Peering facility data pending';
    })() },
    { item: 'Right-of-way confirmation', status: 'REQUIRES INQUIRY', summary: 'Confirm fiber ROW availability along access road. Existing utility corridors reduce buildout cost and timeline.' },
    { item: 'Dark fiber options evaluation', status: 'REQUIRES INQUIRY', summary: enterpriseCarrierName ? `Contact ${enterpriseCarrierName}${isMeiners ? ' and Zayo' : ''} wholesale to confirm lit, wavelength and dark-fiber options; mapped route proximity is not an availability commitment.` : 'Carrier engagement required to identify lit, wavelength and dark-fiber options.' },
  ]});

  // ── 5. Cooling & Environmental ──
  categories.push({ name: 'Cooling & Environmental', items: [
    { item: 'Water availability', status: (() => {
      if (isMeiners) return 'REQUIRES INQUIRY' as const;
      const tier = parcel.water_sourcing_tier;
      // Any known tier (including trucked) is addressed
      if (tier) return 'ADDRESSED' as const;
      // Fallback for parcels not yet enriched
      if (parcel.water_service_area || parcel.aquifer_name) return 'ADDRESSED' as const;
      // Landowner-disclosed water on site (per-parcel override)
      if ((parcel as any).structured_intel?.landowner_disclosures?.water_on_site?.display_note) return 'ADDRESSED' as const;
      return 'FLAGGED' as const;
    })(), summary: (() => {
      const vol = 'BTM air-cooled configuration requires ~300–400K gal/yr (~1,000 GPD) for auxiliary needs (zero evaporative makeup)';
      const tier = parcel.water_sourcing_tier;
      const landownerWater = (parcel as any).structured_intel?.landowner_disclosures?.water_on_site;
      if (isMeiners) return `Carrizo Aquifer and Wintergarden GCD establish the hydrogeologic and permitting context, not a developed supply. The nearest stored TWDB SDR well at 107.47 mi is a limited-dataset coverage gap, not a groundwater-scarcity finding. Seller-reported on-site wells remain unverified; obtain well logs, yield, condition, rights and water-quality confirmation. The nearby 1.4-mi water sample was taken in 1963; all five stored quality samples date from 1956–1977. They provide historical regional context, not current on-site quality or source availability. ${vol}`;
      // Nearby TWDB well stats
      const wells = parcel.nearby_water_wells || [];
      const within2 = wells.filter(w => Number(w.distance_miles) <= 2);
      let wellNote = '';
      if (within2.length > 0) {
        const depths = within2.map(w => w.total_depth_ft).filter(Boolean);
        const yields = within2.map(w => w.yield_gpm).filter(Boolean);
        const parts = [`${within2.length} TWDB-registered well${within2.length > 1 ? 's' : ''} within 2 mi`];
        if (depths.length > 0) parts.push(`avg depth ${Math.round(depths.reduce((a, b) => a + Number(b), 0) / depths.length)} ft`);
        if (yields.length > 0) parts.push(`avg yield ${Math.round(yields.reduce((a, b) => a + Number(b), 0) / yields.length)} GPM`);
        wellNote = ` ${parts.join(', ')}.`;
      } else if (wells.length > 0) {
        const nearest = wells[0];
        wellNote = ` Nearest TWDB well: ${nearest.distance_miles} mi${nearest.total_depth_ft ? `, ${nearest.total_depth_ft} ft deep` : ''}${nearest.yield_gpm ? `, ${nearest.yield_gpm} GPM` : ''}.`;
      }
      if (tier === 'municipal') return `Municipal service area. ${vol} — serviceable via municipal connection.${wellNote}`;
      if (tier === 'aquifer_no_permit') return `${formatAquiferName(parcel.aquifer_name)} aquifer. Outside any Groundwater Conservation District — ${parcel.state === 'TX' ? 'Texas rule of capture applies (no groundwater permit required). TDLR-licensed driller required for well construction.' : 'confirm state and local groundwater permit requirements.'}${wellNote} ${vol}`;
      if (tier === 'aquifer_gcd') return `${formatAquiferName(parcel.aquifer_name)} aquifer. Inside ${parcel.water_regulatory_zone || 'GCD'} — groundwater permit required (60-120 days typical). Contact district for spacing rules, production limits, and fees.${wellNote} ${vol}`;
      if (tier === 'aquifer_az_free') return `${formatAquiferName(parcel.aquifer_name)} aquifer. Outside AMA — Arizona reasonable-use doctrine (unregulated at this volume). ${vol}`;
      if (tier === 'aquifer_ama') return `Inside ${parcel.water_regulatory_zone || 'AMA'} — ADWR permit required. ${parcel.aquifer_name ? `${formatAquiferName(parcel.aquifer_name)} aquifer available.` : ''} ${vol}`;
      if (tier === 'aquifer_nm') return `${formatAquiferName(parcel.aquifer_name)} aquifer. NM prior appropriation — OSE permit required for all commercial groundwater use. ${vol}`;
      if (tier === 'nm_closure') return `NM closure area (${parcel.water_regulatory_zone || 'zone TBD'}) — no new groundwater appropriations. Consider purchasing existing water right or trucked water. ${vol}`;
      if (tier === 'on_site_well') {
        // Server-assigned tier (e.g. Crown Pine A1 override) — without this branch the
        // chain fell through to "No sourcing tier computed".
        return `${within2.length > 0 ? `${within2.length} productive TWDB-registered well${within2.length > 1 ? 's' : ''} within 2 mi confirm` : 'Nearby wells confirm'} groundwater accessibility.${wellNote} ${vol} — on-site well drilling is feasible at this volume.`;
      }
      if (tier === 'trucked') {
        const zone = parcel.water_regulatory_zone ? ` Regulatory zone: ${parcel.water_regulatory_zone}.` : '';
        const effective = computeEffectiveWaterTier(parcel);
        if (effective === 'on_site_well') {
          return `No mapped aquifer boundary, but ${within2.length > 0 ? `${within2.length} productive TWDB-registered well${within2.length > 1 ? 's' : ''} within 2 mi confirm` : 'nearby wells confirm'} groundwater accessibility.${zone}${wellNote} ${vol} — on-site well drilling is feasible at this volume.`;
        }
        return `No municipal service, mapped aquifer, or nearby wells within 2 mi.${zone}${wellNote} ${vol} — at this volume, commercial hauling contract is feasible. Verify sourcing via TWDB well logs before committing.`;
      }
      // Fallback for parcels not yet computed with new tier
      if (parcel.water_service_area) return `Municipal service area. ${vol} — serviceable via municipal connection.`;
      if (parcel.aquifer_name) {
        const fallbackWellNote = parcel.nearest_water_miles != null && Number(parcel.nearest_water_miles) <= 10
          ? ` Existing well ${parcel.nearest_water_miles} mi away confirms groundwater accessibility.`
          : '';
        return `${formatAquiferName(parcel.aquifer_name)} aquifer.${fallbackWellNote} ${vol} — serviceable via on-site well or commercial hauling.`;
      }
      // Landowner-disclosed water on site (per-parcel override when no mapped tier is available)
      if (landownerWater?.display_note) {
        const outstanding = Array.isArray(landownerWater.outstanding_items) && landownerWater.outstanding_items.length > 0
          ? ` Outstanding: ${landownerWater.outstanding_items.join('; ')}.`
          : '';
        return `Landowner-verified water on site — ${landownerWater.display_note}${outstanding} ${vol}.`;
      }
      return `No sourcing tier computed. ${vol} — field verification of water sourcing required.`;
    })() + (() => {
      // BRACS appendix: brackish zone overlap + nearest measured TDS
      const brackish = parcel.brackish_zones || [];
      const tds = parcel.nearby_tds_samples || [];
      if (brackish.length === 0 && tds.length === 0) return '';
      const parts: string[] = [];
      if (brackish.length > 0) {
        const names = brackish
          .map(z => z.aquifer_name)
          .filter(Boolean)
          .map(n => (n as string).replace(/_/g, ' '))
          .join(' + ');
        parts.push(`Brackish groundwater mapped at this parcel (${names || 'BRACS extent'} — TWDB BRACS 2024)${brackish.length > 1 ? ' across multiple aquifer extents' : ''}.`);
      }
      if (tds.length > 0) {
        const t = tds[0];
        const tdsNum = Number(t.tds_mgl);
        const band = tdsNum < 1000 ? 'fresh'
          : tdsNum < 3000 ? 'slightly brackish'
          : tdsNum < 10000 ? 'moderately brackish — BRACS sweet spot for single-pass RO'
          : tdsNum < 35000 ? 'highly brackish — high-pressure RO required'
          : 'brine — thermal treatment only';
        parts.push(`Nearest measured total dissolved solids (TDS): ${tdsNum.toLocaleString()} mg/L at ${t.distance_miles} mi (${band}).`);
      } else if (brackish.length > 0) {
        parts.push('No total dissolved solids (TDS) samples within 25 mi — pull a water sample during site DD to confirm treatment capex.');
      }
      return ' ' + parts.join(' ');
    })() },
    { item: 'EPA attainment status', status: parcel.in_nonattainment == null ? 'REQUIRES INQUIRY' : parcel.in_nonattainment ? 'FLAGGED' : 'ADDRESSED', summary: parcel.in_nonattainment == null ? 'Attainment data pending' : parcel.in_nonattainment ? `Nonattainment area — ${parcel.nonattainment_areas?.[0]?.pollutant || 'pollutant TBD'}${parcel.nonattainment_areas?.[0]?.classification ? ` (${parcel.nonattainment_areas[0].classification})` : ''}. NNSR required; emission offsets likely.` : isA1 ? 'In attainment for all seven NAAQS — early phases fit TCEQ’s registration-based EGU Standard Permit (2007-amended); case-by-case PSD applies at full buildout.' : 'In attainment — standard PSD pathway' },
    { item: 'EJScreen assessment', status: parcel.ej_max_pctile != null ? 'ADDRESSED' : 'FLAGGED', summary: parcel.ej_max_pctile != null ? `${Math.round(Number(parcel.ej_max_pctile))}th percentile${Number(parcel.ej_max_pctile) >= 80 ? ' — elevated, enhanced engagement recommended' : Number(parcel.ej_max_pctile) >= 50 ? ' — moderate' : ' — low concern'}` : 'No EJScreen data for this block group' },
    { item: 'Wetlands screening', ...wetlandScreening() },
    { item: 'Critical habitat (Biological Opinion trigger)', status: p.in_critical_habitat === true ? 'FLAGGED' : p.in_critical_habitat === false ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: (() => {
      if (p.in_critical_habitat) return `Within USFWS critical habitat (${p.critical_habitat_species || 'species TBD'}) — full Section 7 consultation + Biological Opinion required`;
      if (p.in_critical_habitat === false) {
        const dist = p.nearest_critical_habitat_miles != null ? Number(p.nearest_critical_habitat_miles) : null;
        const species = p.critical_habitat_species || null;
        if (dist != null && species) return `No designated critical habitat on parcel. Nearest: ${species} @ ${dist.toFixed(1)} mi`;
        if (species) return `No designated critical habitat on parcel. Nearest species: ${species}`;
        return 'No critical habitat overlay';
      }
      return 'Map layer available (223 zones) — per-parcel scoring not yet computed';
    })() },
    { item: 'ESA listed species ranges (Section 7 trigger)', status: p.te_species_count == null ? 'REQUIRES INQUIRY' : Number(p.te_species_count) > 0 ? 'FLAGGED' : 'ADDRESSED', summary: (() => {
      if (p.te_species_count == null) return 'USFWS species range layer not yet computed for this parcel';
      if (p.te_species_count === 0) return 'No ESA-listed species ranges overlap this parcel';
      const list = p.te_species_list || 'species TBD';
      // Indiana Bat and Northern Long-Eared Bat trigger a tree-clearing window (roosting season)
      // — the operational constraint most BTM sites hit in Appalachia. Call it out when present.
      const hasRoostingBat = /indiana bat|northern long-eared bat|nleb/i.test(list);
      const batNote = hasRoostingBat
        ? ' Primary constraint: Indiana Bat and/or Northern Long-Eared Bat trigger tree-clearing restriction to the Nov 15 – Mar 31 hibernation window. Alternatives: acoustic bat survey ($25–75K) for off-season clearing, or schedule clearing in-window.'
        : '';
      if (isA1) return `${p.te_species_count} listed species range${p.te_species_count === 1 ? '' : 's'} overlap: ${list}. Range-map overlap alone does not trigger Section 7 — consultation requires a federal nexus; here, realistically, the §404 permit action if the north drain is ruled jurisdictional. If triggered, informal consultation would ride that action. Habitat assessment during due diligence scopes it.${batNote}`;
      return `${p.te_species_count} listed species range${p.te_species_count === 1 ? '' : 's'} overlap: ${list}. Section 7 consultation required; formal vs informal determined by effect analysis.${batNote}`;
    })() },
    { item: 'Seismic risk', status: seismicStatus, summary: pga != null ? `PGA: ${pga.toFixed(3)}g (${seismicLabel} risk)` : 'Map layer available (62K grid cells) — per-parcel PGA not yet computed' },
    { item: 'Urban proximity', status: p.nearest_urban_area_miles != null ? 'ADDRESSED' : 'FLAGGED', summary: p.nearest_urban_area_miles != null ? `${Number(p.nearest_urban_area_miles).toFixed(1)} mi to nearest urban area${Number(p.nearest_urban_area_miles) < 5 ? ' — community engagement critical' : ''}` : 'Map layer available (353 areas) — per-parcel distance not yet computed' },
    { item: 'Ambient temperature & humidity profile', status: parcel.climate_design_high_f != null ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: parcel.climate_design_high_f != null ? `Design high: ${Number(parcel.climate_design_high_f).toFixed(1)}°F (hottest month avg). Annual CDD base 65: ${parcel.climate_annual_cdd ? Math.round(Number(parcel.climate_annual_cdd)).toLocaleString() : 'N/A'}. ${Number(parcel.climate_design_high_f) >= 100 ? 'Extreme heat — air-cooled systems require oversizing.' : Number(parcel.climate_design_high_f) >= 95 ? 'Hot climate — standard air-cooled design adequate; evaporative pre-cooling improves PUE.' : 'Moderate climate — favorable for air-cooled and free-cooling hours.'}` : 'ASHRAE climate zone assessment needed. Cooling design temp and wet-bulb hours affect PUE and equipment sizing.' },
    { item: 'Ground conditions & soil analysis', status: 'REQUIRES INQUIRY', summary: 'Geotechnical boring required. Bearing capacity, groundwater depth, and soil type affect foundation design and cost.' },
    // WV-only AML surface-inventory row: inventoried-only → ADDRESSED, any priority → FLAGGED.
    ...(() => {
      if (parcel.state !== 'WV') return [];
      const overlaps = parcel.aml_overlaps || [];
      const amlAc = parcel.aml_overlap_acres != null ? Number(parcel.aml_overlap_acres) : 0;
      if (!overlaps.length || amlAc <= 0) return [];
      const parcelAc = parcel.acreage != null ? Number(parcel.acreage) : null;
      const pct = parcelAc && parcelAc > 0 ? Math.round((amlAc / parcelAc) * 100) : null;
      const cleanAc = parcelAc != null ? Math.max(0, parcelAc - amlAc) : null;
      const priority = overlaps.filter(isPriorityAmlOverlap);
      const inventoriedOnly = priority.length === 0;
      const summary = inventoriedOnly
        ? `${overlaps.length} WVDEP AML surface feature${overlaps.length === 1 ? '' : 's'} overlap (${Math.round(amlAc).toLocaleString()} ac${pct != null ? `, ${pct}%` : ''}) — all unfunded, predominantly not on the federal AMLIS priority list. Pre-SMCRA highwalls — surface hazard only; no underground workings on parcel. ${cleanAc != null ? `~${Math.round(cleanAc).toLocaleString()} ac unaffected, available for pad siting.` : ''} Phase I ESA + Phase II sampling at highwall toes covers DD.`
        : `${overlaps.length} WVDEP AML surface feature${overlaps.length === 1 ? '' : 's'} overlap (${Math.round(amlAc).toLocaleString()} ac${pct != null ? `, ${pct}%` : ''}), of which ${priority.length} carry federally-listed or funded priority — request WVDEP Office of AML file before LOI. Pre-SMCRA highwalls — surface hazard only; no underground workings on parcel. ${cleanAc != null ? `~${Math.round(cleanAc).toLocaleString()} ac unaffected.` : ''}`;
      return [{
        item: 'AML inventory overlap (WV)',
        status: inventoriedOnly ? 'ADDRESSED' as DDStatus : 'FLAGGED' as DDStatus,
        summary,
      }];
    })(),
    { item: 'Elevation & flood risk review', status: floodStatus, summary: `${isMeiners ? 'TWDB non-regulatory 1% planning screen maps 453 of 1,957 acres (23%); about 1,504 total acres and a roughly 1,360-acre largest contiguous piece remain outside the screen before well and pipeline exclusions' : isA1 ? (altRow ? layoutFloodLine(altRow) : 'Minimal flood hazard (FEMA Zone X); 11.32 ac Zone A in one drainage corridor — pad plan routes around it') : floodSev.subLine}. Site elevation and drainage assessment recommended.` },
  ]});

  // ── 6. Infrastructure & Logistics ──
  categories.push({ name: 'Infrastructure & Logistics', items: [
    { item: 'Highway access', status: parcel.nearest_highway_miles != null ? 'ADDRESSED' : 'FLAGGED', summary: parcel.nearest_highway_miles != null ? `${parcel.nearest_highway_miles} mi${parcel.nearest_highway_name ? ` (${parcel.nearest_highway_name})` : ''}` : 'Highway distance not computed' },
    // Heavy-haul road access — only when the payload carries a road-access screen.
    ...(data.roadAccess?.summary ? [(() => {
      const s = data.roadAccess.summary;
      const fr = s.frontage_routes[0];
      const clear = s.interchange_min_clearance_under_ft;
      return {
        item: 'Heavy-haul road access',
        status: (isMeiners || s.structures_posted > 0 ? 'FLAGGED' : 'ADDRESSED') as DDStatus,
        summary: isMeiners
          ? `${s.haul_route_mi} road mi from I-35 Exit 77 to the first FM 469 frontage point; the mapped existing driveway is 5.49 road mi from the interchange. Its suitability as the project gate is unverified. Confirm driveway authority, turning geometry, pavement/structure capacity and TxDOT heavy-haul permits.`
          : `${fr ? `${fr.frontage_mi} mi of ${fr.route} frontage (${fr.maintainer || fr.system_label}-maintained)` : 'No public frontage on the block'}; ${s.haul_route_mi} road mi from ${s.haul_route_from} to the gate with ${s.structures_posted === 0 ? 'no weight-limited bridge or culvert' : `${s.structures_posted} weight-limited structure${s.structures_posted > 1 ? 's' : ''}`} on the route${clear != null ? `, ${feetInches(clear)} clearance under the interstate at the interchange` : ''}. TxDOT district heavy-haul permit review is a pre-construction item.`,
      };
    })()] : []),
    { item: 'Railroad proximity', ...(() => {
      if (parcel.nearest_railroad_miles == null) return { status: 'FLAGGED' as DDStatus, summary: 'Railroad distance not computed' };
      const miles = `${parcel.nearest_railroad_miles} mi${parcel.nearest_railroad_owner ? ` (${parcel.nearest_railroad_owner})` : ''}`;
      const onSiteCorridor = data.carveOutAggregate?.slug === 'paces-creek-woodlands-jasper';
      const note = isMeiners ? ' from the representative parcel boundary to the rail line; about 3.0 mi from the full block boundary. Off-site transload required.' : onSiteCorridor ? ' — rail and US-96 share the corridor at the parcel; no extended final-mile haul' : '';
      return { status: 'ADDRESSED' as DDStatus, summary: `${miles}${note}` };
    })() },
    { item: 'Airport proximity', status: parcel.nearest_commercial_airport_miles != null ? 'ADDRESSED' : 'FLAGGED', summary: (() => { const ma = data.nearestFeatures?.majorAirport; const nearest = parcel.nearest_commercial_airport_miles != null ? `${parcel.nearest_commercial_airport_miles} mi${parcel.nearest_commercial_airport_name ? ` (${titleCaseAirport(parcel.nearest_commercial_airport_name)})` : ''}` : 'Airport data not computed'; const major = (ma && ma.faa_id !== data.nearestFeatures?.airport?.faa_id) ? `; major hub: ${titleCaseAirport(ma.name)} (${ma.faa_id}) ${ma.distance_miles} mi` : ''; const rwy = (parcel.nearest_runway_5000ft_name && parcel.nearest_runway_5000ft_miles != null && parcel.nearest_runway_5000ft_name !== parcel.nearest_commercial_airport_name) ? `; closer paved strip: ${titleCaseAirport(parcel.nearest_runway_5000ft_name)}${parcel.nearest_runway_5000ft_length_ft ? ` (${parcel.nearest_runway_5000ft_length_ft.toLocaleString()} ft)` : ''} ${parcel.nearest_runway_5000ft_miles} mi${isMeiners ? ' from the representative parcel boundary to the runway centerline; the Brief’s 9.4 mi measures from the representative parcel centroid to the airport reference point' : ''}` : ''; return `${nearest}${major}${rwy}`; })() },
    { item: 'Slope / terrain', ...(() => {
      if (altRow?.terrain_status === 'withheld') {
        return {
          status: 'REQUIRES INQUIRY' as DDStatus,
          summary: `${altRow.name} slope and elevation measurements are withheld; the retained raster is qualified only for local prototype use. Independently qualify the source before using terrain metrics for this outline.`,
        };
      }
      // Assembly-weighted when carve-out exists (4-parcel assemblies acquire all 4).
      const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
      const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
      const useAgg = aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
      // A1 Alt: the row follows the outline on the map — mean and under-threshold
      // acreage on that outline's upland (served on the layout row), not the core assembly.
      const altSlope = altRow?.slope_mean_pct_upland != null && altRow.slope_under5_upland_ac != null && altRow.slope_under10_upland_ac != null && altRow.upland_ac > 0 ? altRow : null;
      const altDenom = altSlope ? (altSlope.terrain_reference_upland_ac ?? altSlope.upland_ac) : 1;
      // Crown Pine A1: the 5 m end of the 3–5 m grid range (null on every other briefing).
      const grid5 = useAgg ? data.carveOutAggregate?.slopeGrid5m : undefined;
      const slope5 = altSlope ? altSlope.slope_mean_pct_upland_5m : grid5?.avgSlopePct;
      const pctUnder5At5m = altSlope
        ? (altSlope.slope_under5_upland_ac_5m != null ? (Number(altSlope.slope_under5_upland_ac_5m) / altDenom) * 100 : null)
        : grid5?.pctUnder5Pct;
      const slope = altSlope
        ? Number(altSlope.slope_mean_pct_upland)
        : useAgg && data.carveOutAggregate?.weightedAvgSlopePct != null
        ? Number(data.carveOutAggregate.weightedAvgSlopePct)
        : (parcel.avg_slope_pct != null ? Number(parcel.avg_slope_pct) : null);
      const pctUnder5 = altSlope
        ? (Number(altSlope.slope_under5_upland_ac) / (altSlope.terrain_reference_upland_ac ?? altSlope.upland_ac)) * 100
        : useAgg && data.carveOutAggregate?.weightedPctUnder5Pct != null
        ? Number(data.carveOutAggregate.weightedPctUnder5Pct)
        : (parcel.pct_under_5pct != null ? Number(parcel.pct_under_5pct) : null);
      if (slope == null) return { status: 'REQUIRES INQUIRY' as DDStatus, summary: 'DEM coverage unavailable — site survey needed' };
      const tier = slope < 2 ? ' — ideal' : slope < 5 ? ' — manageable' : ' — staged grading';
      const buildableNote = pctUnder5 != null
        ? isMeiners ? `; ${Math.round(pctUnder5)}% under 5% slope (slope-only, distributed area; not a contiguous pad)` : `, ${pctOr(pctUnder5, pctUnder5At5m)} buildable (<5%)${altSlope ? ` across the ${altSlope.name} upland` : useAgg ? ` across ${aggParcelCount}-parcel assembly` : ''}`
        : '';
      // Crown Pine A1: the tract-wide mean counts every creek bank; the
      // decision-relevant figures are the under-threshold acreages.
      const a1Note = (() => {
        if (data.carveOutAggregate?.slug !== 'crownpine-blk-194-a1') return '';
        if (altSlope) {
          const u10 = Number(altSlope.slope_under10_upland_ac);
          const u10At5m = altSlope.slope_under10_upland_ac_5m;
          return `. ${SLOPE_GRID_SOURCE} on the ${altSlope.name} upland (${Math.round(altDenom).toLocaleString()} ac outside the 100-yr floodplain and NWI wetlands): ~${acOr(Number(altSlope.slope_under5_upland_ac), altSlope.slope_under5_upland_ac_5m)} ac under 5% grade, ~${acOr(u10, u10At5m)} ac (${pctOr((u10 / altDenom) * 100, u10At5m != null ? (Number(u10At5m) / altDenom) * 100 : null)}) under 10%; ${altSlope.pads_status === 'illustrative' ? 'the acreage boxes illustrate the scale of areas across the assembly' : 'steep ground confined to narrow drainage/stream-bank ribbons the acreage boxes route around'}`;
        }
        const agg = data.carveOutAggregate;
        const acres = Number(agg.totalAcreage);
        const at = (pct: number | null | undefined) => (pct != null ? (acres * Number(pct)) / 100 : null);
        return `. ${SLOPE_GRID_SOURCE}: ~${acOr(at(agg.weightedPctUnder5Pct)!, at(agg.slopeGrid5m?.pctUnder5Pct))} ac under 5% grade, ~${acOr(at(agg.weightedPctUnder10Pct)!, at(agg.slopeGrid5m?.pctUnder10Pct))} ac (${pctOr(Number(agg.weightedPctUnder10Pct), agg.slopeGrid5m?.pctUnder10Pct)}) under 10%; steep ground confined to narrow drainage/stream-bank ribbons the pad plan routes around`;
      })();
      return { status: 'ADDRESSED' as DDStatus, summary: isMeiners ? `1.5–1.8% average slope by section${buildableNote}.` : `${altSlope?.terrain_status === 'reference' ? 'Earlier Alt3 terrain screen: ' : ''}${meanOr(slope, slope5)} avg slope${tier}${buildableNote}${a1Note}` };
    })() },
    { item: 'Flood zone classification', status: floodStatus, summary: isMeiners
      ? 'TWDB non-regulatory 1% annual-chance planning screen maps 453 of 1,957 mapped acres (23%), leaving about 1,504 total acres outside the screen and a roughly 1,360-acre largest contiguous clear piece before well and pipeline exclusions.'
      : data.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
      ? (altRow ? `${layoutFloodLine(altRow)}.` : 'Minimal flood hazard (FEMA Zone X) across ~99.5% of the assembly; ~11.3 ac Zone A confined to one drainage corridor, routed around by the pad plan.')
      : floodSev.subLine },
    { item: 'Soil testing (geotechnical)', status: 'REQUIRES INQUIRY', summary: 'Geotechnical investigation required pre-construction. Bearing capacity, settlement, and corrosion potential assessment.' },
    { item: 'Permitting environment review', status: parcel.state === 'TX' ? 'ADDRESSED' : parcel.state === 'WV' ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: parcel.state === 'TX' ? 'TX counties cannot zone. TCEQ air permit is the primary gate. Building permits via county or city if within ETJ.' : parcel.state === 'WV' ? 'WV counties can zone. WV DEP air permit is the primary gate. Harrison County has planning/zoning commission — verify land use compatibility.' : 'Local permitting authority and timeline assessment required.' },
    { item: 'Construction laydown space', ...(() => {
      if (altRow?.terrain_status === 'withheld') {
        return {
          status: 'REQUIRES INQUIRY' as DDStatus,
          summary: `${altRow.name} laydown terrain is not established; selected-outline slope is withheld because the retained raster is qualified only for local prototype use. Confirm 5–10 acres of laydown space through qualified terrain review and site planning.`,
        };
      }
      // Assembly-weighted: use carve-out aggregate acreage + weighted pct_under_5
      // when the parcel sits inside a multi-parcel single-owner assembly.
      const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
      const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
      const useAgg = aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
      const ac = useAgg ? Number(aggAcres) : (parcel.acreage != null ? Number(parcel.acreage) : null);
      const buildablePct = useAgg && data.carveOutAggregate?.weightedPctUnder5Pct != null
        ? Number(data.carveOutAggregate.weightedPctUnder5Pct) / 100
        : (parcel.pct_under_5pct != null ? Number(parcel.pct_under_5pct) / 100 : null);
      const buildableAc = ac != null && buildablePct != null ? ac * buildablePct : ac;
      // Crown Pine A1: the 5 m end of the 3–5 m grid range.
      const buildableAcAt5m = useAgg && ac != null && data.carveOutAggregate?.slopeGrid5m ? (ac * data.carveOutAggregate.slopeGrid5m.pctUnder5Pct) / 100 : null;
      const note = useAgg ? ` across ${aggParcelCount}-parcel assembly${altRow?.terrain_status === 'reference' ? ' (original-core reference)' : ''}` : '';
      if (isMeiners) return { status: 'REQUIRES INQUIRY' as DDStatus, summary: 'After the TWDB screen, 500-ft active/permitted pad rings and a 50-ft-total gathering corridor, about 1,354 total acres remain, with a roughly 730-acre largest contiguous piece. Confirm laydown inside a surveyed development footprint after surface-use terms, easements and hydrology are resolved.' };
      if (buildableAc == null) return { status: 'REQUIRES INQUIRY' as DDStatus, summary: 'Verify adequate laydown area for equipment staging. Typical BTM DC requires 5–10 acres temporary laydown adjacent to site.' };
      if (buildableAc >= 500) return { status: 'ADDRESSED' as DDStatus, summary: `${acOr(buildableAc, buildableAcAt5m)} ac buildable terrain${note} — 5–10 ac laydown is not a constraint at this site scale.` };
      if (buildableAc >= 100) return { status: 'FLAGGED' as DDStatus, summary: `${Math.round(buildableAc).toLocaleString()} ac buildable terrain${note} — laydown space likely available but should be staged in site plan.` };
      return { status: 'REQUIRES INQUIRY' as DDStatus, summary: `Only ~${Math.round(buildableAc).toLocaleString()} ac buildable terrain${note}. Confirm 5–10 ac laydown area available adjacent to site.` };
    })() },
  ]});

  // ── 7. Legal / Regulatory / Incentives ──
  categories.push({ name: 'Legal & Regulatory', items: [
    { item: parcel.state === 'TX' ? 'TCEQ permitting pathway' : 'Air permitting pathway', status: permitPathway.pathway ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: permitPathway.pathway ? `${permitPathway.pathway} (${parcel.state === 'TX' ? 'TCEQ' : parcel.state === 'WV' ? 'WV DEP-DAQ' : 'state air agency'}).` : 'Permitting pathway not determined' },
    { item: 'Permitting timeline estimation', status: permitPathway.pathway ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: permitPathway.pathway ? `${permitPathway.timeline}. Estimate assumes no contested case; NEPA/Section 7 triggers extend timeline if applicable.` : 'Timeline depends on pathway determination.' },
    { item: 'Nonattainment screening', status: parcel.in_nonattainment != null ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: parcel.in_nonattainment ? 'Nonattainment area — NNSR required, emission offsets may apply' : parcel.in_nonattainment === false ? (isA1 ? 'Attainment area — EGU Standard Permit path for early phases; PSD at full buildout' : 'Attainment area — standard PSD pathway') : 'Attainment data pending' },
    ...(parcel.state === 'TX' ? [{ item: 'SB 6 co-location', status: isA1 ? 'REQUIRES INQUIRY' : 'ADDRESSED', summary: isA1
      ? 'As currently structured (fully off-grid BTM, no ERCOT interconnection), the project sits outside SB 6’s >75 MW co-location approval, which applies to grid-connected loads. Confirm with regulatory counsel before commitment — any future grid tie or export changes the analysis, and PUCT implementation rules are still evolving.'
      : 'Fully off-grid BTM is exempt from PUCT co-location approval — applies to fully off-grid configurations only. >75 MW threshold applies only to grid-connected loads (TX SB 6, 2025).' } as DDItem] : []),
    ...(parcel.state === 'WV' ? [{ item: 'WV PSC Siting Certificate', status: 'FLAGGED', summary: 'Wholesale generators in WV require a PSC Siting Certificate under WV Code 24-2-11c — up to 270-day adjudication, runs in parallel with DEP air permit. Fully off-grid BTM self-supply may be exempt as a non-wholesale load, but the threshold is untested; confirm early with PSC staff. PJM co-location policy (FERC docket ER24-2172) is still evolving — not ERCOT SB 6.' } as DDItem] : []),
    ((): DDItem => {
      const factors: string[] = [];
      if (parcel.ej_max_pctile != null) factors.push(`EJ ${Math.round(Number(parcel.ej_max_pctile))}th pctile`);
      if (p.nearest_urban_area_miles != null) factors.push(`urban area ${Number(p.nearest_urban_area_miles).toFixed(1)} mi`);
      if (parcel.in_nonattainment) factors.push('nonattainment area');
      const hasAnyIndicator = parcel.ej_max_pctile != null || p.nearest_urban_area_miles != null || parcel.in_nonattainment != null;
      const base = factors.length > 0 ? `Screening indicators: ${factors.join(', ')}.` : 'Screening indicators unavailable.';
      return {
        item: 'Contested case risk',
        status: hasAnyIndicator ? 'FLAGGED' : 'REQUIRES INQUIRY',
        summary: `${base} Formal contested case assessment (receptor analysis, opposition groups, ${parcel.state === 'TX' ? 'TCEQ' : 'state air agency'} enforcement history) recommended before ${parcel.state === 'TX' ? 'TCEQ' : 'air permit'} application.`,
      };
    })(),
    { item: 'Zoning authority', status: data.countyZoning?.zoning_status ? 'ADDRESSED' : parcel.state === 'TX' ? 'ADDRESSED' : 'FLAGGED', summary: data.countyZoning?.zoning_status ? `${data.countyZoning.zoning_status === 'unzoned' ? 'No county zoning' : data.countyZoning.zoning_status}${data.countyZoning.zoning_notes ? ` — ${data.countyZoning.zoning_notes}` : ''}` : parcel.state === 'TX' ? 'TX counties cannot zone — no local land use restrictions' : 'Zoning data not available for this state' },
    { item: 'Tax incentive identification', status: 'REQUIRES INQUIRY', summary: parcel.state === 'TX' ? 'TX Chapter 313 expired — successor Ch. 403 (TCJIA) available for qualifying projects. County tax abatement agreements possible.' : 'Research state and local tax incentives for data center development.' },
    { item: 'Energy rebate programs', status: 'REQUIRES INQUIRY', summary: 'Evaluate federal ITC/PTC for on-site renewable integration. State-level energy efficiency rebates may apply to cooling systems.' },
    { item: 'ESG/LEED/Uptime compliance', status: 'REQUIRES INQUIRY', summary: 'Determine target certifications. Uptime Tier III/IV, LEED, and ESG reporting requirements affect design and operational standards.' },
  ]});

  // ── 8. Financial & Transactional ──
  // The partner edition drops the two valuation items outright rather than let them
  // degrade into a misleading "assessor value not ingested" line — the value was
  // stripped, not missing. The category and its remaining items stay.
  categories.push({ name: 'Financial & Transactional', items: [
    // A1 reports land control, not valuation. Its branch runs FIRST so the wording
    // never depends on assessment fields being populated, and it does not reference
    // the county assessment at all — the tax basis is not a price signal here.
    // Shown in BOTH editions: it discloses no price and no identity, and the open
    // pricing question is precisely what a partner needs to see. The generic
    // valuation row still drops on the partner edition rather than degrade into a
    // misleading "assessor value not ingested" line — that value was stripped, not
    // missing.
    ...(isA1 || data.redacted !== 'partner' ? [
    { item: isA1 ? 'Land control & pricing' : 'Land value assessment', ...(() => {
      if (isA1) {
        return { status: 'REQUIRES INQUIRY' as DDStatus, summary: `Exact carve-out, seller pricing, and definitive land-control terms remain under negotiation. No asking price, appraisal, or market-value conclusion is presented.` };
      }
      const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
      const aggLandValue = data.carveOutAggregate?.totalLandValue ?? null;
      const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
      const useAgg = aggAcres != null && aggAcres > 0 && aggLandValue != null && aggLandValue > 0 && aggParcelCount > 1;
      const value = useAgg ? Number(aggLandValue) : (parcel.land_value != null ? Number(parcel.land_value) : null);
      const acres = useAgg ? Number(aggAcres) : (parcel.acreage != null ? Number(parcel.acreage) : null);
      if (value == null) return { status: 'REQUIRES INQUIRY' as DDStatus, summary: `Assessor value not ingested for this parcel — verify directly with ${parcel.county} County${parcel.state === 'WV' ? ' Assessor (WV)' : parcel.state === 'TX' ? ' Appraisal District (TX)' : ' Assessor'}. Comparable-sales lookup recommended for acquisition underwriting.` };
      const perAc = acres ? ` ($${Math.round(value / acres).toLocaleString()}/ac)` : '';
      const note = useAgg ? ` — ${aggParcelCount}-parcel assembly` : '';
      return { status: 'ADDRESSED' as DDStatus, summary: `County assessed: $${value.toLocaleString()}${perAc}${note}` };
    })() },
    ] : []),
    // A1: the acquisition score is derived in part from the withheld county
    // assessment and from the absentee-owner heuristic. Publishing the derived
    // grade while withholding its inputs is the same anchor in another form.
    // Nulling land_value is NOT the fix — the formula scores an unknown value
    // higher, which would inflate the grade. Suppressed in both A1 editions.
    ...(isA1 || data.redacted === 'partner' ? [] : [
    { item: isMeiners ? 'Acquisition readiness' : 'Acquisition score', status: isMeiners || data.grades?.acquisition ? 'ADDRESSED' as DDStatus : 'FLAGGED' as DDStatus, summary: isMeiners ? 'A+ — known market / pre-market availability with one ownership counterparty. Price, title, easements and protected surface-use terms remain open.' : data.grades?.acquisition ? (data.grades.region === 'Assembly' ? `Grade: ${data.grades.acquisition.grade} — assembly-level score ${data.grades.acquisition.score}/100` : `Grade: ${data.grades.acquisition.grade} (${data.grades.acquisition.percentile}th percentile in ${data.grades.region})`) : 'Acquisition grade not computed' },
    ]),
    // A1: no absentee-willingness inference. One counterparty is a structural fact
    // about assemblage risk, not evidence that the seller is eager.
    { item: 'Deal structure guidance', ...(isMeiners
      ? { status: 'ADDRESSED' as DDStatus, summary: 'Known market / pre-market opportunity held by a single LLC. Confirm seller authority and agree price, exact property, title and protected surface-use terms.' }
      : isA1
      ? { status: 'REQUIRES INQUIRY' as DDStatus, summary: `Single ownership counterparty across the proposed premises. Confirm authorized seller, exact carve-out, title, and definitive land-control terms.` }
      : { status: 'ADDRESSED' as DDStatus, summary: `${parcel.is_absentee ? 'Absentee owner — typically more willing to transact' : (isEntityOwner(parcel.owner_name) ? 'Locally-based entity — transact via the operating principal' : 'Resident owner — direct negotiation recommended')}. ${isEntityOwner(parcel.owner_name) ? 'Entity ownership may simplify corporate transaction.' : 'Individual ownership — standard purchase agreement.'}` }) },
    { item: 'Utility connection cost estimates', status: 'REQUIRES INQUIRY', summary: (() => {
      const anyOnSite = pipelines && pipelines.some(p => pipelineIsEffectivelyOnSite(parcel, p));
      if (anyOnSite) return 'Request cost estimates from TDU for power interconnect. Pipeline crosses parcel — tap, receipt point, and firm-transport availability subject to standard operator interconnect study.';
      // A1: the fourth pre-priced lateral. Same treatment class as the memo
      // kill-criteria row and the Supply timeline row — an industry $/mile
      // range prices the wrong thing on a site whose premises reach the line.
      if (isA1) return 'Request cost estimates from TDU for power interconnect and from pipeline operator for gas lateral. Gas lateral cost: TBD pending tap engineering.';
      if (lateralEstimatesSuppressed(parcel)) return 'Request project-specific TDU and pipeline-operator estimates. Gas lateral routing, facilities, cost and schedule remain pending the operator interconnect study.';
      return 'Request cost estimates from TDU for power interconnect and from pipeline operator for gas lateral. Typical gas lateral: $1–3M/mile.';
    })() },
    { item: 'Site development cost model', status: 'REQUIRES INQUIRY', summary: 'Pre-construction cost model: grading, foundations, utilities, access roads. Geotechnical report required for accurate estimates.' },
    { item: 'Ongoing OPEX forecast', status: 'REQUIRES INQUIRY', summary: isMeiners ? 'Model project-specific fuel, transport, maintenance, insurance, property tax and staffing costs; no BTM power-price quote is established.' : `Model ongoing costs: fuel, maintenance, insurance, property tax, staffing.${parcel.state === 'TX' ? ' BTM fuel cost est. $9–18/MWh at current Waha pricing.' : ''}` },
    { item: 'Expansion & exit options', status: 'REQUIRES INQUIRY', summary: isMeiners
      ? 'Underwrite the base three-account, 1,803.86-acre block. Existing wells, surface rights, title, easements and a contiguous development footprint determine the go/no-go; adjacent acreage is not part of the current screen. Evaluate exit value after those gates are resolved.'
      : `${hasAssembly && assembly ? `Assembly group: ${assembly.parcel_count} parcels, ${assembly.total_acreage ? Number(assembly.total_acreage).toLocaleString() + ' acres' : 'acreage TBD'} — expansion potential.` : 'Evaluate adjacent land for phased expansion.'} Exit: land + infrastructure resale value.` },
  ]});

  // ── 9. Risk & Resilience ──
  categories.push({ name: 'Risk & Resilience', items: [
    { item: 'Pipeline incident history', status: safety ? (seriousCount > 0 ? 'FLAGGED' : 'ADDRESSED') : 'REQUIRES INQUIRY', summary: incidentSummary },
    { item: 'Seismic hazard', status: seismicStatus, summary: pga != null ? `PGA ${pga.toFixed(3)}g — ${pga < 0.1 ? 'standard construction' : 'seismic design review recommended'}` : 'Map layer available — per-parcel PGA not yet computed' },
    { item: 'Flood risk', status: floodStatus, summary: isMeiners ? 'TWDB non-regulatory 1% annual-chance planning screen maps 453 of 1,957 acres (23%); it is not a FEMA flood-zone determination.' : isA1 ? (altRow ? layoutFloodLine(altRow) : 'Minimal flood hazard (FEMA Zone X); 11.32 ac Zone A in one drainage corridor — pad plan routes around it') : floodSev.subLine },
    { item: 'Wildfire risk', status: isMeiners ? 'ADDRESSED' : data.environmentalContext ? (data.environmentalContext.subject.wildfire.comparison.all_below_service_mean ? 'ADDRESSED' : 'FLAGGED') : p.wildfire_risk_score == null ? 'REQUIRES INQUIRY' : Number(p.wildfire_risk_score) >= 3 ? 'FLAGGED' : 'ADDRESSED', summary: isMeiners ? 'Texas A&M Forest Service TxWRAP desktop screen places all three sections below the statewide mean on the screened indicators. Confirm vegetation management, fire-water supply and emergency access during design.' : environmentalScreeningCopy ? `${environmentalScreeningCopy.rows[0].wildfire}. Texas A&M Forest Service desktop screen; confirm vegetation management, water supply, and emergency-response access during design.` : p.wildfire_risk_score != null ? `${Number(p.wildfire_risk_score) < 1 ? 'Very low' : Number(p.wildfire_risk_score) < 2 ? 'Low' : Number(p.wildfire_risk_score) < 3 ? 'Moderate' : 'High'} wildfire hazard potential (USFS WHP ${Number(p.wildfire_risk_score).toFixed(1)}/5)` : 'USFS Wildfire Risk data not yet computed — field assessment recommended for rural sites' },
    { item: 'Community opposition indicators (proxy screen)', status: oppFactors.length > 0 ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: `${oppFactors.length > 0 ? `Elevated indicators: ${oppFactors.join(', ')}.` : 'No elevated indicators on EJ, urban proximity, or nonattainment.'} Proxy screen only — does not include news/social media monitoring, active opposition groups, or local moratorium tracking. Formal community engagement assessment recommended ${isA1 ? 'before committing to terms' : 'before LOI'}.` },
    { item: 'Power redundancy verification', status: hasRedundancy ? 'ADDRESSED' : 'FLAGGED', summary: hasRedundancy ? `Dual substation access: ${fmtSub(sub1?.name, sub1Kv, sub1Dist, parcel.county)} + ${fmtSub(sub2?.name, sub2Kv, sub2Dist, parcel.county)}` : 'Single substation within range — on-site backup generation required for N+1 power resilience.' },
    { item: 'Security & access control', status: 'REQUIRES INQUIRY', summary: 'Evaluate perimeter security, setback from public roads, and controlled access points. Rural sites favor security but may lack emergency services.' },
    { item: 'Regulatory risk evaluation', status: parcel.state === 'WV' ? 'FLAGGED' : parcel.state === 'TX' && oppFactors.length === 0 ? 'ADDRESSED' : 'REQUIRES INQUIRY', summary: parcel.state === 'TX' ? (data.carveOutAggregate?.slug === 'paces-creek-woodlands-jasper' ? 'Low contested-case risk — rural Jasper County, no organized opposition; PSD path validated by the Sandow Lakes precedent (TCEQ rejected contested-case request 90-second vote, Oct 2025). SB 6 co-location applies only to grid-connected loads >75 MW; fully off-grid BTM is exempt.' : isA1 ? 'Rural western Angelina County, greenfield timber tract — no organized opposition identified; the county’s active precedent (Amp Z) drew its friction from a brownfield/urban-edge site, which this tract sidesteps. SB 6 co-location applies to grid-connected loads >75 MW; fully off-grid configuration sits outside it as structured (confirm with counsel). Air path: EGU Standard Permit early phases, PSD at buildout.' : oppFactors.length === 0 ? 'TX state govt hostile to local moratoriums. SB 6 co-location applies only to grid-connected loads >75 MW; fully off-grid BTM is exempt. No elevated indicators on this parcel — standard PSD pathway expected.' : 'TX state govt hostile to local moratoriums. SB 6 co-location rules apply >75 MW grid-connected. Monitor TCEQ contested case risk.') : parcel.state === 'WV' ? 'WV pro-energy development. Multiple GW-scale gas plants approved 2024-2025 (Wolf Summit 600 MW, CPV Shay 2,060 MW). WV PSC supportive of gas generation. Harrison County zoning ordinance requires verification.' : 'Evaluate local and state regulatory environment for DC development.' },
    { item: 'Insurance requirements review', status: 'REQUIRES INQUIRY', summary: 'Property, liability, builder\'s risk, and business interruption coverage required. Flood/wind zones affect premiums significantly.' },
  ]});

  // ── 10. Equipment / Prime Movers ──
  categories.push({ name: 'Equipment & Prime Movers', items: [
    { item: 'Generators — POs executed?', status: 'REQUIRES INQUIRY', summary: 'Confirm generator purchase orders and delivery schedule. Lead times: recip engines 12–24 mo, gas turbines 5–7 yr.' },
    { item: 'Downpayment', status: 'REQUIRES INQUIRY', summary: 'Typical generator downpayment: 10–30% at PO execution. Confirm payment milestones and cancellation terms.' },
    { item: '345/34.5 kV MV gear — POs executed?', status: 'REQUIRES INQUIRY', summary: 'HV/MV switchgear and transformer PO status. Power transformers: 128–144 wk lead time. MV gear: 40–60 wk.' },
    { item: 'Block size', status: 'ADDRESSED', summary: `${data.redacted === 'partner' ? '160 MW initial tranche.' : '160 MW per Integra profile.'} ${permitPathway.ladder ? `Phased permitting: ${permitPathway.ladder.phases.map(p => p.mwRange).join(' → ')}` : 'Single-phase deployment'}` },
    { item: 'Parallel generators', status: 'REQUIRES INQUIRY', summary: 'Determine parallel gen configuration for N+1 redundancy. Recip engines parallelize easily; turbines require synchronization gear.' },
    { item: 'Diesel engines', status: 'REQUIRES INQUIRY', summary: 'Backup/black-start diesel gen capacity. Typically 2–5 MW units for site auxiliaries and emergency power.' },
    { item: 'Natural gas engines', status: 'REQUIRES INQUIRY', summary: 'Recip gas engines: 12–24 mo lead time, fastest path to power. 5–20 MW units, modular deployment.' },
    { item: 'Gas turbines', status: 'REQUIRES INQUIRY', summary: 'Large frame turbines: 5–7 yr lead. Aeroderivatives: 2–3 yr. Higher efficiency but longer procurement.' },
    { item: 'Fuel cells — emerging', status: 'REQUIRES INQUIRY', summary: 'Bloom Energy, FuelCell Energy. 250 kW–1.4 MW modules. Near-zero NOx. Higher capex, lower emissions profile.' },
    { item: 'Microturbines', status: 'REQUIRES INQUIRY', summary: 'Capstone/Ansaldo 30–250 kW units. Ultra-low emissions, modular. Best for small auxiliary loads, not primary generation.' },
    { item: 'Emissions per tranche', status: 'ADDRESSED', summary: isMeiners
      ? `Screening-level est. ~${Math.round(160 * BTM_CALIBRATION.GHG_TPY_PER_MW).toLocaleString()} tCO2e/yr, ~98 TPY NOx per 160 MW tranche (160 MW × 0.14 lb/MWh × 8,760 hr/yr ÷ 2,000 lb/ton). The 0.14 lb/MWh value is the EGU Standard Permit limit used for pathway screening, not an equipment forecast or guarantee of permit eligibility. Engine/turbine selection, controls, operating profile, source aggregation and other pollutants remain to be modeled; formal emissions inventory required.`
      : isA1
      ? `Screening-level est. ~${Math.round(160 * BTM_CALIBRATION.GHG_TPY_PER_MW).toLocaleString()} tCO2e/yr, ~${Math.round(160 * BTM_CALIBRATION.NOX_TPY_PER_MW_SP)} TPY NOx per 160 MW tranche at the EGU Standard Permit limit (0.14 lb/MWh), gas ~${(160 * BTM_CALIBRATION.GAS_MMCF_PER_MW).toFixed(0)} MMcf/d. Controls equipment-specific (precedents used SCR ± oxidation catalysts). Formal emissions inventory required for TCEQ registration.`
      : `Screening-level est. ~${Math.round(160 * BTM_CALIBRATION.GHG_TPY_PER_MW).toLocaleString()} tCO2e/yr, ~${Math.round(160 * BTM_CALIBRATION.NOX_TPY_PER_MW_SCR)} TPY NOx (with SCR), gas ~${(160 * BTM_CALIBRATION.GAS_MMCF_PER_MW).toFixed(0)} MMcf/d. Formal emissions inventory required for ${parcel.state === 'TX' ? 'TCEQ' : 'state air agency'} application.` },
  ]});

  // ── 11. Environmental & Sustainability ──
  categories.push({ name: 'Environmental & Sustainability', items: [
    { item: 'Phase I ESA', status: 'REQUIRES INQUIRY', summary: 'Phase I Environmental Site Assessment required per ASTM E1527-21. Identifies recognized environmental conditions (RECs).' },
    { item: 'Phase II ESA (if required)', status: 'REQUIRES INQUIRY', summary: 'Triggered by Phase I findings. Subsurface investigation: soil borings, groundwater sampling, vapor intrusion assessment.' },
    { item: 'Noise & emissions compliance', status: (parcel.state === 'TX' || parcel.state === 'WV') && permitPathway.pathway ? 'FLAGGED' : 'REQUIRES INQUIRY', summary: parcel.state === 'TX' ? (isMeiners
      ? 'TCEQ EGU Standard Permit pathway screen: ~98 TPY NOx per 160 MW tranche (160 MW × 0.14 lb/MWh × 8,760 hr/yr ÷ 2,000). This uses the permit limit, not an equipment forecast or eligibility assurance. Equipment, controls, operating profile, aggregation, other pollutants and ambient noise remain project-specific.'
      : isA1
      ? `TCEQ pathway: EGU Standard Permit (2007-amended) early phases; PSD at buildout. Screening emissions: ~${Math.round(160 * BTM_CALIBRATION.NOX_TPY_PER_MW_SP)} TPY NOx per 160 MW tranche at the 0.14 lb/MWh limit — controls equipment-specific. Ambient noise study required for sites near receptors.`
      : `TCEQ pathway: ${permitPathway.pathway || 'TBD'}. Screening emissions: ~${Math.round(160 * BTM_CALIBRATION.NOX_TPY_PER_MW_SCR)} TPY NOx (with SCR, per tranche). Ambient noise study required for sites near receptors.`) : parcel.state === 'WV' ? `WV DEP-DAQ pathway: ${permitPathway.pathway || 'TBD'}. Screening emissions: ~${Math.round(160 * BTM_CALIBRATION.NOX_TPY_PER_MW_SCR)} TPY NOx (with SCR, per tranche) — above the 100 TPY listed-category threshold for fossil-fuel steam electric >250 MMBTU/hr. PSC Siting Certificate noise/odor rules and county nuisance ordinances apply; ambient noise study expected for sites within ~1 mi of residences.` : 'State environmental agency noise and emissions limits apply.' },
    { item: 'Renewable energy options', status: 'REQUIRES INQUIRY', summary: `Evaluate co-located solar for auxiliary loads or REC generation.${parcel.state === 'TX' ? ' TX solar resource: 5.5–6.5 kWh/m2/day.' : ''}` },
    { item: 'Carbon footprint analysis', status: 'ADDRESSED', summary: `Screening-level est. ${Math.round(160 * BTM_CALIBRATION.GHG_TPY_PER_MW).toLocaleString()} tCO2e/yr at 160 MW. Formal GHG inventory required for ESG reporting; offset and capture options per tenant/ESG requirements.` },
    { item: 'Wetlands species desktop survey', ...wetlandSpeciesSurvey() },
    { item: 'Wetlands delineation report', ...wetlandDelineation() },
  ]});

  // ── 12. Documentation & Stakeholder Review ──
  categories.push({ name: 'Documentation & Stakeholder Review', items: [
    { item: 'Utility capacity confirmation letters', status: 'REQUIRES INQUIRY', summary: 'Obtain Will Serve or capacity confirmation letters from electric utility and gas pipeline operator.' },
    { item: 'Fiber provider maps & contracts', status: 'REQUIRES INQUIRY', summary: 'Request route maps and service agreements from fiber carriers. Confirm lit vs. dark fiber availability and pricing.' },
    { item: 'Geotechnical & environmental reports', status: 'REQUIRES INQUIRY', summary: 'Commission Phase I ESA, geotechnical investigation, and ALTA survey. Compile for investment committee package.' },
    { item: 'Survey & title commitment review', status: 'REQUIRES INQUIRY', summary: 'ALTA/NSPS survey and title commitment from title company. Review exceptions, easements, and encumbrances.' },
    { item: 'Development timeline & milestones', status: 'FLAGGED', summary: 'Screening timeline: 18–36 months typical BTM (land closing → permitting → construction → energization). Master schedule required per project.' },
  ]});

  return isUvaldeBriefing(data) ? reconcileUvaldeChecklist(categories) : categories;
}

// Category-to-section mapping for click-to-navigate
const CATEGORY_SECTION_MAP: Record<string, BriefingSection> = {
  'Site & Title': 'kill-sheet',
  'Power Infrastructure': 'power-infrastructure',
  'Gas Supply & Logistics': 'gas-infrastructure',
  'Network Connectivity': 'fiber-connectivity',
  'Cooling & Environmental': 'water-sourcing',
  'Infrastructure & Logistics': 'physical-site',
  'Legal & Regulatory': 'permitting',
  'Financial & Transactional': 'land-ownership',
  'Risk & Resilience': 'risk-register',
  'Equipment & Prime Movers': 'power-infrastructure',
  'Environmental & Sustainability': 'infrastructure',
  'Documentation & Stakeholder Review': 'kill-sheet',
};

// Heat strip abbreviations
export const CATEGORY_ABBREV: Record<string, string> = {
  'Site & Title': 'SITE',
  'Power Infrastructure': 'PWR',
  'Gas Supply & Logistics': 'GAS',
  'Network Connectivity': 'NET',
  'Cooling & Environmental': 'ENV',
  'Infrastructure & Logistics': 'INFRA',
  'Legal & Regulatory': 'LEGAL',
  'Financial & Transactional': 'FIN',
  'Risk & Resilience': 'RISK',
  'Equipment & Prime Movers': 'EQUIP',
  'Environmental & Sustainability': 'ESG',
  'Documentation & Stakeholder Review': 'DOCS',
};

const ChecklistRow: React.FC<{
  item: DDItem;
  isBanded: boolean;
  onClick: () => void;
}> = ({ item, isBanded, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'baseline', minHeight: 20, cursor: 'pointer',
        background: hovered ? `${REPORT.accent}0A` : (isBanded ? REPORT.white : undefined),
        margin: '0 -28px',
        padding: '2px 28px',
      }}
    >
      <span style={{ width: '40%', fontSize: '10px', fontWeight: 600, color: REPORT.heading, flexShrink: 0, paddingRight: 8 }}>
        {item.item}
      </span>
      <span style={{
        width: '12%', fontSize: '8.5px', fontWeight: 600, flexShrink: 0,
        fontFamily: 'monospace', letterSpacing: '0.04em', textTransform: 'uppercase' as const,
        color: DD_COLORS[item.status],
      }}>
        {item.status === 'REQUIRES INQUIRY' ? 'Inquiry' : item.status === 'ADDRESSED' ? 'Addressed' : 'Flagged'}
      </span>
      <span style={{ width: '48%', fontSize: '10px', fontWeight: 400, color: REPORT.body, lineHeight: 1.4 }}>
        {item.summary}
      </span>
    </div>
  );
};

const ChecklistView: React.FC<{
  categories: DDCategory[];
  addressedCount: number;
  flaggedCount: number;
  inquiryCount: number;
  siteLabel: string;
  onNavigate: (section: BriefingSection, categoryName: string) => void;
}> = ({ categories, addressedCount, flaggedCount, inquiryCount, siteLabel, onNavigate }) => {
  return (
    <div style={{ background: REPORT.surface, padding: '22px 28px 18px', color: REPORT.body }}>
      {/* Print-only site identifier */}
      <div className="briefing-print-only" style={{
        display: 'none', fontSize: '10px', color: REPORT.secondary,
        fontFamily: 'monospace', letterSpacing: '0.02em', marginBottom: 8,
      }}>
        {siteLabel}
      </div>
      {/* Header: serif title + action counts */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 14 }}>
        <span style={{ fontFamily: 'Georgia, serif', fontSize: '18px', fontWeight: 400, color: REPORT.heading }}>
          Due Diligence
        </span>
        <span style={{ fontSize: '10px', color: REPORT.secondary, fontFamily: 'monospace', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          <span style={{ color: REPORT.heading, fontWeight: 600 }}>{inquiryCount} require inquiry</span>
          {' \u00b7 '}
          <span style={{ color: REPORT.caution, fontWeight: 600 }}>{flaggedCount} flagged</span>
          {' \u00b7 '}
          {addressedCount} addressed
        </span>
      </div>

      {/* Heat strip — text color = status, self-explanatory */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
        {categories.map(cat => {
          const catAddressed = cat.items.filter(d => d.status === 'ADDRESSED').length;
          const hasInquiry = cat.items.some(d => d.status === 'REQUIRES INQUIRY');
          const hasFlags = cat.items.some(d => d.status === 'FLAGGED');
          const statusClass = catAddressed === cat.items.length ? 'complete' : hasInquiry ? 'inquiry' : hasFlags ? 'flagged' : 'complete';
          const color = statusClass === 'complete' ? REPORT.pass : statusClass === 'inquiry' ? REPORT.secondary : REPORT.caution;
          const bg = statusClass === 'complete' ? `${REPORT.pass}14` : statusClass === 'inquiry' ? `${REPORT.secondary}18` : `${REPORT.caution}14`;
          return (
            <div key={cat.name} style={{ flex: 1, textAlign: 'center', padding: '4px 0 3px', borderRadius: 3, background: bg }}>
              <div style={{ fontSize: '8px', fontFamily: 'monospace', color, letterSpacing: '0.04em', textTransform: 'uppercase' as const, lineHeight: 1 }}>
                {CATEGORY_ABBREV[cat.name] || cat.name.slice(0, 4).toUpperCase()}
              </div>
              <div style={{ fontSize: '9px', fontFamily: 'monospace', fontWeight: 600, color, lineHeight: 1, marginTop: 2 }}>
                {catAddressed}/{cat.items.length}
              </div>
            </div>
          );
        })}
      </div>

      {/* Flat item list — banded rows, no borders, no accordions */}
      {categories.map(cat => {
        const section = CATEGORY_SECTION_MAP[cat.name] || 'header';
        return (
          <div key={cat.name} className="no-break">
            {/* Category header band — distinct background to separate sections */}
            <div data-dd-category={cat.name} style={{
              padding: '5px 28px 4px', margin: '12px -28px 0', borderBottom: `1.5px solid rgba(46,56,64,0.12)`,
              background: REPORT.sand,
            }}>
              <span style={{ fontSize: '10px', fontWeight: 700, color: REPORT.heading, textTransform: 'uppercase' as const, letterSpacing: '0.08em' }}>
                {cat.name}
              </span>
            </div>

            {/* Item rows — banding resets per category */}
            {cat.items.map((d, i) => (
              <ChecklistRow key={i} item={d} isBanded={i % 2 === 1} onClick={() => onNavigate(section, cat.name)} />
            ))}
          </div>
        );
      })}

      <SourceFootnote>
        Status reflects automated screening only. All items require field verification before commitment.
      </SourceFootnote>
    </div>
  );
};

export const BriefingContent: React.FC<BriefingContentProps> = ({ data: rawData, mapSnapshots, parcelId, token, lensProfile = 'btm', onLensChange, siteLayout = 'original' }) => {
  // Per-parcel curated overrides for the Paces 4-parcel assembly. Highway and
  // railroad distances reflect the assembly's western-edge proximity to the
  // US-96 / BNSF corridor (~0.6 mi). The DB's nearest_*_miles columns are
  // computed against the rep parcel polygon only — for a 2,356-acre 4-parcel
  // assembly that understates real campus proximity (DB returns 2.66/2.42 mi
  // from rep parcel; user-measured 0.6 mi from assembly edge). Until proximity
  // compute supports carve-out polygons, hardcode the assembly-edge values.
  // Airport fields remain hardcoded because no airports table is ingested yet.
  // Aquifer field dropped 2026-05-12 — DB now serves GULF_COAST directly.
  const isPaces = PACES_PARCEL_IDS.has(rawData.parcel.id);
  // Partner (non-client) edition: the server already stripped price, valuation, and
  // the client's name from the payload. The flag only selects the replacement copy.
  const isRedacted = rawData.redacted === 'partner';
  // The alternative outline the map is showing, if any (A1 Alt edition only).
  const activeLayoutRow = rawData.siteLayouts && siteLayout !== 'original'
    ? (rawData.siteLayouts.rows.find(r => r.key === siteLayout) ?? null)
    : null;
  const formatLayoutAcres = (row: NonNullable<typeof activeLayoutRow>) => row.measured_acres != null
    ? row.acres.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(row.acres).toLocaleString();
  const activeCalculationAcres = activeLayoutRow?.measured_acres ?? activeLayoutRow?.acres ?? null;
  const activeLayoutTerrainWithheld = activeLayoutRow?.terrain_status === 'withheld';
  const activeLayoutTerrainReference = activeLayoutRow?.terrain_status === 'reference';
  // Per-layout Site Intelligence text (A1 Alt): sections replace the seeded body
  // under the same heading; a note renders above the narrative. Original = none.
  const activeLayoutNarrative = siteLayout !== 'original' ? (rawData.siteLayoutNarratives?.[siteLayout] ?? null) : null;
  // A1 carve-out is an Integra-facing deliverable → show the Atwater × Integra
  // lockup for it too (default 55K view keeps Atwater-only: parcel not in the set,
  // no carveOut param). The partner edition names no client, so no co-brand.
  const showIntegraCobrand = !isRedacted
    && (INTEGRA_COBRAND_IDS.has(rawData.parcel.id)
      || rawData.carveOutAggregate?.slug === 'crownpine-blk-194-a1');
  const data = isPaces
    ? {
        ...rawData,
        parcel: {
          ...rawData.parcel,
          nearest_highway_name: 'US Hwy 96',
          nearest_highway_miles: 0.6,
          nearest_railroad_owner: 'BNSF',
          nearest_railroad_miles: 0.6,
          nearest_commercial_airport_name: 'Jack Brooks Regional',
          nearest_commercial_airport_miles: 73,
        } as ParcelDetail,
        nearestFeatures: {
          ...(rawData.nearestFeatures || {}),
          majorAirport: {
            ...(rawData.nearestFeatures?.majorAirport || {}),
            name: 'George Bush Intercontinental',
            faa_id: 'IAH',
            distance_miles: 125,
          } as any,
        },
        // Inject the two Paces pipelines the briefing API hero-dedups out:
        // a 2nd Gulf South line (14", crosses parcel) and TGP 100 30"
        // (3.07 mi). The Gulf South entry carries Panhandle-B values, NOT
        // the 3,296 MMcf/d EIA system-wide rating that bled in earlier and
        // produced an implausible 16.7 GW headroom on a 14" lateral —
        // see parcel-overrides.tsx for the per-row reasoning.
        //
        // Pre-process the auto-detected Gulf South 16" Magasco lateral so
        // the table surfaces the Magasco subsystem name (the API rolls all
        // Gulf South subsystems under the registry name "Gulf South Pipeline
        // Co" via system_name; for Paces the operator-confirmable lateral is
        // "Magasco → Call Junction"). Also force distance_miles=0 — the
        // pipeline-systems aggregator measures from the rep parcel centroid,
        // and a 0.83 mi value mid-assembly does not reflect that the line
        // visually crosses the 4-parcel polygon.
        pipelines: [
          ...((rawData.pipelines || []).map(p => {
            const isGsMagasco = p.operator
              && /gulf south/i.test(p.operator)
              && p.diameter_inches === 16
              && Number(p.distance_miles) < 5;
            if (!isGsMagasco) return p;
            return {
              ...p,
              system_name: 'Magasco → Call Junction Lateral',
              name: p.name || 'MAGASCO C.S. TO CALL JUNCTION',
              distance_miles: 0,
              // Render as a normal tap candidate (no sand background, no
              // Headline badge), keeping its real attrs. The Paces table
              // carries no headline row — every line is a tap candidate on
              // equal footing.
              headline_override: false,
            };
          })),
          {
            id: '5d92f91a-32a7-44fe-a8ee-62b80053fc54',
            name: 'Gulf South Pipeline Co',
            operator: 'Gulf South Pipeline Co',
            distance_miles: 0,
            capacity_mmcf_day: null,
            mw_potential: null,
            capacity_source: null,
            source: 'EIA',
            diameter_inches: 14,
            utilization_pct: null,
            utilization_source: null,
            local_utilization_pct: null,
            local_util_source: null,
            flow_volume_mmcf_day: null,
            maop_psig: null,
            maop_source: null,
            system_type: 'transmission',
            pressure_grade: null,
            is_gathering: false,
          },
          {
            id: 'b86f2082-2f83-4dcc-bb62-89c45b912e97',
            name: 'TGP 100 SYSTEM',
            operator: 'Tennessee Gas Pipeline Co, L.L.C.',
            distance_miles: 3.07,
            // Diameter is physical context; native capacity and utilization
            // remain unknown until an operator source is scoped to this system.
            capacity_mmcf_day: null,
            mw_potential: null,
            capacity_source: null,
            source: 'EIA',
            diameter_inches: 30,
            utilization_pct: null,
            utilization_source: null,
            local_utilization_pct: null,
            local_util_source: null,
            flow_volume_mmcf_day: null,
            maop_psig: null,
            maop_source: null,
            system_type: 'transmission',
            pressure_grade: null,
            is_gathering: false,
          },
        ] as NearbyPipeline[],
      }
    : rawData;
  const { parcel, pipelines, gasSupply, grades, listings, freshness } = data;
  // Declared BEFORE every hook that references it — a later declaration put the
  // structuredRisks dependency array in the temporal dead zone (render crash).
  const isA1CarveOut = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
  const meinersOverrides = getMeinersBriefingOverrides(data.carveOutAggregate?.slug);
  const isMeiners = meinersOverrides != null;
  const fuelOptions = selectedPipelineCapacitySummary(pipelines || [], (parcel as any).structured_intel?.fuel_delivery_options, isMeiners ? meinersPipelineCapacity : undefined);
  const isUvalde = isUvaldeBriefing(data);
  const isCooperDraw = data.carveOutAggregate?.slug === COOPER_DRAW_SLUG;

  // Grid lens: page-1 and section order branch on the opt-in grid payload.
  // gridScore is present ONLY when the briefing was fetched with ?profile=grid
  // — the default (BTM) response never carries it, so the BTM render path is
  // structurally unreachable from grid data and vice versa.
  const gridScore = data.gridScore || null;
  const isGrid = !!gridScore;

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const gates = useMemo(() => computeGateStatus(parcel, pipelines), [parcel, pipelines]);
  const floodSev = useMemo(() => applyCuratedFloodGate(floodSeverity(parcel, data.carveOuts?.[0] ?? null), parcel), [parcel, data.carveOuts]);
  const structuredRisks = useMemo((): StructuredRisk[] => {
    if (isUvalde) return uvaldeRisks;
    if (meinersOverrides) {
      return meinersOverrides.risks.map(risk => ({
        description: `${risk.title} — ${risk.description}`,
        severity: risk.severity,
        mitigation: risk.mitigation,
      }));
    }
    // Crown Pine A1: the auto-derived register surfaces only two LOW risks, which
    // understates the real diligence posture. Curated honest set — a committee
    // trusts a register that found something.
    if (isA1CarveOut) {
      return [
        { description: 'Gas commercial terms — single gas counterparty: availability and supply confirmed, but firm transport, tariff terms, and tap engineering remain unexecuted', severity: 'MED', mitigation: 'Gas availability and supply confirmed, with delivery expected in tranches as project phases build out; in-service 30″ transmission crossing the proposed western band — candidate tap and meter siting may remain within the proposed premises, subject to title, survey, routing, and ET approval.' },
        { description: 'Air permitting path and timeline — EGU Standard Permit eligibility beyond Phase 1 depends on engine-specific CO/PTE and source aggregation; PSD at buildout adds 18–24 months', severity: 'MED', mitigation: 'Attainment county; Longhorn/Stargate/Misae precedents permitted 360–709 MW under the same instrument. Parallel-track: file Phase 1 registration, run PSD pre-application during construction.' },
        { description: 'WOTUS / jurisdictional determination on the north drain — if ruled jurisdictional, in-rect fill needs §404 authorization and Section 7 rides the federal action', severity: 'LOW', mitigation: 'Strong non-jurisdiction case documented (119-ac drainage, 1.87 mi to perennial water, non-hydric soils); AJD path scoped, 440-ac under-cap pad fallback exists.' },
        { description: 'Seasonal high water table on the Diboll core (~1 ft, Apr–Jun) — site-prep cost and schedule item', severity: 'LOW', mitigation: 'Priceable, standard playbook: engineered fill (2–3 ft separation), subsurface drainage, dry-season mass earthwork; drier southern/NE ground carries the pad. Borings confirm.' },
        { description: `Single-counterparty land dependency — the entire assembly rests on one seller (${isRedacted ? 'single private owner; identity withheld at this stage' : 'Crown Pine Timber 1 LP'})`, severity: 'LOW', mitigation: 'One ownership counterparty reduces assemblage risk, but seller authority, exact carve-out, title, and definitive land-control terms remain open. Resolve before public announcement.' },
      ];
    }
    return computeStructuredRisks(parcel, pipelines);
  }, [parcel, pipelines, isA1CarveOut, isRedacted, meinersOverrides, isUvalde]);
  const verdict = useMemo(() => isMeiners
    ? `${parcel.county}, ${parcel.state} — ${Number(data.carveOutAggregate!.totalAcreage).toLocaleString()}-acre, three-account assembly. Existing wells and surface control are the first go/no-go decision. Both operators’ surface-use terms, mineral and pipeline rights, and future drilling must support a protected, contiguous development footprint. Gas delivery and flood layout remain open; failure to secure surface protections can end the project.`
    : isUvalde ? 'The 1,465-acre offered tract has gentle terrain and FM 1023 frontage. Gas delivery, utility service, airport-compatible layout and title remain unresolved. The 19.3 MW model is a screening estimate; the north tract is separately owned.' : generateVerdict(parcel, pipelines, structuredRisks, data.carveOuts?.[0] ?? null), [parcel, pipelines, structuredRisks, data.carveOuts, data.carveOutAggregate, isMeiners, isUvalde]);
  const confidenceItems = useMemo(() => {
    const items = buildConfidenceItems(parcel, pipelines, data.carveOuts?.[0] ?? null);
    if (isUvalde) return reconcileUvaldeConfidence(items);
    // Crown Pine A1: the Data Confidence slope row reads the rep parcel (3.01%,
    // coarse 10 m) — swap in the controlling assembly figure on the 3–5 m grids.
    if (isA1CarveOut) {
      const agg = data.carveOutAggregate;
      return items.map(it => {
        if (it.field === 'Avg slope') return { ...it, value: `${meanOr(Number(agg?.weightedAvgSlopePct), agg?.slopeGrid5m?.avgSlopePct)} (3–5 m grids, assembly)`, source: 'USGS 3DEP 1 m DEM, averaged to 3 m and 5 m grids' };
        // The generic TX line says "no SCR" — controls are equipment-specific and the
        // major precedents (Longhorn, Misae) used SCR. Keep one consistent story.
        if (it.field === 'Air permit pathway') return { ...it, value: 'EGU Standard Permit (2007-amended) — 160 MW Phase 1, ~98 TPY NOx; controls equipment-specific', source: 'TCEQ registrations: Longhorn 177263, Stargate 180842, Misae' };
        return it;
      });
    }
    if (isMeiners) {
      const mapped = items.map(it => {
        if (it.field === 'Available capacity') return { ...it, field: 'Camino Real total capacity', value: 'Unconfirmed · 12.75-inch intrastate transmission', confidence: 'pending' as const, source: 'Operator-rated capacity, tap rights, pressure, receipt point and firm delivery require WTG confirmation' };
        if (it.field === 'Nearest gas processing plant') return { ...it, value: '6.4 mi to Gardendale condensate stabilizer; Brasada 12.35 mi; Raptor 12.74 mi', source: 'RRC/EIA facility classification; distances from Meiners gas-supply evidence' };
        if (it.field === 'Avg slope') return { ...it, value: '1.70% area-weighted mean across 1,803.86 CAD acres', confidence: 'confirmed' as const, source: 'USGS 3DEP 10 m terrain screen; aggregate basis, not a contiguity finding' };
        if (it.field === 'Wetlands (NWI)') return { ...it, value: '10.97 ac whole block (0.6%); center-section representative parcel 0.7 ac', source: 'USFWS NWI desktop screen; field delineation and USACE jurisdiction remain open' };
        if (it.field === 'Fiber coverage') return { ...it, field: 'Enterprise carrier routes', value: 'FiberLight ~3.1 mi · Zayo ~6.3 mi from block boundary', confidence: 'preliminary' as const, source: 'Mapped carrier routes; serviceability, capacity, construction scope and SLA unconfirmed' };
        if (it.field === 'Fiber type & latency') return { ...it, value: 'Lit/dark service, capacity, latency and physical diversity unconfirmed', confidence: 'pending' as const, source: 'Direct FiberLight, Zayo and other enterprise-carrier inquiry required' };
        if (it.field === 'Water access') return { ...it, value: 'Carrizo Aquifer · Wintergarden GCD permit context; seller-reported on-site wells unverified', confidence: 'pending' as const, source: 'Well logs, yield, condition, rights and water quality required; nearby 1.4-mi TDS sample addresses quality, not supply' };
        if (it.field === 'Well feasibility') return { ...it, value: 'Nearest stored TWDB SDR well 107.47 mi', confidence: 'pending' as const, source: 'Limited stored-dataset coverage, not a groundwater-scarcity finding; verify seller-reported on-site wells' };
        if (it.field === 'Flood zone') return { ...it, value: 'TWDB 1% planning screen: 453 of 1,957 mapped ac (23%)', confidence: 'preliminary' as const, source: 'TWDB State Flood Plan existing-conditions layer; non-regulatory, not FEMA NFHL' };
        if (it.field === 'Air permit pathway') return { ...it, value: 'EGU Standard Permit screen · 160 MW · ~98 TPY NOx', confidence: 'assumed' as const, source: '160 MW × 0.14 lb/MWh permit limit × 8,760 ÷ 2,000; not equipment forecast or eligibility assurance; controls, aggregation and other pollutants remain' };
        if (it.field === 'Firm transport') return { ...it, source: 'Intrastate transmission; firm terms unconfirmed' };
        return it;
      });
      if (!mapped.some(it => it.field === 'Flood zone')) mapped.push({ field: 'Flood zone', value: 'TWDB 1% planning screen: 453 of 1,957 mapped ac (23%)', confidence: 'preliminary', source: 'TWDB State Flood Plan existing-conditions layer; non-regulatory, not FEMA NFHL' });
      if (!mapped.some(it => it.field === 'Wildfire hazard')) mapped.push({ field: 'Wildfire hazard', value: 'All three sections below statewide mean on screened indicators', confidence: 'preliminary', source: 'Texas A&M Forest Service TxWRAP desktop screen; confirm vegetation, fire-water and emergency access' });
      return mapped;
    }
    // Any other briefing with a slope file states the 3–5 m range and its basis.
    const agg = data.carveOutAggregate;
    if (agg?.slopeGrid5m && agg.weightedAvgSlopePct != null) {
      return items.map(it => it.field === 'Avg slope'
        ? { ...it, value: `${meanOr(Number(agg.weightedAvgSlopePct), agg.slopeGrid5m!.avgSlopePct)} (3–5 m grids, assembly)`, source: 'USGS 3DEP 1 m DEM, averaged to 3 m and 5 m grids' }
        : it);
    }
    return items;
  }, [parcel, pipelines, data.carveOuts, data.carveOutAggregate, isA1CarveOut, isMeiners, isUvalde]);
  const featured = useMemo(() => findFeaturedPipeline(parcel, pipelines, data.carveOutAggregate?.slug), [parcel, pipelines, data.carveOutAggregate?.slug]);
  const featuredCapacity = featured ? pipelineCapacityDisplay(featured).capacityMmcf ?? undefined : undefined;
  // A1's gas panel carries its labeled scenario; its hero has no derived MW claim.
  const permitPathway = useMemo(
    () => computePermitPathway(parcel, featuredCapacity, isA1CarveOut ? { crownpineA1: true } : undefined),
    [parcel, featuredCapacity, isA1CarveOut]
  );

  // DD checklist data — computed once, used in both modes
  const ddCategories = useMemo(() => buildUnifiedDDChecklist(data, parcel, permitPathway, false, activeLayoutRow), [data, parcel, permitPathway, activeLayoutRow]);
  const ddAllItems = useMemo(() => ddCategories.flatMap(c => c.items), [ddCategories]);
  const ddAddressedCount = ddAllItems.filter(d => d.status === 'ADDRESSED').length;
  const ddFlaggedCount = ddAllItems.filter(d => d.status === 'FLAGGED').length;
  const ddInquiryCount = ddAllItems.filter(d => d.status === 'REQUIRES INQUIRY').length;

  const activeListing = useMemo(() => {
    if (!listings || listings.length === 0) return null;
    const onSite = listings.find(l => Number(l.distance_miles) < 0.1);
    const nearby = onSite ? null : listings.find(l => Number(l.distance_miles) < 1);
    const listing = onSite || nearby;
    if (!listing) return null;
    return {
      listing,
      isOnSite: !!onSite,
      priceStr: listing.price ? `$${Number(listing.price).toLocaleString()}` : null,
      acresStr: listing.acreage ? `${Number(listing.acreage).toLocaleString()} acres` : null,
      pricePerAcre: listing.price && listing.acreage && Number(listing.acreage) > 0
        ? `$${Math.round(Number(listing.price) / Number(listing.acreage)).toLocaleString()}/acre`
        : null,
      url: listing.listing_url || listing.landwatch_url,
    };
  }, [listings]);

  const resolveItems = useMemo(() => {
    if (isUvalde) return [
      { what: 'Confirm gas delivery and project scale', why: 'Obtain WTG confirmation of a delivery point, pressure, firm volume, lateral route and cost. The 19.3 MW model does not establish site capacity.', cost: 'Operator quote required', timeline: 'Unconfirmed' },
      { what: 'Define the controlled development footprint', why: 'Resolve offered-tract title, airport restrictions and any north-tract agreement before fixing equipment locations or building heights.', cost: '', timeline: '' },
    ];
    if (isMeiners) return [
      { what: 'Secure a protected development footprint', why: 'Resolve both operators’ surface-use terms, mineral and gathering rights, existing facilities and future drilling. Failure to secure a contiguous footprint can end the project.', cost: '', timeline: '' },
      { what: 'Confirm gas delivery by lateral', why: 'Camino Real is 5.3 mi from the west boundary. Obtain operator confirmation of route, tap, pressure, volume and firm transport before pricing the project.', cost: 'Not estimated', timeline: 'Operator inquiry' },
    ];
    const items = computeResolveItems(parcel, pipelines, featured, data.powerInfra?.tduName ? fmtTdu(data.powerInfra.tduName) : null, !!data.gasSupplyZone, data.carveOutAggregate?.slug === 'crownpine-blk-194-a1');
    // Crown Pine A1: TWDB records show productive wells within ~0.3–1 mi (an 820 ft
    // Upper Yegua public-supply well ~0.3 mi off the SE line), so an on-site supply
    // well is a proven option — the "Water sourcing verification" pre-LOI gate (which
    // assumes "no productive wells within 2 mi") does not apply here.
    if (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1') {
      return items.filter(i => i.what !== 'Water sourcing verification');
    }
    return items;
  }, [isMeiners, isUvalde, parcel, pipelines, featured, data.powerInfra?.tduName, data.carveOutAggregate?.slug, data.gasSupplyZone]);

  const keyNumbers = useMemo((): KeyNumberItem[] => {
    const items: KeyNumberItem[] = [];
    // 1. Gas capacity context stays in native units and remains scoped to the
    // operator system or project that published it. It is not site deliverability.
    const aoiCapacityBcfd = data.gasSupplyZone?.peakBcfd != null
      ? Number(data.gasSupplyZone.peakBcfd)
      : null;
    const useAoi = aoiCapacityBcfd != null;
    if (isA1CarveOut && !useAoi) {
      items.push({ value: '30\u2033 \u00b7 on-site band', label: 'Gas Access', note: 'ET Texoma crosses the western band ~0.52 mi \u2014 gas availability and supply confirmed; firm transport and tap engineering remaining' });
    } else if (isMeiners) {
      items.push({ value: '5.28 mi', label: 'Camino Real · 12.75″', note: 'WTG Bexar Transmission · full block boundary · total capacity and site delivery unconfirmed' });
    } else if (useAoi) {
      items.push({
        value: `${aoiCapacityBcfd!.toLocaleString(undefined, { maximumFractionDigits: 2 })} Bcf/d`,
        label: 'Operator supply forecast',
        note: `${data.gasSupplyZone!.operatorName} system context; site delivery and firm transport unconfirmed`,
      });
    } else {
      items.push({ value: '\u2014', label: 'Pipeline capacity' });
    }
    // 2. Acreage \u2014 assembly-aware for carve-out parcels (4-parcel Paces block,
    // multi-parcel Crown Pine subdivisions, etc.). Falls back to individual
    // parcel acreage for non-carve-out parcels.
    const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
    const useAssemblyAcres = aggAcres != null && aggAcres > 0;
    const acreageValue = useAssemblyAcres ? aggAcres : (parcel.acreage ? Number(parcel.acreage) : null);
    const acreageLabel = useAssemblyAcres ? 'Assembly Acreage' : 'Site Acreage';
    const acreageNote = useAssemblyAcres
      ? `${data.carveOutAggregate!.parcelCount} parcels \u2014 ${(parcel as any).structured_intel?.acreage_owner_note || 'single owner'}`
      : undefined;
    // A1: the site figure is the premises (analyzed core + western band), not the
    // core alone. carveOutAggregate keeps the core \u2014 it is the grade input \u2014 so the
    // headline is served from `premises` and the split is spelled out beneath it.
    items.push(activeLayoutRow ? {
      // A1 Alt with an alternative on the map: the hero follows its stated
      // acreage basis. Alt3 keeps its mapped calculation acreage separate.
      value: `${formatLayoutAcres(activeLayoutRow)} ac`,
      // Never "Site Acreage": canon §3 reserves that phrase for the premises.
      label: 'Proposed Layout',
      note: activeLayoutRow.measured_acres != null
        ? `${activeLayoutRow.name} on the map · Crown Pine tract acreage from supplied LRM inventory · spatial screens use the ${activeLayoutRow.measured_acres.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}-acre fitted polygon · ${Math.round(activeLayoutRow.upland_ac).toLocaleString()} ac upland · original premises ${data.premises ? data.premises.total_acres.toLocaleString() : '3,037.93'} ac`
        : `${activeLayoutRow.name} on the map — ${activeLayoutRow.proposer ?? 'seller'} outline, measured · ${Math.round(activeLayoutRow.upland_ac).toLocaleString()} ac upland · original premises ${data.premises ? data.premises.total_acres.toLocaleString() : '3,037.93'} ac`,
    } : data.premises ? {
      value: `${data.premises.total_acres.toLocaleString()} ac`,
      label: 'Site Acreage',
      note: `${data.premises.core_acres.toLocaleString()}-ac analyzed core + ${data.premises.band_acres.toLocaleString()}-ac western band (parcel 21865, in part)${
        // Point at the seller alternatives when they are served (A1 only) — the
        // full comparison table lives under Land & Ownership.
        data.siteLayouts && data.siteLayouts.rows.length > 1
          ? ` · ${data.siteLayouts.rows.length - 1} proposed alternative layouts (${data.siteLayouts.rows.slice(1).map(r => Math.round(r.acres).toLocaleString()).join(' / ')} ac) — compared above`
          : ''}`,
    } : {
      value: acreageValue != null ? `${Number(acreageValue).toLocaleString()} ac` : '\u2014',
      label: acreageLabel,
      // Curator-supplied sub-line (structured_intel.acreage_note) \u2014 e.g. Cinco Frio's
      // optional Boeck add-on. Absent the key, unchanged.
      note: acreageNote ?? (parcel as any).structured_intel?.acreage_note,
    });
    // 3. Permit Path
    items.push({ value: permitPathway.pathway || '\u2014', label: parcel.state === 'TX' ? 'TCEQ Permit Path' : 'Air Permit Path', note: isUvalde ? 'Illustrative screening example; equipment and scale unconfirmed' : permitPathway.pathway === 'Standard' ? `Phase 1\u20132 at ${BTM_CALIBRATION.TRANCHE_MW} MW/phase` : undefined });
    // 4. Price/Acre — omitted entirely on the partner edition, and on A1. The
    // county assessment is a tax basis, not the transaction price: the A1
    // premises are under negotiation at a figure well above it, so a headline
    // $/ac tile reads as an asking price and anchors the buyer on the wrong
    // number. An empty tile still announces a withheld price, so the tile is
    // dropped rather than blanked. No assessed figure survives anywhere on A1 —
    // the Acquisition card is reframed as Land Control and carries none either.
    if (isRedacted || isA1CarveOut) {
      // no price tile
    } else if (activeListing?.pricePerAcre) {
      // For carve-out parcels with an on-site listing covering the whole assembly,
      // surface that the price reflects an active sale, not a tax assessment.
      const listingNote = activeListing.isOnSite
        ? (useAssemblyAcres ? 'Currently listed via Land.com' : 'Currently listed')
        : undefined;
      items.push({ value: activeListing.pricePerAcre, label: 'Price / Acre', note: listingNote });
    } else if (useAssemblyAcres && data.carveOutAggregate!.totalLandValue && aggAcres) {
      // Assembly assessed-value fallback: SUM of the 4 parcels' land_values divided by the assembly acreage.
      items.push({
        value: `$${Math.round(Number(data.carveOutAggregate!.totalLandValue) / aggAcres).toLocaleString()}`,
        label: 'Assessed / Acre',
        note: 'Tax assessed \u2014 assembly total',
      });
    } else if (parcel.land_value && parcel.acreage && Number(parcel.acreage) > 0) {
      items.push({ value: `$${Math.round(Number(parcel.land_value) / Number(parcel.acreage)).toLocaleString()}`, label: 'Assessed / Acre', note: 'Tax assessed \u2014 not market' });
    } else {
      items.push({ value: '\u2014', label: 'Price / Acre' });
    }
    return items;
  }, [featured, parcel, permitPathway, activeListing, data.gasSupplyZone, data.carveOutAggregate, isRedacted, activeLayoutRow, isMeiners]);

  const { setHoverFeature, setFootprintScreen, isFullView, setFullView, briefingMode, setBriefingMode, navigatedFromChecklist, scrollToSection } = useScrollSync();

  const headerRef = useSectionRef('header');
  const killSheetRef = useSectionRef('kill-sheet');
  const gasInfraRef = useSectionRef('gas-infrastructure');
  const permittingRef = useSectionRef('permitting');
  const physicalSiteRef = useSectionRef('physical-site');
  const oilGasWellsRef = useSectionRef('oil-gas-wells');
  const landRef = useSectionRef('land-ownership');
  const infraRef = useSectionRef('infrastructure');
  const powerInfraRef = useSectionRef('power-infrastructure');
  const waterSourcingRef = useSectionRef('water-sourcing');
  const fiberConnectivityRef = useSectionRef('fiber-connectivity');
  const mineHazardsRef = useSectionRef('mine-hazards');
  const riskRef = useSectionRef('risk-register');
  const confRef = useSectionRef('data-confidence');

  const freshnessLabel = freshness.status === 'current' ? 'Data current' : freshness.status === 'aging' ? 'Data aging' : 'Data stale';

  return (
    <div style={{ fontSize: '11px', lineHeight: 1.5 }}>
      <style>{`@media print { .briefing-no-print { display: none !important; } .briefing-print-only { display: block !important; } .briefing-evidence-body { display: block !important; } }`}</style>

      {/* ── Cover Page (print only) — mirrors the briefing rhythm: header band, map, traits, KPI strip, verdict, gates strap ── */}
      <div className="briefing-cover" style={{ display: 'none', flexDirection: 'column' }}>
        {/* Header band — same visual language as the live briefing header */}
        <div style={{ background: REPORT.heading, padding: '22px 26px 20px', borderRadius: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '12px' }}>
            <img src="/atwater-logo-light.svg" alt="Atwater Energy" style={{ height: 24, width: 'auto', display: 'block' }} />
            {showIntegraCobrand && (
              <>
                <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15, lineHeight: 1, color: REPORT.patina, opacity: 0.75 }}>×</span>
                <img src="/integra-logo-light.webp" alt="Integra" style={{ height: 13, width: 'auto', display: 'block', opacity: 0.95 }} />
              </>
            )}
          </div>
          <div style={{ fontSize: '34px', fontWeight: 400, color: REPORT.white, fontFamily: 'Georgia, serif', lineHeight: 1.15 }}>
            {parcel.county}, {parcel.state}
          </div>
          <div style={{ fontSize: '11px', color: REPORT.patina, marginTop: '6px', letterSpacing: '0.03em' }}>
            {(() => {
              const traits: string[] = [];
              if (isA1CarveOut || isMeiners) traits.push('BTM gas-to-wire');
              else if (pipelines && pipelines.length > 0) traits.push('BTM candidate');
              const ixpDist = parcel.nearest_ixp_miles != null ? Number(parcel.nearest_ixp_miles) : null;
              if (ixpDist != null && ixpDist > 20) traits.push('AI/HPC training (latency-insensitive)');
              else if (ixpDist != null && ixpDist <= 20) traits.push('General compute');
              traits.push('Air-cooled (zero evaporative makeup)');
              const ac = parcel.acreage ? Number(parcel.acreage) : 0;
              if (ac >= 500 && !(parcel as any).structured_intel?.suppress_phase_trait) traits.push(`Phased ${BTM_CALIBRATION.TRANCHE_MW} MW+`);
              return traits.join(' · ');
            })()}
          </div>
          <div style={{ fontSize: '10px', color: REPORT.secondary, marginTop: '6px', fontFamily: 'monospace', letterSpacing: '0.02em' }}>
            {parcel.parcel_id_local || parcel.id}
            {parcel.lat != null && parcel.lng != null && (
              <> &middot; {Number(parcel.lat).toFixed(4)}, {Number(parcel.lng).toFixed(4)}</>
            )}
            &nbsp;&middot; {today}
          </div>
        </div>

        {/* Map block — satellite if captured, branded fallback otherwise */}
        {mapSnapshots?.cover ? (
          <img
            src={mapSnapshots.cover}
            alt="Site satellite overview"
            style={{ width: '100%', height: '3.2in', objectFit: 'cover', marginTop: '12px', borderRadius: '4px', border: `1px solid ${REPORT.rule}` }}
          />
        ) : (
          <div style={{
            width: '100%', height: '3.2in', marginTop: '12px', borderRadius: '4px',
            background: `linear-gradient(135deg, ${REPORT.highlight} 0%, ${REPORT.secondary} 100%)`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: REPORT.patina, fontFamily: 'monospace',
            border: `1px solid ${REPORT.rule}`,
          }}>
            <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase' as const, marginBottom: '10px' }}>
              Site location
            </div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '28px', color: REPORT.white, letterSpacing: '-0.01em' }}>
              {parcel.county}, {parcel.state}
            </div>
            {parcel.lat != null && parcel.lng != null && (
              <div style={{ fontSize: '10px', color: REPORT.patina, marginTop: '8px', letterSpacing: '0.08em' }}>
                {Number(parcel.lat).toFixed(4)}, {Number(parcel.lng).toFixed(4)}
              </div>
            )}
            <div style={{ fontSize: '8px', color: REPORT.patina, marginTop: '14px', letterSpacing: '0.14em', textTransform: 'uppercase' as const, opacity: 0.7 }}>
              Satellite overview available via in-app print
            </div>
          </div>
        )}

        {/* Key Numbers — identical to briefing page 2 */}
        <div style={{ marginTop: '14px' }}>
          <KeyNumbers items={keyNumbers} />
        </div>

        {/* Verdict — exec summary */}
        <div style={{
          fontSize: '11.5px', color: REPORT.body, lineHeight: 1.65,
          marginTop: '4px',
        }}>
          {verdict}
        </div>

        {/* Gates strap — all gates pass banner + Prepared / Diligence */}
        <div style={{
          marginTop: '16px', padding: '10px 14px',
          border: `1px solid ${REPORT.rule}`, borderRadius: '4px',
          display: 'flex', alignItems: 'center', gap: '24px', fontSize: '11px',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '8px', color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'monospace', marginBottom: '2px' }}>Gates</div>
            <div style={{ fontWeight: 700, color: isMeiners ? REPORT.caution : gates.hardGatePass ? REPORT.pass : REPORT.fail, fontSize: '13px' }}>
              {meinersOverrides ? meinersOverrides.gateSummary : isUvalde ? 'Desktop screen · key inquiries open' : gates.hardGatePass ? (isA1CarveOut ? '✓ 6 screening gates pass' : '✓ All gates pass') : '✗ Failure detected'}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '8px', color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'monospace', marginBottom: '2px' }}>Diligence</div>
            <div style={{ fontWeight: 600, color: REPORT.heading, fontSize: '12px' }}>
              {ddAddressedCount} of {ddAllItems.length} addressed
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '8px', color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'monospace', marginBottom: '2px' }}>Freshness</div>
            <div style={{ fontWeight: 600, color: REPORT.heading, fontSize: '12px' }}>
              {freshnessLabel}
            </div>
          </div>
        </div>

        {/* Confidential strap */}
        <div style={{
          marginTop: '12px', paddingTop: '10px',
          borderTop: `1px solid ${REPORT.rule}`,
          fontSize: '9px', color: REPORT.tertiary, letterSpacing: '0.04em',
          textAlign: 'center',
        }}>
          Pre-feasibility screening &middot; Confidential &middot; {parcel.state === 'TX' ? 'TX RRC, ' : ''}EIA, {isMeiners ? 'TWDB non-regulatory 1% planning screen' : 'FEMA'}, FCC, EPA, HIFLD, FERC, PHMSA
        </div>
      </div>

      {/* ── Header ── */}
      <div ref={headerRef} style={{ background: REPORT.heading, padding: '20px 28px 16px', marginBottom: '3px', position: 'relative' }}>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/atwater-logo-light.svg" alt="Atwater Energy" style={{ height: 24, width: 'auto', display: 'block' }} />
            {showIntegraCobrand && (
              <>
                <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15, lineHeight: 1, color: REPORT.patina, opacity: 0.75 }}>×</span>
                <img src="/integra-logo-light.webp" alt="Integra" style={{ height: 13, width: 'auto', display: 'block', opacity: 0.95 }} />
              </>
            )}
          </div>
          {/* Toggle stack — absolute so it doesn't push the title down */}
          <div className="briefing-no-print" style={{ position: 'absolute', top: 20, right: 28, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            {/* BTM | Grid lens — in-app only (onLensChange is undefined on token views) */}
            {onLensChange && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '9px', color: REPORT.secondary, fontFamily: 'monospace', letterSpacing: '0.1em', textTransform: 'uppercase', marginRight: 10 }}>Lens</span>
                <span
                  onClick={() => lensProfile !== 'btm' && onLensChange('btm')}
                  style={{
                    fontSize: '11px', cursor: 'pointer', padding: '2px 0', letterSpacing: '0.03em',
                    color: lensProfile === 'btm' ? REPORT.patina : REPORT.secondary,
                    fontWeight: lensProfile === 'btm' ? 600 : 400,
                  }}
                >BTM</span>
                <div style={{ width: 1, height: 12, background: REPORT.secondary, opacity: 0.4, margin: '0 10px' }} />
                <span
                  onClick={() => lensProfile !== 'grid' && onLensChange('grid')}
                  style={{
                    fontSize: '11px', cursor: 'pointer', padding: '2px 0', letterSpacing: '0.03em',
                    color: lensProfile === 'grid' ? REPORT.patina : REPORT.secondary,
                    fontWeight: lensProfile === 'grid' ? 600 : 400,
                  }}
                >Grid</span>
              </div>
            )}
            {/* Brief | Checklist — plain text toggle (Rams); hidden under the
                grid lens (no grid DD checklist in v1) */}
            {!isGrid && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span
                onClick={() => setBriefingMode('brief')}
                style={{
                  fontSize: '11px', cursor: 'pointer', padding: '2px 0', letterSpacing: '0.03em',
                  color: briefingMode === 'brief' ? REPORT.patina : REPORT.secondary,
                  fontWeight: briefingMode === 'brief' ? 600 : 400,
                }}
              >Brief</span>
              <div style={{ width: 1, height: 12, background: REPORT.secondary, opacity: 0.4, margin: '0 10px' }} />
              <span
                onClick={() => setBriefingMode('checklist')}
                style={{
                  fontSize: '11px', cursor: 'pointer', padding: '2px 0', letterSpacing: '0.03em',
                  color: briefingMode === 'checklist' ? REPORT.patina : REPORT.secondary,
                  fontWeight: briefingMode === 'checklist' ? 600 : 400,
                }}
              >Checklist</span>
            </div>
            )}
            {/* Guided / Full — hidden on mobile */}
            <div className="briefing-view-toggle" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '10px', color: isFullView ? REPORT.secondary : REPORT.patina, fontWeight: isFullView ? 400 : 600, letterSpacing: '0.03em' }}>
                Guided
              </span>
              <div
                onClick={() => setFullView(!isFullView)}
                style={{
                  width: 36, height: 18, borderRadius: 9, cursor: 'pointer',
                  background: isFullView ? REPORT.accent : 'rgba(255,255,255,0.25)',
                  position: 'relative', transition: 'background 0.2s',
                }}
              >
                <div style={{
                  width: 14, height: 14, borderRadius: '50%', background: 'white',
                  position: 'absolute', top: 2,
                  left: isFullView ? 20 : 2,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }} />
              </div>
              <span style={{ fontSize: '10px', color: isFullView ? REPORT.accent : REPORT.secondary, fontWeight: isFullView ? 600 : 400, letterSpacing: '0.03em' }}>
                Full
              </span>
            </div>
            {/* Memo View — distinct pill that signals "different view", not another mode.
                Hidden under the grid lens (grid is a v1 surface only), and hidden
                on the partner-redacted edition: that reader gets the analytical
                brief with the map, never the investor memo. Presentation policy —
                the payload is already redacted either way. */}
            {parcelId && !isGrid && !isRedacted && !(parcel as any).structured_intel?.suppress_memo_view && (
              <a
                href={(() => {
                  // Carry the active carve-out into the memo view — without it a
                  // carve-out briefing (e.g. Crown Pine A1) silently reverts to the
                  // full-assembly default. No-op when no carveOut param is present.
                  const q = new URLSearchParams();
                  if (token) q.set('token', token);
                  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
                  const activeCarveOut = search ? search.get('carveOut') : null;
                  if (activeCarveOut) q.set('carveOut', activeCarveOut);
                  // Same for the seller-alternatives edition flag (A1 Alt link).
                  if (search && search.get('layouts') === 'alt') q.set('layouts', 'alt');
                  const qs = q.toString();
                  return `/briefing/${parcelId}/v2${qs ? `?${qs}` : ''}`;
                })()}
                title="Switch to the investor-facing memo"
                className="briefing-memo-pill"
                style={{
                  fontSize: '10px', letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: REPORT.accent, textDecoration: 'none',
                  border: `1px solid ${REPORT.accent}`, borderRadius: 3,
                  padding: '4px 10px', background: 'rgba(196,112,90,0.08)',
                  fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                Memo View <span className="briefing-memo-arrow" style={{ display: 'inline-block', transition: 'transform 0.15s' }}>&rarr;</span>
              </a>
            )}
          </div>
        </div>
        <div style={{ fontSize: '22px', fontWeight: 400, color: REPORT.white, fontFamily: 'Georgia, serif', lineHeight: 1.2 }}>
          {parcel.county}, {parcel.state}
        </div>
        <div style={{ fontSize: '10px', color: REPORT.patina, marginTop: '4px', letterSpacing: '0.03em' }}>
          {(() => {
            // Grid lens header traits — composed from the payload, zero
            // geographic hardcodes (a second node renders the same way).
            if (isGrid) {
              const gtraits: string[] = ['Grid interconnection candidate'];
              const nodeName = data.gridNodeIntel?.display_name || data.gridNodeIntel?.node_name;
              if (nodeName) gtraits.push(String(nodeName).replace(/\s*\(.*\)\s*$/, ''));
              if (gridScore?.anchor?.kv != null) gtraits.push(`${gridScore.anchor.kv} kV anchor`);
              if (parcel.acreage != null && Number(parcel.acreage) >= 100) gtraits.push('Phased 100 MW+');
              return gtraits.join(' · ');
            }
            const traits: string[] = [];
            // Power model
            if (isA1CarveOut || isMeiners) traits.push('BTM gas-to-wire');
            else if (pipelines && pipelines.length > 0) traits.push('BTM candidate');
            // Workload fit
            const ixpDist = parcel.nearest_ixp_miles != null ? Number(parcel.nearest_ixp_miles) : null;
            if (ixpDist != null && ixpDist > 20) traits.push('AI/HPC training (latency-insensitive)');
            else if (ixpDist != null && ixpDist <= 20) traits.push('General compute');
            // Cooling
            traits.push('Air-cooled (zero evaporative makeup)');
            // Scale
            const ac = parcel.acreage ? Number(parcel.acreage) : 0;
            if (ac >= 500 && !(parcel as any).structured_intel?.suppress_phase_trait) traits.push(`Phased ${BTM_CALIBRATION.TRANCHE_MW} MW+`);
            return traits.join(' \u00b7 ');
          })()}
        </div>
        <div style={{ fontSize: '11px', color: REPORT.secondary, marginTop: '6px' }}>
          {parcel.parcel_id_local || parcel.id}
          {parcel.lat != null && parcel.lng != null && (
            <> &middot; {Number(parcel.lat).toFixed(4)}, {Number(parcel.lng).toFixed(4)}</>
          )}
          &nbsp;&middot; {today}
        </div>
      </div>

      {/* ── MODE: Brief or Checklist (checklist is BTM-only in v1) ── */}
      {briefingMode === 'checklist' && !isGrid ? (
        <>
          <ChecklistView
            categories={ddCategories}
            addressedCount={ddAddressedCount}
            flaggedCount={ddFlaggedCount}
            inquiryCount={ddInquiryCount}
            siteLabel={`${parcel.parcel_id_local || parcel.id} \u00b7 ${parcel.county}, ${parcel.state} \u00b7 ${today}`}
            onNavigate={scrollToSection}
          />
          <div style={{
            background: REPORT.heading, padding: '16px 28px', marginTop: '3px',
            fontSize: '9px', textAlign: 'center',
          }}>
            <div style={{ letterSpacing: '0.08em', fontFamily: 'monospace', color: REPORT.secondary }}>Atwater Energy &middot; {today} &middot; Confidential</div>
            <div style={{ marginTop: '3px', color: REPORT.tertiary }}>Pre-feasibility screening only &middot; {parcel.state === 'TX' ? 'TX RRC, ' : ''}EIA, {isMeiners ? 'TWDB non-regulatory 1% planning screen' : 'FEMA'}, FCC, EPA, HIFLD, FERC, PHMSA &middot; Atwater overlays</div>
          </div>
        </>
      ) : (
      <>
      {/* Back to Checklist — floating centered pill */}
      {navigatedFromChecklist && (
        <div className="briefing-no-print" style={{
          position: 'sticky', top: 12, zIndex: 20,
          display: 'flex', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <span
            onClick={() => setBriefingMode('checklist')}
            style={{
              pointerEvents: 'auto', cursor: 'pointer',
              fontSize: '10px', fontWeight: 500, color: REPORT.patina,
              background: REPORT.heading, padding: '6px 16px', borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)', letterSpacing: '0.02em',
            }}
          >
            &larr; Back to Checklist
          </span>
        </div>
      )}
      {/* ── Kill Sheet — key numbers + gates + dimensions + resolve ── */}
      <div ref={killSheetRef} className="kill-sheet" style={{ padding: '16px 28px' }}>

        {/* Data coverage disclosure — for states outside our primary ingest scope */}
        {!isGrid && <CoverageBanner state={parcel.state} hasGrades={!!grades?.fuel_delivery} />}

        {/* Operator gas-supply zone callout — only renders for parcels inside an AOI
            with a dedicated commitment (e.g. Revenant East TX). Reframes the gas
            story upstream of the gas-section, where pipeline_available_mw becomes
            background info rather than the headline number. */}
        {!isGrid && data.gasSupplyZone && <OperatorGasZoneCallout zone={data.gasSupplyZone} />}

        {/* Candidate carve-out — when this parcel is a member of a curated 2-5k ac
            sub-cluster polygon inside an operator AOI. Renders one panel per
            carve-out the parcel belongs to (typically zero or one). Suppressed
            for Paces because the masthead + Site Intelligence box already cover
            the assembly framing. */}
        {!isGrid && !isPaces && data.carveOutAggregate?.slug !== 'crownpine-blk-194-a1' && (data.carveOuts || []).map(co => (
          <CandidateCarveOutPanel
            key={co.id}
            carveOut={co}
            representativeParcelLabel={
              co.representativeParcelId === parcel.id
                ? 'this parcel (representative)'
                : null
            }
          />
        ))}

        {/* A1 Alt: when the map shows an alternative, say so above the
            narrative — the narrative and every grade describe the original. */}
        {activeLayoutRow && (
          <div className="no-break" style={{ margin: '0 0 10px', padding: '8px 12px', background: REPORT.amberWash, border: `1px solid ${REPORT.border}`, borderRadius: 4, fontSize: '10.5px', color: REPORT.body, lineHeight: 1.55 }}>
            <strong style={{ color: REPORT.heading }}>Map shows {activeLayoutRow.name}</strong> — {formatLayoutAcres(activeLayoutRow)} ac{activeLayoutRow.measured_acres != null ? ` Crown Pine tract acreage from the supplied LRM inventory, pending title examination and any elected survey; spatial screens use the ${activeLayoutRow.measured_acres.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}-acre fitted polygon without scaling` : ` (${activeLayoutRow.proposer ?? 'seller'} outline, measured)`} · {Math.round(activeLayoutRow.upland_ac).toLocaleString()} ac upland outside the 100-yr floodplain and NWI wetlands{activeLayoutRow.slope_under5_upland_ac != null ? ` · ${acOr(activeLayoutRow.slope_under5_upland_ac, activeLayoutRow.slope_under5_upland_ac_5m)} ac under 5% slope${activeLayoutTerrainReference ? ' in the earlier Alt3 terrain screen' : ' (3–5 m grids)'}` : ''} · gas line crossing ~{activeLayoutRow.texoma_crossing_mi.toFixed(2)} mi · {activeLayoutRow.parcels_note}. {activeLayoutTerrainReference ? 'Flood and water lines follow the updated outline; terrain figures retain the earlier Alternative #3 screening as a reference. Grades, gate verdicts and the remaining Key Numbers still describe' : activeLayoutTerrainWithheld ? 'Selected-outline slope and elevation measurements are withheld because the retained raster is qualified only for local prototype use; flood and water lines follow this outline, while grades, gate verdicts and the remaining Key Numbers still describe' : activeLayoutNarrative?.sections ? 'The Site Intelligence narrative and the flood, slope and elevation lines below follow this outline; grades, gate verdicts and the remaining Key Numbers still describe' : 'The flood, slope and elevation lines below follow this outline; the narrative, grades and gate verdicts still describe'} the original {data.premises ? data.premises.total_acres.toLocaleString() : '3,037.93'}-acre premises and its {data.premises ? data.premises.core_acres.toLocaleString() : '2,443.62'}-ac analyzed core.
          </div>
        )}
        {/* Seller-proposed alternative layouts (Crown Pine A1 only — served as
            `siteLayouts` on that payload). Sits at the top of the page, above Site
            Intelligence, so the comparison is the first thing the reader sees
            whichever outline the map's SITE LAYOUT switcher is showing.
            Presentation only: every grade and gate elsewhere on this page stays
            computed on the original analyzed core; selected terrain follows its
            source-qualification gate. Names no
            party and no price; the server withholds it from the partner edition
            anyway (canon §4). */}
        {data.siteLayouts && data.siteLayouts.rows.length > 0 && (() => {
          const rows = data.siteLayouts.rows;
          const hasSlope = rows.some(r => r.slope_under5_upland_ac != null);
          const ac = (v: number | null | undefined) => v == null ? '—' : Math.round(v).toLocaleString();
          const num = { padding: '3px 4px', textAlign: 'right' as const, color: REPORT.body, whiteSpace: 'nowrap' as const };
          return (
            <div className="no-break" style={{ margin: '0 0 10px', padding: '10px 12px', background: REPORT.white, border: `1px solid ${REPORT.border}`, borderRadius: '4px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: REPORT.heading, marginBottom: '2px' }}>Site Layout Alternatives</div>
              <div style={{ fontSize: '10.5px', color: REPORT.secondary, lineHeight: 1.5, marginBottom: '6px' }}>
                Two seller-proposed outlines received September 2, 2026, and Alternative #3 proposed in response on September 3 are compared with the original premises. Alternative #3 now uses the Crown Pine tract acreage from the supplied LRM inventory as its headline; mapped screens remain on the fitted polygon. These are planning references pending title examination and any elected survey.
              </div>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${REPORT.rule}` }}>
                    <th style={{ ...TH, fontSize: '10px', textAlign: 'left' }}>Layout</th>
                    <th style={{ ...TH, fontSize: '10px', textAlign: 'right' }}>Acreage basis</th>
                    <th style={{ ...TH, fontSize: '10px', textAlign: 'right' }}>100-yr floodplain</th>
                    <th style={{ ...TH, fontSize: '10px', textAlign: 'right' }}>NWI wetlands</th>
                    <th style={{ ...TH, fontSize: '10px', textAlign: 'right' }}>Upland</th>
                    {hasSlope && <th style={{ ...TH, fontSize: '10px', textAlign: 'right' }}>Upland under 5% slope</th>}
                    <th style={{ ...TH, fontSize: '10px', textAlign: 'right' }}>Gas line crossing</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key} style={{ borderBottom: `1px solid ${REPORT.rule}80`, background: r.key === siteLayout ? REPORT.amberWash : undefined }}>
                      <td style={{ padding: '3px 4px', color: REPORT.heading, fontWeight: r.key === 'original' ? 600 : 500 }}>
                        {r.name}{r.key === siteLayout ? <span style={{ marginLeft: 6, fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em', color: REPORT.accent }}>ON MAP</span> : null}
                        <div style={{ fontSize: '9.5px', color: REPORT.tertiary, fontWeight: 400, lineHeight: 1.4 }}>
                          {r.parcels_note}{r.key !== 'original' ? ` · +${ac(r.added_ac)} ac added / −${ac(r.dropped_ac)} ac dropped vs original` : ''}
                        </div>
                      </td>
                      <td style={num}>{r.acres.toLocaleString(undefined, { minimumFractionDigits: r.key === 'original' || r.measured_acres != null ? 2 : 0, maximumFractionDigits: r.key === 'original' || r.measured_acres != null ? 2 : 0 })}</td>
                      <td style={num}>{ac(r.flood_100yr_ac)}</td>
                      <td style={num}>{ac(r.nwi_wetland_ac)}</td>
                      <td style={{ ...num, fontWeight: 600, color: REPORT.heading }}>{ac(r.upland_ac)}</td>
                      {hasSlope && <td style={num}>{r.slope_under5_upland_ac == null ? '—' : acOr(r.slope_under5_upland_ac, r.slope_under5_upland_ac_5m)}</td>}
                      <td style={num}>~{r.texoma_crossing_mi.toFixed(2)} mi</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: '9.5px', color: REPORT.tertiary, lineHeight: 1.5, marginTop: '5px' }}>
                Acres. {data.siteLayouts.basis} The original premises acreage is the sum of parcels of record ({rows[0]?.acres_basis}); Alternatives #1 and #2 are measured from their proposed outlines. Alternative #3 shows Crown Pine&rsquo;s stated 4,131.04 tract acres from the supplied LRM inventory. Its mapped calculations use the retained 4,131.82-acre fitted polygon as drawn; no geometry or deduction was scaled to force the stated acreage. The original premises&rsquo; floodplain figure includes Zone A within the western band, which lies outside the core&rsquo;s flood screen. Every grade and gate elsewhere on this page is computed on the original {data.premises ? data.premises.core_acres.toLocaleString() : '2,443.62'}-ac analyzed core.
              </div>
            </div>
          );
        })()}
        {/* Site Intelligence — prominent position for sites with analyst notes */}
        {parcel.site_notes && (
          <SiteIntelligenceNotes
            notes={parcel.site_notes}
            intel={(parcel as any).structured_intel?.site_intel}
            overrides={activeLayoutNarrative?.sections}
            note={activeLayoutNarrative?.note}
            pipelines={data.pipelines}
            isMeiners={isMeiners}
            onInspectWells={isMeiners ? () => setFootprintScreen(500) : undefined}
          />
        )}


        {/* Operator Supply Story — moved up so the Revenant case sells the
            opportunity before the kill-sheet metrics. Renders only for AOI
            parcels (those with a gasSupplyZone commitment). Previously
            rendered after Land & Ownership; that position made it feel like
            an afterthought rather than the headline operator commitment. */}
        {!isGrid && data.gasSupplyZone && (
          <div style={{ marginBottom: '12px' }}>
            <OperatorSupplySection zone={data.gasSupplyZone} suppressPrecedent={isPaces} />
          </div>
        )}

        {/* Grid lens page-1 — replaces the BTM key-numbers/gates/dimensions/resolve run */}
        {isGrid && gridScore && (
          <GridKillSheet
            data={data}
            parcel={parcel}
            gridScore={gridScore}
            nodeIntel={data.gridNodeIntel ?? null}
            developableLand={data.developableLand ?? null}
            redFlags={data.redFlags ?? []}
          />
        )}

        {!isGrid && <>
        {/* 1. Key Numbers */}
        <div className="no-break">
          <KeyNumbers items={keyNumbers} />
        </div>

        {/* 2. Gate strip */}
        <div className="no-break" style={{ marginBottom: '12px' }}>
          <div style={{
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', marginBottom: '8px',
            color: isMeiners ? REPORT.caution : gates.hardGatePass ? (floodSev.level === 'partial' ? REPORT.caution : REPORT.pass) : REPORT.fail,
            textTransform: 'uppercase', fontFamily: 'monospace',
          }}>
            {/* A1: six public-data screening gates \u2014 "ALL GATES PASS" read as a
                project-level clearance while the checklist carries open inquiries.
                A caveated gate (PASS*) must not be summarised as an unqualified pass
                either: when flood is unmapped or a real drainage corridor is present,
                the strip says so in the banner rather than only in the row a reader
                may skim past. */}
            {isUvalde ? 'Desktop screen · gas delivery and development approvals remain open' : meinersOverrides ? meinersOverrides.gateSummary
              : gates.hardGatePass
              ? (floodSev.level === 'partial'
                  ? (isA1CarveOut ? '\u2713 6 SCREENING GATES PASS \u2014 1 WITH OPEN INQUIRY' : '\u2713 GATES PASS \u2014 1 WITH OPEN INQUIRY')
                  : (isA1CarveOut ? '\u2713 6 SCREENING GATES PASS' : '\u2713 ALL GATES PASS'))
              : '\u2717 GATE FAILURE DETECTED'}
          </div>
          {[
            ...(isMeiners ? [{ passed: true, open: true, label: 'Wells & surface control', context: 'Operator agreements, mineral and pipeline rights, future drilling and a protected contiguous footprint remain unresolved.' }] : []),
            { passed: !gates.no_gas_path, open: isMeiners || isUvalde, label: isUvalde ? 'Gas Delivery' : 'Gas Access', context: (() => {
              if (isUvalde) return 'Mapped WTG feed; tap, pressure and firm volume unconfirmed';
              if (!pipelines || pipelines.length === 0) return 'No pipeline within 10 mi';
              // Spotlight prefers an actual tap candidate over a gathering line — a
              // gathering line near the parcel doesn't represent BTM-tappable gas access,
              // and would mislead the gate context line. Headline pipeline always counts.
              const tapCandidate = pipelines.find(p => !p.is_gathering || p.headline_override);
              const spotlight = featured || tapCandidate || pipelines[0];
              const locator = pipelineIsEffectivelyOnSite(parcel, spotlight) ? 'crossing the parcel' : `at ${spotlight.distance_miles} mi`;
              // Prefer registry-canonical system_name (e.g. "El Paso Natural Gas") over
              // raw RRC subsystem name (e.g. "3000 SERIES PERMIAN NORTH FIELD FEEDER L")
              // so the gate label matches the briefing intro and the headline row.
              const displayName = spotlight.system_name || spotlight.name || spotlight.operator || 'Pipeline';
              // Curator co-headline (structured_intel) shares the gate line: name the
              // nearer tap candidate first, then the headline/ceiling line.
              const co = pipelines.find(p => (p as any).co_headline && p !== spotlight);
              return co
                ? `${co.system_name || co.name || co.operator} at ${co.distance_miles} mi · ${displayName} ${locator}`
                : `${displayName} ${locator}`;
            })() },
            { passed: !gates.too_small, label: 'Acreage', context: (() => {
              // Carve-out reps: show the assembly acreage (matches the hero + grade
              // cards) instead of the single rep parcel's appraisal acreage. Non-carve-out
              // briefings (e.g. WV Goff) keep the exact rep-parcel rendering.
              const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
              const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
              const useAgg = aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
              // A1: the gate is decided on the analyzed core (unchanged), but the
              // context line states the premises the reader is being shown.
              if (data.premises) return `${data.premises.total_acres.toLocaleString()} acres (${data.premises.core_acres.toLocaleString()}-ac analyzed core + ${Math.round(data.premises.band_acres).toLocaleString()}-ac band)`;
              if (useAgg) return `${Number(aggAcres).toLocaleString()} acres (assembly)`;
              return parcel.acreage ? `${parcel.acreage} acres` : 'Unknown acreage';
            })() },
            { passed: !gates.nonattainment, label: isUvalde ? 'Air Quality Screen' : 'Air Permitting', context: (() => {
              if (parcel.in_nonattainment == null) return 'Attainment data pending';
              if (parcel.in_nonattainment) {
                const na0 = parcel.nonattainment_areas?.[0];
                const pol = na0?.pollutant ? ` for ${na0.pollutant}` : '';
                const cls = na0?.classification ? ` (${na0.classification})` : '';
                return `Nonattainment${pol}${cls} - NNSR required`;
              }
              const aq = data.airQualityHeadroom;
              if (aq && aq.length > 0) {
                const worst = aq[0];
                const hrPct = Number(worst.headroom_pct);
                if (hrPct < 0) return `In attainment - but ${worst.pollutant} exceeds NAAQS (designation risk)`;
                if (hrPct < 15) return `In attainment - ${worst.pollutant} at ${hrPct.toFixed(0)}% headroom (watch)`;
                return `In attainment - ${hrPct.toFixed(0)}% headroom`;
              }
              return 'In attainment - no monitors in county';
            })() },
            // floodSev.source === 'none' = no pct measurement AND no zone code: FEMA has
            // never mapped this county. That is an absence of data, not a caveated pass —
            // the chip reads INQUIRY, never PASS*. Parcels with a real zone or pct
            // (Goff X, A1 X, Paces carve-out pct) are untouched by this branch.
            { passed: !gates.floodway, open: isMeiners, label: 'Flood Zone', caveat: floodSev.level === 'partial', inquiry: floodSev.source === 'none' && floodSev.level === 'partial', context: isMeiners ? '23% in the TWDB 1% screen, concentrated along Cibolo Creek — layout remains open' : isA1CarveOut ? (activeLayoutRow ? layoutFloodLine(activeLayoutRow) : 'Minimal flood hazard (FEMA Zone X); 11.32 ac Zone A in one drainage corridor — pad plan routes around it') : floodSev.subLine },
            // A1: the gate tests government ownership. Its context line reported the
            // absentee heuristic, which reads as "easy target" — replaced by the
            // structural fact the gate actually establishes. PASS logic untouched.
            { passed: !gates.government_owner, label: 'Private Owner', context: isUvalde ? 'Two owners of record' : isA1CarveOut ? 'Single ownership entity \u00b7 LP' : (parcel.owner_name ? `${parcel.is_absentee ? 'Absentee' : (isEntityOwner(parcel.owner_name) ? 'Local' : 'Resident')} - ${isEntityOwner(parcel.owner_name) ? getEntityType(parcel.owner_name) : 'Individual'}` : 'Unknown') },
            { passed: !gates.ej_concern, label: 'EJ Screen', context: parcel.ej_max_pctile != null ? `${Math.round(Number(parcel.ej_max_pctile))}th percentile${Number(parcel.ej_max_pctile) >= 90 ? ' - EJ community' : ''}` : 'No EJ data' },
          ].map((gate, i) => (
            <div key={i} className="no-break" style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '5px 0', borderBottom: i < 4 ? `1px solid ${REPORT.rule}80` : 'none',
            }}>
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: gate.passed ? (gate.caveat || gate.open ? REPORT.caution + '18' : REPORT.pass + '18') : REPORT.fail + '18',
                color: gate.passed ? (gate.caveat || gate.open ? REPORT.caution : REPORT.pass) : REPORT.fail,
                fontSize: '12px', fontWeight: 700,
              }}>
                {gate.passed ? (gate.inquiry || gate.open ? '?' : '\u2713') : '\u2717'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: REPORT.heading }}>{gate.label}</div>
                <div style={{ fontSize: '10px', color: REPORT.secondary }}>{gate.context}</div>
              </div>
              <div style={{
                fontSize: '9px', fontWeight: 600, padding: '2px 8px', borderRadius: '3px',
                background: gate.passed ? (gate.caveat || gate.open ? REPORT.caution + '15' : REPORT.pass + '15') : REPORT.fail + '15',
                color: gate.passed ? (gate.caveat || gate.open ? REPORT.caution : REPORT.pass) : REPORT.fail,
                fontFamily: 'monospace', letterSpacing: '0.04em',
              }}>
                {gate.passed ? (gate.inquiry || gate.open ? 'OPEN' : isMeiners || isUvalde ? 'SCREENED' : gate.caveat ? 'PASS*' : 'PASS') : 'FAIL'}
              </div>
            </div>
          ))}
        </div>

        {/* 3. Dimension bars — side by side, data vertical inside each */}
        <div style={{ marginBottom: '12px' }}>
          {grades ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <div className="no-break" style={{ flex: 1 }}>
            <DimensionBar
              grade={isMeiners || fuelOptions ? null : grades.fuel_delivery} label="Fuel Delivery" color={REPORT.pass}
              region={grades.region} totalParcels={grades.total_parcels}
              data={fuelOptions ? [
                { value: fuelOptions.totalMmcfDay == null ? 'Unconfirmed' : formatOptionCapacity(fuelOptions.totalMmcfDay), label: `${fuelOptions.hasEstimates ? 'Est. total' : 'Total'}${fuelOptions.projectMmcfDay ? ' incl. projects' : ' · combined'}` },
                ...(fuelOptions.operatingMmcfDay != null ? [{ value: formatOptionCapacity(fuelOptions.operatingMmcfDay), label: 'Operating systems' }] : []),
                ...(fuelOptions.projectMmcfDay != null && fuelOptions.projectMmcfDay > 0 ? [{ value: formatOptionCapacity(fuelOptions.projectMmcfDay), label: 'Project design' }] : []),
                { value: fuelOptions.diameterLabel, label: `Diameter · ${fuelOptions.count} systems` },
                { value: fuelOptions.distanceLabel, label: data.pipelinesDistanceBasis?.type === 'carve_out_boundary' ? 'Distance · block' : 'Distance' },
              ] : [
                // Revenant (or other operator-zone) peak supply leads the
                // card for AOI parcels \u2014 the actual headline number for a
                // BTM site inside an operator gas-supply commitment.
                ...(data.gasSupplyZone?.peakBcfd != null ? [{
                  value: `${Number(data.gasSupplyZone.peakBcfd).toLocaleString(undefined, { maximumFractionDigits: 2 })} Bcf/d`,
                  label: `${data.gasSupplyZone.operatorName} peak${data.gasSupplyZone.peakYear ? ` (${data.gasSupplyZone.peakYear})` : ''}`,
                }] : []),
                isA1CarveOut
                  ? { value: '30\u2033 \u00b7 1.35 mi', label: 'ET Texoma \u00b7 supply confirmed' }
                  : isMeiners ? { value: meinersPipelineCapacity(featured || {}).value, label: 'Pipeline total capacity' }
                  : { value: featured ? (pipelineCapacityDisplay(featured).capacityMmcf != null ? `${pipelineCapacityDisplay(featured).capacityMmcf!.toLocaleString()} MMcf/d` : 'Unconfirmed') : 'Unconfirmed', label: 'Published capacity' },
                { value: featured?.diameter_inches ? `${featured.diameter_inches}\u2033` : '\u2014', label: 'Diameter' },
                { value: featured ? (pipelineIsEffectivelyOnSite(parcel, featured) ? 'Crosses parcel' : featured.distance_miles != null ? `${featured.distance_miles} mi` : '\u2014') : '\u2014', label: 'Distance' },
                { value: 'Unconfirmed', label: 'Operator observations' }, /* utilization is shown only from the native observation endpoint */
                { value: featured?.maop_psig ? `${Number(featured.maop_psig).toLocaleString()} psig` : '\u2014', label: 'MAOP' },
              ]}
              note={fuelOptions ? {
                text: `${fuelOptions.names.join(' · ')}.\n${isMeiners && fuelOptions.totalMmcfDay != null ? 'Combined published system totals plus Blackcomb project design (in-service expected Q4 2026). GCX is fully subscribed. Distances from the full block boundary.' : fuelOptions.details.join(' ')}\nSite delivery, tap, pressure and firm transport remain unconfirmed.${isMeiners ? '\nNearer option: Camino Real, 12.75″ at 5.28 mi; total capacity unconfirmed.' : ''}`,
                type: 'info',
              } : featured && isDesertSW(featured.name || '') ? {
                text: `Desert Southwest \u2014 under construction, 2027 in-service. Interim supply: ${pipelines?.find(p => !isDesertSW(p.name || '') && Number(p.distance_miles) <= 5)?.name || 'EPNG'} at ${pipelines?.find(p => !isDesertSW(p.name || '') && Number(p.distance_miles) <= 5)?.distance_miles ?? '< 5'} mi.`,
                type: 'info',
              } : undefined}
              methodology={{
                inputs: [
                  isA1CarveOut
                    ? { name: 'Gas access basis (30\u2033 RRC-verified transmission crossing the site\u2019s western band; 1.35 mi from the analyzed core)', value: 'access-screening', weight: '65%' }
                    : isMeiners
                    ? { name: 'Camino Real total capacity', value: 'Unconfirmed', weight: 'Not rated' }
                    : { name: 'Published capacity context', value: featured && pipelineCapacityDisplay(featured).capacityMmcf != null ? `${pipelineCapacityDisplay(featured).capacityMmcf!.toLocaleString()} MMcf/d` : 'Unconfirmed', weight: 'Context' },
                  { name: 'Pipeline infra score', value: parcel.pipeline_infra_score != null ? `${parcel.pipeline_infra_score}/10` : '\u2014', weight: '35%' },
                ],
                thresholds: [
                  // A1: the grade is anchored on proximity to the RRC-verified 30" ET
                  // Texoma transmission line \u2014 NOT on an observed-headroom figure, which
                  // this briefing deliberately does not claim. Contingent on commercial
                  // confirmation (the ET interconnect study).
                  ...(isA1CarveOut
                    ? [
                        { grade: 'A', requirement: 'Large transmission (\u226520") within 2mi; A1 basis: 30\u2033 ET Texoma crossing the site\u2019s western band (1.35 mi from the analyzed core) \u2014 gas availability and supply confirmed; firm transport and tap engineering remaining' },
                        { grade: 'B', requirement: 'Transmission within 5mi; site delivery and firm transport unconfirmed' },
                      ]
                    : [
                        { grade: 'A', requirement: 'Large transmission (\u226520") within 2mi; site delivery and firm transport unconfirmed' },
                        { grade: 'B', requirement: 'Transmission within 5mi; site delivery and firm transport unconfirmed' },
                      ]),
                  { grade: 'C', requirement: 'Pipeline access within 10mi, any capacity' },
                  { grade: 'D', requirement: 'Marginal pipeline access or small gathering lines only' },
                  { grade: 'F', requirement: 'No pipeline access within 10mi' },
                ],
                // A1's line describes what the grade covers, not what it leaves
                // out — "Not scored:" read as a disclaimer on its own scope.
                notScoredLabel: isA1CarveOut ? 'Scope' : undefined,
                notScored: isA1CarveOut
                  ? 'Fuel-access screening: proximity and infrastructure only. Deliverability, pressure, capacity and firm service unconfirmed.'
                  : isMeiners
                  ? 'Pipeline access facts only. Capacity, delivery, pressure, tap rights, receipt point and firm transport require operator confirmation.'
                  : 'Pipeline access grade covers proximity and infrastructure. Published system or project capacity does not establish local delivery or firm transport; confirm both with the operator.',
              }}
            />
            </div>
            <div className="no-break" style={{ flex: 1 }}>
            <DimensionBar
              grade={grades.site_readiness} label={isMeiners || isUvalde ? 'Desktop Site Screen' : 'Site Readiness'} color={REPORT.highlight}
              region={grades.region} totalParcels={grades.total_parcels}
              data={[
                { value: isMeiners ? '1.70%' : (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' || data.carveOutAggregate?.slopeGrid5m != null) && data.carveOutAggregate?.weightedAvgSlopePct != null ? meanOr(Number(data.carveOutAggregate.weightedAvgSlopePct), data.carveOutAggregate.slopeGrid5m?.avgSlopePct) : (parcel.avg_slope_pct != null ? `${parcel.avg_slope_pct}%` : '\u2014'), label: isUvalde ? 'Representative parcel slope' : isMeiners ? 'Area-weighted mean slope' : 'Avg slope' },
                { value: parcel.in_nonattainment === false ? 'Attainment' : parcel.in_nonattainment ? 'Nonattainment' : '\u2014', label: 'EPA status' },
                { value: (() => {
                  if (isMeiners) return '3.1 mi';
                  if (parcel.nearest_carrier_fiber_miles != null) return `${parcel.nearest_carrier_fiber_miles} mi`;
                  return 'Unknown';
                })(), label: (() => {
                  if (isMeiners) return 'FiberLight route (block boundary)';
                  if (parcel.nearest_carrier_fiber_miles != null) return `Enterprise carrier route (${parcel.nearest_carrier_fiber_name || 'carrier'})`;
                  return 'Enterprise carrier route';
                })() },
                { value: (() => {
                    if (isMeiners || isUvalde) return 'Unverified';
                    const ld = (parcel as any).structured_intel?.landowner_disclosures?.water_on_site;
                    if (ld?.display_note) return 'Landowner-verified';
                    return waterTierLabel(computeEffectiveWaterTier(parcel));
                  })(),
                  label: (() => {
                    if (isUvalde) return 'Water supply';
                    if (isMeiners) return 'Seller-reported water wells';
                    const ld = (parcel as any).structured_intel?.landowner_disclosures?.water_on_site;
                    if (ld?.display_note) return 'Water (pending specs)';
                    if (parcel.aquifer_name) return `Water (${formatAquiferName(parcel.aquifer_name)})`;
                    if (parcel.water_regulatory_zone) return `Water (${parcel.water_regulatory_zone})`;
                    return 'Water sourcing';
                  })() },
              ]}
              note={(() => {
                if (isUvalde) return { text: 'Water supply, contiguous development area, airport requirements and firm utilities remain unverified.', type: 'info' as const };
                const parts: string[] = [];
                if (isMeiners) parts.push('Score excludes well and surface-use constraints, title, contiguous development footprint, geotechnical conditions and firm utilities');
                if (isMeiners) parts.push('FiberLight ~3.1 mi and Zayo ~6.3 mi from block boundary; service, capacity, SLA and diversity unconfirmed');
                const wt = computeEffectiveWaterTier(parcel);
                if (wt === 'aquifer_no_permit') parts.push('Groundwater, no permit');
                else if (wt === 'municipal') parts.push('Municipal water');
                else if (wt === 'aquifer_gcd') parts.push(`${parcel.water_regulatory_zone || 'GCD'} permit req.`);
                else if (wt === 'on_site_well') {
                  const w = parcel.nearby_water_wells?.[0];
                  if (w) parts.push(`On-site well potential (nearest ${w.distance_miles} mi, ${w.yield_gpm || '?'} GPM @ ${w.total_depth_ft || '?'} ft)`);
                  else parts.push('On-site well potential (wells within 2 mi)');
                }
                else if (wt === 'trucked') parts.push('Trucked water (no wells within 2 mi)');
                {
                  const ixpNets = parcel.nearest_ixp_networks != null ? Number(parcel.nearest_ixp_networks) : null;
                  if (parcel.nearest_ixp_name && parcel.nearest_ixp_miles != null && ixpNets != null && ixpNets >= 5) {
                    const latMs = isA1CarveOut
                      ? (Number(parcel.nearest_ixp_miles) * 0.022).toFixed(1)
                      : (Number(parcel.nearest_ixp_miles) * 1.609 * 5 / 1000).toFixed(1);
                    parts.push(`Nearest IXP: ${parcel.nearest_ixp_name}, ~${parcel.nearest_ixp_miles} mi (est. ${latMs} ms${isA1CarveOut ? ' RTT' : ''})`);
                  } else if (parcel.nearest_colo_facility && parcel.nearest_colo_miles != null) {
                    const latMs = isA1CarveOut
                      ? (Number(parcel.nearest_colo_miles) * 0.022).toFixed(1)
                      : (Number(parcel.nearest_colo_miles) * 1.609 * 5 / 1000).toFixed(1);
                    parts.push(`Nearest peering: ${parcel.nearest_colo_facility}, ~${parcel.nearest_colo_miles} mi (est. ${latMs} ms${isA1CarveOut ? ' RTT' : ''})`);
                  }
                }
                if (hasLandownerEarthwork(parcel)
                    && parcel.avg_slope_pct != null
                    && Number(parcel.avg_slope_pct) >= 5) {
                  parts.push('Slope mitigated \u2014 landowner committed to earthwork & site preparation');
                }
                return parts.length > 0 ? { text: parts.map(p => `\u2022 ${p}`).join('\n'), type: 'confirmed' as const } : undefined;
              })()}
              methodology={{
                inputs: [
                  { name: 'Acreage', value: isMeiners ? '1,803.86 ac (three-account aggregate)' : parcel.acreage ? `${Number(parcel.acreage).toLocaleString()} ac` : '\u2014', weight: '25%' },
                  { name: 'Avg slope', value: isMeiners ? '1.70% (area-weighted aggregate)' : (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' || data.carveOutAggregate?.slopeGrid5m != null) && data.carveOutAggregate?.weightedAvgSlopePct != null ? `${meanOr(Number(data.carveOutAggregate.weightedAvgSlopePct), data.carveOutAggregate.slopeGrid5m?.avgSlopePct)} (3–5 m grids)` :(parcel.avg_slope_pct != null ? `${parcel.avg_slope_pct}%` : '\u2014'), weight: '25%' },
                  { name: 'EPA attainment', value: parcel.in_nonattainment == null ? '\u2014' : parcel.in_nonattainment ? 'No' : 'Yes', weight: '25%' },
                  { name: 'Flood zone', value: isMeiners ? 'TWDB 1% screen · 23.15%' : parcel.flood_zone || '\u2014', weight: '10%' },
                  { name: 'Fiber (FCC BDC scoring input)', value: parcel.fiber_available == null ? '\u2014' : parcel.fiber_available ? 'Coverage present' : parcel.nearest_fiber_miles != null ? `${parcel.nearest_fiber_miles} mi to coverage` : 'No coverage', weight: '10%' },
                  { name: 'Highway', value: parcel.nearest_highway_miles != null ? `${parcel.nearest_highway_miles} mi` : '\u2014', weight: '5%' },
                ],
                thresholds: isMeiners ? [
                  { grade: 'A', requirement: '85–100 points' },
                  { grade: 'B+', requirement: '75–84 points' },
                  { grade: 'B', requirement: '65–74 points' },
                  { grade: 'C+', requirement: '55–64 points' },
                  { grade: 'C', requirement: '45–54 points' },
                  { grade: 'D', requirement: '30–44 points' },
                  { grade: 'F', requirement: 'Below 30 points' },
                ] : [
                  { grade: 'A', requirement: '2,500+ acres, <2% slope, attainment, no flood risk' },
                  { grade: 'B', requirement: '1,000+ acres, <5% slope, attainment area' },
                  { grade: 'C', requirement: '500+ acres, moderate terrain, attainment area' },
                  { grade: 'D', requirement: 'Under 500 acres or terrain/permitting challenges' },
                  { grade: 'F', requirement: '<40 acres or hard gate failure' },
                ],
                notScored: isMeiners
                  ? 'Desktop score excludes existing-well constraints, surface-use rights, title, easements, development-footprint contiguity, geotechnical conditions and firm gas, electric, water and enterprise-fiber service. Resolve these go/no-go items before treating the grade as site readiness.'
                  : 'Water sourcing tier and IXP latency are displayed but not weighted. BTM air-cooled configuration requires ~300\u2013400K gal/yr auxiliary water (zero evaporative makeup) \u2014 sourcing method shown as a logistics data point, not a viability gate.',
              }}
            />
            </div>
            {/* Acquisition is built out of price and valuation — the partner edition
                has no acquisition grade in its payload, so the whole card drops. */}
            {grades.acquisition && (
            <div className="no-break" style={{ flex: 1 }}>
            <DimensionBar
              grade={isA1CarveOut || isMeiners ? null : grades.acquisition} gradeLabel={isMeiners ? 'A+' : undefined} label={isA1CarveOut ? 'Land Control' : 'Acquisition'} color={REPORT.highlight}
              region={grades.region} totalParcels={grades.total_parcels}
              data={[
                // A1 carries NO land-valuation figure in this card. Both the county
                // assessment and its $/ac read as a transaction price, and no price is
                // established \u2014 it is being set in negotiation (see the note below).
                // The `grade` is already forced null on A1, so the methodology drawer
                // (which carries a Land value input) never renders here either.
                ...(isA1CarveOut ? [] : [
                { value: isMeiners ? (data.carveOutAggregate?.totalLandValue != null ? `$${Number(data.carveOutAggregate.totalLandValue).toLocaleString()}` : 'Unconfirmed') : activeListing?.priceStr || (parcel.land_value != null ? `$${Number(parcel.land_value).toLocaleString()}` : (isEntityOwner(parcel.owner_name) ? 'Not publicly assessed' : '\u2014')), label: isMeiners ? 'CAD land value · 3 accounts' : activeListing ? 'Asking price' : 'Assessed value' },
                { value: activeListing?.pricePerAcre || (isEntityOwner(parcel.owner_name) && parcel.land_value == null ? 'Not listed' : '\u2014'), label: 'Price per acre' },
                ]),
                // A1: `is_absentee` only means the owner's mailing address is out of
                // county \u2014 a cold-outreach lead signal that reads as idle land. The
                // counterparty is an institutional timberland owner; its platform is
                // Caddo Sustainable Timberlands (canon \u00a73). Name the structure instead.
                { value: isUvalde ? 'Two owners across the assembly' : isA1CarveOut ? 'Institutional timberland owner (LP)' : (parcel.is_absentee ? 'Absentee' : (isEntityOwner(parcel.owner_name) ? 'Local' : 'Resident')), label: 'Owner type' },
                ...(isA1CarveOut ? [
                { value: 'Caddo Sustainable Timberlands', label: 'Ownership platform' },
                { value: 'Terms under negotiation', label: 'Control status' },
                ] : []),
                { value: (parcel as any).structured_intel?.jurisdiction_note || (parcel.state === 'TX' ? 'No zoning' : parcel.state === 'WV' ? 'Limited county zoning' : '\u2014'), label: 'Jurisdiction' },
              ]}
              note={isMeiners ? { text: 'A+ · Known market / pre-market availability. Single ownership counterparty. Price, title and protected surface-use terms remain to be agreed.', type: 'confirmed' } : data.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
                // No figure to caption any more \u2014 the note carries the posture instead.
                // Platform scale and any forward-precedent claim are deliberately absent:
                // neither belongs in a transaction card for this premises.
                ? { text: `CROWN PINE TIMBER 1 LP \u00b7 single ownership counterparty. Exact carve-out, price, and definitive land-control terms remain under negotiation.`, type: 'confirmed' }
                : isUvalde ? { text: 'Offered tract: Run Well Ranches. North tract separately owned. Title, easements and airport restrictions require review.', type: 'info' }
                : (activeListing?.isOnSite ? { text: 'Actively listed \u00b7 Single owner \u00b7 Unincorporated \u2014 no municipal approval needed', type: 'confirmed' } : undefined)}
              methodology={{
                inputs: [
                  { name: 'Absentee owner', value: parcel.is_absentee ? 'Yes' : 'No', weight: '30%' },
                  { name: 'Entity type', value: isEntityOwner(parcel.owner_name) ? getEntityType(parcel.owner_name) : 'Individual', weight: '20%' },
                  { name: 'Co-located owners', value: parcel.colocated_owners ? String(parcel.colocated_owners.length) : '0', weight: '20%' },
                  { name: isMeiners ? 'CAD land value · 3 accounts' : 'Land value', value: (isMeiners || data.carveOutAggregate?.slug === 'crownpine-blk-194-a1') && data.carveOutAggregate?.totalLandValue != null ? `$${Number(data.carveOutAggregate.totalLandValue).toLocaleString()}` : (parcel.land_value != null ? `$${Number(parcel.land_value).toLocaleString()}` : '\u2014'), weight: '15%' },
                  { name: 'Active listing', value: activeListing?.isOnSite ? 'Listed' : 'Not listed', weight: '15%' },
                ],
                thresholds: [
                  { grade: 'A', requirement: 'Absentee owner, actively listed, single parcel, low land value' },
                  { grade: 'B', requirement: 'Absentee or entity owner with favorable signals' },
                  { grade: 'C', requirement: 'Average acquisition profile' },
                  { grade: 'D', requirement: 'Resident owner or high fragmentation' },
                  { grade: 'F', requirement: 'Government owner or severe obstacles' },
                ],
              }}
            />
            </div>
            )}
          </div>
          ) : (
            <div style={{ fontSize: '10px', color: REPORT.tertiary, padding: '8px 0' }}>
              Grade computation not yet available for this parcel.
            </div>
          )}
        </div>

        {/* 5. Resolve Before LOI */}
        <div className="no-break">
          {/* Curator opt-out (structured_intel.suppress_resolve_before_loi): curated
              briefings whose Site Intelligence narrative already carries the diligence
              directives can drop the auto-computed box (Matthew, 2026-08-26, Cinco). */}
          {!(parcel as any).structured_intel?.suppress_resolve_before_loi && (
            <ResolveBeforeLOI items={resolveItems} milestone={isA1CarveOut ? 'terms' : undefined} />
          )}
        </div>
        </>}
      </div>

      {/* ── Grid lens sections: site & incentives + node intelligence lead, then
          Power Infrastructure moves up (the BTM order renders it later, gated
          !isGrid below) ── */}
      {isGrid && gridScore && (
        <>
          <GridSitePanel nodeIntel={data.gridNodeIntel ?? null} />
          <GridNodeSection nodeIntel={data.gridNodeIntel ?? null} gridScore={gridScore} />
          <PowerInfraSection ref={powerInfraRef} data={data} parcel={parcel} />
        </>
      )}

      {/* ── Data Confidence (print-only, promoted to front per Jobs review) ── */}
      <div className="briefing-print-only briefing-page-break briefing-section" style={{ display: 'none', background: '#ffffff', padding: '18px 28px 12px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Data Confidence</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px', }}>
            What we know, what we believe, what we're assuming, and what still needs confirmation — so the investment committee can weigh every finding in the pages that follow.
          </div>
        </div>
        {(['confirmed', 'preliminary', 'assumed', 'pending'] as ConfidenceLevel[]).map(level => {
          const group = confidenceItems.filter(item => item.confidence === level);
          if (group.length === 0) return null;
          return (
            <div key={level} className="no-break" style={{ marginBottom: '10px', background: REPORT.surface, borderRadius: '6px', padding: '8px 10px', border: `1px solid ${REPORT.rule}` }}>
              <div style={{ fontSize: '9px', fontWeight: 700, color: CONFIDENCE_COLORS[level], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                {CONFIDENCE_LABELS[level]}
              </div>
              {group.map((item, i) => (
                <div key={i} style={{ display: 'flex', fontSize: '10px', padding: '1px 0', borderBottom: i < group.length - 1 ? `1px solid ${REPORT.rule}80` : 'none' }}>
                  <span style={{ width: '35%', color: REPORT.secondary, flexShrink: 0 }}>{item.field}</span>
                  <span style={{ width: '30%', fontWeight: 600, color: REPORT.heading }}>{item.value}</span>
                  <span style={{ flex: 1, color: REPORT.tertiary, fontSize: '9px' }}>{item.source}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── Fuel Delivery + Permitting Triage — BTM lens only ── */}
      {!isGrid && <>
      {/* ── Fuel Delivery ── */}
      <div className="briefing-page-break" />
      {mapSnapshots?.fuelDelivery && <MapSnapshot src={mapSnapshots.fuelDelivery} caption="Fuel Delivery — Pipeline network and distance annotations" maxHeight="380px" />}
      <div className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Fuel Delivery</SectionTitle>
          {(() => { const intro = isUvalde ? 'WTG’s 4.5-inch line models 19.3 MW at default utilization. Confirm a delivery point, operating pressure, firm volume and lateral route before sizing generation.' : generateFuelIntro(pipelines, parcel, null, featured, isA1CarveOut, isMeiners); return intro ? <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '10px', }}>{intro}</div> : null; })()}
        </div>
        {/* Crown Pine A1: verified single-track gas facts — surfaces the strong data
            (RRC-verified Texoma line + on-owned-ground lateral + FGT downstream signal)
            so the section leads with what's confirmed. Gated to A1; other sites unaffected. */}
        {data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' && (
          <div style={{ border: `1px solid ${REPORT.rule}`, borderRadius: 6, padding: '12px 14px', marginBottom: 10, background: REPORT.surface }}>
            <TierEyebrow color={REPORT.pass} marginBottom={8}>Gas Access · Energy Transfer Texoma · Advance to Operator Diligence</TierEyebrow>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: REPORT.secondary, fontWeight: 700 }}>The line (RRC-verified)</div>
                <div style={{ fontSize: '11px', color: REPORT.heading, fontWeight: 600, marginTop: '2px' }}>Energy Transfer Texoma 30″</div>
                <div style={{ fontSize: '10px', color: REPORT.secondary, lineHeight: 1.4 }}>In-service · crosses the site’s western band (~0.52 mi) · 1.35 mi from the analyzed core · T-4 00749 · HPL system (Bammel storage on the HPL system)</div>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: REPORT.secondary, fontWeight: 700 }}>Lateral corridor</div>
                <div style={{ fontSize: '11px', color: REPORT.heading, fontWeight: 600, marginTop: '2px' }}>On-premises routing</div>
                <div style={{ fontSize: '10px', color: REPORT.secondary, lineHeight: 1.4 }}>The line crosses the 594-ac western band — tap and meter siting on the premises; route subject to survey, routing, and ET tap approval</div>
              </div>
            </div>
            <div style={{ marginTop: '10px', padding: '8px 10px', background: REPORT.pass + '12', borderRadius: '4px' }}>
              <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: REPORT.pass, fontWeight: 700 }}>Downstream scheduling context · FGT "HPL Texoma" receipt point</div>
              <div style={{ fontSize: '10px', color: REPORT.body, marginTop: '2px', lineHeight: 1.45 }}>Twelve point-in-time Final-cycle snapshots (20th of each month, Aug 2025 – Jul 2026) at the downstream FGT "HPL Texoma" receipt point averaged 26.7% of its posted 400,000 MMBtu/d operating capacity (max 36.0%), with ~293,000 MMBtu/d average posted operationally-available capacity. Downstream scheduling context only — this does not establish capacity, pressure, flow direction, firm availability, or headroom near A1.</div>
            </div>
            <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '6px', lineHeight: 1.45 }}>The 30″ diameter is a physical line fact; no local capacity or gas-to-power conversion is claimed. Gas availability and supply have been confirmed, with delivery expected in tranches as project phases build out; firm transport and tap engineering are the remaining commercial steps.</div>
          </div>
        )}
        {(data.pipelinesExtendedSearch || (isMeiners && data.pipelinesDistanceBasis)) && (
          <div style={{
            fontSize: '10px', color: REPORT.tertiary, marginBottom: '6px',
            fontStyle: 'italic',
          }}>
            {isMeiners && data.pipelinesDistanceBasis?.type === 'carve_out_boundary'
              ? 'Distances from the full three-account block boundary; straight-line proximity, not engineered lateral routes.'
              : isUvalde ? `Search includes mapped feed segments within ${data.pipelinesSearchRadiusMi || 10} mi; table distances measure from the representative parcel centroid.`
              : data.carveOutAggregate?.slug === COOPER_DRAW_SLUG ? `Search widened to ${data.pipelinesSearchRadiusMi || 14} mi to include the 42-inch header lines. Lines that cross the representative parcel read 0 mi; other distances measure from its centroid.`
              : data.pipelinesSearchRadiusMi
              ? `Tap-candidate search widened to ${data.pipelinesSearchRadiusMi} mi — the nearest gas transmission lies beyond the 5-mile default; distances measure from the tract centroid.`
              : 'Extended search (5–10 mi) — no candidates within 5 miles.'}
          </div>
        )}
        {pipelines && pipelines.length > 0 ? (
          <PipelineTable pipelines={pipelines} parcel={parcel} isMeiners={isMeiners} onHover={setHoverFeature} />
        ) : (
          <div style={{ fontSize: '10px', color: REPORT.tertiary }}>No gas pipelines within 10 miles</div>
        )}

        {/* Basin context badges — conditional footnotes */}
        {gasSupply?.has_data && gasSupply.supply_indicators?.supply_reliability && (
          <div style={{ display: 'flex', gap: '6px', marginTop: '10px', marginBottom: '8px' }}>
              <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: REPORT.heading }}>
                  {gasSupply.supply_indicators.supply_reliability}
                </div>
                <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Supply Profile</div>
                {gasSupply.supply_indicators.associated_gas_pct != null && (
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
                    {Math.round(gasSupply.supply_indicators.associated_gas_pct)}% associated gas
                  </div>
                )}
              </div>
          </div>
        )}

        {/* Well count footnote — only if significant */}
        {gasSupply?.has_data && gasSupply.radii && (() => {
          const r5 = gasSupply.radii.find(r => r.radius_miles === 5);
          const wellCount = r5?.well_count || 0;
          return wellCount > 10 ? (
            <div style={{ fontSize: '10px', color: REPORT.tertiary, marginBottom: '8px' }}>
              {wellCount} active wells within 5 mi — supplemental supply
            </div>
          ) : null;
        })()}

        {/* Interconnect Summary — plain English for the deal team */}
        {pipelines && pipelines.length > 0 && (
          <div style={{
            background: REPORT.amberWash,
            padding: '12px 14px', borderRadius: '4px', marginTop: '8px',
          }}>
            <TierEyebrow color={REPORT.accent} marginBottom={8}>
              What This Means
            </TierEyebrow>
            <div style={{ fontSize: '11px', lineHeight: 1.55, color: REPORT.body }}>
              {(() => {
                // Crown Pine Site A1 (carve-out focus): curated single-track "what this
                // means" carrying the verified data points (RRC-verified Texoma line +
                // FGT downstream signal + confirmed supply), replacing the generic
                // headroom framing. Gated to A1; all other sites keep the logic below.
                if (isUvalde) return 'The nearest mapped 4.5-inch WTG member is 1.53 mi from the representative parcel centroid. Confirm the connected supply path and any lateral or tap before relying on its modeled capacity.';
                if (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1') {
                  return "The position is direct and verifiable: an in-service 30″ Energy Transfer transmission line crosses the site's western band for ~0.52 mi, with tap and meter siting available on the premises. Gas availability and supply have been confirmed, with delivery expected in tranches as project phases build out; firm transport and tap engineering are the remaining commercial steps. The verified downstream signal above provides downstream scheduling context; utilization and capacity figures remain screening estimates, not a firm-capacity grant.";
                }
                // The `revenant-operator-gathering` pseudo-row was retired 2026-08-07;
                // the id filter stays as a no-op guard. A build-to-site commitment row
                // isn't a current geometry signal, so the crosses-parcel / utilization
                // framing below doesn't apply to it. The operator-commitment lede is
                // rendered separately as the first part of the prose.
                if (isMeiners) return 'Camino Real is the nearest transmission tap candidate, 5.28 miles from the full block boundary. Total pipeline capacity does not establish volume available at this site. Confirm a delivery point, lateral route, pressure and firm transportation with the operator.';
                const realLines = pipelines.filter(p => p.id !== 'revenant-operator-gathering');
                const primary = realLines.find(p => p.headline_override) || realLines[0] || pipelines[0];
                const name = primary.system_name || primary.name || primary.operator || 'The nearest pipeline';
                const dist = primary.distance_miles;
                const onSite = pipelineIsEffectivelyOnSite(parcel, primary);
                const ownerOps = ownerOwnsPipeline(parcel.owner_name, primary);
                const parts: string[] = [];

                // Lede: when the parcel is in an operator-zone AOI, lead with
                // the operator-build commitment. The public-midstream lines
                // are redundancy / scale, not the firm path.
                const zone = data.gasSupplyZone;
                if (zone) {
                  const op = zone.operatorName || 'The zone operator';
                  parts.push(`${op} has indicated they will build operator gathering to qualified BTM buyers in the AOI — that is the firm-path supply for this site`);
                }

                if (onSite) {
                  parts.push(ownerOps
                    ? `${name} crosses the parcel and is operated by the landowner — tap grantable on-parcel without a third-party interconnect study`
                    : `${name} crosses the parcel — geometry signal only; tap, receipt point, and firm-transport availability subject to a standard operator interconnect study`);
                } else if (primary) {
                  // Curator-designated co-headline (structured_intel) shares tap-candidate
                  // billing with the compute-picked headline — name both.
                  const co = (data.pipelines || []).find(pp => (pp as any).co_headline);
                  parts.push(co
                    ? `${co.name} at ${co.distance_miles} miles and ${name} at ${dist} miles are the featured tap candidates`
                    : `${name} at ${dist} miles is the nearest public-midstream tap candidate`);
                }

                if (primary.maop_psig) {
                  const psig = Number(primary.maop_psig);
                  parts[parts.length - 1] += ` with ${psig.toLocaleString()} psig operating pressure`;
                }

                if (isDesertSW(primary.name)) {
                  parts.push(`Desert Southwest is under construction with an expected in-service date of 2027`);
                }
                // Existing utilization fields are system or model summaries, not
                // native observations for this selected pipeline. Keep the
                // commercial uncertainty explicit until the observation endpoint
                // supplies a dated operator posting.
                parts.push('Operator-posted operating capacity, scheduled quantity, and firm delivery terms require confirmation');

                return parts.join('. ') + '.';
              })()}
            </div>
          </div>
        )}

        {(() => {
          if (isMeiners) return null;
          // EBB portal footnote — surface the operator websites we sourced
          // utilization data from, so the IC can verify the freshness signals
          // (gas-day readings, capacity rollups) directly. Mirrors the
          // Revenant zone briefing's data-sources block. Mapping is operator
          // family → portal URL; we de-dup so the same portal isn't listed
          // twice when multiple operator subsidiaries appear in the table.
          // A1: gated off. The only line in the table is intrastate HPL/Texoma,
          // which posts nothing on the ETP EBB — listing the portal implies a
          // utilization source that was never available for this corridor.
          if (isA1CarveOut) return null;
          const portals: Array<{ label: string; url: string }> = [];
          const seen = new Set<string>();
          const add = (label: string, url: string) => {
            if (seen.has(url)) return;
            seen.add(url);
            portals.push({ label, url });
          };
          const ops = (pipelines || []).map(p => `${p.operator || ''} ${p.name || ''}`.toLowerCase()).join(' | ');
          if (/kinder morgan|\btgp\b|tennessee gas|\bngpl\b|natural gas pipeline of america|el paso natural gas|\bepng\b/i.test(ops)) {
            add('NGPL / TGP / EPNG (Kinder Morgan)', 'pipeline2.kindermorgan.com');
          }
          if (/gulf south|boardwalk|\bbwp\b/i.test(ops)) {
            add('Gulf South (Boardwalk Pipeline Partners)', 'reporting.prod.bwpmlp.org');
          }
          if (/texas eastern|tetco|enbridge|spectra/i.test(ops)) {
            add('Texas Eastern (Enbridge LINK)', 'rtba.enbridge.com');
          }
          if (/energy transfer|\betp\b|panhandle eastern|trunkline/i.test(ops)) {
            add('Energy Transfer (ETP EBB)', 'ebb.energytransfer.com');
          }
          if (/equitrans|\bmvp\b|mountain valley/i.test(ops)) {
            add('Equitrans / MVP', 'equitransebb.energy.com');
          }
          if (/northern natural|\bnng\b/i.test(ops)) {
            add('Northern Natural Gas', 'tnt.northernnaturalgas.com');
          }
          if (portals.length === 0) return null;
          return (
            <div style={{ fontSize: '9.5px', color: REPORT.tertiary, marginTop: '8px', lineHeight: 1.55 }}>
              <span style={{ fontWeight: 600, color: REPORT.secondary, marginRight: 4 }}>EBB portals consulted:</span>
              {portals.map((p, i) => (
                <span key={p.url}>
                  {p.label} <code style={{ fontSize: '9px' }}>{p.url}</code>
                  {i < portals.length - 1 ? ' · ' : ''}
                </span>
              ))}
            </div>
          );
        })()}

        {/* A1 drops the archive sentence: the twelve FGT artifacts behind this
            page's downstream signal are not yet re-pulled and committed (claims
            audit O-5). Generic briefings keep it — their EBB pipes ARE archived.
            Element-level branch so the non-A1 footnote stays byte-identical. */}
        {isMeiners
          ? <SourceFootnote>Source: TX RRC pipeline geometry and permits; published operator system and project capacities. Distances use the full block boundary. Site delivery requires commercial and engineering confirmation.</SourceFootnote>
          : isA1CarveOut
          ? <SourceFootnote>Source: Derived from {parcel.state === 'TX' ? 'TX RRC pipeline data + ' : ''}EIA pipeline flows, PHMSA NPMS, FERC Electronic Bulletin Board (EBB) postings — operator portals listed above.</SourceFootnote>
          : <SourceFootnote>Source: Derived from {parcel.state === 'TX' ? 'TX RRC pipeline data + ' : ''}EIA pipeline flows, PHMSA NPMS, FERC Electronic Bulletin Board (EBB) postings — operator portals listed above. Raw EBB CSV/XLSX archived per gas day for provenance.</SourceFootnote>}
      </div>

      {/* ── Permitting Triage ── */}
      <div ref={permittingRef} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Permitting Triage</SectionTitle>
          {isUvalde && <p style={{ fontSize: '11px', color: REPORT.body }}>The 160 MW calculations below are a template screening example. Project scale, equipment, emissions and permit eligibility remain unconfirmed.</p>}
        </div>

        {/* Phased Permitting Ladder (TX attainment) or single badge fallback */}
        {permitPathway.ladder ? (() => {
          const ladder = permitPathway.ladder;
          const ep = permitPathway.emissionsProfile;
          const rungColors = { green: REPORT.pass, amber: REPORT.caution };
          return (
            <>
              {/* The Ladder */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                  {isUvalde ? `Illustrative Permitting Screen (${ladder.trancheMw} MW example)` : <>Phased Permitting Pathway ({ladder.trancheMw} MW / phase)</>}
                </div>

                {/* Rung 1 */}
                {ladder.phases.map((phase, i) => (
                  <div key={i}>
                    {/* Threshold divider between rungs */}
                    {i === 1 && (
                      <div style={{ padding: '4px 0 4px 110px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1, height: '2px', background: REPORT.caution, opacity: 0.5 }} />
                          <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.caution, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                            {ladder.thresholdLabel}
                          </div>
                          <div style={{ flex: 1, height: '2px', background: REPORT.caution, opacity: 0.5 }} />
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 0, borderRadius: '6px', overflow: 'hidden', marginBottom: '4px' }}>
                      <div style={{
                        width: '110px', flexShrink: 0, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        background: `${rungColors[phase.color]}10`, border: `1px solid ${rungColors[phase.color]}15`, borderRight: 'none',
                      }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: REPORT.heading }}>{isUvalde ? `Illustrative ${phase.mwRange}` : phase.label}</div>
                        <div style={{ fontSize: '9px', color: REPORT.secondary, marginTop: '1px' }}>{phase.mwRange}</div>
                      </div>
                      <div style={{
                        flex: 1, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '12px',
                        background: `${rungColors[phase.color]}04`, border: `1px solid ${rungColors[phase.color]}15`, borderLeft: 'none',
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: rungColors[phase.color] }}>{phase.pathway}</div>
                          <div style={{ fontSize: '9px', color: REPORT.secondary, lineHeight: 1.4 }}>{phase.detail}</div>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 700, fontFamily: 'Georgia, serif', color: rungColors[phase.color], flexShrink: 0, textAlign: 'right' }}>
                          {phase.timeline}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Next steps */}
                <div style={{
                  padding: '10px 14px', borderRadius: '6px', border: `1px solid ${REPORT.highlight}15`,
                  background: `${REPORT.highlight}04`, marginTop: '8px', fontSize: '9px', color: REPORT.secondary, lineHeight: 1.6,
                }}>
                  <ul style={{ margin: 0, paddingLeft: '14px' }}>
                    {ladder.nextSteps.map((step, i) => {
                      // Bold the leading phrase before the first colon
                      const colonIdx = step.indexOf(':');
                      return (
                        <li key={i} style={{ marginBottom: '3px' }}>
                          {colonIdx > 0 ? (
                            <><strong style={{ color: REPORT.heading, fontWeight: 600 }}>{step.slice(0, colonIdx + 1)}</strong>{step.slice(colonIdx + 1)}</>
                          ) : step}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Phase 1 Emissions Profile */}
              {ep && (
                <div className="no-break" style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                    {isUvalde ? 'Illustrative Emissions Profile' : 'Phase 1 Emissions Profile'} ({Math.round(ep.mw).toLocaleString()} MW)
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <div style={{ flex: 1, padding: '8px 10px', background: REPORT.white, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: REPORT.caution }}>
                        ~{ep.ghg_tpy >= 1000000 ? `${(ep.ghg_tpy / 1000000).toFixed(1)}M` : `${Math.round(ep.ghg_tpy / 1000).toLocaleString()}K`}
                      </div>
                      <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>tCO2e/yr</div>
                      <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Reporting threshold: 25,000 (40 CFR 98)</div>
                    </div>
                    <div style={{ flex: 1, padding: '8px 10px', background: REPORT.white, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: REPORT.pass }}>
                        ~{ladder.noxTpy} TPY
                      </div>
                      <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>NOx Emissions</div>
                      <div style={{ fontSize: '9px', color: REPORT.tertiary }}>{ladder.noxPct}% of 250 TPY threshold</div>
                    </div>
                    <div style={{ flex: 1, padding: '8px 10px', background: REPORT.white, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: isUvalde || isMeiners ? REPORT.caution : ep.gas_vs_capacity === 'within' ? REPORT.pass : ep.gas_vs_capacity === 'exceeds' ? REPORT.fail : REPORT.tertiary }}>
                        ~{ep.gas_mmcf_day} MMcf/d
                      </div>
                      <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Gas Requirement</div>
                      <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
                        {isUvalde ? '160 MW screening example; project scale and firm gas delivery remain unconfirmed.' : isMeiners ? 'Modeled phase demand. Firm delivery and pressure remain unconfirmed.' : isA1CarveOut ? 'Supply confirmed — tranche sizing via firm transport' : ep.gas_vs_capacity === 'within' ? 'Within pipeline rated capacity' : ep.gas_vs_capacity === 'exceeds' ? 'Exceeds pipeline rated capacity' : 'Pipeline capacity unconfirmed'}
                      </div>
                    </div>
                  </div>
                  {/* CO callout */}
                  <div style={{
                    padding: '6px 10px', borderRadius: '6px', border: `1px solid ${REPORT.caution}20`,
                    background: `${REPORT.caution}04`, marginBottom: '6px', fontSize: '9px', color: REPORT.secondary, lineHeight: 1.5,
                  }}>
                    <strong style={{ color: REPORT.caution, fontWeight: 600 }}>CO may be the binding pollutant.</strong>{' '}
                    {ladder.coWarning.replace('CO may be the binding pollutant. ', '')}
                  </div>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary, fontStyle: 'italic' }}>
                    {isMeiners
                      ? 'NOx pathway screen: 160 MW × 0.14 lb/MWh permit limit × 8,760 hr/yr ÷ 2,000 lb/ton = ~98 TPY. This is not an equipment forecast or assurance of Standard Permit eligibility. Equipment, controls, operating profile, source aggregation and other pollutants remain to be evaluated.'
                      : isA1CarveOut
                      ? 'NOx: TCEQ EGU Standard Permit (2007-amended) limit — 0.14 lb/MWh for units >300 hr/yr. (The separate Jan 30, 2025 natural-gas EGU standard permit — six engines, 400 hr/yr — is a limited-use instrument and not this pathway.) Actual emissions vary by engine platform and load profile. GHG calibrated from 3 TX BTM TCEQ permits.'
                      : 'NOx: TCEQ EGU Standard Permit limit (0.14 lb/MWh, 2007-amended, East TX units >300 hr/yr). Actual emissions vary by engine platform and load profile. GHG calibrated from 3 TX BTM TCEQ permits.'}
                  </div>
                </div>
              )}
            </>
          );
        })() : (
          <>
            {/* Fallback: single badge for non-TX / nonattainment */}
            <div className="no-break" style={{
              padding: '10px 14px', borderRadius: '6px', marginBottom: '10px',
              border: `1px solid ${permitPathway.riskLevel === 'low' ? REPORT.pass : permitPathway.riskLevel === 'high' ? REPORT.fail : REPORT.caution}40`,
              background: `${permitPathway.riskLevel === 'low' ? REPORT.pass : permitPathway.riskLevel === 'high' ? REPORT.fail : REPORT.caution}08`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: REPORT.heading }}>{permitPathway.pathway}</div>
                  {permitPathway.agency && <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '1px' }}>{permitPathway.agency}</div>}
                </div>
                <div style={{
                  marginLeft: 'auto', flexShrink: 0,
                  fontSize: '14px', fontWeight: 700, fontFamily: 'Georgia, serif',
                  color: permitPathway.riskLevel === 'low' ? REPORT.pass : permitPathway.riskLevel === 'high' ? REPORT.fail : REPORT.caution,
                }}>
                  {permitPathway.timeline}
                </div>
              </div>
              {permitPathway.bullets && permitPathway.bullets.length > 0 && (
                <ul style={{ margin: '0 0 6px 0', paddingLeft: '16px', fontSize: '10px', color: REPORT.secondary, lineHeight: 1.6 }}>
                  {permitPathway.majorSourceThreshold && (
                    <li><span style={{ color: REPORT.heading, fontWeight: 600 }}>Major source threshold: {permitPathway.majorSourceThreshold}</span></li>
                  )}
                  {permitPathway.offsetRatio && (
                    <li><span style={{ color: REPORT.fail, fontWeight: 600 }}>Emission offsets required at {permitPathway.offsetRatio} ratio</span></li>
                  )}
                  {permitPathway.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </div>

            {/* Estimated Facility Profile (non-ladder) */}
            {permitPathway.emissionsProfile && (() => {
              const ep = permitPathway.emissionsProfile;
              const areaLabel = parcel.in_nonattainment ? 'nonattainment' : 'attainment';
              const ghgColor = ep.ghg_tpy < 100000 ? REPORT.pass : ep.ghg_tpy < 1000000 ? REPORT.caution : REPORT.fail;
              const regColor = ep.major_source_status === 'below' ? REPORT.pass : ep.major_source_status === 'uncontrolled-only' ? REPORT.caution : REPORT.fail;
              const gasColor = isUvalde || isMeiners ? REPORT.caution : ep.gas_vs_capacity === 'within' ? REPORT.pass : ep.gas_vs_capacity === 'exceeds' ? REPORT.fail : REPORT.tertiary;
              return (
                <div className="no-break" style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                    Estimated Facility Profile at {Math.round(ep.mw).toLocaleString()} MW
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <div style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: ghgColor }}>
                        ~{ep.ghg_tpy >= 1000000 ? `${(ep.ghg_tpy / 1000000).toFixed(1)}M` : `${Math.round(ep.ghg_tpy / 1000).toLocaleString()}K`}
                      </div>
                      <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>tCO2e/yr</div>
                      <div style={{ fontSize: '9px', color: REPORT.tertiary }}>40 CFR Part 98 threshold: 25,000</div>
                    </div>
                    <div style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: regColor }}>
                        ~{ep.regulated_tpy.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>TPY Regulated</div>
                      <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Major source: {ep.major_source_mw_scr} MW w/SCR ({areaLabel})</div>
                    </div>
                    <div style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: gasColor }}>
                        ~{ep.gas_bcf_day ? `${ep.gas_bcf_day} Bcf/d` : `${ep.gas_mmcf_day} MMcf/d`}
                      </div>
                      <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Gas Requirement</div>
                      <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
                        {isUvalde ? '160 MW screening example; project scale and firm gas delivery remain unconfirmed.' : isMeiners ? 'Modeled phase demand. Firm delivery and pressure remain unconfirmed.' : isA1CarveOut ? 'Supply confirmed — tranche sizing via firm transport' : ep.gas_vs_capacity === 'within' ? 'Within pipeline rated capacity' : ep.gas_vs_capacity === 'exceeds' ? 'Exceeds pipeline rated capacity' : 'Pipeline capacity unconfirmed'}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary, fontStyle: 'italic' }}>
                    Emission estimates based on gas engine engineering specs (not state-specific). Actual emissions depend on engine type, controls, and operating profile{parcel.state === 'WV' ? '. RICE use the generic 250 TPY major source threshold \u2014 confirm source classification with WV DEP-DAQ during pre-application' : ''}.
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* Air quality headroom — binding constraint */}
        {(() => {
          const aq = data.airQualityHeadroom;
          if (!aq || aq.length === 0) {
            return (
              <div className="no-break" style={{ fontSize: '10px', color: REPORT.secondary, padding: '6px 10px', marginBottom: '8px', background: REPORT.surface, borderRadius: '4px', border: `1px solid ${REPORT.rule}` }}>
                <strong>Air quality monitors:</strong> {isUvalde ? 'None in county. No nonattainment designation; permit eligibility requires equipment-specific emissions review.' : 'None in county. No nonattainment designation. Standard permit pathway expected.'}
              </div>
            );
          }
          const binding = aq[0];
          const headroom = Number(binding.headroom);
          const hrPct = Number(binding.headroom_pct);
          const exceeds = headroom < 0;
          const pct = Math.abs(hrPct);
          const usedPct = Math.round(100 - hrPct);
          const color = exceeds ? REPORT.fail : hrPct < 15 ? REPORT.caution : REPORT.pass;
          const pollutantNames: Record<string, string> = {
            'PM-2.5': 'fine particles (PM-2.5)', 'PM-10': 'coarse particles (PM-10)',
            'Ozone': 'ozone', 'SO2': 'sulfur dioxide', 'NO2': 'nitrogen dioxide',
            'CO': 'carbon monoxide', 'Lead': 'lead',
          };
          const pollutantPlain = pollutantNames[binding.pollutant] || binding.pollutant;
          const verdictText = exceeds
            ? `Air quality exceeds the federal limit for ${pollutantPlain}. This area may face nonattainment designation, which would significantly increase permitting time and cost.`
            : hrPct < 15
              ? `Air quality is tight \u2014 ${pollutantPlain} is at ${usedPct}% of the federal limit, leaving limited room for a new emission source. Expect closer regulatory scrutiny.`
              : hrPct < 30
                ? `Air quality has moderate room \u2014 ${pollutantPlain} is at ${usedPct}% of the federal limit. A gas plant is permittable but the margin warrants early engagement with the air agency.`
                : `Air quality is clean \u2014 ${pollutantPlain} (the tightest pollutant) uses ${usedPct}% of the federal limit, leaving comfortable room for a gas plant.`;
          return (
            <div className="no-break" style={{ fontSize: '10px', color: REPORT.body, padding: '8px 10px', marginBottom: '8px', background: REPORT.surface, borderRadius: '4px', border: `1px solid ${color}40` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color, flexShrink: 0 }}>
                  {exceeds ? `\u2717` : hrPct < 15 ? '\u26A0' : '\u2713'}
                </div>
                <div>
                  <strong style={{ color }}>{exceeds ? 'Exceeds federal limit' : hrPct < 15 ? 'Near federal limit' : 'Within federal limits'}</strong>
                  <div style={{ marginTop: '2px' }}>{verdictText}</div>
                </div>
              </div>
              <div style={{ marginTop: '6px', fontSize: '9px', color: REPORT.tertiary }}>
                {binding.pollutant}: {binding.design_value} {binding.design_value_units} measured vs {binding.naaqs_threshold} {binding.design_value_units} NAAQS standard
                {exceeds ? ` (${pct.toFixed(1)}% over)` : ` (${hrPct.toFixed(1)}% headroom)`}
              </div>
              {aq.length > 1 && Number(aq[1].headroom_pct) < 20 && (() => {
                const second = aq[1];
                const secondPlain = pollutantNames[second.pollutant] || second.pollutant;
                const secondExceeds = Number(second.headroom) < 0;
                return (
                  <div style={{ marginTop: '4px', fontSize: '9px', color: REPORT.secondary }}>
                    Also watch: {secondPlain} {secondExceeds
                      ? `exceeds the federal limit by ${Math.abs(Number(second.headroom_pct)).toFixed(1)}%`
                      : `is at ${Math.round(100 - Number(second.headroom_pct))}% of the federal limit`}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: parcel.in_nonattainment ? REPORT.fail : parcel.in_nonattainment == null ? REPORT.tertiary : REPORT.pass }}>
              {parcel.in_nonattainment == null ? '\u2014' : parcel.in_nonattainment ? 'No' : 'Yes'}
            </div>
            <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>EPA Attainment</div>
            <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
              {parcel.in_nonattainment == null ? 'Data pending' : parcel.in_nonattainment ? 'Nonattainment - NNSR review required' : isUvalde ? 'Attainment screen; permit review pending' : 'Standard permit pathway'}
            </div>
          </div>
          {parcel.ej_max_pctile != null && (
            <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: Number(parcel.ej_max_pctile) >= 80 ? REPORT.fail : Number(parcel.ej_max_pctile) >= 50 ? REPORT.caution : REPORT.pass }}>
                {Math.round(Number(parcel.ej_max_pctile))}th
              </div>
              <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>EJ Percentile</div>
              <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
                {Number(parcel.ej_max_pctile) >= 80 ? 'EJ community - enhanced engagement' : Number(parcel.ej_max_pctile) >= 50 ? 'Moderate EJ exposure' : 'Low EJ concern'}
              </div>
            </div>
          )}
        </div>
        {parcel.nonattainment_areas && parcel.nonattainment_areas.length > 0 && (
          <div style={{ fontSize: '10px', color: REPORT.secondary, marginBottom: '4px' }}>
            {parcel.nonattainment_areas.map((na, i) => (
              <div key={i}>{na.pollutant}: {na.classification} ({na.area_name})</div>
            ))}
          </div>
        )}
        {parcel.ej_detail?.max_ej_indicator && (
          <div style={{ fontSize: '10px', color: REPORT.secondary, marginBottom: '4px' }}>
            Primary EJ indicator: {parcel.ej_detail.max_ej_indicator}
          </div>
        )}
        {parcel.ej_max_pctile != null && Number(parcel.ej_max_pctile) >= 50 && (
          <div style={{ marginTop: '6px', padding: '6px 10px', background: REPORT.surface, borderRadius: '4px', border: `1px solid ${REPORT.rule}` }}>
            <div style={{ fontSize: '10px', fontWeight: 600, color: Number(parcel.ej_max_pctile) >= 80 ? REPORT.fail : REPORT.caution, marginBottom: '2px' }}>
              EJ Exposure: {Math.round(Number(parcel.ej_max_pctile))}th percentile {Number(parcel.ej_max_pctile) >= 80 ? ' - EJ community' : ' - moderate'}
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '10px', color: REPORT.secondary, lineHeight: 1.6 }}>
              {Number(parcel.ej_max_pctile) >= 80 ? (<>
                <li>{isA1CarveOut ? 'Maximum EJ indicator above the 80th percentile — the screening threshold agencies have used for enhanced review' : 'Classified as EJ community (>80th percentile)'}</li>
                <li>Enhanced public notice and extended comment periods likely required</li>
                <li>Community engagement per EPA EJ guidance</li>
                <li>Factor 2–4 additional months into permitting timeline</li>
              </>) : (<>
                <li>{isA1CarveOut ? 'Maximum EJ indicator below the 80th-percentile screening threshold (screening data, not an official designation)' : 'Not classified as EJ community (>80th threshold)'}</li>
                <li>Proactive community engagement during permitting recommended</li>
                <li>May reduce risk of delays from public opposition</li>
              </>)}
            </ul>
          </div>
        )}
        {/* Regulatory watch: what changes if county gets designated / PM2.5 revision */}
        {(() => {
          const aq = data.airQualityHeadroom;
          const alerts: Array<{ title: string; bullets: string[] }> = [];

          // County exceeds NAAQS but not yet designated nonattainment
          if (aq && aq.length > 0 && !parcel.in_nonattainment) {
            const exceeding = aq.filter(r => Number(r.headroom) < 0);
            if (exceeding.length > 0) {
              const worst = exceeding[0];
              alerts.push({
                title: `${worst.pollutant} exceeds NAAQS - designation pending`,
                bullets: [
                  `Current design value: ${worst.design_value} ${worst.design_value_units} (standard: ${worst.naaqs_threshold} ${worst.design_value_units})`,
                  'County has not yet been formally designated nonattainment',
                  'If designated, permit pathway changes to NNSR (12\u201318+ months)',
                  'Emission offset credits would be required',
                  'Major source thresholds may be reduced',
                ],
              });
            }
          }

          // PM2.5 regulatory uncertainty — show if county's PM2.5 annual DV > 9.0 µg/m³
          if (aq) {
            const pm25annual = aq.find(r => r.pollutant === 'PM-2.5' && r.standard === '2012');
            if (pm25annual && Number(pm25annual.design_value) > 9.0) {
              alerts.push({
                title: 'PM2.5 NAAQS revision — not being implemented',
                bullets: [
                  'Revised annual PM2.5 standard: 9 \u00B5g/m\u00B3 (finalized Feb 2024, prior: 12 \u00B5g/m\u00B3)',
                  `This county\u2019s PM2.5 design value: ${pm25annual.design_value} \u00B5g/m\u00B3`,
                  'County would exceed the revised standard if enforced',
                  'EPA has not issued nonattainment designations under the 9 \u00B5g/m\u00B3 standard',
                  'EPA filed court motion for vacatur (Nov 2025) and is pursuing formal rescission',
                ],
              });
            }
          }

          if (alerts.length === 0) return null;
          return (
            <div style={{ marginTop: '8px', padding: '8px 10px', background: `${REPORT.caution}08`, borderRadius: '4px', border: `1px solid ${REPORT.caution}30` }}>
              <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.caution, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                Regulatory Watch
              </div>
              {alerts.map((alert, i) => (
                <div key={i} style={{ marginBottom: i < alerts.length - 1 ? '8px' : 0 }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: REPORT.heading, marginBottom: '2px' }}>{alert.title}</div>
                  <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '10px', color: REPORT.secondary, lineHeight: 1.6 }}>
                    {alert.bullets.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          );
        })()}
        {/* Regional BTM Precedents — curated for Paces (Lufkin highlighted), auto for everyone else */}
        {(() => {
          const precedents = isPaces ? PACES_PRECEDENTS : permitPathway.precedents;
          if (!precedents || precedents.length === 0) return null;
          return (
            <div style={{ marginTop: '8px', marginBottom: '6px' }}>
              <div style={{ fontSize: '9px', fontWeight: 700, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                {parcel.state || 'Regional'} BTM Precedents
              </div>
              {precedents.map((p, i) => {
                const isHighlight = (p as PacesPrecedent).highlight === true;
                return (
                  <div key={i} className="no-break" style={{
                    padding: '8px 10px',
                    background: REPORT.white,
                    borderRadius: '6px',
                    border: `1px solid ${REPORT.rule}`,
                    marginBottom: '6px',
                  }}>
                    {isHighlight && (
                      <div style={{ fontSize: '8px', fontWeight: 700, color: REPORT.accent, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px' }}>
                        In-AOI precedent · the working playbook
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: isHighlight ? REPORT.accent : REPORT.heading }}>{p.name}</div>
                      <div style={{ fontSize: '10px', fontWeight: 600, fontFamily: 'Georgia, serif', color: REPORT.pass }}>{p.timeline}</div>
                    </div>
                    <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '1px' }}>{p.location}</div>
                    <div style={{ fontSize: '9px', color: REPORT.secondary, marginTop: '4px', lineHeight: 1.5 }}>{p.detail}</div>
                  </div>
                );
              })}
            </div>
          );
        })()}
        {/* Scope line + source */}
        {permitPathway.ladder && (
          <div style={{ fontSize: '8px', color: REPORT.tertiary, textAlign: 'center', marginTop: '12px', paddingTop: '8px', borderTop: `1px solid ${REPORT.rule}` }}>
            {permitPathway.ladder.scopeLine}
          </div>
        )}
        <SourceFootnote>Source: EPA Green Book, Design Values 2022-2024, EJScreen v2.3.{parcel.state === 'TX' ? (isA1CarveOut
          ? ' Permit facts verified against TCEQ records July 2026: Longhorn/Crusoe Reg 177263 (Taylor Co.), VoltaGrid/Oracle Stargate Reg 180842 (Shackelford Co.), Misae Gas Power (San Antonio area), Sandow Lakes (Lee Co.), GW Ranch (Pecos Co.), Fermi Matador (Carson Co.).'
          : ' Processing times: TCEQ Biennial Report FY 2023-2024 Appendix B. Emission estimates: TCEQ permits for Sandow Lakes (Lee Co.), GW Ranch (Pecos Co.), Fermi Matador (Carson Co.), Misae Gas Power (Bexar Co.), VoltaGrid SAT-1 (Bexar Co.).') : parcel.state === 'WV' ? ' WV DEP Rules 45CSR13/14/30. HB 2014 + HB 4983 microgrid framework. Emission factors are engine-platform based (turbine specs, not state-specific); validated against filed TX BTM permit applications. RICE use the generic 250 TPY major source threshold; confirm with DEP-DAQ that facility is not classified as steam electric (100 TPY).' : ' Emission factors are engine-platform based; validated against filed TX BTM permit applications. Actual permitting occurs under state air agency in project state.'}</SourceFootnote>
      </div>
      </>}

      {/* ── Physical Site ── */}
      <div ref={physicalSiteRef} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Physical Site</SectionTitle>
        </div>
        {data.slopeGrids && <SlopeMethodNote grids={data.slopeGrids} />}
        {isMeiners && (
          <div style={{ marginBottom: 10, padding: '8px 10px', border: `1px solid ${REPORT.rule}`, background: REPORT.sand, color: REPORT.body, fontSize: '10px', lineHeight: 1.5 }}>
            <strong>Meiners three-account block — 1,803.86 CAD acres / 1,957 mapped acres; screening geometry only.</strong><br />
            Current 500-ft screen leaves about 1,354 acres; largest piece about 730 acres. At 1,000 ft, about 1,128 acres remain; largest piece about 594 acres. The screen excludes the TWDB flood area, active/permitted well rings and a 50-ft-total gathering corridor (25 ft each side). Surface-use terms, survey, easements and operator rights control the buildable pad.
            <div className="briefing-no-print" style={{ marginTop: 7 }}>
              <button type="button" onClick={() => setFootprintScreen(500)} style={{ border: `1px solid ${REPORT.highlight}`, background: REPORT.surface, color: REPORT.highlight, borderRadius: 4, padding: '5px 9px', fontSize: '9px', fontWeight: 600, cursor: 'pointer' }}>
                Inspect well exclusions and candidate footprints
              </button>
            </div>
          </div>
        )}
        {(() => {
          // Assembly-aware terrain values: when the parcel sits inside a curated
          // multi-parcel carve-out, the IC is acquiring all 4 parcels — slope
          // distribution and acreage should reflect the assembly, not the rep.
          const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
          const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
          const useAgg = aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
          // Retained terrain keeps its earlier outline denominator; withheld rows
          // cannot fall back to analyzed-core values.
          const altTerrain = isA1CarveOut && activeLayoutRow?.slope_mean_pct_upland != null && activeLayoutRow.slope_under5_upland_ac != null && activeLayoutRow.slope_under10_upland_ac != null && activeLayoutRow.upland_ac > 0 ? activeLayoutRow : null;
          const altShare = (ac: number | null | undefined) => altTerrain && ac != null ? (Number(ac) / (altTerrain.terrain_reference_upland_ac ?? altTerrain.upland_ac)) * 100 : null;
          const displayAcres = activeLayoutTerrainWithheld ? null : altTerrain ? (altTerrain.terrain_reference_upland_ac ?? altTerrain.upland_ac) : useAgg ? Number(aggAcres) : (parcel.acreage != null ? Number(parcel.acreage) : null);
          const displayAvgSlope = altTerrain ? Number(altTerrain.slope_mean_pct_upland)
            : activeLayoutTerrainWithheld
            ? null
            : useAgg && data.carveOutAggregate?.weightedAvgSlopePct != null
            ? Number(data.carveOutAggregate.weightedAvgSlopePct)
            : (parcel.avg_slope_pct != null ? Number(parcel.avg_slope_pct) : null);
          const displayPctUnder2 = altTerrain ? altShare(altTerrain.slope_under2_upland_ac)
            : activeLayoutTerrainWithheld
            ? null
            : useAgg && data.carveOutAggregate?.weightedPctUnder2Pct != null
            ? Number(data.carveOutAggregate.weightedPctUnder2Pct)
            : (parcel.pct_under_2pct != null ? Number(parcel.pct_under_2pct) : null);
          const displayPctUnder5 = altTerrain ? altShare(altTerrain.slope_under5_upland_ac)
            : activeLayoutTerrainWithheld
            ? null
            : useAgg && data.carveOutAggregate?.weightedPctUnder5Pct != null
            ? Number(data.carveOutAggregate.weightedPctUnder5Pct)
            : (parcel.pct_under_5pct != null ? Number(parcel.pct_under_5pct) : null);
          const displayPctUnder10 = altTerrain ? altShare(altTerrain.slope_under10_upland_ac)
            : activeLayoutTerrainWithheld
            ? null
            : useAgg && data.carveOutAggregate?.weightedPctUnder10Pct != null
            ? Number(data.carveOutAggregate.weightedPctUnder10Pct)
            : (parcel.pct_under_10pct != null ? Number(parcel.pct_under_10pct) : null);
          const displayPctUnder15 = altTerrain ? altShare(altTerrain.slope_under15_upland_ac)
            : activeLayoutTerrainWithheld
            ? null
            : useAgg && data.carveOutAggregate?.weightedPctUnder15Pct != null
            ? Number(data.carveOutAggregate.weightedPctUnder15Pct)
            : (parcel.pct_under_15pct != null ? Number(parcel.pct_under_15pct) : null);
          // Crown Pine A1: the 5 m end of the 3–5 m grid range (null on every other briefing).
          const grid5 = !altTerrain && !activeLayoutTerrainWithheld && useAgg ? data.carveOutAggregate?.slopeGrid5m : undefined;
          const at5m = altTerrain && altTerrain.slope_under5_upland_ac_5m != null
            ? { u2: altShare(altTerrain.slope_under2_upland_ac_5m), u5: altShare(altTerrain.slope_under5_upland_ac_5m), u10: altShare(altTerrain.slope_under10_upland_ac_5m), u15: altShare(altTerrain.slope_under15_upland_ac_5m) }
            : grid5 ? { u2: grid5.pctUnder2Pct, u5: grid5.pctUnder5Pct, u10: grid5.pctUnder10Pct, u15: grid5.pctUnder15Pct }
            : null;
          const acAt5m = (pct: number | null | undefined) => (displayAcres != null && pct != null ? (displayAcres * pct) / 100 : null);
          const acreageLabel = useAgg ? 'assembly' : 'parcel';

          const factors: string[] = [];
          // A1 Alt: displayAvgSlope already follows the outline; the flood clause does below.
          if (isMeiners) factors.push('1.5–1.8% average slope by section with 97–98% under 5% (distributed terrain screen)');
          else if (displayAvgSlope != null) factors.push(displayAvgSlope < 2 ? 'ideal terrain' : displayAvgSlope < 5 ? 'manageable terrain' : 'rolling terrain');
          if (isMeiners) factors.push('23% in the TWDB 1% flood screen, concentrated along Cibolo Creek');
          else if (isA1CarveOut) factors.push(activeLayoutRow
            ? `${Math.round(activeLayoutRow.flood_100yr_ac).toLocaleString()} ac FEMA Zone A (${((activeLayoutRow.flood_100yr_ac / (activeCalculationAcres ?? activeLayoutRow.acres)) * 100).toFixed(1)}% of the ${activeLayoutRow.name} mapped outline) with ${Math.round(activeLayoutRow.upland_ac).toLocaleString()} ac of upland outside it and the NWI wetlands`
            : '~99.5% FEMA Zone X; 11.32 ac Zone A in one drainage corridor');
          else if ((parcel as any).structured_intel?.flood_gate?.status === 'PASS') factors.push('flood screened via the TWDB 1% overlay');
          else if (floodSev.level === 'severe') factors.push(`${Math.round(floodSev.pct ?? 0)}% in FEMA Zone A`);
          else if (floodSev.level === 'partial') factors.push('drainage corridor');
          else if (floodSev.level === 'mostly_clear') factors.push('mostly clear of FEMA flood zones');
          else if (floodSev.level === 'clear') factors.push('clear of FEMA flood zones');
          const hasLandReadiness = hasLandownerEarthwork(parcel);
          const terrainIsChallenge = displayAvgSlope != null && displayAvgSlope >= 5;
          const buildableAcUnder5 = displayAcres != null && displayPctUnder5 != null
            ? Math.round(displayAcres * displayPctUnder5 / 100)
            : null;
          const idealAcUnder2 = displayAcres != null && displayPctUnder2 != null
            ? Math.round(displayAcres * displayPctUnder2 / 100)
            : null;

          return (
            <>
              {factors.length > 0 && (
                <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '10px', }}>
                  Physical site assessment shows {factors.join(', ')}.
                  {hasLandReadiness && terrainIsChallenge && (
                    <span style={{ color: REPORT.pass, fontStyle: 'italic' }}> Landowner has committed to earthwork and site preparation for qualified buyers.</span>
                  )}
                </div>
              )}
              {displayAvgSlope == null ? (
                <div style={{ padding: '12px 16px', background: REPORT.surface, border: `1px solid ${REPORT.caution}`, borderRadius: 6, marginBottom: 6 }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: REPORT.caution }}>Terrain data unavailable</div>
                  <div style={{ fontSize: '10px', color: REPORT.secondary, marginTop: 4 }}>{activeLayoutTerrainWithheld ? `${activeLayoutRow?.name} slope and elevation measurements are withheld because the retained raster is qualified only for local prototype use. Independently qualify the source before using terrain metrics for this outline.` : `Slope assessment incomplete for this ${acreageLabel}. Buildability cannot be verified.`}</div>
                </div>
              ) : (
                <>
                  {/* Buildable terrain lead — anchors on the under-5% number (the threshold a
                      BTM developer can lay pads on without staged grading). When the assembly
                      is large enough, this is a meaningful absolute number — show it. */}
                  {buildableAcUnder5 != null && buildableAcUnder5 >= 100 && displayAcres != null && (
                    <div style={{ marginBottom: 8, padding: '10px 14px', background: REPORT.surface, border: `1px solid ${REPORT.rule}`, borderRadius: 6 }}>
                      <span style={{ fontSize: '18px', fontWeight: 700, color: REPORT.heading }}>
                        {acOr(buildableAcUnder5, acAt5m(at5m?.u5))}
                      </span>
                      {/* A1: terrain is measured on the analyzed core only — the western
                          band is in the premises but outside every slope/flood/water
                          computation, so label the denominator for what it is. */}
                      <span style={{ fontSize: '11px', color: REPORT.body }}>{isUvalde ? ' ac under 5% slope across both blocks. This distributed terrain area is not a confirmed development footprint; airport surfaces, title, wetlands and drainage still apply.' : isMeiners ? ' ac under 5% slope (slope-only, distributed area; not a contiguous pad).' : isCooperDraw ? ' ac under 5% slope, mostly in narrow strips between draws. The largest connected piece outside mapped flood and wetland areas and at least 330 ft wide is about 130 ac (pad screen in the evidence section).' : <> of {data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? Number(displayAcres).toLocaleString() : Math.round(displayAcres).toLocaleString()} {data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? (altTerrain ? `${altTerrain.name}${activeLayoutTerrainReference ? ' reference' : ''} upland` : 'analyzed-core') : acreageLabel} acres {data.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
                        ? <>sit under 5% grade{displayPctUnder10 != null ? <>, and <strong>{acOr(displayAcres * displayPctUnder10 / 100, acAt5m(at5m?.u10))} ({pctOr(displayPctUnder10, at5m?.u10)}) under 10%</strong></> : ''}{idealAcUnder2 != null && idealAcUnder2 >= 50 ? `; ${acOr(idealAcUnder2, acAt5m(at5m?.u2))} under 2%` : ''} — ample gentle ground for a campus pad (distributed area, siting still selects contiguous pads).</>
                        : <>have buildable slope ({'<'}5%) — pad-ready without staged grading{idealAcUnder2 != null && idealAcUnder2 >= 50 ? `; ${idealAcUnder2.toLocaleString()} of those are under 2% (no grading)` : ''}.</>}</>}</span>
                      <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: 4 }}>{data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? (altTerrain ? activeLayoutTerrainReference ? "Earlier Alternative #3 terrain screening retained as a reference, on the 1 m lidar averaged to 3 m and 5 m grids — distributed area, not a contiguous pad" : `${SLOPE_GRID_SOURCE} on the outline's upland (outline less the 100-yr floodplain and NWI wetlands) — distributed area, not a contiguous pad` : `${SLOPE_GRID_SOURCE}, exact footprint — distributed area, not a contiguous pad`) : `USGS DEM sampling, area-weighted across ${useAgg ? `${aggParcelCount}-parcel assembly` : 'parcel'} — not a contiguity analysis`}</div>
                    </div>
                  )}
                  {displayAvgSlope >= 5 && (() => {
                    // Crown Pine A1: reframe the generic "grading costs expected" banner to
                    // the honest-but-positive native read — most of the tract is under 10%
                    // and the steep ground is drainage ribbons, not broad slopes.
                    if (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1') {
                      return (
                        <div style={{ padding: '10px 14px', background: REPORT.caution + '12', border: `1px solid ${REPORT.caution}`, borderRadius: 6, marginBottom: 6 }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: REPORT.caution }}>Rolling upland terrain — selective pad siting</div>
                          <div style={{ fontSize: '10px', color: REPORT.secondary, marginTop: 4 }}>
                            {altTerrain && altTerrain.elev_p5_ft_upland != null && altTerrain.elev_p95_ft_upland != null && altTerrain.elev_range_ft_gross != null
                              ? `The central 90% of the ${altTerrain.name} upland lies within a ${Math.round(Number(altTerrain.elev_p95_ft_upland) - Number(altTerrain.elev_p5_ft_upland))}-foot elevation band (${Math.round(Number(altTerrain.elev_p5_ft_upland))}–${Math.round(Number(altTerrain.elev_p95_ft_upland))} ft). The full ${Math.round(Number(altTerrain.elev_range_ft_gross))}-foot min-to-max range of the outline reflects localized high and low extremes, while steeper ground is concentrated along narrow drainage corridors. Native 1 m USGS 3DEP lidar.`
                              : 'The central 90% of the tract lies within a 70-foot elevation band (224–294 ft). The full 182-foot min-to-max range reflects localized high and low extremes, while steeper ground is concentrated along narrow drainage corridors. Native 1 m USGS 3DEP lidar.'}
                          </div>
                        </div>
                      );
                    }
                    const earthworkDisclosed = hasLandownerEarthwork(parcel);
                    const accentColor = earthworkDisclosed ? REPORT.caution : REPORT.fail;
                    const headline = earthworkDisclosed ? 'Rolling terrain — grading required' : 'Rolling terrain — grading costs expected';
                    return (
                      <div style={{ padding: '10px 14px', background: accentColor + '12', border: `1px solid ${accentColor}`, borderRadius: 6, marginBottom: 6 }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: accentColor }}>{headline}</div>
                        {earthworkDisclosed && (
                          <div style={{ fontSize: '10px', color: REPORT.body, marginTop: 6 }}>
                            Mitigated: landowner has committed to earthwork and site preparation for qualified buyers.
                          </div>
                        )}
                        {displayPctUnder5 != null && (
                          <div style={{ fontSize: '10px', color: REPORT.secondary, marginTop: 4 }}>
                            {Math.round(displayPctUnder5)}% of {acreageLabel} has acceptable slope ({'<'}5%){displayPctUnder10 != null ? `; ${Math.round(displayPctUnder10)}% under 10%` : ''}.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                      <SlopeHistogram
                        pctUnder5={displayPctUnder5}
                        pctUnder10={displayPctUnder10}
                        pctUnder15={displayPctUnder15}
                        at5m={at5m?.u5 != null && at5m.u10 != null && at5m.u15 != null ? { pctUnder5: at5m.u5, pctUnder10: at5m.u10, pctUnder15: at5m.u15 } : undefined}
                        acreage={isMeiners || isUvalde ? null : displayAcres}
                        areaLabel={isUvalde ? "assembly" : undefined}
                        landownerEarthwork={hasLandownerEarthwork(parcel)}
                      />
                    </div>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                      <ElevationCrossSection parcelId={parcel.id} rangeLabel={data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? 'Representative transect variation' : undefined} />
                    </div>
                  </div>
                </>
              )}
            </>
          );
        })()}
        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
          {(parcel.avg_slope_pct != null || isMeiners) && (() => {
            // Crown Pine A1: the stat tile reads the rep parcel (3.01%); show the
            // area-weighted assembly slope (3.74%) instead so it matches the DD row
            // + narrative. Gated to A1; all other sites read the rep parcel as before.
            // Aggregate slope on the 3–5 m basis: A1, or any carve-out briefing with a slope file.
            const isA1Slope = (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' || data.carveOutAggregate?.slopeGrid5m != null) && data.carveOutAggregate?.weightedAvgSlopePct != null;
            if (activeLayoutTerrainWithheld) {
              return (
                <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.caution}` }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: REPORT.caution }}>—</div>
                  <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Avg Slope · Withheld</div>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Selected-outline source is local prototype</div>
                </div>
              );
            }
            // A1 Alt: the tile follows the outline on the map — mean on its upland.
            const altSlopeRow = isA1Slope && activeLayoutRow?.slope_mean_pct_upland != null && activeLayoutRow.slope_under5_upland_ac != null && activeLayoutRow.slope_under10_upland_ac != null ? activeLayoutRow : null;
            const slopeNum = altSlopeRow ? Number(altSlopeRow.slope_mean_pct_upland) : isA1Slope || isMeiners ? Number(data.carveOutAggregate?.weightedAvgSlopePct) : Number(parcel.avg_slope_pct);
            // Crown Pine A1: slopeNum is the 3 m grid; these are the 5 m end of the range.
            const grid5 = isA1Slope ? data.carveOutAggregate?.slopeGrid5m : undefined;
            const slopeAt5m = altSlopeRow ? altSlopeRow.slope_mean_pct_upland_5m : grid5?.avgSlopePct;
            const earthworkDisclosed = hasLandownerEarthwork(parcel);
            // Mirror the SlopeHistogram 4-tier breakout: <2 / <5 / 5–10 / 10–15 / >=15.
            // 5–10 uses REPORT.passText (same hue family as REPORT.pass, darker + more
            // saturated for text contrast). >=10 stays caution → fail. Crown Pine A1 stays
            // green: on the 3–5 m grids its mean falls in the 2–5% band, whose amber would
            // read as a worse site than the 5–10% green it showed on the raw 1 m grid.
            const slopeColor = isA1Slope && slopeNum < 5 ? REPORT.passText : slopeNum < 2
              ? REPORT.pass
              : slopeNum < 5
                ? REPORT.caution
                : earthworkDisclosed
                  ? REPORT.caution
                  : slopeNum < 10
                    ? REPORT.passText
                    : slopeNum < 15
                      ? REPORT.caution
                      : REPORT.fail;
            const slopeLabel = slopeNum < 2
              ? 'Ideal for development'
              : slopeNum < 5
                ? 'Moderate grading'
                : earthworkDisclosed ? 'Earthwork mitigated' : slopeNum < 10 ? 'Light grading' : 'Staged grading';
            return (
              <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: slopeColor }}>
                  {isA1Slope && !isMeiners ? meanOr(slopeNum, slopeAt5m) : <>{isMeiners ? '1.5–1.8' : parcel.avg_slope_pct}%</>}
                </div>
                <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{isUvalde ? 'Avg Slope · Representative Parcel' : isMeiners ? 'Avg Slope · By Section' : altSlopeRow ? activeLayoutTerrainReference ? 'Avg Slope · Reference upland' : 'Avg Slope · Upland, 3–5 m grids' : 'Avg Slope'}</div>
                <div style={{ fontSize: '9px', color: REPORT.tertiary }}>{isUvalde ? 'Desktop terrain screen' : isMeiners ? '97–98% under 5% · slope-only' : slopeLabel}</div>
                {isA1Slope && (
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
                    {altSlopeRow
                      ? `~${acOr(Number(altSlopeRow.slope_under10_upland_ac), altSlopeRow.slope_under10_upland_ac_5m)} ac under 10% grade · ${acOr(Number(altSlopeRow.slope_under5_upland_ac), altSlopeRow.slope_under5_upland_ac_5m)} ac under 5%`
                      : (() => {
                        const acres = Number(data.carveOutAggregate?.totalAcreage);
                        const u10 = Number(data.carveOutAggregate?.weightedPctUnder10Pct);
                        const u10At5m = grid5?.pctUnder10Pct;
                        return `~${acOr((acres * u10) / 100, u10At5m != null ? (acres * u10At5m) / 100 : null)} ac (${pctOr(u10, u10At5m)}) under 10% grade`;
                      })()}
                  </div>
                )}
              </div>
            );
          })()}
          {parcel.elevation_range_ft != null && (() => {
            // Crown Pine A1: lead with the representative core spread and keep the full
            // 182 ft min–max as transparent secondary. The core = the native 1 m 3DEP
            // P5–P95 band (224.12–293.95 ft → 69.83 ft, from all 9,865,155 cells inside
            // the exact A1 boundary; verified 2026-07-22). parcel.elevation_range_ft is the
            // rep parcel (77 ft), not the assembly, so don't show it for A1.
            const isA1el = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
            if (activeLayoutTerrainWithheld) {
              return (
                <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.caution}` }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: REPORT.caution }}>—</div>
                  <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Elevation · Withheld</div>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Selected-outline source is local prototype</div>
                </div>
              );
            }
            // A1 Alt: the tile follows the outline on the map — same 1 m 3DEP run as the
            // slope fields, served on the layout row (outline min–max + upland relief).
            const altEl = isA1el && activeLayoutRow?.elev_range_ft_gross != null && activeLayoutRow.elev_min_ft != null && activeLayoutRow.elev_max_ft != null ? activeLayoutRow : null;
            return (
            <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: REPORT.heading }}>
                {altEl ? Math.round(Number(altEl.elev_range_ft_gross)).toLocaleString() : isA1el ? '70' : Number(parcel.elevation_range_ft).toLocaleString()} ft
              </div>
              <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{altEl ? activeLayoutTerrainReference ? 'Elevation Range · Reference' : 'Elevation Range · Outline' : isA1el ? 'Core Elevation Spread' : 'Elevation Range'}</div>
              {altEl ? (
                <>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>{activeLayoutTerrainReference ? 'Reference outline' : 'Outline'} min–max: {Math.round(Number(altEl.elev_min_ft))}–{Math.round(Number(altEl.elev_max_ft))} ft (1 m 3DEP)</div>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>{altEl.elev_range_ft_upland != null ? `Upland relief: ${Math.round(Number(altEl.elev_range_ft_upland))} ft · ` : ''}broad fall SSW</div>
                </>
              ) : isA1el ? (
                <>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Central 90%: 224–294 ft</div>
                  <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Full min–max: 182 ft · broad fall SSW</div>
                </>
              ) : (
                <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Across parcel</div>
              )}
            </div>
            );
          })()}
          <div className="no-break" style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: isA1CarveOut && activeLayoutRow ? REPORT.heading : floodSev.color }}>
              {/* A1: ~11.3 ac fall in Zone A. "Minimal" is FEMA's own Zone X term
                  ("area of minimal flood hazard") — defensible and precise.
                  A1 Alt: the tile follows the outline on the map — its upland (outline
                  less the 100-yr floodplain and NWI) leads, the Zone A share beneath. */}
              {isMeiners
                ? '1,504 ac'
                : data.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
                ? (activeLayoutRow ? `${Math.round(activeLayoutRow.upland_ac).toLocaleString()} ac` : 'Minimal')
                : floodSev.buildableAcres != null
                ? `${Math.round(floodSev.buildableAcres).toLocaleString()} ac`
                : floodSev.pct != null
                  ? `${Math.round(100 - (floodSev.pct ?? 0))}%`
                  : (floodSev.level === 'severe' || floodSev.level === 'partial' ? 'Risk' : 'Clear')}
            </div>
            <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>
              {isUvalde ? 'Area outside mapped floodplain' : isMeiners || isCooperDraw ? 'Outside TWDB 1% Screen' :(isA1CarveOut && activeLayoutRow) || floodSev.buildableAcres != null || floodSev.pct != null ? 'Upland Buildable' : 'Flood Risk'}
            </div>
            <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
              {isUvalde ? 'Both tracts · flood-only calculation, not a development footprint' : isMeiners
                ? 'Flood-only, distributed area; not a contiguous pad'
                : data.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
                ? (activeLayoutRow
                  ? `${Math.round(activeLayoutRow.flood_100yr_ac).toLocaleString()} ac FEMA Zone A · ${((activeLayoutRow.flood_100yr_ac / (activeCalculationAcres ?? activeLayoutRow.acres)) * 100).toFixed(1)}% of the ${activeLayoutRow.measured_acres != null ? activeLayoutRow.measured_acres.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Math.round(activeLayoutRow.acres).toLocaleString()}-ac mapped outline`
                  : 'Area of minimal flood hazard')
                : floodSev.subLine}
            </div>
            {data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' && (
              <div style={{ fontSize: '9px', color: REPORT.tertiary }}>
                {activeLayoutRow
                  ? `${Math.round(activeLayoutRow.nwi_wetland_ac).toLocaleString()} ac NWI wetland · analyzed core's 11.32 ac Zone A unchanged`
                  : 'FEMA Zone X (minimal flood hazard); 11.32 ac Zone A of the 2,443.62-ac analyzed core — one drainage corridor'}
              </div>
            )}
          </div>
        </div>
        <SourceFootnote>{isMeiners ? 'Source: USGS 3DEP terrain; TWDB State Flood Plan existing-conditions 1% annual-chance planning screen (non-regulatory; not FEMA NFHL)' : activeLayoutTerrainReference ? activeLayoutRow?.terrain_note : activeLayoutTerrainWithheld ? 'Source: FEMA NFHL; selected-outline terrain withheld pending source qualification' : data.slopeGrids?.source.status === 'lidar_1m' ? 'Source: USGS 3DEP 1 m lidar averaged to 3 m and 5 m grids; FEMA NFHL' : 'Source: USGS DEM, FEMA NFHL'}</SourceFootnote>
        {parcel.climate_design_high_f != null && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: Number(parcel.climate_design_high_f) >= 100 ? REPORT.fail : REPORT.pass }}>
                  {Number(parcel.climate_design_high_f).toFixed(1)}°F
                </div>
                {/* A1: value is a NOAA climate normal (hottest-month avg high), not an
                    ASHRAE design condition — "Design High" would overstate the basis. */}
                <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? 'Summer High' : 'Design High'}</div>
                <div style={{ fontSize: '9px', color: REPORT.tertiary }}>{data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? 'Hottest month avg high (NOAA normal)' : 'Hottest month avg high'}</div>
              </div>
              <div style={{ flex: 1, padding: '8px 10px', background: REPORT.surface, borderRadius: '6px', border: `1px solid ${REPORT.rule}` }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: REPORT.body }}>
                  {parcel.climate_annual_cdd ? Math.round(Number(parcel.climate_annual_cdd)).toLocaleString() : '—'}
                </div>
                <div style={{ fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Annual CDD</div>
                <div style={{ fontSize: '9px', color: REPORT.tertiary }}>Cooling degree days (base 65°F)</div>
              </div>
            </div>
            <SourceFootnote>Source: NOAA Climate Normals 1991–2020{parcel.climate_station_dist_miles ? ` (station ${Number(parcel.climate_station_dist_miles).toFixed(0)} mi away)` : ''}</SourceFootnote>
          </div>
        )}
      </div>

      {/* ── Land & Ownership ── */}
      {/* ── Oil & Gas Surface Activity ── (renders only when a targeted RRC well screen exists for the parcel) */}
      <OilGasWellsSection ref={oilGasWellsRef} data={data} />

      <div ref={landRef} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Land &amp; Ownership</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '10px', }}>
            {/* Curated copy, not payload fields. Both editions now state the same
                land-control posture — one counterparty rather than an assemblage, with
                acreage, boundary, price and terms all still open. Neither carries an
                assessed value, a "one signature" claim, or any willingness-to-sell
                inference; only the owner identity differs between them. */}
            {data.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
              ? (isRedacted
                ? 'A1 comprises a proposed 3,037.93-acre premises held by one private ownership entity: a 2,443.62-acre analyzed core of six contiguous parcels plus a 594.31-acre western band (parcel 21865, in part). The premises present a single ownership counterparty rather than a multi-owner assemblage. Exact acreage, boundary, price, and definitive land-control terms remain subject to negotiation and title/survey diligence.'
                : 'A1 comprises a proposed 3,037.93-acre premises held of record by CROWN PINE TIMBER 1 LP: a 2,443.62-acre analyzed core of six contiguous parcels plus a 594.31-acre western band (parcel 21865, in part). The premises present a single ownership counterparty rather than a multi-owner assemblage. Exact acreage, boundary, price, and definitive land-control terms remain subject to negotiation and title/survey diligence.')
              : isPaces
              ? (isRedacted
                ? 'Held by PACES CREEK WOODLANDS LP — a single-owner LP entity headquartered in Charlotte, NC. The four-parcel, 2,356-acre block trades as one signature; the absentee position simplifies the conversation. Currently listed through HomeLand Properties.'
                : 'Held by PACES CREEK WOODLANDS LP — a single-owner LP entity headquartered in Charlotte, NC. The four-parcel, 2,356-acre block trades as one signature; the absentee position simplifies the conversation. Currently listed through HomeLand Properties at $9,777,400 — $4,150 per acre, institutional timber comps.')
              : isUvalde ? 'Run Well Ranches owns the offered tract. The north tract is separately owned; the eight-parcel assembly total does not imply common ownership or a combined offering. Confirm the conveyed boundary, title and any required north-tract agreement.' : generateLandIntro(parcel)}
          </div>
        </div>
        <InfoRow label="Owner" value={parcel.owner_name} source={(parcel as any).structured_intel?.owner_scope_note} />
        {isEntityOwner(parcel.owner_name) && (
          <InfoRow label="Entity Type" value={getEntityType(parcel.owner_name)} />
        )}
        {(() => {
          const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
          const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
          const useAgg = aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
          if (data.premises) {
            return <InfoRow label="Premises Acreage" value={`${data.premises.total_acres.toLocaleString()} ac`} source={`${data.premises.core_acres.toLocaleString()}-ac analyzed core + ${data.premises.band_acres.toLocaleString()}-ac western band · ${data.premises.parcels}`} />;
          }
          const label = useAgg ? 'Assembly Acreage' : 'Acreage';
          const value = useAgg
            ? `${Number(aggAcres).toLocaleString()} ac`
            : (parcel.acreage ? `${parcel.acreage} ac` : null);
          const source = useAgg ? `${aggParcelCount}-parcel assembly` : undefined;
          return <InfoRow label={label} value={value} source={source} />;
        })()}
        <InfoRow label="Land Use" value={parcel.land_use_category || parcel.land_use_code} />
        {data.countyZoning && (
          <>
            <InfoRow label="County Zoning" value={
              isUvalde ? 'Airport and local restrictions require verification' : data.countyZoning.zoning_status === 'unzoned' ? 'No county zoning'
              : data.countyZoning.zoning_status === 'county-regulated' ? 'County land use authority'
              : data.countyZoning.zoning_status === 'county-zoned' ? 'County zoned'
              : data.countyZoning.zoning_status === 'limited' ? 'Limited county authority'
              : data.countyZoning.zoning_status
            } />
            {data.countyZoning.zoning_notes && !isUvalde && (
              <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '2px', paddingLeft: '12px', lineHeight: 1.5 }}>
                {data.countyZoning.zoning_notes}
              </div>
            )}
          </>
        )}
        {/* A1: absentee is a tax-mailing-address datum, not a transaction signal. */}
        {!isA1CarveOut && !isUvalde && <InfoRow label="Absentee" value={parcel.is_absentee ? 'Yes' : 'No'} />}
        {/* Owner mailing address shown in parcel detail panel, not investment brief */}
        {(() => {
          const aggLandValue = data.carveOutAggregate?.totalLandValue ?? null;
          const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
          const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
          const useAgg = aggLandValue != null && aggLandValue > 0 && aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
          // A1 publishes no county-assessed land value. The negotiated price will
          // land well above the tax basis, so the assessment anchors the reader on
          // the wrong number — fine as a pure tax figure on other briefings, not here.
          // This must return BEFORE the assembly fallback, which would otherwise
          // substitute the core-only $6,327,710 rather than render nothing.
          if (isA1CarveOut) return null;
          const value = useAgg ? Number(aggLandValue) : (parcel.land_value != null ? Number(parcel.land_value) : null);
          if (value == null) return null;
          const label = useAgg ? 'Assembly Land Value' : 'Land Value';
          const source = useAgg ? `${aggParcelCount}-parcel assessed` : undefined;
          return <InfoRow label={label} value={`$${value.toLocaleString()}`} source={source} />;
        })()}
        {parcel.colocated_owners && parcel.colocated_owners.length > 0 && (
          <div style={{ marginTop: '6px', fontSize: '10px', color: REPORT.secondary }}>
            Co-located: {parcel.colocated_owners.map(co => co.owner_name).join(', ')}
          </div>
        )}
        {/* A1 has no listing today; this guard is preventive. A scraped nearby or
            on-site listing must never reintroduce a price here by accident — a Crown
            Pine asking price may only appear via a deliberate, reviewed A1-specific
            change sourced from the seller. */}
        {activeListing && !isA1CarveOut && (() => {
          const listing = activeListing.listing;
          const acreageDiff = listing.acreage && parcel.acreage
            && Math.abs(Number(listing.acreage) - Number(parcel.acreage)) / Number(parcel.acreage) > 0.1;
          const countyDiff = listing.county && parcel.county
            && listing.county.toLowerCase() !== parcel.county.toLowerCase();
          return (<>
          <div style={{
            marginTop: '8px', padding: '8px 12px', borderRadius: '4px',
            background: REPORT.passWash, display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span style={{
              fontSize: '9px', fontWeight: 500, color: REPORT.passText,
              textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'monospace',
            }}>
              {activeListing.isOnSite ? 'For sale' : 'Nearby listing'}
            </span>
            {activeListing.priceStr && <span style={{ fontSize: '12px', fontWeight: 500, color: REPORT.heading }}>{activeListing.priceStr}</span>}
            {listing.acreage && <span style={{ fontSize: '11px', color: REPORT.secondary }}>{Number(listing.acreage).toLocaleString()} ac</span>}
            {activeListing.pricePerAcre && <span style={{ fontSize: '11px', color: REPORT.secondary }}>{activeListing.pricePerAcre}</span>}
            {activeListing.url && (
              <a className="briefing-no-print" href={activeListing.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: REPORT.accent, fontWeight: 500, textDecoration: 'none', marginLeft: 'auto' }}>
                View listing &rarr;
              </a>
            )}
          </div>
          {(acreageDiff || countyDiff) && (
            <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '4px', paddingLeft: '12px' }}>
              Note:
              {acreageDiff ? ` listing acreage (${Number(listing.acreage).toLocaleString()} ac) differs from appraisal (${Number(parcel.acreage).toLocaleString()} ac)` : ''}
              {acreageDiff && countyDiff ? ';' : ''}
              {countyDiff ? ` county attribution (${listing.county}) differs from appraisal (${parcel.county})` : ''}.
            </div>
          )}
          </>);
        })()}
        <SourceFootnote>Source: County appraisal district records</SourceFootnote>
      </div>

      {/* ── Site Access — BTM lens only in v1 ── */}
      {!isGrid && <>
      {mapSnapshots?.infrastructure && <div className="briefing-no-print"><MapSnapshot src={mapSnapshots.infrastructure} caption="Site Access — Highway, rail, and airport proximity" /></div>}
      <div ref={infraRef} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Site Access</SectionTitle>
          <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '10px', }}>
            {isUvalde ? 'FM 1023 borders the offered tract; US 90 and US 83 provide regional connections. Legal entrances, turning clearances and heavy-haul suitability remain unassessed.' : 'Site access governs three things: turbines and generators in, crews on site, clients through the door. Heavy components arrive by road or rail; construction and operations crews commute via the nearest regional airport; executives and investment committees route through the nearest major hub.'}
          </div>
        </div>
        {/* ProximityRings turned off for Site Access review — kept in tree for restoration
        <ProximityRings parcel={parcel} pipelines={pipelines} onFeatureHover={setHoverFeature} />
        */}

        {(() => {
          // Local spacing + badge primitives — keep uniform rhythm across rows and subgroups.
          const SUBGROUP_STYLE: React.CSSProperties = { marginBottom: '8px' };
          const SUBGROUP_HEADER_STYLE: React.CSSProperties = {
            fontSize: '9px', fontWeight: 600, color: REPORT.secondary,
            textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '2px',
          };
          const ROW_STYLE: React.CSSProperties = { marginBottom: '0' };
          const ROW_LINE_STYLE: React.CSSProperties = {
            display: 'flex', justifyContent: 'space-between', fontSize: '11px', gap: '8px',
            padding: '2px 0',
          };
          const ROW_LABEL_STYLE: React.CSSProperties = {
            color: REPORT.secondary, display: 'flex', alignItems: 'center', flexShrink: 0,
          };
          const ROW_VALUE_STYLE: React.CSSProperties = {
            fontWeight: 600, color: REPORT.heading, textAlign: 'right',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          };
          const DESC_STYLE: React.CSSProperties = {
            fontSize: '9px', color: REPORT.tertiary, marginTop: '0',
            paddingLeft: '12px', lineHeight: 1.3,
          };
          const renderBadge = (text: string, tone: 'strong' | 'caution') => (
            <span style={{
              fontSize: '7.5px', fontWeight: 700,
              color: tone === 'strong' ? REPORT.passText : REPORT.cautionText,
              background: tone === 'strong' ? REPORT.passWash : REPORT.cautionWash,
              textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'monospace',
              padding: '2px 5px', borderRadius: '2px',
              marginLeft: '6px', whiteSpace: 'nowrap',
            }}>{text}</span>
          );

          const hwyMi = parcel.nearest_highway_miles != null ? Number(parcel.nearest_highway_miles) : null;
          const hwyName = parcel.nearest_highway_name || '';
          const isMajorRoute = /^(US|I-|Interstate)\b/i.test(hwyName);
          const hwyBadge = isUvalde ? { text: 'Haul review needed', tone: 'caution' as const } : hwyMi == null ? null
            : hwyMi < 5 && isMajorRoute ? { text: 'Direct haul', tone: 'strong' as const }
            : hwyMi > 15 ? { text: 'Haul study', tone: 'caution' as const }
            : null;

          const railMi = parcel.nearest_railroad_miles != null ? Number(parcel.nearest_railroad_miles) : null;
          const railBadge = isPaces ? { text: 'Rail-to-haul corridor', tone: 'strong' as const }
            : railMi == null ? null
            : railMi < 2 ? { text: 'Rail-adjacent', tone: 'strong' as const }
            : railMi > 10 ? { text: 'No practical rail', tone: 'caution' as const }
            : null;

          // A1: the crew-commute field is Angelina County (LFK, GA/corp-jet, 17.7 mi);
          // the parcel's nearest_commercial_* now carries East Texas Rgnl (scheduled) at 84.9.
          const regMi = isA1CarveOut ? 17.7 : (parcel.nearest_commercial_airport_miles != null ? Number(parcel.nearest_commercial_airport_miles) : null);
          const regBadge = regMi != null && regMi > 75 ? { text: 'Remote commute', tone: 'caution' as const } : null;

          const hubMi = data.nearestFeatures?.majorAirport?.distance_miles != null ? Number(data.nearestFeatures.majorAirport.distance_miles) : null;
          const hubVisible = !!data.nearestFeatures?.majorAirport && data.nearestFeatures.majorAirport.faa_id !== data.nearestFeatures?.airport?.faa_id;
          const hubBadge = hubMi != null && hubMi > 150 ? { text: 'Distant hub', tone: 'caution' as const } : null;

          return (
            <>
              {(hwyMi != null || railMi != null) && (
                <div style={SUBGROUP_STYLE}>
                  <div style={SUBGROUP_HEADER_STYLE}>Delivery &middot; Heavy Haul &amp; Freight</div>
                  {hwyMi != null && (
                    <HoverLink feature={{ type: 'highway' }} style={{ display: 'block' }}>
                      <div style={ROW_STYLE}>
                        <div style={ROW_LINE_STYLE}>
                          <span style={ROW_LABEL_STYLE}>
                            Highway
                            {hwyBadge && renderBadge(hwyBadge.text, hwyBadge.tone)}
                          </span>
                          <span style={ROW_VALUE_STYLE}>
                            {isUvalde ? '1.3 mi (US 90 · offered tract edge)' : <>{hwyMi} mi{hwyName ? ` (${hwyName})` : ''}</>}
                          </span>
                        </div>
                        <div style={DESC_STYLE}>
                          {(parcel as any).structured_intel?.access_notes?.highway
                            ?? (hwyMi < 5
                            ? 'Direct haul route for turbine trailers, generator skids, and construction equipment.'
                            : hwyMi < 15
                            ? 'Short connecting route to primary haul corridor — confirm load ratings on intervening roads.'
                            : 'Extended connecting route — haul study and potential road upgrades required for oversize loads.')}
                        </div>
                      </div>
                    </HoverLink>
                  )}
                  {railMi != null && (
                    <HoverLink feature={{ type: 'railroad' }} style={{ display: 'block' }}>
                      <div style={ROW_STYLE}>
                        <div style={ROW_LINE_STYLE}>
                          <span style={ROW_LABEL_STYLE}>
                            Railroad
                            {railBadge && renderBadge(railBadge.text, railBadge.tone)}
                          </span>
                          <span style={ROW_VALUE_STYLE}>
                            {isUvalde ? '2.2 mi (Union Pacific · offered tract edge)' : <>{railMi} mi{parcel.nearest_railroad_owner ? ` (${parcel.nearest_railroad_owner})` : ''}{isMeiners && <span style={{ display: 'block', fontSize: '9px', color: REPORT.secondary }}>Representative parcel boundary to rail line</span>}</>}
                          </span>
                        </div>
                        <div style={DESC_STYLE}>
                          {(parcel as any).structured_intel?.access_notes?.railroad
                            ?? (isPaces
                            ? 'BNSF mainline parallels US-96 ~0.6 mi west of the assembly — rail and highway share the corridor at the property, so turbines and compressors offload at the rail-side interchange and roll directly to the parcel with no extended final-mile haul.'
                            : railMi! < 2
                            ? 'Rail-adjacent site — potential spur for bulk material or backup fuel delivery.'
                            : railMi! < 10
                            ? 'Rail freight reachable via short truck haul from nearest siding.'
                            : 'No practical rail access — all inbound freight will route by truck.')}
                        </div>
                      </div>
                    </HoverLink>
                  )}
                </div>
              )}

              {data.roadAccess?.summary && (() => {
                // Road-access screen (parcel_proximity 'road_access_screen'): TxDOT frontage
                // along the boundary, the network haul from the interstate, NBI structures and
                // the OSM ranch tracks inside the fence. Absent on every briefing without the row.
                const ra = data.roadAccess;
                const s = ra.summary;
                const fmt = (v: number | null | undefined, d = 0) => v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
                const fr = s.frontage_routes[0] || null;
                const primary = fr ? (ra.frontage.find(f => f.route === fr.route) || null) : null;
                const noFrontage = (ra.scope.sections || []).filter(sec => !s.frontage_routes.some(r => r.sections.includes(sec)));
                const nearestOther = ra.nearby_public_roads.find(r => !s.frontage_routes.some(f => f.route === r.route)) || null;
                const overpassClear = s.interchange_min_clearance_under_ft;
                const routeStructs = ra.structures.filter(x => x.on_route && !x.at_interchange);
                const frontStructs = ra.structures.filter(x => x.on_frontage && !x.on_route);
                const listed = ra.structures.filter(x => x.on_route || x.on_frontage || x.at_interchange);
                const entrance = fr ? (ra.gate_candidates.find(g => g.route === fr.route) || null) : null;
                const tracks = ra.tracks.filter(t => t.kind !== 'public');
                const tieIns = tracks.filter(t => t.tie_in);
                const seg0 = ra.haul_route.segments[0];
                const busiest = ra.haul_route.segments.reduce<typeof seg0 | null>((m, x) => (x.aadt || 0) > (m?.aadt || 0) ? x : m, null);
                const st = ra.traffic_station;
                const sideText = fr?.sides?.length ? `${fr.sides.join(' and ')} line${fr.sides.length > 1 ? 's' : ''}` : 'boundary';
                const cell: React.CSSProperties = { padding: '3px 6px', fontSize: '9px', color: REPORT.body, borderBottom: `1px solid ${REPORT.sand}`, verticalAlign: 'top' };
                const head: React.CSSProperties = { ...cell, fontWeight: 700, color: REPORT.heading, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '8px' };
                return (
                  <div style={SUBGROUP_STYLE}>
                    <div style={SUBGROUP_HEADER_STYLE}>Road Access Screen &middot; TxDOT Inventory, Bridge Inventory &amp; OSM</div>
                    <div style={ROW_STYLE}>
                      <div style={ROW_LINE_STYLE}>
                        <span style={ROW_LABEL_STYLE}>
                          Public frontage
                          {fr ? renderBadge(fr.maintainer ? `${fr.maintainer}-maintained` : fr.system_label, 'strong') : renderBadge('No public frontage', 'caution')}
                        </span>
                        <span style={ROW_VALUE_STYLE}>{fr ? `${fmt(fr.frontage_mi, 2)} mi (${fr.route})` : `None within ${ra.scope.frontage_tolerance_m} m`}</span>
                      </div>
                      <div style={DESC_STYLE}>
                        {fr && primary
                          ? `${fr.route} (${fr.system_label}, ${fr.maintainer || 'maintainer not recorded'}) runs ${fmt(fr.frontage_mi, 2)} mi along the ${sideText} of section${fr.sections.length > 1 ? 's' : ''} ${fr.sections.join(' and ')}: ${primary.lanes ?? '—'} lanes, ${primary.surface_width_ft ? `${primary.surface_width_ft} ft wide` : 'width not recorded'}${primary.shoulder_width_ft ? ` with ${primary.shoulder_width_ft}-ft shoulders` : ''}, ${roadSurfaceShort(primary.surface) || primary.surface.toLowerCase()}${primary.speed_limit_mph ? `, ${primary.speed_limit_mph} mph` : ''}.`
                          : `No TxDOT-inventoried public road runs along the block boundary within ${ra.scope.frontage_tolerance_m} m.`}
                        {noFrontage.length > 0 && ` Section${noFrontage.length > 1 ? 's' : ''} ${noFrontage.join(' and ')} ${noFrontage.length > 1 ? 'have' : 'has'} no public frontage.`}
                        {nearestOther && ` Nearest other public road: ${nearestOther.route} (${nearestOther.system_label.toLowerCase()}${nearestOther.maintainer ? `, ${nearestOther.maintainer.toLowerCase()}-maintained` : ''}) ${fmt(nearestOther.min_distance_mi, 2)} mi from the boundary.`}
                        {s.public_roads_inside.length > 0 && ` Public road inside the fence: ${s.public_roads_inside.map(r => `${r.route} ${fmt(r.inside_mi, 2)} mi`).join(', ')}.`}
                      </div>
                    </div>
                    <div style={ROW_STYLE}>
                      <div style={ROW_LINE_STYLE}>
                        <span style={ROW_LABEL_STYLE}>
                          Heavy-haul route
                          {s.structures_posted > 0
                            ? renderBadge(`${s.structures_posted} weight-limited`, 'caution')
                            : (overpassClear != null && overpassClear < 16 ? renderBadge('Over-height check', 'caution') : renderBadge('No weight limits', 'strong'))}
                        </span>
                        <span style={ROW_VALUE_STYLE}>{fmt(s.haul_route_mi, 2)} mi ({s.haul_route_from})</span>
                      </div>
                      <div style={DESC_STYLE}>
                        {`Network distance from ${s.haul_route_from} to the first point where ${ra.scope.route} meets the block (${fmt(s.haul_route_straight_mi, 2)} mi straight-line)`}
                        {seg0 && s.haul_route_segments === 1
                          ? `, entirely on ${seg0.route}: ${seg0.lanes ?? '—'} lanes, ${seg0.surface_width_ft ? `${seg0.surface_width_ft} ft wide` : 'width not recorded'}, ${roadSurfaceShort(seg0.surface) || seg0.surface.toLowerCase()}.`
                          : ` over ${s.haul_route_segments} TxDOT segments (minimum ${s.haul_route_min_lanes} lanes and ${s.haul_route_min_surface_width_ft} ft wide; ${s.haul_route_surfaces.map(x => x.toLowerCase()).join(', ')}).`}
                        {busiest?.aadt ? ` Average daily traffic on ${busiest.route} is ${fmt(busiest.aadt)} (${busiest.aadt_year})${busiest.truck_pct ? `, ${fmt(busiest.truck_pct, 0)}% of it trucks${busiest.aadt_combination != null ? ` — ${fmt(busiest.aadt_combination)} tractor-trailers and ${fmt(busiest.aadt_single_unit)} single-unit trucks a day; pickups count as cars` : ''}` : ''}.` : ''}
                        {st && ` TxDOT's count station on ${st.on_road} put it at ${fmt(st.latest_adt)} a day in ${st.latest_year}${st.peak_year !== st.latest_year ? `, down from about ${fmt(Math.round(st.peak_adt / 100) * 100)} at the ${st.peak_year} peak` : ''}.`}
                        {entrance && (entrance.network_mi_from_interchange != null
                          ? ` The mapped ranch entrance off ${entrance.route} is ${fmt(entrance.network_mi_from_interchange, 2)} road miles from the interchange${entrance.network_mi_from_interchange > s.haul_route_mi ? `, ${fmt(entrance.network_mi_from_interchange - s.haul_route_mi, 2)} mi past the first frontage point` : ''}.`
                          : ` The mapped ranch entrance off ${entrance.route} is ${fmt(entrance.straight_mi_from_interchange, 2)} mi straight-line from the interchange.`)}
                        {routeStructs.length === 0
                          ? ' No bridge or culvert stands between the interchange and the gate.'
                          : ` ${routeStructs.length} structure${routeStructs.length > 1 ? 's' : ''} on the route: ${routeStructs.map(x => `${x.facility} over ${x.feature_crossed} (${x.year_built || 'year not recorded'}, ${x.posting_label === 'not posted' ? 'no weight limit' : x.posting_label})`).join('; ')}.`}
                        {overpassClear != null && ` The ${ra.scope.interchange} mainlanes over ${ra.scope.route} carry ${feetInches(overpassClear)} of clearance underneath: loads taller than that cannot cross under the interstate here and must approach on the near-side frontage road.`}
                      </div>
                    </div>
                    <div style={ROW_STYLE}>
                      <div style={ROW_LINE_STYLE}>
                        <span style={ROW_LABEL_STYLE}>Structures on the frontage</span>
                        <span style={ROW_VALUE_STYLE}>{frontStructs.length ? `${frontStructs.length} · ${frontStructs.every(x => !x.posted) ? 'no weight limit' : `${frontStructs.filter(x => x.posted).length} weight-limited`}` : 'None'}</span>
                      </div>
                      <div style={DESC_STYLE}>
                        {frontStructs.length
                          ? `${frontStructs.map(x => `${x.facility} over ${x.feature_crossed}: ${x.type}, built ${x.year_built ?? '—'}${x.spans && x.max_span_ft ? `, ${x.spans} × ${x.max_span_ft}-ft spans` : ''}${x.roadway_width_ft ? `, ${x.roadway_width_ft}-ft roadway` : ''}, ${x.posting_label === 'not posted' ? 'no weight limit' : x.posting_label}${x.condition_class ? `, condition ${x.condition_class.toLowerCase()}` : ''}${x.inspected ? `, inspected ${x.inspected}` : ''}`).join('; ')}.`
                          : `No TxDOT bridge-inventory structure on the ${ra.scope.route} frontage.`}
                      </div>
                    </div>
                    <div style={ROW_STYLE}>
                      <div style={ROW_LINE_STYLE}>
                        <span style={ROW_LABEL_STYLE}>Ranch roads inside the fence</span>
                        <span style={ROW_VALUE_STYLE}>{fmt(s.tracks_inside_mi, 2)} mi · {s.tracks_inside} mapped</span>
                      </div>
                      <div style={DESC_STYLE}>
                        {tracks.length
                          ? `${tracks.length} OpenStreetMap ${tracks.length === 1 ? 'way' : 'ways'} (${[...new Set(tracks.map(t => t.kind))].join(', ')}) inside the fence; ${tieIns.length ? `${tieIns.length} ${tieIns.length === 1 ? 'ties' : 'tie'} into a public road: ${tieIns.map(t => `a ${t.kind} from ${t.tie_in!.route} (${t.tie_in!.system === 'CR' ? 'county road' : t.tie_in!.system}) across section${t.sections.length > 1 ? 's' : ''} ${t.sections.join(' and ')}, ${fmt(t.inside_mi, 2)} mi inside`).join('; ')}.` : 'none ties into a public road.'}`
                          : 'No ranch road is mapped inside the fence.'}
                        {' Tracks are an OpenStreetMap feature, not a title or easement record.'}
                      </div>
                    </div>
                    {listed.length > 0 && (
                      <div style={{ marginTop: '6px', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ ...head, textAlign: 'left' }}>Structure</th>
                              <th style={{ ...head, textAlign: 'left' }}>Crosses</th>
                              <th style={{ ...head, textAlign: 'left' }}>Type</th>
                              <th style={{ ...head, textAlign: 'right' }}>Built</th>
                              <th style={{ ...head, textAlign: 'left' }}>Weight limit</th>
                              <th style={{ ...head, textAlign: 'right' }}>Clear. under</th>
                              <th style={{ ...head, textAlign: 'left' }}>Condition</th>
                              <th style={{ ...head, textAlign: 'left' }}>Where</th>
                              <th style={{ ...head, textAlign: 'left' }}>NBI</th>
                            </tr>
                          </thead>
                          <tbody>
                            {listed.map(x => (
                              <tr key={x.nbi_id}>
                                <td style={cell}>{x.facility}</td>
                                <td style={cell}>{x.feature_crossed}</td>
                                <td style={cell}>{x.type}</td>
                                <td style={{ ...cell, textAlign: 'right' }}>{x.year_built ?? '—'}</td>
                                <td style={cell}>{x.posted ? x.posting_label : 'none'}</td>
                                <td style={{ ...cell, textAlign: 'right' }}>{x.min_vert_clear_under_ft != null ? feetInches(x.min_vert_clear_under_ft) : '—'}</td>
                                <td style={cell}>{x.condition_class || '—'}</td>
                                <td style={cell}>{x.at_interchange ? 'Interchange' : x.on_route ? 'Haul route' : 'Frontage'}</td>
                                <td style={{ ...cell, fontFamily: 'monospace' }}>{x.nbi_id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div style={{ ...DESC_STYLE, marginTop: '4px' }}>
                      Source: TxDOT Roadway Inventory ({ra.sources.roadway_inventory.fetched_at}), TxDOT traffic count stations{st ? ` (station ${st.id}, ${st.series.length}-year history)` : ''}, TxDOT bridge inventory / NBI ({ra.sources.bridges.fetched_at}), OpenStreetMap ({ra.sources.osm.fetched_at}). Traffic figures are average daily counts; trucks are the six-tire-and-larger classes (pickups count as cars). Maintained mileage is TxDOT-certified, not a title opinion; posting and clearance are inventory values to confirm with the TxDOT district before a permitted move. Map layers: Haul Route, Frontage &amp; Ranch Roads.
                    </div>
                  </div>
                );
              })()}

              {regMi != null && (() => {
                const ncRaw = data.nearestFeatures?.nearestCommercial;
                // A1: the parcel column names Angelina County (LFK, 17.7 mi) but
                // nearestCommercial is the larger-field query (East Texas Rgnl · GGG,
                // 85 mi) — merging them printed "Angelina County · GGG · 10,000 ft".
                // Use the record whose name matches the parcel column for code/runway.
                const nc = isA1CarveOut
                  ? (((ncRaw?.name || '').toUpperCase() === (parcel.nearest_commercial_airport_name || '').toUpperCase())
                      ? ncRaw
                      : (data.nearestFeatures?.airport as typeof ncRaw))
                  : ncRaw;
                const ncRwy = longestPavedRunway(nc?.runway_data as RunwayLike[] | null);
                return (
                  <div style={SUBGROUP_STYLE}>
                    <div style={SUBGROUP_HEADER_STYLE}>Workforce &middot; Crew &amp; Vendor Commute</div>
                    <HoverLink feature={{ type: 'airport' }} style={{ display: 'block' }}>
                      <div style={ROW_STYLE}>
                        <div style={ROW_LINE_STYLE}>
                          <span style={ROW_LABEL_STYLE}>
                            Regional Airport
                            {regBadge && renderBadge(regBadge.text, regBadge.tone)}
                          </span>
                          <span style={ROW_VALUE_STYLE}>
                            {isA1CarveOut
                              ? `17.7 mi (Angelina County · LFK · 5,400 ft — GA/corp-jet)`
                              : <>{regMi} mi{parcel.nearest_commercial_airport_name ? ` (${titleCaseAirport(parcel.nearest_commercial_airport_name)}${nc?.faa_id ? ` · ${nc.faa_id}` : ''}${ncRwy ? ` · ${fmtRunway(ncRwy)}` : ''})` : ''}</>}
                          </span>
                        </div>
                        <div style={DESC_STYLE}>
                          {isUvalde ? 'Airport-record distance is straight-line. Confirm flight service and road travel time for crew planning.' : regMi < 30
                            ? 'Serviceable for rotating construction crews, OEM field techs, and out-of-state vendors.'
                            : regMi < 75
                            ? 'Workable commute for crew rotations with vehicle transport from airport.'
                            : 'Distant from regional air service — plan on extended ground transit for fly-in workforce.'}
                          {isA1CarveOut ? ' Nearest scheduled commercial service: East Texas Rgnl (GGG) 84.9 mi; major hub IAH ~97 mi.' : ''}
                        </div>
                      </div>
                    </HoverLink>
                  </div>
                );
              })()}

              {(() => {
                // Corp-jet strips (paved ≥5,000 ft). We surface up to three so the IC can
                // see the closest plus a backup. The closest is often the same airport as
                // the FAR-139 regional above (e.g. Goff WV → CKB serves both crew-commute and
                // private-jet access); that's a feature, not a duplicate, because the two
                // sections answer different questions ("can crews fly in commercial?" vs
                // "can a Citation/Global land here?"). Dedupe only across the three
                // private-jet rows themselves.
                if (isUvalde) return (
                  <div style={SUBGROUP_STYLE}>
                    <div style={SUBGROUP_HEADER_STYLE}>Adjacent Airport &amp; Layout Constraints</div>
                    <HoverLink feature={{ type: 'airport' }} style={{ display: 'block' }}>
                      <div style={ROW_LINE_STYLE}>
                        <span style={ROW_LABEL_STYLE}>Garner Field (UVA)</span>
                        <span style={ROW_VALUE_STYLE}>Adjoins the offered tract</span>
                      </div>
                      <div style={DESC_STYLE}>Garner Field adjoins the western boundary and is a primary layout constraint. Check airport zoning, FAA notice requirements and runway plans for each building, stack and construction crane. The Part 77 screen above shows the affected areas by height.</div>
                    </HoverLink>
                  </div>
                );
                const pr = data.nearestFeatures?.pavedRunway;
                const alt = data.nearestFeatures?.altPavedRunway;
                const alt2 = data.nearestFeatures?.altPavedRunway2;
                const seen = new Set<string>();
                const candidates = [pr, alt, alt2]
                  .filter((r): r is NonNullable<typeof r> => !!r && r.distance_miles != null)
                  .filter(r => {
                    const key = r.faa_id || `${r.name}-${r.distance_miles}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });
                if (candidates.length === 0) return null;
                return (
                  <div style={SUBGROUP_STYLE}>
                    <div style={SUBGROUP_HEADER_STYLE}>Private Plane Access</div>
                    {isMeiners && <div style={DESC_STYLE}>Distances from the representative parcel centroid to each airport reference point. The Checklist’s 8.9 mi to Cotulla measures from the representative parcel boundary to the runway centerline.</div>}
                    {candidates.map((r, idx) => {
                      const mi = Number(r.distance_miles);
                      const rwy = longestPavedRunway(r.runway_data as RunwayLike[] | null);
                      const badge = mi < 10 ? { text: 'Strip within 10 mi', tone: 'strong' as const } : null;
                      const narrow = rwy && rwy.width != null && rwy.width < 75;
                      const label = idx === 0 ? 'Closest Strip' : idx === 1 ? 'Alt Strip' : '3rd Option';
                      const baseDesc = mi < 15
                        ? 'Paved 5,000 ft+ strip within 15 mi — investor tours and OEM commissioning crews can fly direct.'
                        : mi < 40
                        // A1: the closest strip IS the regional airport above (LFK, GA) —
                        // "the regional commercial airport" would mislabel the same GA field.
                        ? (isA1CarveOut
                            ? 'Closest paved 5,000 ft+ runway for IC visits and time-critical parts; scheduled commercial service is at East Texas Rgnl (GGG), 84.9 mi.'
                            : 'Closest paved 5,000 ft+ runway for IC visits and time-critical parts; shorter ground transit than the regional commercial airport.')
                        : 'Backup corp-jet option — meaningful drive, but adds redundancy if the closer strip is weather-grounded or short.';
                      const desc = narrow
                        ? `${baseDesc} Runway is narrow (${rwy!.width} ft) — confirms with operators flying larger jets (Global/G650 class prefer ≥100 ft).`
                        : baseDesc;
                      return (
                        <HoverLink key={r.faa_id || idx} feature={{ type: 'airport' }} style={{ display: 'block' }}>
                          <div style={ROW_STYLE}>
                            <div style={ROW_LINE_STYLE}>
                              <span style={ROW_LABEL_STYLE}>
                                {label}
                                {badge && renderBadge(badge.text, badge.tone)}
                              </span>
                              <span style={ROW_VALUE_STYLE}>
                                {mi} mi ({titleCaseAirport(r.name)}{r.faa_id ? ` · ${r.faa_id}` : ''}{rwy ? ` · ${fmtRunway(rwy)}` : ''})
                              </span>
                            </div>
                            <div style={DESC_STYLE}>{desc}</div>
                          </div>
                        </HoverLink>
                      );
                    })}
                  </div>
                );
              })()}

              {hubVisible && hubMi != null && (() => {
                const ma = data.nearestFeatures!.majorAirport!;
                const maRwy = longestPavedRunway(ma.runway_data as RunwayLike[] | null);
                return (
                <div style={{ ...SUBGROUP_STYLE, marginBottom: '4px' }}>
                  <div style={SUBGROUP_HEADER_STYLE}>Commercial Hub &middot; Connecting Flights</div>
                  <HoverLink feature={{ type: 'airport' }} style={{ display: 'block' }}>
                    <div style={ROW_STYLE}>
                      <div style={ROW_LINE_STYLE}>
                        <span style={ROW_LABEL_STYLE}>
                          Major Hub
                          {hubBadge && renderBadge(hubBadge.text, hubBadge.tone)}
                        </span>
                        <span style={ROW_VALUE_STYLE}>
                          {hubMi} mi ({titleCaseAirport(ma.name)}{ma.faa_id ? ` · ${ma.faa_id}` : ''}{maRwy ? ` · ${fmtRunway(maRwy)}` : ''})
                        </span>
                      </div>
                      <div style={DESC_STYLE}>
                        {isUvalde ? 'Confirm flight service and road travel time. This is an airport-record proximity measure, not a driving distance.' : hubMi < 60
                          ? 'Major commercial hub within standard commute — out-of-state vendors and clients can connect through this airport, then ground-transit to site.'
                          : hubMi < 150
                          ? 'Major hub a reasonable drive for connecting flights from coastal investors, international OEMs, and out-of-state crew. Private planes use the closer corp-jet strips above.'
                          : 'Distant from major hub — most fly-in traffic should route through the regional airport above or a closer corp-jet strip.'}
                      </div>
                    </div>
                  </HoverLink>
                </div>
                );
              })()}
            </>
          );
        })()}

        <div style={{ marginTop: '6px' }}>
          <SourceFootnote>Source: FHWA, FRA, FAA</SourceFootnote>
        </div>
      </div>
      </>}

      {/* Operator Supply Story moved up to the kill-sheet (right after Site
          Intelligence). Old position retained as a comment for context. */}

      {/* ── Gas Supply & Logistics ── (still rendered for AOI parcels as "tap candidates" — pipeline data is non-binding but useful for delivery planning) */}
      {!isGrid && <GasSupplyLogisticsSection ref={gasInfraRef} data={data} parcel={parcel} />}

      {/* ── Power Infrastructure & Capacity ── (grid lens renders it earlier, right after the node section) */}
      {!isGrid && <PowerInfraSection ref={powerInfraRef} data={data} parcel={parcel} />}

      {/* ── Fiber & Network Connectivity ── */}
      <FiberConnectivitySection ref={fiberConnectivityRef} data={data} parcel={parcel} />

      {/* ── Water Sourcing ── (BTM logistics framing; grid renders red-flag screens instead) */}
      {!isGrid && <WaterSourcingSection ref={waterSourcingRef} data={data} parcel={parcel} />}

      {/* ── Mine Hazards (Atwater Intelligence, WV-only data right now) ── grid lens surfaces mine voids via the red-flag screens */}
      {!isGrid && <MineHazardsSection ref={mineHazardsRef} parcel={parcel} redacted={isRedacted} />}

      {/* ── Risk Register ── */}
      {structuredRisks.length > 0 && (
        <div ref={riskRef} className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
          <div className="briefing-section-head">
            <SectionTitle>Risk Register</SectionTitle>
            {(() => { const intro = isUvalde ? 'Resolve gas delivery, land control and the airport-compatible layout before committing to a project scale.' : isMeiners ? 'Surface control is the first hurdle; gas delivery and flood-aware layout remain open behind it.' : generateRiskIntro(structuredRisks); return intro ? <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '10px', }}>{intro}</div> : null; })()}
          </div>
          <table style={{ width: '100%', fontSize: '10.5px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${REPORT.rule}` }}>
                <th style={{ ...TH, textAlign: 'left' }}>Risk</th>
                <th style={{ ...TH, textAlign: 'center', width: '60px' }}>Severity</th>
                <th style={{ ...TH, textAlign: 'left' }}>Mitigation</th>
              </tr>
            </thead>
            <tbody>
              {structuredRisks.map((risk, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${REPORT.rule}80` }}>
                  <td style={{ padding: '3px 4px', color: REPORT.body }}>{risk.description}</td>
                  <td style={{ textAlign: 'center', padding: '3px 4px' }}>
                    <span style={{
                      fontSize: '9px', padding: '1px 6px', borderRadius: '2px',
                      background: risk.severity === 'HIGH' ? REPORT.failWash : risk.severity === 'LOW' ? REPORT.surface : REPORT.cautionWash,
                      color: risk.severity === 'HIGH' ? REPORT.failText : risk.severity === 'LOW' ? REPORT.secondary : REPORT.cautionText, fontWeight: 600,
                    }}>
                      {risk.severity}
                    </span>
                  </td>
                  <td style={{ padding: '3px 4px', color: REPORT.secondary, fontSize: '9px' }}>
                    {risk.mitigation}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Data Confidence (screen only — print renders an earlier copy near the kill-sheet) ── */}
      <div ref={confRef} className="briefing-section briefing-no-print" style={{ background: REPORT.white, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Data Confidence</SectionTitle>
        </div>
        {isGrid && gridScore && (() => {
          const prov = gridScore.components.filter(c => c.tier === 'provisional');
          if (prov.length === 0) return null;
          const pts = prov.reduce((s, c) => s + c.points, 0);
          const max = prov.reduce((s, c) => s + c.max, 0);
          return (
            <div style={{ fontSize: '10px', color: REPORT.secondary, marginBottom: '10px' }}>
              Grid lens: provisional factors ({prov.map(c => c.label.toLowerCase()).join(', ')}) are held at neutral placeholders — they contribute {pts.toFixed(1)} pts of the {gridScore.total} against a {max}-pt max.
            </div>
          );
        })()}
        {(['confirmed', 'preliminary', 'assumed', 'pending'] as ConfidenceLevel[]).map(level => {
          const group = confidenceItems.filter(item => item.confidence === level);
          if (group.length === 0) return null;
          return (
            <div key={level} className="no-break" style={{ marginBottom: '10px', background: REPORT.surface, borderRadius: '6px', padding: '8px 10px', border: `1px solid ${REPORT.rule}` }}>
              <div style={{
                fontSize: '9px', fontWeight: 700, color: CONFIDENCE_COLORS[level],
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px',
              }}>
                {CONFIDENCE_LABELS[level]}
              </div>
              {group.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', fontSize: '10px', padding: '1px 0',
                  borderBottom: i < group.length - 1 ? `1px solid ${REPORT.rule}80` : 'none',
                }}>
                  <span style={{ width: '35%', color: REPORT.secondary, flexShrink: 0 }}>{item.field}</span>
                  <span style={{ width: '30%', fontWeight: 600, color: REPORT.heading }}>{item.value}</span>
                  <span style={{ flex: 1, color: REPORT.tertiary, fontSize: '9px' }}>{item.source}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── DD Summary (print only) ── */}
      <div className="briefing-print-only briefing-page-break" style={{ display: 'none', background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <SectionTitle>Due Diligence Summary</SectionTitle>
        <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px', }}>
          {ddAddressedCount} of {ddAllItems.length} items addressed across {ddCategories.length} categories. {ddInquiryCount > 0 ? `${ddInquiryCount} items require site-specific inquiry.` : ''} {ddFlaggedCount > 0 ? `${ddFlaggedCount} items flagged for review.` : ''}
        </div>
        <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${REPORT.highlight}` }}>
              <th style={{ textAlign: 'left', padding: '4px 0', fontSize: '9px', fontWeight: 700, color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Category</th>
              <th style={{ textAlign: 'center', padding: '4px 8px', fontSize: '9px', fontWeight: 700, color: REPORT.pass, textTransform: 'uppercase', letterSpacing: '0.08em', width: '70px' }}>Addressed</th>
              <th style={{ textAlign: 'center', padding: '4px 8px', fontSize: '9px', fontWeight: 700, color: REPORT.caution, textTransform: 'uppercase', letterSpacing: '0.08em', width: '70px' }}>Inquiry</th>
              <th style={{ textAlign: 'center', padding: '4px 8px', fontSize: '9px', fontWeight: 700, color: REPORT.fail, textTransform: 'uppercase', letterSpacing: '0.08em', width: '70px' }}>Flagged</th>
            </tr>
          </thead>
          <tbody>
            {ddCategories.map((cat, i) => {
              const addressed = cat.items.filter(d => d.status === 'ADDRESSED').length;
              const inquiry = cat.items.filter(d => d.status === 'REQUIRES INQUIRY').length;
              const flagged = cat.items.filter(d => d.status === 'FLAGGED').length;
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${REPORT.rule}` }}>
                  <td style={{ padding: '5px 0', color: REPORT.heading, fontWeight: 500 }}>{cat.name}</td>
                  <td style={{ textAlign: 'center', padding: '5px 8px', color: addressed > 0 ? REPORT.pass : REPORT.tertiary, fontWeight: 600 }}>{addressed}</td>
                  <td style={{ textAlign: 'center', padding: '5px 8px', color: inquiry > 0 ? REPORT.caution : REPORT.tertiary, fontWeight: 600 }}>{inquiry || '\u2014'}</td>
                  <td style={{ textAlign: 'center', padding: '5px 8px', color: flagged > 0 ? REPORT.fail : REPORT.tertiary, fontWeight: 600 }}>{flagged || '\u2014'}</td>
                </tr>
              );
            })}
            <tr style={{ borderTop: `2px solid ${REPORT.highlight}` }}>
              <td style={{ padding: '5px 0', color: REPORT.heading, fontWeight: 700 }}>Total</td>
              <td style={{ textAlign: 'center', padding: '5px 8px', color: REPORT.pass, fontWeight: 700 }}>{ddAddressedCount}</td>
              <td style={{ textAlign: 'center', padding: '5px 8px', color: ddInquiryCount > 0 ? REPORT.caution : REPORT.tertiary, fontWeight: 700 }}>{ddInquiryCount || '\u2014'}</td>
              <td style={{ textAlign: 'center', padding: '5px 8px', color: ddFlaggedCount > 0 ? REPORT.fail : REPORT.tertiary, fontWeight: 700 }}>{ddFlaggedCount || '\u2014'}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: '16px', fontSize: '9px', color: REPORT.tertiary, lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600, marginBottom: '2px' }}>Status definitions</div>
          <div><strong style={{ color: REPORT.pass }}>Addressed</strong> &mdash; data-backed assessment from public records and computed analysis.</div>
          <div><strong style={{ color: REPORT.caution }}>Requires Inquiry</strong> &mdash; standard diligence items requiring site-specific or third-party verification.</div>
          <div><strong style={{ color: REPORT.fail }}>Flagged</strong> &mdash; data gap or condition requiring attention before proceeding.</div>
        </div>
        <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: `1px solid ${REPORT.rule}`, fontSize: '8px', color: REPORT.tertiary, lineHeight: 1.5 }}>
          This report is a pre-feasibility screening based on publicly available data. It does not constitute a Phase I Environmental Site Assessment, title opinion, survey, or engineering study. All findings should be independently verified through site-specific due diligence before making investment decisions. Data sources: {parcel.state === 'TX' ? 'TX RRC, ' : ''}EIA, {isMeiners ? 'TWDB State Flood Plan non-regulatory 1% planning screen' : 'FEMA NFHL'}, FCC BDC, EPA (Green Book, EJScreen, GHGRP), HIFLD, FERC, PHMSA, USGS, USFWS.
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="briefing-footer-print no-break" style={{
        background: REPORT.heading, padding: '16px 28px', marginTop: '3px',
        fontSize: '9px', textAlign: 'center',
      }}>
        <div style={{ letterSpacing: '0.08em', fontFamily: 'monospace', color: REPORT.secondary }}>Atwater Energy &middot; {today} &middot; Confidential</div>
        <div style={{ marginTop: '3px', color: REPORT.tertiary }}>Pre-feasibility screening only &middot; {parcel.state === 'TX' ? 'TX RRC, ' : ''}EIA, {isMeiners ? 'TWDB non-regulatory 1% planning screen' : 'FEMA'}, FCC, EPA, HIFLD, FERC, PHMSA &middot; Atwater overlays</div>
      </div>
      </>
      )}
    </div>
  );
};
